import type { Tool } from '@anthropic-ai/sdk/resources/messages';
import { AppointmentService } from '../../api/services/AppointmentService';
import { AnalyticsDashboardService } from '../../api/services/AnalyticsDashboardService';
import { MasterDataService } from '../../api/services/MasterDataService';
import { RoutemizationService } from '../../api/services/RoutemizationService';
import { ContactService } from '../../api/services/ContactService';
import { ContactAddressService } from '../../api/services/ContactAddressService';
import { BookingFormService } from '../../api/services/BookingFormService';
import { BookingFormSettingService } from '../../api/services/BookingFormSettingService';
import { BookingFormUserService } from '../../api/services/BookingFormUserService';
import { BookingFormQuestionService } from '../../api/services/BookingFormQuestionService';
import { PublicBookingFormService } from '../../api/services/PublicBookingFormService';
import { GoogleCalendarService } from '../../api/services/GoogleCalendarService';
import { RequestsService } from '../../api/services/RequestsService';
import { UserService } from '../../api/services/UserService';
import { ApiError } from '../../api/core/ApiError';
import type { Routemize_DTOs_ContactDTO } from '../../api/models/Routemize_DTOs_ContactDTO';
import type { Routemize_DTOs_ContactAddressDTO } from '../../api/models/Routemize_DTOs_ContactAddressDTO';
import type { Routemize_DTOs_TeamMemberListDto } from '../../api/models/Routemize_DTOs_TeamMemberListDto';
import type { Routemize_DTOs_AppointmentDTO } from '../../api/models/Routemize_DTOs_AppointmentDTO';
import type { Routemize_DTOs_AppointmentListItem } from '../../api/models/Routemize_DTOs_AppointmentListItem';
import type { Routemize_DTOs_SearchParameters_PagedSortedAndFilteredRequestDto_1 } from '../../api/models/Routemize_DTOs_SearchParameters_PagedSortedAndFilteredRequestDto_1';
import type { Routemize_DTOs_BookingFormDto } from '../../api/models/Routemize_DTOs_BookingFormDto';
import type { Routemize_DTOs_BookingFormSettingDto } from '../../api/models/Routemize_DTOs_BookingFormSettingDto';
import type { Routemize_DTOs_BookingFormQuestionDto } from '../../api/models/Routemize_DTOs_BookingFormQuestionDto';
import type { Routemize_DTOs_CalendarSyncStatusDto } from '../../api/models/Routemize_DTOs_CalendarSyncStatusDto';
import { Routemize_Constants_FieldType } from '../../api/models/Routemize_Constants_FieldType';
import type { Routemize_DTOs_CreateRequestsDTO } from '../../api/models/Routemize_DTOs_CreateRequestsDTO';
import type { Routemize_DTOs_UpdateRequestsDTO } from '../../api/models/Routemize_DTOs_UpdateRequestsDTO';
import type { Routemize_DTOs_RequestSearchResponse } from '../../api/models/Routemize_DTOs_RequestSearchResponse';
import type { Routemize_DTOs_CreateAppointmentDTO } from '../../api/models/Routemize_DTOs_CreateAppointmentDTO';
import type { Routemize_DTOs_CreateContactDTO } from '../../api/models/Routemize_DTOs_CreateContactDTO';
import type { Routemize_DTOs_SlotSuggestionDTO } from '../../api/models/Routemize_DTOs_SlotSuggestionDTO';
import { Routemize_Constants_WorkingHourStatus } from '../../api/models/Routemize_Constants_WorkingHourStatus';
import { Routemize_Constants_RequestStatus } from '../../api/models/Routemize_Constants_RequestStatus';
import { Routemize_Constants_ScheduleMethod } from '../../api/models/Routemize_Constants_ScheduleMethod';
import { Routemize_Constants_RequestOrigin } from '../../api/models/Routemize_Constants_RequestOrigin';
import { Routemize_Constants_BookingFormStatus } from '../../api/models/Routemize_Constants_BookingFormStatus';
import { Routemize_Constants_SlotRejectionReason } from '../../api/models/Routemize_Constants_SlotRejectionReason';
import { Routemize_Constants_EventNature } from '../../api/models/Routemize_Constants_EventNature';
import { resolvePreferredTimeOption, type PreferredTimeRangeOption } from '../../utils/preferredTimeRanges';
import { resolveSchedulingErrorMessage, isOpaqueBackendMessage, isRawTechnicalError } from '../../utils/bookingErrorMessage';
import { isUsOrCanada, normalizeCountryToIso, isValidRegionCode } from '../../utils/addressRegions';
import { notesManager } from '../notes/NotesManager';
import { appointmentReasons, briefingNotesFromBundle, joinNoteContents } from '../notes/noteKinds';
import type { Routemize_DTOs_SchedulingResponse } from '../../api/models/Routemize_DTOs_SchedulingResponse';
import type { Routemize_DTOs_MasterData } from '../../api/models/Routemize_DTOs_MasterData';
import { AvailabilityOverrideManager } from '../availability/AvailabilityOverrideManager';
import { AppointmentManager } from '../appointments/AppointmentManager';
// Type-only + method-level usage keeps the routyTools -> aiSummaryService ->
// appointmentBriefingAgent -> RoutyAgent -> routyTools import cycle inert (no
// module-init evaluation crosses the cycle).
import { AISummaryService, type AppointmentSummaryData } from '../../services/aiSummaryService';
import { UserRoleConfigManager } from '../users/UserRoleConfig';
import type { SlotRescheduleCandidate } from '../appointments/slotRescheduleCandidateTypes';
import { notifyAppointmentsChanged } from '../appointments/appointmentEvents';
import { rankCandidatesForSlot } from '../../components/timeline/utils/slotCandidateRanking';
import { GeocodingService } from '../../services/geocodingService';
import { searchPlaces, getPlaceDetails, getCachedPlace, type PlaceDetails, type PlaceSearchResult } from '../../services/placesSearchService';
import { googleMapsDistanceService } from '../../services/googleMapsDistanceService';
import { Routemize_Constants_AppointmentStatus } from '../../api/models/Routemize_Constants_AppointmentStatus';
import { Routemize_DTOs_TimelineItemType } from '../../api/models/Routemize_DTOs_TimelineItemType';
import type { Routemize_DTOs_UpdateAppointmentDTO } from '../../api/models/Routemize_DTOs_UpdateAppointmentDTO';
import {
  findStopSlotsForDay,
  findStopSlotsMultiDay,
  findOpenGaps,
  isDayAvailable,
  type StopSlot,
} from '../../utils/stopGapFinder';
// The timeline's own gap threshold. Shared deliberately: an audit that counts
// windows the timeline doesn't draw is an audit the operator can't reconcile
// with what's on their screen.
import { MIN_GAP_MINUTES } from '../../components/timeline/utils/constants';
import {
  tenantLocalToUtcDate,
  tenantShiftedNow,
  dateKeyInTenantTz,
  formatTimeInTenantTz,
} from '../../utils/tenantTime';
import { isValidEmail, isValidName, isValidNameLength, isTenDigitPhone, MAX_NAME_LENGTH } from '../../utils/validation';
import {
  parseLocalDateTime,
  parseBackendDate,
  addMinutes,
  formatTimeString,
  formatTimeOfDay24,
  formatDateMedium,
  formatWeekdayLong,
  formatDurationMinutes,
  formatRelativeTime,
  getTimeDifferenceInMinutes,
  roundToStepMinutes,
  timeStringToMinutes,
  addMinutesToTimeString,
  daysBetweenDateKeys,
  getWeekdayFromDateKey,
  formatDateKeyMedium,
} from '../../utils/timeUtils';
import type { Appointment } from '../../components/timeline/types';
import {
  isAllDayExternalEvent,
  isBlockingExternalEvent,
  describeAddressGaps,
} from '../../components/timeline/utils/externalEventBlocking';
import { calculateDistance } from '../../utils/geoDistance';
import type {
  RoutyArtifact,
  AvailabilityBlocker,
  AvailabilityLongBlock,
  AvailabilityTravelLoad,
  AvailabilityOpenSlot,
  AvailabilityServiceFit,
  AvailabilityFormRule,
  AvailabilityFormRuleReason,
  AvailabilitySlotPreference,
  SlotAuditLine,
  RoutyPlaceCard,
  DriveLeg,
  RoutySlotOption,
  BookingFormOption,
  BookingCollectorPrefill,
  RoutyFillCandidate,
  RoutyDaySummaryArtifact,
  WeekReportMemberRow,
} from './routyArtifacts';
import {
  describeDayOff,
  fetchDayAppointments,
  fetchRouteEndpoints,
  fetchUserOverridesByDate,
  fetchWorkScheduleByDay,
  getEffectiveDayAvailability,
  getWorkingHoursForDate,
  toDateKey,
  type EffectiveDayAvailability,
  type RoutyEnvironment,
} from './routyContext';
import { validateAndOptimize, type BulkStopDraft, type BulkRouteContext } from '../booking/BulkRouteOptimizer';
import { teamMemberAvailabilityManager } from '../availability/TeamMemberAvailabilityManager';
import {
  describeDayOffOverride,
  describeRescheduleBlockers,
  onlySoftRescheduleBlockers,
  type RescheduleBlocker,
} from './rescheduleAvailability';
import { checkReassignment, isOffForm, offFormCardText } from './reassignAppointment';
import { formNameMatchesHint } from './routySetupHelpers';

export type TimePreference = 'Morning' | 'Afternoon' | 'Evening' | 'Flexible';

/** A single labeled row shown on a confirmation card. */
export interface ConfirmLine {
  icon?: 'map' | 'clock' | 'calendar' | 'bell' | 'user' | 'repeat';
  text: string;
}

/**
 * One stop in a structured stop-list confirmation (e.g. add_multiple_stops,
 * repeat_stop). Rendered as its own distinct row with the duration called out
 * as a badge, instead of blending into the flat `lines` list.
 */
export interface ConfirmStopItem {
  title: string;
  address: string;
  dateLabel: string;
  timeLabel: string;
  durationMinutes: number;
  /** e.g. "fixed time" when the user asked for an exact time. */
  badge?: string;
  /** Exact coordinates, so the confirm card and swap/keep flows can carry the
   * resolved location forward without re-geocoding a name to a wrong branch. */
  latitude?: number;
  longitude?: number;
  /** Drive leg INTO this stop (from the previous stop, or the route start),
   * so the chat card can show how tight the route is between stops. */
  driveFromLabel?: string;
  driveFromMinutes?: number | null;
  driveFromMiles?: number | null;
}

/**
 * Optional customer-notification control on a confirmation card. When present,
 * the card shows an explicit "notify the customer?" toggle (sensitive, so the
 * user opts in). The UI reports the choice back via `onDecision` right before
 * the confirmation promise resolves.
 */
export interface ConfirmNotifyOption {
  /** e.g. "SMS", "email", "SMS & email" - what would be sent. */
  channelLabel: string;
  /** Whether the toggle starts on. Defaults to off. */
  defaultOn?: boolean;
  /** Called by the UI with the user's choice when they confirm. */
  onDecision?: (notify: boolean) => void;
}

/**
 * Structured before/after time comparison (e.g. reschedule) rendered as two
 * distinct "was" / "now" rows instead of a single plain-text new-time line, so
 * sensitive changes to a customer's appointment can be visually verified
 * rather than parsed out of prose.
 */
export interface ConfirmTimeChange {
  /** e.g. "Fri, Jul 3". */
  oldDateLabel: string;
  /** e.g. "9:50 AM – 10:50 AM". */
  oldTimeLabel: string;
  /** e.g. "Fri, Jul 3" (may equal oldDateLabel when only the time-of-day changes). */
  newDateLabel: string;
  /** e.g. "1:00 PM – 2:00 PM · 1 hr". */
  newTimeLabel: string;
}

/** One setting being changed, rendered as "label: from → to". */
export interface ConfirmChange {
  label: string;
  from: string;
  to: string;
}

/** One row of an indented outline, e.g. a form's questions with follow-ups nested under their answers. */
export interface ConfirmOutlineRow {
  text: string;
  /** 0 for top level; each level nests one step further. */
  depth: number;
  tag?: 'New' | 'Edited' | 'Removed';
  /** Secondary text, e.g. the question type and its answers. */
  detail?: string;
}

/**
 * A write action awaiting user approval. The tool layer composes a
 * display-ready request and the UI renders a generic confirmation card, so any
 * new write tool gets a confirmation step for free.
 */
export interface ConfirmRequest {
  title: string;
  /** Short line shown under the title, e.g. the contact/appointment name being changed. */
  subtitle?: string;
  lines: ConfirmLine[];
  confirmLabel: string;
  tone?: 'default' | 'danger';
  /** When set, the card shows a customer-notification opt-in toggle. */
  notify?: ConfirmNotifyOption;
  /** Structured per-stop rows (add_multiple_stops/repeat_stop), rendered above `lines`. */
  stops?: ConfirmStopItem[];
  /** Before/after time comparison (e.g. reschedule), rendered above `lines`. */
  timeChange?: ConfirmTimeChange;
  /** Settings being changed, rendered above `lines`. */
  changes?: ConfirmChange[];
  /** Indented outline (e.g. the form's questions after the edit), rendered below `lines`. */
  outline?: ConfirmOutlineRow[];
}

export interface RoutyToolDeps {
  env: RoutyEnvironment;
  /** Surface a short "what Routy is doing" status to the chat panel. */
  onActivity?: (label: string) => void;
  /** Ask the user to approve a write action. Resolves true when approved. */
  requestConfirmation: (request: ConfirmRequest) => Promise<boolean>;
  /** Push a rich UI artifact (e.g. place cards) to the chat panel. */
  onArtifact?: (artifact: RoutyArtifact) => void;
}

/** Anthropic tool definitions exposed to Claude. */
export const ROUTY_TOOLS: Tool[] = [
  {
    name: 'get_schedule',
    description:
      "Get the operator's schedule (appointments, stops, working hours) for a single day, including " +
      'each appointment id, location, and whether it is upcoming. Use this before suggesting, moving, ' +
      'or adding anything, and to find an appointment to act on (e.g. "my next appointment"). Synced ' +
      'EXTERNAL calendar events (Google/Outlook/Zoom) come back separately as externalEvents - use them ' +
      'to answer "any Google Calendar conflicts?"-style questions; they block time but are read-only. ' +
      'When the user wants NOTES in their agenda ("with notes", "including any notes/details"), pass ' +
      'includeNotes=true - each appointment then also carries its internal staff notes; NEVER claim an ' +
      'appointment has no notes unless you asked with includeNotes=true and none came back. ' +
      'The result\'s isWorkDay/dayStatus reflect the person\'s TRUE availability for that date (weekly ' +
      'schedule + their date-specific day-off/vacation overrides) - report off vs open from those fields, ' +
      'never from whether appointments happen to be booked. ' +
      'Pass assignedUserId (from find_team_member) to check a TEAMMATE\'s schedule instead of the ' +
      'operator\'s own — requires manage-all-bookings permission.',
    input_schema: {
      type: 'object',
      properties: {
        date: { type: 'string', description: 'The day to look up, YYYY-MM-DD.' },
        includeNotes: {
          type: 'boolean',
          description:
            'Set true when the user asked for notes/details in the agenda - each appointment then includes ' +
            'its notes: customerNote, preBookingNotes and appointmentNotes (each only when present). ' +
            'Omit for plain schedule lookups (keeps the call fast).',
        },
        assignedUserId: { type: 'string', description: 'Check this team member\'s schedule instead of your own (from find_team_member).' },
        assignedUserName: { type: 'string', description: 'The team member\'s name, for display, if assignedUserId is set.' },
      },
      required: ['date'],
    },
  },
  {
    name: 'find_contact',
    description:
      "Look up a person or business in the operator's Routemize contacts by name. Returns their saved " +
      'address(es), phone, and contactId. Use this FIRST whenever the user refers to someone by name — ' +
      'ESPECIALLY a possessive reference to a real person ("Amy Wright\'s house", "stop at Alan Parer\'s ' +
      'place", "the Smiths\' job") — before ever asking for an address. Also retries with individual name ' +
      'words if the full name finds nothing, so a real, spelled-correctly contact should always be found. ' +
      'Do NOT use this for a coworker/teammate who should DO or be ASSIGNED a job — use find_team_member ' +
      'for that instead.',
    input_schema: {
      type: 'object',
      properties: {
        name: { type: 'string', description: 'Name or business to search for, e.g. "Alan Parer".' },
      },
      required: ['name'],
    },
  },
  {
    name: 'find_team_member',
    description:
      'Look up a coworker/teammate by name to find WHO should do or be assigned a job — for "schedule ' +
      'this for Chris", "assign the Daines stop to Mike", "what does Chris\'s day look like", "what\'s ' +
      'Chris\'s starting location?". Returns each match\'s userId, role, canReceiveAppointments, and their ' +
      'REAL startingAddress/endingAddress (home or office, whichever the company\'s Route Preferences ' +
      'setting uses) - use these values verbatim for any location question; NEVER guess or infer someone\'s ' +
      'starting point from an appointment address. CHECK canReceiveAppointments before offering to book/' +
      'assign anything for them: false means their role only schedules FOR others (e.g. Scheduling ' +
      'Assistant) and they cannot be assigned appointments or stops - warn the user up front instead of ' +
      'continuing the scheduling flow. Use this tool (NOT find_contact) whenever a name refers to who does ' +
      'the work, as opposed to the customer/site the work is for. Acting on another team member\'s calendar ' +
      'requires the operator to have manage-all-bookings permission; if they don\'t, this tool says so — ' +
      'tell the user plainly and offer to handle it on their own calendar instead.',
    input_schema: {
      type: 'object',
      properties: {
        name: { type: 'string', description: 'Name to search for, e.g. "Chris".' },
      },
      required: ['name'],
    },
  },
  {
    name: 'request_missing_address',
    description:
      'Show a small in-chat card asking for ONE missing address, instead of asking in a paragraph of text. ' +
      'Use this ONLY after find_contact has returned ZERO matches (retried) for a named person, or ' +
      'search_places found nothing usable for a named business, and you genuinely cannot resolve the ' +
      'address yourself. If OTHER stops in the same request already resolved, schedule those now via ' +
      'add_multiple_stops/add_stop in this same turn - do not wait on this one gap to act on the rest. ' +
      'Your text reply alongside this card must still say what you already scheduled (if anything) and ' +
      'what you are waiting on - never reply with just "Done" or similar. After calling this, wait for the ' +
      'user\'s next message, which contains the address they entered, so you can add that one remaining stop.',
    input_schema: {
      type: 'object',
      properties: {
        subject: { type: 'string', description: 'Whose/what address is needed, e.g. "Amy Wright".' },
        context: { type: 'string', description: 'Optional short context, e.g. "for her job stop today".' },
        purpose: { type: 'string', description: 'What you will do once you have it, echoed back with the address.' },
      },
      required: ['subject'],
    },
  },
  {
    name: 'search_places',
    description:
      'Search for real businesses/places by name or category (e.g. "Starbucks", "lunch near SR-200", ' +
      '"barber shop"). Results are RANKED BY REAL DRIVING TIME from the bias point (nearLat/nearLng, or the ' +
      "operator's starting address by default) - the FIRST result is already the closest to drive to. " +
      'THE BIAS POINT MUST BE THE ANCHOR THE USER NAMED: for "closest X to my stop / to my 2pm / to the ' +
      'Smith job", pass THAT appointment\'s latitude/longitude (from get_schedule/find_appointments) as ' +
      'nearLat/nearLng - NEVER leave them out and let it default to the route start, which ranks against ' +
      'the wrong point and picks the wrong branch. For a named chain/business (e.g. "Sherwin Williams") ' +
      'when scheduling a stop, take the top result as the correct location - do NOT ask the user "which ' +
      'location" first. When you are recommending/choosing a place for the user (a single stop, "closest ' +
      'X", a suggestion), ALWAYS follow up with present_places for the chosen place - even when there is ' +
      'only ONE pick - so they see the card (photo, rating, drive legs) instead of a prose description; ' +
      'skip the card only inside routine multi-stop batches where the batch confirm card already shows ' +
      'every stop.',
    input_schema: {
      type: 'object',
      properties: {
        query: { type: 'string', description: 'What to search for, e.g. "Starbucks Ocala FL".' },
        nearLat: { type: 'number', description: 'Latitude to bias/rank by - REQUIRED when the user anchored the request to a stop/appointment (use that stop\'s latitude).' },
        nearLng: { type: 'number', description: 'Longitude to bias/rank by - REQUIRED when the user anchored the request to a stop/appointment (use that stop\'s longitude).' },
        maxResults: { type: 'number', description: 'Max results to return (default 5).' },
      },
      required: ['query'],
    },
  },
  {
    name: 'present_places',
    description:
      'Show the user rich Google cards for 1-3 specific places you recommend (by placeId from ' +
      'search_places), with photo, rating, reviews, and a Schedule button. ALWAYS pass the proposed ' +
      'date and startTime so each card shows the real DRIVING distance and time TO the place (from the ' +
      'prior stop) and FROM it (to the next stop) — this is the key info the user needs. Call this ' +
      'whenever you recommend a place for a time, then briefly note the added driving and ask if you ' +
      'should schedule it.',
    input_schema: {
      type: 'object',
      properties: {
        placeIds: {
          type: 'array',
          items: { type: 'string' },
          description: '1-3 placeIds from search_places, best first.',
        },
        durationMinutes: { type: 'number', description: 'Planned visit length in minutes (default 15).' },
        date: { type: 'string', description: 'Proposed day, YYYY-MM-DD (needed for drive legs).' },
        startTime: { type: 'string', description: 'Proposed start time, 24h HH:MM (needed for drive legs).' },
      },
      required: ['placeIds'],
    },
  },
  {
    name: 'find_stop_slots',
    description:
      'Find the most route-efficient open slots to fit a new stop of a given duration at an address, ' +
      'between existing appointments. Use for "fit me in wherever it works". Ranked best-first by route ' +
      'efficiency, with drive time to/from neighbors. Pass assignedUserId to fit the stop into a ' +
      'TEAMMATE\'s route instead of the operator\'s own.',
    input_schema: {
      type: 'object',
      properties: {
        address: { type: 'string', description: 'Full address or business name of the stop.' },
        durationMinutes: { type: 'number', description: 'How long the stop will take, in minutes.' },
        startDate: { type: 'string', description: 'First day to search, YYYY-MM-DD.' },
        endDate: {
          type: 'string',
          description: 'Optional last day (inclusive), YYYY-MM-DD. Use a range when the user says "any day works".',
        },
        timePreference: {
          type: 'string',
          enum: ['Morning', 'Afternoon', 'Evening', 'Flexible'],
          description: 'Preferred time of day. Default Flexible.',
        },
        assignedUserId: { type: 'string', description: 'Fit into this team member\'s route instead of your own (from find_team_member).' },
        assignedUserName: { type: 'string', description: 'The team member\'s name, for display, if assignedUserId is set.' },
      },
      required: ['address', 'durationMinutes', 'startDate'],
    },
  },
  {
    name: 'find_service_along_route',
    description:
      'BEST tool for "find me a [haircut / lunch / car wash / hardware store] this week" style requests ' +
      'where the user wants a TYPE of place, not a specific one. It finds the open gaps in the schedule, ' +
      'searches that category near the appointments bracketing each gap, and ranks options by how little ' +
      'they add to the existing route (proximity to real appointments, not just home). Shows rich cards ' +
      'with drive in/out and a proposed time. Ask the category + a rough time preference first if unclear.',
    input_schema: {
      type: 'object',
      properties: {
        category: { type: 'string', description: 'The kind of place, e.g. "barbershop", "hair salon", "lunch", "car wash".' },
        startDate: { type: 'string', description: 'First day to consider, YYYY-MM-DD.' },
        endDate: { type: 'string', description: 'Optional last day (inclusive), YYYY-MM-DD. Use for "this week".' },
        timePreference: {
          type: 'string',
          enum: ['Morning', 'Afternoon', 'Evening', 'Flexible'],
          description: 'Preferred time of day. Default Flexible.',
        },
        durationMinutes: { type: 'number', description: 'Visit length in minutes (default 30).' },
        assignedUserId: { type: 'string', description: 'Search around this team member\'s route instead of your own (from find_team_member).' },
        assignedUserName: { type: 'string', description: 'The team member\'s name, for display, if assignedUserId is set.' },
      },
      required: ['category', 'startDate'],
    },
  },
  {
    name: 'check_time_availability',
    description:
      'Check whether the operator can be at a specific address for a specific start time and duration ' +
      '(e.g. "1pm meeting tomorrow at Starbucks"). Returns whether the exact time is free within working ' +
      'hours and conflict-free, and suggests alternative slots that day if not. Pass assignedUserId to ' +
      'check a TEAMMATE\'s availability instead of the operator\'s own.',
    input_schema: {
      type: 'object',
      properties: {
        address: { type: 'string', description: 'Full address or business name.' },
        date: { type: 'string', description: 'The day, YYYY-MM-DD.' },
        startTime: { type: 'string', description: 'Desired start time, 24h HH:MM (e.g. 13:00).' },
        durationMinutes: { type: 'number', description: 'Duration in minutes.' },
        assignedUserId: { type: 'string', description: 'Check this team member\'s availability instead of your own (from find_team_member).' },
        assignedUserName: { type: 'string', description: 'The team member\'s name, for display, if assignedUserId is set.' },
      },
      required: ['address', 'date', 'startTime', 'durationMinutes'],
    },
  },
  {
    name: 'get_day_briefing',
    description:
      'Give a quick summary of a day: number of stops, first/last appointment, total drive miles and ' +
      'time, number of open gaps, and the longest drive leg. Synced EXTERNAL calendar events ' +
      '(Google/Outlook/Zoom) come back separately as externalEvents - ALWAYS include them in your day ' +
      'summary (labeled by source) so the user sees their complete day. Use for "what does my day look ' +
      'like?". Pass assignedUserId to summarize a TEAMMATE\'s day instead of the operator\'s own.',
    input_schema: {
      type: 'object',
      properties: {
        date: { type: 'string', description: 'Day to summarize, YYYY-MM-DD. Default today.' },
        assignedUserId: { type: 'string', description: 'Summarize this team member\'s day instead of your own (from find_team_member).' },
        assignedUserName: { type: 'string', description: 'The team member\'s name, for display, if assignedUserId is set.' },
      },
    },
  },
  {
    name: 'get_daily_summary',
    description:
      'THE tool for "give me a daily summary" / "how did the team do today?" / "summarize today" / "how many ' +
      'did we book today?". Renders a rich summary CARD: when the operator can manage all bookings it covers ' +
      'the WHOLE COMPANY - one row per team member (completed vs remaining, what they completed by service), ' +
      'NEW bookings created today (bookedToday - even ones scheduled for future dates), and the day\'s top ' +
      'lead sources; otherwise it covers their own day. Counts exclude cancelled/no-show. Your text reply ' +
      'must be ONE short sentence - the card is the detail; never restate its numbers as prose or a list.',
    input_schema: {
      type: 'object',
      properties: {
        date: { type: 'string', description: 'Day to summarize, YYYY-MM-DD. Default today.' },
      },
    },
  },
  {
    name: 'get_week_report',
    description:
      'THE tool for a TEAM/company summary of a week (or any multi-day range) - "summarize the team\'s ' +
      'week", "how is the company looking next week?", "give me a week report". Renders a comprehensive ' +
      'company report CARD: company totals, one row per team member (appointments, completed vs still ' +
      'scheduled, their service mix, top lead sources, and total drive miles), the busiest day, and ' +
      'who is driving the most. Works for past, current, and upcoming ranges. Requires manage-all-bookings ' +
      '(refuses otherwise). For ONE person\'s week (the operator\'s own or a named teammate\'s) do NOT use ' +
      'this - call get_schedule for each day instead. Your text reply must be ONE short sentence - the ' +
      'card is the detail; never restate its numbers as prose or a list.',
    input_schema: {
      type: 'object',
      properties: {
        startDate: { type: 'string', description: 'First day of the range, YYYY-MM-DD.' },
        endDate: { type: 'string', description: 'Last day of the range (inclusive), YYYY-MM-DD.' },
      },
      required: ['startDate', 'endDate'],
    },
  },
  {
    name: 'get_appointment_brief',
    description:
      'Generate the Routy AI Summary briefing for ONE appointment (by id from get_schedule/' +
      'find_appointments) and render it as a card - the same briefing the appointment details view shows: ' +
      'headline, key points, before-arrival instructions, sales notes, follow-up, and sales prep when the ' +
      'meeting is a sales opportunity. Use for "brief me on my 2pm", "prep me for the Smith estimate", ' +
      '"what do I need to know before my next appointment?". Your text reply must be ONE short sentence - ' +
      'the card is the detail; never restate its content.',
    input_schema: {
      type: 'object',
      properties: {
        appointmentId: { type: 'string', description: 'The appointment id from get_schedule/find_appointments.' },
      },
      required: ['appointmentId'],
    },
  },
  {
    name: 'reschedule_to_best_slot',
    description:
      'Move an existing appointment (by id) to the most route-efficient slot the scheduling engine ' +
      'suggests. Use for "find a better time for the Smith job". Optionally notify the customer. User confirms.',
    input_schema: {
      type: 'object',
      properties: {
        appointmentId: { type: 'string', description: 'The appointment id from get_schedule.' },
        preferredDate: { type: 'string', description: 'Optional preferred day, YYYY-MM-DD.' },
        notifyCustomer: { type: 'boolean', description: 'Notify the customer. Default true.' },
      },
      required: ['appointmentId'],
    },
  },
  {
    name: 'optimize_route',
    description:
      "Analyze a day's stop order for the least driving and report how much time/distance could be saved " +
      'plus the recommended visit order. If the improvement is a simple swap of two stops, it can apply ' +
      'it (user confirms). Use for "what\'s the best order to run today?". Pass assignedUserId to ' +
      "optimize a TEAMMATE's route instead of the operator's own.",
    input_schema: {
      type: 'object',
      properties: {
        date: { type: 'string', description: 'Day to optimize, YYYY-MM-DD. Default today.' },
        assignedUserId: { type: 'string', description: 'Optimize this team member\'s route instead of your own (from find_team_member).' },
        assignedUserName: { type: 'string', description: 'The team member\'s name, for display, if assignedUserId is set.' },
      },
    },
  },
  {
    name: 'get_business_stats',
    description:
      'Get business analytics for a date range: status breakdown (completed, cancelled, no-show), ' +
      'completion rate, lead sources, channels, route efficiency, team performance. CAUTION: every breakdown ' +
      'here covers ALL appointments the range touches (occurring in it, not just booked in it), so for ' +
      '"what was booked today/this week and what were the lead sources?" use find_recently_booked instead - ' +
      'mixing the two sets produces numbers that do not add up. Use this tool for period-level health ' +
      'questions like "how did we do last month?" or "what is our no-show rate?".',
    input_schema: {
      type: 'object',
      properties: {
        startDate: { type: 'string', description: 'Range start, YYYY-MM-DD.' },
        endDate: { type: 'string', description: 'Range end (inclusive), YYYY-MM-DD. Same as start for a single day.' },
      },
      required: ['startDate', 'endDate'],
    },
  },
  {
    name: 'add_stop',
    description:
      'Add a stop to the schedule at a specific date, time, and address. ALWAYS confirm the exact slot ' +
      'with the user first; they will also see a confirmation card. Prefer a slot from find_stop_slots ' +
      'or check_time_availability. Pass assignedUserId to add this stop to a TEAMMATE\'s calendar instead ' +
      "of the operator's own (from find_team_member) — do NOT confuse this with contactId, which is the " +
      'customer/site the stop is FOR. ' +
      'CRITICAL: when the user picked a SPECIFIC place from a card (e.g. a find_service_along_route / ' +
      'search_places result), that place has EXACT coordinates. Pass them as latitude+longitude and use the ' +
      'card\'s full street address as `address` — do NOT pass the business NAME as the address. Geocoding a ' +
      'chain name (e.g. "King Cutz Barber Salon") resolves to an arbitrary/nearest branch, which is how the ' +
      'wrong location gets booked. When latitude+longitude are provided, the tool uses them verbatim and ' +
      'skips geocoding entirely. If the chosen day is a DAY OFF, the confirm card says so and confirming ' +
      'also records an availability override for the stop\'s window (so the Availability calendar reflects ' +
      'the exception) — relay that to the user from the result. A date/time that has ALREADY PASSED is ' +
      'allowed only when the user explicitly asked to backdate (e.g. documenting a visit that happened) — ' +
      'tell them the date has passed and get their OK first; the confirm card repeats the past-date warning.',
    input_schema: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'Short label (e.g. "Interview at Starbucks").' },
        address: { type: 'string', description: 'Full street address (NOT just the business name). For a place picked from a card, use that card\'s exact address string.' },
        latitude: { type: 'number', description: 'Exact latitude of a place the user picked from a card. When provided with longitude, the tool uses these coordinates directly and does NOT geocode the address.' },
        longitude: { type: 'number', description: 'Exact longitude of a place the user picked from a card. Provide together with latitude.' },
        date: { type: 'string', description: 'The day, YYYY-MM-DD.' },
        startTime: { type: 'string', description: 'Start time, 24h HH:MM.' },
        durationMinutes: { type: 'number', description: 'Duration in minutes.' },
        contactId: { type: 'string', description: 'Optional contactId from find_contact to link this stop to a contact.' },
        accessCode: {
          type: 'string',
          description:
            'Gate/access code or entry instructions the user mentioned (e.g. "12345", "#1234"). Saved on ' +
            'the stop\'s Gate/Access Code field. CRITICAL: if the user mentioned one at ANY point, pass it ' +
            'here - details mentioned in chat but not passed are silently lost.',
        },
        notes: {
          type: 'string',
          description:
            'Any extra note/instruction the user mentioned for this stop ("beware of the dog", "use the ' +
            'side entrance", service details). Saved as an internal note on the stop. CRITICAL: if the ' +
            'user mentioned one, pass it here - details mentioned in chat but not passed are silently lost.',
        },
        assignedUserId: { type: 'string', description: 'Add this stop to this team member\'s calendar instead of your own (from find_team_member).' },
        assignedUserName: { type: 'string', description: 'The team member\'s name, for display, if assignedUserId is set.' },
        allowServiceTitle: {
          type: 'boolean',
          description:
            'A stop titled after a published booking form/service is refused (it is a customer booking in ' +
            'disguise - use the booking pipeline). Set true ONLY after the user explicitly confirms they ' +
            'want a bare ad-hoc stop with no customer/booking attached. Omit otherwise.',
        },
      },
      required: ['title', 'address', 'date', 'startTime', 'durationMinutes'],
    },
  },
  {
    name: 'reschedule_appointment',
    description:
      'Move an existing appointment (by id, from get_schedule) to a new date/time, keeping its duration ' +
      'unless overridden. To EXTEND or SHORTEN an appointment (change only its length, e.g. "extend my ' +
      'job stop by an hour"), OMIT newDate and newStartTime and pass only durationMinutes set to the new ' +
      'total length (current length + the change) — this keeps the original start time exactly. Only pass ' +
      'newDate/newStartTime when actually moving it to a different time. Optionally notify the customer ' +
      '(ignored for ad-hoc stops that have no customer). The user confirms first. Moving to a date/time ' +
      'that has ALREADY PASSED is allowed only when the user explicitly asked to backdate — tell them the ' +
      'date has passed and get their OK first; the confirm card repeats the past-date warning. Checks the ' +
      'assignee\'s availability (day off / working hours, appointments, availability-blocking calendar ' +
      'events, daily appointment cap) BEFORE showing the confirmation card. If the time is unavailable it ' +
      'returns unavailable: true with the reasons plus alternative time cards instead of a card.',
    input_schema: {
      type: 'object',
      properties: {
        appointmentId: { type: 'string', description: 'The appointment id from get_schedule.' },
        newDate: { type: 'string', description: 'New day, YYYY-MM-DD. Omit to keep the current day (e.g. when only changing duration).' },
        newStartTime: { type: 'string', description: 'New start time, 24h HH:MM. Omit to keep the current start time.' },
        durationMinutes: {
          type: 'number',
          description:
            'New total duration in minutes; defaults to the current duration. For "extend by an hour" pass ' +
            'the current length + 60.',
        },
        notifyCustomer: { type: 'boolean', description: 'Whether to send the customer a confirmation. Default true (auto-off for ad-hoc stops).' },
        acknowledgeUnavailableDay: {
          type: 'boolean',
          description:
            'Set true ONLY after the user, having been told the day is off / outside working hours, explicitly ' +
            'insists on that exact time. Has no effect on overlapping appointments, blocking calendar events or ' +
            'a reached daily cap - those can never be overridden. Omit otherwise.',
        },
      },
      required: ['appointmentId'],
    },
  },
  {
    name: 'update_appointment_location',
    description:
      'Change ONLY an appointment\'s address/location (not its date or time) — for "actually that meeting ' +
      'is at the Riverside branch, not downtown" or "move that job to 123 Main St instead". Geocodes the ' +
      'new address, checks it does not create scheduling gaps that no longer make sense, and confirms ' +
      'before saving.',
    input_schema: {
      type: 'object',
      properties: {
        appointmentId: { type: 'string', description: 'The appointment id from get_schedule.' },
        address: { type: 'string', description: 'The new full address or business name.' },
      },
      required: ['appointmentId', 'address'],
    },
  },
  {
    name: 'reassign_appointment',
    description:
      'Change WHO an existing appointment is assigned to (by id) WITHOUT changing its date, time, length or ' +
      'location - for "give the Bazaj estimate to Derrick instead of Nick", "move my 2pm over to Chris". Resolve ' +
      'the appointment first (get_schedule / find_appointments) and the new person with find_team_member (pass ' +
      'their userId as assignedUserId). Any active team member who can receive appointments may be chosen, even ' +
      'one who is NOT on the appointment\'s booking form (internal override) - the confirm card says so. Requires ' +
      'manage-all-bookings permission. Checks the new person\'s availability (appointments, blocking calendar ' +
      'events, daily cap, day off / working hours) BEFORE the card; returns unavailable: true with reasons instead ' +
      'of a card when they can\'t take it. The user confirms first. Do NOT use reschedule_appointment for this.',
    input_schema: {
      type: 'object',
      properties: {
        appointmentId: { type: 'string', description: 'The appointment id from get_schedule / find_appointments.' },
        assignedUserId: { type: 'string', description: 'The NEW assignee\'s userId (from find_team_member).' },
        assignedUserName: { type: 'string', description: 'The new assignee\'s name, for display.' },
        acknowledgeUnavailableDay: {
          type: 'boolean',
          description:
            'Set true ONLY after the user, told the new person is off / outside working hours then, explicitly ' +
            'insists. Has no effect on overlapping appointments, blocking calendar events or a reached daily cap. ' +
            'Omit otherwise.',
        },
      },
      required: ['appointmentId', 'assignedUserId'],
    },
  },
  {
    name: 'delay_remaining_appointments',
    description:
      'Push back (delay) all of the operator\'s remaining appointments on a day by a number of minutes — ' +
      'for "I\'m running 15 minutes behind, slide everything and notify them". Optionally notify customers. ' +
      'The user confirms first. Pass assignedUserId to delay a TEAMMATE\'s remaining appointments instead ' +
      "of the operator's own.",
    input_schema: {
      type: 'object',
      properties: {
        delayMinutes: { type: 'number', description: 'Minutes to push everything back by.' },
        date: { type: 'string', description: 'Day to adjust, YYYY-MM-DD. Default today.' },
        fromTime: { type: 'string', description: 'Only shift appointments at/after this 24h HH:MM. Default now.' },
        notifyCustomers: { type: 'boolean', description: 'Whether to notify affected customers. Default true.' },
        assignedUserId: { type: 'string', description: 'Delay this team member\'s appointments instead of your own (from find_team_member).' },
        assignedUserName: { type: 'string', description: 'The team member\'s name, for display, if assignedUserId is set.' },
      },
      required: ['delayMinutes'],
    },
  },
  {
    name: 'move_day_appointments',
    description:
      'Bulk move (or duplicate) EVERY appointment on one day to a different day, in ONE batch confirmation ' +
      '- for "move everything from Monday to Tuesday, I\'m taking Monday off" or "duplicate today\'s route ' +
      'to next Monday". Each appointment keeps its time-of-day on the new date (conflict-checked against ' +
      'anything already on the target day). Use this instead of calling reschedule_appointment once per ' +
      'appointment - much faster and one confirm. "duplicate" only works for ad-hoc stops (no linked ' +
      'customer); customer appointments can only be moved, not duplicated.',
    input_schema: {
      type: 'object',
      properties: {
        sourceDate: { type: 'string', description: 'The day whose appointments you are moving/duplicating, YYYY-MM-DD.' },
        targetDate: { type: 'string', description: 'The destination day, YYYY-MM-DD.' },
        mode: {
          type: 'string',
          enum: ['move', 'duplicate'],
          description: 'move = relocate them (source day becomes empty); duplicate = copy ad-hoc stops, leaving the source day intact. Default move.',
        },
        notifyCustomers: { type: 'boolean', description: 'Whether to notify affected customers. Default false.' },
        assignedUserId: { type: 'string', description: 'Move this team member\'s day instead of your own (from find_team_member).' },
        assignedUserName: { type: 'string', description: 'The team member\'s name, for display, if assignedUserId is set.' },
      },
      required: ['sourceDate', 'targetDate'],
    },
  },
  {
    name: 'update_appointment_status',
    description:
      'Update an appointment\'s status (by id from get_schedule): OnTheWay, InProgress, Completed, ' +
      'Cancelled, or NoShow. Optionally notify the customer. For "send the on-the-way message" / "text ' +
      'them I\'m on the way", use status OnTheWay with notifyChannel "sms". Cancelling/NoShow asks the ' +
      'user to confirm. Cancelled is NOT a deletion - it keeps the appointment on record. If the user ' +
      'asked to DELETE/remove/erase an appointment, do NOT reach for this tool first: say you cannot ' +
      'delete (that is manual-only, from the appointment\'s details) and only cancel after they ' +
      'explicitly accept cancellation as the alternative.',
    input_schema: {
      type: 'object',
      properties: {
        appointmentId: { type: 'string', description: 'The appointment id from get_schedule.' },
        status: {
          type: 'string',
          enum: ['OnTheWay', 'InProgress', 'Completed', 'Cancelled', 'NoShow'],
          description: 'The new status.',
        },
        notifyCustomer: { type: 'boolean', description: 'Whether to notify the customer. Default false.' },
        notifyChannel: {
          type: 'string',
          enum: ['sms', 'email', 'both'],
          description:
            'How to notify the customer. Default "sms" for OnTheWay (the on-the-way text), otherwise "both". ' +
            'Setting this implies notifyCustomer=true.',
        },
      },
      required: ['appointmentId', 'status'],
    },
  },
  {
    name: 'add_multiple_stops',
    description:
      'Fit several stops into the schedule at once, route-efficiently (e.g. "add Home Depot, the bank, ' +
      'and a gas stop this week"). Flexible stops (no fixedDate/fixedStartTime) are each placed in the ' +
      'best gap. If some stops MUST be at exact times ("I have 6 stops tomorrow, 3 at fixed times, fit the ' +
      'rest around them"), give those a fixedDate + fixedStartTime - they are placed as given (conflict-' +
      'checked) FIRST, then the flexible ones are slotted around them. For a DEADLINE instead of an exact ' +
      'time ("Tropical Smoothie before 12pm"), use deadlineDate + deadlineTime instead of fixedStartTime - ' +
      'the stop stays flexible/route-optimized but the engine only considers slots that finish by that time, ' +
      'so it still picks whichever slot adds the least driving (never hand-pick a specific time yourself for ' +
      'a deadline - that skips real route optimization). For a FLEXIBLE named chain/business (not a single ' +
      'known address), give businessQuery instead of address (e.g. "Winn-Dixie") - the tool checks SEVERAL ' +
      'real branch locations of that chain against every gap in the day(s), not just the one nearest your ' +
      'starting point, and picks whichever specific branch+slot combo adds the least driving. This matters ' +
      'most on days with appointments spread across a wide area, where the closest-to-home branch may be ' +
      'nowhere near the actual gaps - do NOT pre-resolve a chain to one address yourself with search_places ' +
      'when the day spans multiple areas; let businessQuery do the multi-branch comparison instead. If a ' +
      'stop genuinely cannot fit, the "skipped" result says whether extending the workday by a small amount ' +
      '(e.g. 15-30 min) would fix it - offer that to the user, and if they agree, re-call this tool with ' +
      'extendEndTimeMinutes set on that one stop. The user confirms the whole batch once.',
    input_schema: {
      type: 'object',
      properties: {
        stops: {
          type: 'array',
          description: 'The stops to add.',
          items: {
            type: 'object',
            properties: {
              title: { type: 'string', description: 'Short label, e.g. "Home Depot".' },
              address: { type: 'string', description: 'A single already-resolved full STREET address (use for a known person/place, or a fixed-time stop). Never a bare business name if you can avoid it - pair it with latitude+longitude when the place came from a search_places/find_service_along_route result.' },
              latitude: { type: 'number', description: 'Exact latitude of an already-picked place (from a find_service_along_route/search_places result). When provided with longitude, this stop uses these coordinates directly and skips geocoding, so the exact branch is booked.' },
              longitude: { type: 'number', description: 'Exact longitude of an already-picked place. Provide together with latitude and address.' },
              businessQuery: { type: 'string', description: 'A chain/business NAME (not yet resolved to one address) for a FLEXIBLE stop - e.g. "Winn-Dixie". The tool compares multiple real branch locations against the day\'s gaps itself. Do not combine with address or with fixedDate/fixedStartTime. If the user already picked a SPECIFIC place from a card, use address+latitude+longitude instead of businessQuery.' },
              durationMinutes: { type: 'number', description: 'Minutes (default 30).' },
              fixedDate: { type: 'string', description: 'If this stop has a locked day, YYYY-MM-DD.' },
              fixedStartTime: { type: 'string', description: 'If this stop has a locked start time, 24h HH:MM (requires fixedDate and address, not businessQuery).' },
              deadlineDate: { type: 'string', description: 'If this stop must be done BY a certain day, YYYY-MM-DD (still route-optimized, not a locked time).' },
              deadlineTime: { type: 'string', description: 'If this stop must be done BY a certain time, 24h HH:MM (requires deadlineDate) - e.g. "before 12pm" -> "12:00". Do not combine with fixedStartTime.' },
              extendEndTimeMinutes: { type: 'number', description: 'Only set after the user explicitly approves extending the workday to fit THIS stop (offered to you in a prior "skipped" result). Minutes past the normal end time to allow for this stop only.' },
            },
            required: ['title'],
          },
        },
        startDate: { type: 'string', description: 'First day to consider for the FLEXIBLE stops, YYYY-MM-DD.' },
        endDate: { type: 'string', description: 'Optional last day (inclusive) for the flexible stops, YYYY-MM-DD.' },
        assignedUserId: { type: 'string', description: 'Add these stops to this team member\'s calendar instead of your own (from find_team_member).' },
        assignedUserName: { type: 'string', description: 'The team member\'s name, for display, if assignedUserId is set.' },
        allowServiceTitle: {
          type: 'boolean',
          description:
            'A stop titled after a published booking form/service is refused (it is a customer booking in ' +
            'disguise - use the booking pipeline). Set true ONLY after the user explicitly confirms they ' +
            'want a bare ad-hoc stop with no customer/booking attached. Omit otherwise.',
        },
      },
      required: ['stops', 'startDate'],
    },
  },
  {
    name: 'plan_route',
    description:
      'BEST for "plan/schedule my whole day" style requests with SEVERAL stops on ONE day (e.g. "I need 6 ' +
      'stops today: a haircut, Walmart, a pickup at Daines\'s, a job at Steve\'s at 12pm, a flower shop, and ' +
      'lunch at McAllister\'s Deli"). This runs the real Bulk Route optimizer: it takes ALL the ' +
      'already-RESOLVED stops at once, computes the globally most route-efficient visit order (Google route ' +
      'optimization) around the day\'s existing appointments and working hours, and presents ONE proposed ' +
      'schedule with per-leg drive times for the user to confirm. Prefer this over add_multiple_stops when ' +
      'the user is planning a full day of many stops on a single date. ' +
      'CRITICAL: resolve every stop to a real place FIRST (find_contact + present_contacts for people; ' +
      'search_places for a category/chain), then pass each stop here with its EXACT address + latitude + ' +
      'longitude (and contactId for a person). Do NOT pass bare names/categories. A stop the user pinned to ' +
      'a time (e.g. "Steve\'s at 12pm") gets fixedStartTime; the rest are ordered optimally around it.',
    input_schema: {
      type: 'object',
      properties: {
        date: { type: 'string', description: 'The single day to plan, YYYY-MM-DD.' },
        stops: {
          type: 'array',
          description: 'All stops to place on that day, each already resolved to a real address + coordinates.',
          items: {
            type: 'object',
            properties: {
              title: { type: 'string', description: 'Short label incl. the resolved place, e.g. "Haircut - Sport Clips", "Pickup - Daines Rodriguez".' },
              address: { type: 'string', description: 'Full resolved street address (never a bare business name).' },
              latitude: { type: 'number', description: 'Exact latitude of the resolved place.' },
              longitude: { type: 'number', description: 'Exact longitude of the resolved place.' },
              durationMinutes: { type: 'number', description: 'Time at this stop in minutes (default 30).' },
              fixedStartTime: { type: 'string', description: 'Only if the user pinned this stop to an exact time, 24h HH:MM (e.g. "12:00"). Others are optimized around it.' },
              contactId: { type: 'string', description: 'contactId from find_contact/present_contacts when this stop is for a known contact.' },
            },
            required: ['title', 'address'],
          },
        },
        assignedUserId: { type: 'string', description: 'Plan on this team member\'s calendar instead of your own (from find_team_member).' },
        assignedUserName: { type: 'string', description: 'The team member\'s name, for display, if assignedUserId is set.' },
      },
      required: ['date', 'stops'],
    },
  },
  {
    name: 'repeat_stop',
    description:
      'Add the SAME stop on several future occurrences at a fixed cadence - for "book this every Tuesday ' +
      'for the next 6 weeks", "repeat this job weekly". IMPORTANT: Routemize has no true recurring-series ' +
      'concept yet, so this creates N INDEPENDENT stops (not a linked series) in one batch confirm - tell ' +
      'the user that up front so they know changing/cancelling one will not affect the others. Occurrences ' +
      'that land on a non-working day or conflict with something already on the calendar are skipped and ' +
      'reported, not silently dropped.',
    input_schema: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'Short label, e.g. "Weekly maintenance".' },
        address: { type: 'string', description: 'Full address or business name.' },
        durationMinutes: { type: 'number', description: 'Minutes per occurrence (default 30).' },
        startDate: { type: 'string', description: 'First occurrence, YYYY-MM-DD.' },
        startTime: { type: 'string', description: 'Start time for every occurrence, 24h HH:MM.' },
        occurrences: { type: 'number', description: 'How many total occurrences to create (max 12).' },
        cadenceDays: { type: 'number', description: 'Days between occurrences (7 = weekly, 14 = biweekly). Default 7.' },
        contactId: { type: 'string', description: 'Optional contactId from find_contact to link every occurrence.' },
        assignedUserId: { type: 'string', description: 'Add these occurrences to this team member\'s calendar instead of your own (from find_team_member).' },
        assignedUserName: { type: 'string', description: 'The team member\'s name, for display, if assignedUserId is set.' },
      },
      required: ['title', 'address', 'startDate', 'startTime', 'occurrences'],
    },
  },
  {
    name: 'block_time',
    description:
      'Block time on the operator\'s own calendar (or a TEAMMATE\'s, via assignedUserId): a full day off, ' +
      'the REST of today (from now until the end of the workday), a lunch break, or a personal block. Use ' +
      'for "take Friday off" (dayOff), "turn off the rest of my day / I\'m out of office" (restOfDay), ' +
      '"block me 12-1 for lunch" (lunch), "hold 3-4pm Thursday for a dentist appt" (personal), or "give ' +
      'Chris Friday off" (dayOff + assignedUserId). IMPORTANT: when the user names BOTH a start and end ' +
      'time (e.g. "block 8-9:30", "make me unavailable 1pm to 3pm", "block 8am-12pm for a doctor appt"), ' +
      'ALWAYS use type: "personal" so only that window is blocked and the rest of the day stays available - ' +
      'never dayOff or restOfDay. Use restOfDay ONLY for open-ended "turn off the rest of my day / I\'m ' +
      'heading out" requests, and dayOff ONLY for a whole day off. For lunch/personal, pass endDate too for a RANGE ' +
      '("block lunch 12-1 every day this week") - it applies the same window to every working day in the ' +
      'range with ONE confirm. If appointments already remain in the window (any day), this tool returns ' +
      'them WITHOUT blocking so you can ask the user whether to cancel/notify first; re-call with ' +
      'acknowledgeAppointments=true once they decide.',
    input_schema: {
      type: 'object',
      properties: {
        date: { type: 'string', description: 'The day, YYYY-MM-DD. Defaults to today for restOfDay.' },
        endDate: {
          type: 'string',
          description: 'Optional last day (inclusive), YYYY-MM-DD - lunch/personal only, applies the same window to every working day in range.',
        },
        type: {
          type: 'string',
          enum: ['dayOff', 'restOfDay', 'lunch', 'personal'],
          description: 'Kind of block.',
        },
        startTime: { type: 'string', description: 'Start 24h HH:MM (required for lunch/personal).' },
        endTime: { type: 'string', description: 'End 24h HH:MM (required for lunch/personal).' },
        reason: { type: 'string', description: 'Optional label, e.g. "Dentist" or "Out of office".' },
        assignedUserId: { type: 'string', description: 'Block this team member\'s calendar instead of your own (from find_team_member).' },
        assignedUserName: { type: 'string', description: 'The team member\'s name, for display, if assignedUserId is set.' },
        acknowledgeAppointments: {
          type: 'boolean',
          description:
            'Set true after the user has been told about (and accepted) any appointments that remain in ' +
            'the blocked window (on any day), so the block proceeds anyway.',
        },
      },
      required: ['date', 'type'],
    },
  },
  {
    name: 'block_days_off',
    description:
      'Mark a RANGE of days as off (unavailable) — for "turn off the rest of my week", "I\'m on vacation ' +
      'next week", "block me out Mon-Fri", or "put Chris on vacation next week" (+ assignedUserId). ' +
      'Confirms once for the whole range. If appointments remain on any of those days, it returns them ' +
      'WITHOUT blocking so you can ask the user to handle them first; re-call with ' +
      'acknowledgeAppointments=true once they decide.',
    input_schema: {
      type: 'object',
      properties: {
        startDate: { type: 'string', description: 'First day off, YYYY-MM-DD.' },
        endDate: { type: 'string', description: 'Last day off (inclusive), YYYY-MM-DD.' },
        reason: { type: 'string', description: 'Optional label, e.g. "Vacation".' },
        assignedUserId: { type: 'string', description: 'Block this team member\'s days off instead of your own (from find_team_member).' },
        assignedUserName: { type: 'string', description: 'The team member\'s name, for display, if assignedUserId is set.' },
        acknowledgeAppointments: {
          type: 'boolean',
          description: 'Set true after the user has accepted any appointments still on those days.',
        },
      },
      required: ['startDate', 'endDate'],
    },
  },
  {
    name: 'get_reschedule_options',
    description:
      'Get the ranked, route-efficient time choices for moving an existing appointment (by id from ' +
      'get_schedule) — the same engine the reschedule screen uses. Use for "reschedule my 2pm", "find a ' +
      'better time for the Smith job". Presents selectable cards (Top Choice / Proximity / Customer ' +
      'Preference); when the user picks one, call reschedule_appointment with that date/time.',
    input_schema: {
      type: 'object',
      properties: {
        appointmentId: { type: 'string', description: 'The appointment id from get_schedule.' },
        preferredDate: { type: 'string', description: 'Optional preferred day, YYYY-MM-DD.' },
        timePreference: {
          type: 'string',
          enum: ['Morning', 'Afternoon', 'Evening'],
          description: 'Optional preferred time of day.',
        },
      },
      required: ['appointmentId'],
    },
  },
  {
    name: 'list_booking_forms',
    description:
      'List the published services / booking forms the operator can book (name, id, default duration, ' +
      'and whether same-day booking is on). Use to resolve which service the user means before booking ' +
      'OR before audit_availability — pass the matching bookingFormId so the audit can read that form\'s ' +
      'settings (same-day, min notice, allowed weekdays). For SETUP (editing a form, its questions or ' +
      'settings) pass includeDrafts=true to list every form that is not archived, drafts and lead-source ' +
      'forms included, with each one\'s status.',
    input_schema: {
      type: 'object',
      properties: {
        includeDrafts: {
          type: 'boolean',
          description: 'List drafts and lead-source forms too, for setup and editing. Leave off when booking.',
        },
      },
    },
  },
  {
    name: 'collect_booking',
    description:
      'Open the in-chat booking form so the user can enter the customer, service address, service, and ' +
      'preferred date/time — the same inputs the New Booking modal collects. Use this whenever the user ' +
      'wants to schedule/book a real appointment (not a quick personal stop). NEVER ask the user whether ' +
      'the customer is new or existing — CHECK yourself by calling find_contact with their name first: one ' +
      'clear match = mode "existing" with that contactId (the form pre-fills); several possible matches = ' +
      'present_contacts to disambiguate; zero = mode "new". CRITICAL: pass EVERY detail the user already told you (name, email, ' +
      'phone, address, preferred date/time of day) via the fields below so the form opens PRE-FILLED - ' +
      'never make the user retype information they already gave in chat, and never list the details in ' +
      'your text telling them to copy the values in. After calling this tool, STOP and wait — the user ' +
      'submits the form and their next message contains the details for create_booking_request.',
    input_schema: {
      type: 'object',
      properties: {
        mode: { type: 'string', enum: ['new', 'existing'], description: 'New or existing customer.' },
        contactId: { type: 'string', description: 'Existing contactId from find_contact (existing mode).' },
        bookingFormId: { type: 'string', description: 'Pre-select this service/booking form if known.' },
        serviceHint: {
          type: 'string',
          description:
            'Pre-select: the service TYPE the user named ("estimate", "sales appointment", "paint") when ' +
            'you do not know the exact bookingFormId - the tool matches it against the published forms ' +
            'itself (and, with assignedUserId, prefers a form that teammate is actually on). ALWAYS pass ' +
            'this when the user named any kind of service so the right form arrives preselected.',
        },
        assignedUserId: {
          type: 'string',
          description:
            'The teammate this booking is FOR (from find_team_member) - used with serviceHint to pick a ' +
            'form that teammate belongs to. Also pass the SAME id to create_booking_request afterwards.',
        },
        firstName: { type: 'string', description: 'Pre-fill: customer first name the user already gave (new mode).' },
        lastName: { type: 'string', description: 'Pre-fill: customer last name the user already gave (new mode).' },
        email: { type: 'string', description: 'Pre-fill: customer email the user already gave (new mode).' },
        phone: { type: 'string', description: 'Pre-fill: customer phone the user already gave (new mode).' },
        address: { type: 'string', description: 'Pre-fill: the service address the user already gave (either mode - overrides the contact\'s saved address).' },
        preferredDate: { type: 'string', description: 'Pre-select: preferred day the user already named, YYYY-MM-DD (resolve "tomorrow"/"ASAP" to a real date yourself; ASAP = today or the next working day).' },
        preferredTimeRange: {
          type: 'string',
          enum: ['Morning', 'Afternoon', 'Evening'],
          description: 'Pre-select: time of day the user already named ("in the evening" -> Evening). Omit for "any time"/ASAP.',
        },
      },
      required: ['mode'],
    },
  },
  {
    name: 'find_pending_requests',
    description:
      'List unscheduled leads/requests (booking intent captured but no appointment created yet) - for ' +
      '"do I have any pending leads?", "schedule the Smith lead that\'s been sitting", "any requests I ' +
      'haven\'t booked?". Optionally filter by contact/text or a specific service. Use the returned ' +
      'requestId with create_booking_request (as existingRequestId) to continue that lead into a booking ' +
      'instead of starting a new request from scratch.',
    input_schema: {
      type: 'object',
      properties: {
        text: { type: 'string', description: 'Match by contact name or free text, e.g. "Smith".' },
        bookingFormId: { type: 'string', description: 'Only requests for this service, if known.' },
        maxResults: { type: 'number', description: 'Max requests to return (default 10).' },
      },
    },
  },
  {
    name: 'create_booking_request',
    description:
      'Create the booking request and fetch the route-efficient time options (GetSlots), then present ' +
      'them as selectable cards. Call this with the details the user submitted in the booking form. When ' +
      'the user picks a time, call confirm_booking to actually create the appointment. Requires a service ' +
      '(bookingFormId), a service address, a preferred date, and either an existing contactId or full ' +
      'new-contact details (first/last/email/phone). CONTINUING A PENDING REQUEST (from ' +
      'find_pending_requests, an audit, or one the user named): pass its id as existingRequestId and the ' +
      'contact, service and ADDRESS are read straight off the request - so you only need to supply what you ' +
      'are CHANGING, usually just preferredDate. NEVER ask the user for an address, service or customer ' +
      'that is already on a request you have the id for. It also updates that request in place instead of ' +
      'creating a new one. To ASSIGN ' +
      'the appointment to a specific TEAMMATE ("book it for Chris", "assign it to Joe"), pass their ' +
      'assignedUserId (from find_team_member) - the time options will then come from THAT person\'s ' +
      'route/availability. NEVER switch a customer booking to add_stop just because a teammate should do ' +
      'the job - add_stop makes an ad-hoc stop with no customer/notifications, which is the wrong thing. ' +
      'If the result says outsideServiceArea, the address failed the service-area/territory check: warn ' +
      'the user and get an explicit go-ahead before re-calling with bypassServiceArea=true. If the result ' +
      'says needsQuestionAnswers, the service has REQUIRED intake questions: ask the user the listed ' +
      'questions, then re-call with the SAME details plus questionAnswers - never invent answers and never ' +
      'reroute the booking to a job stop over it. If the result says requestOnly, the booking was saved as ' +
      'a PENDING REQUEST for manual scheduling (request-mode form, or an answer flagged for review) - no ' +
      'time cards appear and confirm_booking is never called; relay the reason to the user.',
    input_schema: {
      type: 'object',
      properties: {
        existingRequestId: {
          type: 'string',
          description:
            'The requestId of a request to CONTINUE instead of creating a new one — from ' +
            'find_pending_requests, an earlier create_booking_request in this conversation, or an ' +
            'audit_availability result. With this set, the customer, service and address are read off the ' +
            'request, so pass only what is CHANGING (usually just preferredDate).',
        },
        mode: {
          type: 'string',
          enum: ['new', 'existing'],
          description: 'New or existing customer. Omit when passing existingRequestId — the request decides.',
        },
        contactId: { type: 'string', description: 'Existing contactId (existing mode).' },
        contactAddressId: { type: 'string', description: 'Existing contactAddressId, if known.' },
        firstName: { type: 'string', description: 'New contact first name.' },
        lastName: { type: 'string', description: 'New contact last name.' },
        email: { type: 'string', description: 'New contact email.' },
        phone: { type: 'string', description: 'New contact phone.' },
        address: {
          type: 'string',
          description:
            'Full service address. Required for a NEW booking. Do NOT pass it (and never ask the user for ' +
            'it) when you have existingRequestId: the request already stores the address, and re-supplying ' +
            'a guess would overwrite it.',
        },
        latitude: { type: 'number', description: 'Service address latitude, if known.' },
        longitude: { type: 'number', description: 'Service address longitude, if known.' },
        bookingFormId: {
          type: 'string',
          description:
            'The service / booking form id. Required for a NEW booking; omit with existingRequestId unless ' +
            'the service itself is what is changing.',
        },
        preferredDate: { type: 'string', description: 'Preferred day, YYYY-MM-DD.' },
        preferredTimeRange: {
          type: 'string',
          enum: ['Morning', 'Afternoon', 'Evening'],
          description: 'Optional preferred time of day.',
        },
        assignedUserId: {
          type: 'string',
          description:
            'Assign the appointment to this team member (from find_team_member) - slots come from their ' +
            'schedule. Omit to let the system pick/rotate automatically.',
        },
        assignedUserName: { type: 'string', description: 'The team member\'s name, for display, if assignedUserId is set.' },
        questionAnswers: {
          type: 'object',
          description:
            'The USER\'s answers to the service\'s intake questions, keyed by questionId (from ' +
            'requiredQuestions/missingRequiredQuestions in a collect_booking or previous result), value = ' +
            'the answer text. Choice questions must use the exact listed options; join multi-select picks ' +
            'with "||". Ask the USER for these - NEVER invent answers.',
        },
        bypassServiceArea: {
          type: 'boolean',
          description:
            'Set true ONLY after a previous call returned outsideServiceArea AND the user explicitly said ' +
            'to book anyway (pass the same existingRequestId back). Omit on the first attempt - never ' +
            'bypass the service-area check on your own.',
        },
      },
      // Only the date is universally required. Marking mode/address/
      // bookingFormId required here is what made continuing a request ask the
      // operator to re-type an address the request already holds — the schema
      // demanded a field the tool then throws away. Anything genuinely missing
      // comes back as a specific note instead.
      required: ['preferredDate'],
    },
  },
  {
    name: 'confirm_booking',
    description:
      'Create the appointment from a chosen slot after create_booking_request. The user confirms first. ' +
      'For the on-the-way / reminder toggles use the notifyChannel and reminders flags. CRITICAL: if the ' +
      'user mentioned a GATE/ACCESS CODE or any extra note/instruction at ANY point in the conversation ' +
      '(e.g. "the gate code is 12345", "watch out for the dog"), pass them via accessCode/notes here so ' +
      'they are saved ON the appointment - details mentioned in chat but not passed are silently lost.',
    input_schema: {
      type: 'object',
      properties: {
        requestId: { type: 'string', description: 'The requestId from create_booking_request.' },
        slotSuggestionId: { type: 'string', description: 'The chosen slot id.' },
        date: { type: 'string', description: 'The chosen slot\'s day, YYYY-MM-DD (from the slot card/result) - shown on the confirmation card.' },
        startTime: { type: 'string', description: 'The chosen slot\'s start time, 24h HH:MM (from the slot card/result) - shown on the confirmation card.' },
        bookingFormId: { type: 'string', description: 'The service / booking form id.' },
        notifyChannel: {
          type: 'string',
          enum: ['sms', 'email', 'both', 'none'],
          description: 'Send the customer their confirmation. Default "both".',
        },
        accessCode: {
          type: 'string',
          description: 'Gate/access code or entry instructions the user mentioned (e.g. "12345", "#1234"). Saved on the appointment\'s Gate/Access Code field.',
        },
        notes: {
          type: 'string',
          description: 'Any extra internal note/instruction the user mentioned for this job. Saved as an internal note on the appointment.',
        },
      },
      required: ['requestId', 'slotSuggestionId'],
    },
  },
  {
    name: 'smart_fill_gap',
    description:
      "Fill an open gap in a day with an EXISTING upcoming appointment that is near the gap — the Smart " +
      'Fill feature. Use for "I have a gap today, what can I fill it with so I\'m not sitting around". ' +
      'Finds the gap, then ranks your other scheduled appointments by how little driving they add, and ' +
      'presents selectable cards. When the user picks one, call confirm_fill to move it into the gap. ' +
      'Pass assignedUserId to find a gap in a TEAMMATE\'s day instead of the operator\'s own.',
    input_schema: {
      type: 'object',
      properties: {
        date: { type: 'string', description: 'Day with the gap, YYYY-MM-DD. Default today.' },
        gapStartTime: {
          type: 'string',
          description: 'Optional 24h HH:MM to target a specific gap; otherwise the largest open gap is used.',
        },
        assignedUserId: { type: 'string', description: 'Find the gap in this team member\'s day instead of your own (from find_team_member).' },
        assignedUserName: { type: 'string', description: 'The team member\'s name, for display, if assignedUserId is set.' },
      },
    },
  },
  {
    name: 'confirm_fill',
    description:
      'Move an existing appointment into an open gap (smart fill), by rescheduling it to the proposed ' +
      'time. The user confirms first. Optionally notify the customer.',
    input_schema: {
      type: 'object',
      properties: {
        appointmentId: { type: 'string', description: 'The appointment to move (from smart_fill_gap).' },
        date: { type: 'string', description: 'Target day, YYYY-MM-DD.' },
        newStartTime: { type: 'string', description: 'Target start, 24h HH:MM.' },
        durationMinutes: { type: 'number', description: 'Visit length in minutes.' },
        notifyCustomer: { type: 'boolean', description: 'Notify the customer. Default true.' },
      },
      required: ['appointmentId', 'date', 'newStartTime'],
    },
  },
  {
    name: 'set_reminders',
    description:
      "Turn appointment reminders on or off for a day's appointments — for \"turn reminders on for my " +
      'appointments today". Updates every appointment on that day. The user confirms first. Pass ' +
      "assignedUserId to update a TEAMMATE's appointments instead of the operator's own.",
    input_schema: {
      type: 'object',
      properties: {
        date: { type: 'string', description: 'Day to update, YYYY-MM-DD. Default today.' },
        channel: {
          type: 'string',
          enum: ['sms', 'email', 'both'],
          description: 'Which reminder channel(s) to change. Default "both".',
        },
        enabled: { type: 'boolean', description: 'Turn reminders on (true) or off (false). Default true.' },
        assignedUserId: { type: 'string', description: 'Update this team member\'s appointments instead of your own (from find_team_member).' },
        assignedUserName: { type: 'string', description: 'The team member\'s name, for display, if assignedUserId is set.' },
      },
    },
  },
  {
    name: 'find_open_day',
    description:
      'Scan upcoming days to see, for each, whether it is a working day and whether it ALREADY has a ' +
      'matching stop/appointment (by title and/or address). Use this BEFORE duplicating or repeating a ' +
      'stop on "another day" / "the next open day", so you know which days already have it, the next open ' +
      'day, and the next open BUSINESS day. Never propose a day that already has that stop - check here ' +
      'first. Pass assignedUserId to scan a TEAMMATE\'s days instead of the operator\'s own.',
    input_schema: {
      type: 'object',
      properties: {
        startDate: { type: 'string', description: 'First day to check, YYYY-MM-DD (usually today or tomorrow).' },
        dayCount: { type: 'number', description: 'How many days to scan (default 10, max 21).' },
        matchTitle: {
          type: 'string',
          description: 'Treat a day as taken if it has an appointment whose title/type contains this, e.g. "Job Stop".',
        },
        matchAddress: { type: 'string', description: 'Also treat a day as taken if an appointment address contains this.' },
        assignedUserId: { type: 'string', description: 'Scan this team member\'s days instead of your own (from find_team_member).' },
        assignedUserName: { type: 'string', description: 'The team member\'s name, for display, if assignedUserId is set.' },
      },
      required: ['startDate'],
    },
  },
  {
    name: 'pick_date',
    description:
      'Show an in-chat DATE PICKER and let the user choose a day. Use this whenever you cannot confidently ' +
      'decide the day yourself - genuine ambiguity, a scheduling conflict, or when the user should choose. ' +
      'Offer 1-3 smart quick-pick suggestions (e.g. next open day, next business day) and flag days that ' +
      'already have the item via takenDates. After calling this, STOP and wait - the user picks a day and ' +
      'their next message contains it, then you continue the action on that day.',
    input_schema: {
      type: 'object',
      properties: {
        prompt: { type: 'string', description: 'Short question shown on the card, e.g. "Which day for the job stop?".' },
        purpose: { type: 'string', description: 'What the date is for, echoed back with the choice (e.g. "the extra job stop").' },
        suggestedDates: {
          type: 'array',
          description: 'Quick-pick options, best first.',
          items: {
            type: 'object',
            properties: {
              date: { type: 'string', description: 'YYYY-MM-DD.' },
              label: { type: 'string', description: 'Short label, e.g. "Next open day" or "Next business day (Mon)".' },
              note: { type: 'string', description: 'Optional sub-note, e.g. "Fri, Jul 4 - no job stop yet".' },
            },
            required: ['date', 'label'],
          },
        },
        defaultDate: { type: 'string', description: 'Preselected day for the free picker, YYYY-MM-DD.' },
        minDate: { type: 'string', description: 'Earliest selectable day, YYYY-MM-DD (usually today).' },
        takenDates: {
          type: 'array',
          items: { type: 'string' },
          description: 'Days that already have the item, YYYY-MM-DD (flagged in the picker).',
        },
      },
    },
  },
  {
    name: 'find_appointments',
    description:
      "Search appointments/stops across a date range by optional text (title/type/address) and/or contact " +
      'name. Works for PAST dates exactly like future ones - THE tool for "verify the appointments I had ' +
      'for [customer] yesterday", "what did we do for the Smiths last week". When you search by contact/text ' +
      'and the operator can manage all bookings, matches come from EVERY team member\'s calendar automatically ' +
      '(each match\'s assignedTo says whose calendar it is on) - so it also answers "which user had them". ' +
      'Use when the user refers to an appointment LOOSELY ("the job I\'m working on today", "that Smith job", ' +
      '"my afternoon stop") and you need to find candidates - and ESPECIALLY when the day you expected ' +
      '(e.g. "today") has no match: widen the window to nearby days / this week instead of giving up. ' +
      'Returns candidates with id, title, contact, date (YYYY-MM-DD) plus dateLabel (weekday label - quote it ' +
      'verbatim), time, status, assignedTo, and ADDRESS so you can ' +
      "reuse the location. Pass assignedUserId to pin the search to ONE teammate's calendar only.",
    input_schema: {
      type: 'object',
      properties: {
        startDate: { type: 'string', description: 'First day to search, YYYY-MM-DD.' },
        endDate: { type: 'string', description: 'Last day (inclusive), YYYY-MM-DD. Defaults to ~1 week after startDate.' },
        text: { type: 'string', description: 'Match appointments whose title/type/address contains this, e.g. "job stop".' },
        contactName: { type: 'string', description: 'Match appointments for this contact/customer name.' },
        assignedUserId: { type: 'string', description: 'Search this team member\'s appointments instead of your own (from find_team_member).' },
        assignedUserName: { type: 'string', description: 'The team member\'s name, for display, if assignedUserId is set.' },
      },
      required: ['startDate'],
    },
  },
  {
    name: 'find_recently_booked',
    description:
      'Find out WHO JUST BOOKED/SCHEDULED something - i.e. appointments by WHEN THEY WERE CREATED (booked), ' +
      'NOT by when they occur. Use this for "who just scheduled/booked today?", "any new bookings?", "what ' +
      'came in this week?", "did anyone book with Chris today?" - these are all about NEW bookings hitting ' +
      'the calendar, which is completely different from get_schedule/find_appointments (those show what\'s ' +
      "happening on a day, regardless of when it was booked - a job scheduled weeks ago that occurs today " +
      'is NOT a "new" booking). If the operator can manage all bookings, this returns bookings for THE WHOLE ' +
      'COMPANY by default (not just their own) - pass assignedUserId to narrow to one team member. Returns ' +
      'each booking\'s contact, service/appointment type, lead source, channel, WHEN it occurs, WHO it is ' +
      'assigned to, and how long ago it was booked, plus service/lead-source/channel breakdowns. This is THE ' +
      'tool for "how many appointments were booked today and what were the lead sources?" - its numbers match ' +
      'the dashboard\'s Booked Today widget exactly (do NOT use get_business_stats for that; its breakdowns ' +
      'cover every appointment the range touches, not just new bookings, so the numbers will not add up).',
    input_schema: {
      type: 'object',
      properties: {
        window: {
          type: 'string',
          enum: ['today', 'this-week', 'last-7-days', 'last-30-days'],
          description: 'How far back to look for NEW bookings. Default "today".',
        },
        assignedUserId: { type: 'string', description: 'Only show bookings assigned to this team member (from find_team_member). Omit to show everyone (if permitted).' },
        assignedUserName: { type: 'string', description: 'The team member\'s name, for display, if assignedUserId is set.' },
        maxResults: { type: 'number', description: 'Max bookings to return, default 15, max 30.' },
      },
    },
  },
  {
    name: 'present_appointments',
    description:
      'Show 1-3 candidate appointments as confirmable cards ("Do you mean this one?") when you inferred ' +
      'which appointment the user meant but are not certain - instead of just asking in prose or giving ' +
      'up. The user taps the right one (or "None"), then you continue where you left off (e.g. reuse that ' +
      "job's address to schedule the new stop). After calling this, STOP and wait for their choice.",
    input_schema: {
      type: 'object',
      properties: {
        prompt: { type: 'string', description: 'Short question, e.g. "Do you mean the job stop tomorrow?".' },
        purpose: { type: 'string', description: 'What you will do with the chosen appointment, echoed back (e.g. "scheduling the new job stop next week").' },
        allowNone: { type: 'boolean', description: 'Show a "None of these" button. Default true.' },
        candidates: {
          type: 'array',
          description: '1-3 candidate appointments (from find_appointments / get_schedule).',
          items: {
            type: 'object',
            properties: {
              appointmentId: { type: 'string' },
              title: { type: 'string' },
              contactName: { type: 'string' },
              date: { type: 'string', description: 'YYYY-MM-DD.' },
              startTime: { type: 'string', description: '24h HH:MM.' },
              endTime: { type: 'string', description: '24h HH:MM.' },
              address: { type: 'string' },
            },
            required: ['appointmentId', 'title', 'date', 'startTime'],
          },
        },
      },
      required: ['candidates'],
    },
  },
  {
    name: 'present_contacts',
    description:
      'Show 2-5 candidate contacts as tappable cards ("Which one did you mean?") when find_contact matched ' +
      'more than one person/business for a name - instead of asking in prose. The user taps the right one ' +
      '(or "None"), then you continue where you left off using that contactId/address. After calling this, ' +
      'STOP and wait for their choice.',
    input_schema: {
      type: 'object',
      properties: {
        prompt: { type: 'string', description: 'Short question, e.g. "Which Mike did you mean?".' },
        purpose: { type: 'string', description: 'What you will do with the chosen contact, echoed back (e.g. "scheduling the touchup stop").' },
        allowNone: { type: 'boolean', description: 'Show a "None of these" button. Default true.' },
        candidates: {
          type: 'array',
          description: '2-5 candidate contacts (from find_contact).',
          items: {
            type: 'object',
            properties: {
              contactId: { type: 'string' },
              name: { type: 'string' },
              businessName: { type: 'string' },
              phone: { type: 'string' },
              address: { type: 'string', description: "Contact's primary/first saved address, if any." },
            },
            required: ['contactId', 'name'],
          },
        },
      },
      required: ['candidates'],
    },
  },
  {
    name: 'audit_availability',
    description:
      'THE tool for "why can\'t I book this day?" / "why is the earliest time so far out?". Audits ONE day ' +
      'and renders a card that answers it. ALWAYS pass bookingFormId (or serviceHint when you only have ' +
      'the name, e.g. "Sales Appointment") — without the form, the audit can only read the calendar and ' +
      'will miss the usual reason the booking engine skipped an otherwise-open day: form settings like ' +
      'same-day booking off, a same-day cutoff, minimum notice, a weekday the form does not offer, or a ' +
      'date past the form\'s booking window. Those come back as formRule; when formRule.holds is true ' +
      'THAT is the answer, not the openings on the card. It also reports whether the availability ' +
      'schedule closes the day, the working window, any date-specific time off, and every appointment ' +
      'AND synced calendar event that OVERLAPS the working hours - each with its id, length, and whether ' +
      'it has an address. Events that fall entirely outside working hours (a 5 AM alarm against a 9-5) ' +
      'cost no bookable time and are left out on purpose, so never present the card as a full list of ' +
      'the day. It ALSO returns two things a clock-only reading misses: `longBlocks` (all-day / 4h+ ' +
      'synced "Busy" events that swallow the day on their own - the permanent fix is switching them from ' +
      'Busy to Free in the source calendar), and `travel` (how far apart the day\'s located events ' +
      'actually are). When travel.exceedsWorkday is true the operator is TRAVELLING - the day is ' +
      'physically unbookable no matter how the gaps read, so lead with that, name where they are going, ' +
      'and do not offer to clear events. Synced calendar events WITHOUT an address are flagged: they are ' +
      'usually personal blocks (focus time, reminders, birthdays) rather than real field work, and the ' +
      'card lets the user clear them with remove_calendar_events to open the day back up. It also ' +
      'returns `openSlots` - every open window with what pins each end - and `serviceFit` (the job\'s ' +
      'length + buffer), so a day that reads as "packed" is really "two openings, neither long enough". ' +
      'When the day IS open, the job fits, and no form rule skips it, the result carries `slotPreference`: ' +
      'nothing is blocking the day, it was simply never asked for, because scheduling only offers the Top ' +
      'Choice, a Proximity option, and times inside the request\'s PREFERRED dates. Pass `requestId` and ' +
      'that becomes fixable on the spot - offer to re-run create_booking_request on the same request with ' +
      'this day as preferredDate rather than telling the operator to redo the booking. ' +
      'Keep your text to ONE or TWO sentences - the card carries the detail, and each opening has its ' +
      'own "Audit slot" button for the deep breakdown.',
    input_schema: {
      type: 'object',
      properties: {
        date: { type: 'string', description: 'The day the user wanted, YYYY-MM-DD.' },
        bookingFormId: {
          type: 'string',
          description:
            'The service/booking form being booked. PASS THIS whenever you know it (it is on the request ' +
            'you are booking, and on list_booking_forms): it resolves the visit length, buffers, AND the ' +
            'form settings that decide whether the engine will offer this day at all (same-day booking, ' +
            'cutoff, min notice, allowed weekdays, booking window).',
        },
        serviceHint: {
          type: 'string',
          description:
            'The service name the user said ("Sales Appointment", "estimate") when you do not have the ' +
            'exact bookingFormId. The tool matches it against published forms so form settings still load.',
        },
        serviceDurationMinutes: {
          type: 'number',
          description: 'Visit length, only when there is no bookingFormId to resolve it from.',
        },
        serviceBufferMinutes: {
          type: 'number',
          description: 'Total pre + post buffer, only when there is no bookingFormId to resolve it from.',
        },
        requestedTime: {
          type: 'string',
          description:
            'What they wanted beyond the day, if anything: 24h HH:MM for an exact time, or a time of day ' +
            '("Morning"/"Afternoon"/"Evening"). Omit when they only named a day.',
        },
        earliestOfferedDate: {
          type: 'string',
          description:
            'YYYY-MM-DD the booking engine offered as the earliest available instead, when the user ' +
            'mentioned one - shown on the card for contrast.',
        },
        requestId: {
          type: 'string',
          description:
            'The booking request this is about, whenever one exists (the requestId from ' +
            'create_booking_request or find_pending_requests). PASS IT: the request supplies the booking ' +
            'form for free, the audit can then say which day the request actually asks for, and — when ' +
            'the day turns out to be open and simply not requested — the fix becomes a re-run you offer ' +
            'on that same request instead of the operator redoing the booking.',
        },
        assignedUserId: { type: 'string', description: "Audit a TEAMMATE's calendar instead of the operator's." },
        assignedUserName: { type: 'string', description: 'That teammate\'s display name, for the card.' },
      },
      required: ['date'],
    },
  },
  {
    name: 'audit_slot',
    description:
      'The DEEP breakdown of ONE open window, run when the user taps "Audit slot" on an audit_availability ' +
      'card (or asks why a specific opening will not take the job). Renders a card that spends the window ' +
      'minute by minute in day order - drive in from the stop before, pre-buffer, the visit itself, ' +
      'post-buffer, drive out to the stop after - and lands on a single number: fits, or short by N ' +
      'minutes. Pass serviceAddress whenever you know where the job is (it is on the request) so the ' +
      'drive legs are measured against the real address instead of estimated from the surrounding stops. ' +
      'Reply in ONE sentence naming the shortfall or the fit; the card carries every number, so do not ' +
      'restate the breakdown in prose.',
    input_schema: {
      type: 'object',
      properties: {
        date: { type: 'string', description: 'The day, YYYY-MM-DD.' },
        startTime: { type: 'string', description: 'Window start, 24h HH:MM, exactly as the audit card gave it.' },
        endTime: { type: 'string', description: 'Window end, 24h HH:MM, exactly as the audit card gave it.' },
        bookingFormId: {
          type: 'string',
          description: 'The service being booked — resolves the visit length and buffers. Pass it when known.',
        },
        serviceDurationMinutes: { type: 'number', description: 'Visit length, when there is no bookingFormId.' },
        serviceBufferMinutes: { type: 'number', description: 'Total pre + post buffer, when there is no bookingFormId.' },
        serviceAddress: {
          type: 'string',
          description:
            "The job's street address, when known. With it the drive legs are real Google routes to and " +
            'from the actual location; without it they fall back to the drive between the bracketing ' +
            'stops, which is only a floor.',
        },
        assignedUserId: { type: 'string', description: "Audit a TEAMMATE's day instead of the operator's." },
        assignedUserName: { type: 'string', description: 'That teammate\'s display name, for the card.' },
      },
      required: ['date', 'startTime', 'endTime'],
    },
  },
  {
    name: 'remove_calendar_events',
    description:
      'Clear one or more SYNCED calendar events (Google/Outlook/Zoom) off Routemize so they stop blocking ' +
      'availability, after the user chose to remove them from an audit_availability card. The user confirms ' +
      'on a card first. This removes only the Routemize copy - the original event stays in the source ' +
      'calendar untouched, and a future re-sync can bring it back. Only ever pass ids the audit marked ' +
      'removable; Routemize appointments are NOT removable this way (cancel them instead). Removing a ' +
      'PARTIAL set leaves the day blocked, so prefer removeUnlocated=true over copying ids by hand.',
    input_schema: {
      type: 'object',
      properties: {
        removeUnlocated: {
          type: 'boolean',
          description:
            'PREFERRED when the user chose to clear the address-less blocks: with date set, this resolves ' +
            'and removes EVERY synced event on that day that has no address, so no ids have to be copied ' +
            'and none can be dropped. Use this instead of eventIds whenever the request is "remove the ' +
            'ones without an address".',
        },
        eventIds: {
          type: 'array',
          description:
            'Ids of the synced calendar events to clear, from the audit_availability result. Pass the ' +
            'COMPLETE list - removing a subset leaves the day blocked. Not needed when removeUnlocated is true.',
          items: { type: 'string' },
        },
        expectedCount: {
          type: 'number',
          description:
            'How many events the user asked to remove. The call FAILS if eventIds does not match, which ' +
            'catches a truncated list instead of silently clearing one event. Always pass it with eventIds.',
        },
        date: { type: 'string', description: 'The day they are on, YYYY-MM-DD. Required for removeUnlocated.' },
        assignedUserId: {
          type: 'string',
          description: "Pass through the teammate id if the audit was run on someone else's calendar.",
        },
      },
      required: ['date'],
    },
  },
  {
    name: 'get_contact_history',
    description:
      "Look up a contact's appointment history ACROSS THE WHOLE COMPANY: last visit, recent PAST " +
      'appointments (each with date, time, status, and which team member it was assigned to), totals, and ' +
      'upcoming bookings. Use for "when did I last see Alan?", "what\'s on the books for the Smiths?", or ' +
      'to verify a customer\'s past appointments and who handled them.',
    input_schema: {
      type: 'object',
      properties: {
        name: { type: 'string', description: 'Contact name to search (or use contactId).' },
        contactId: { type: 'string', description: 'Contact id if already known (from find_contact).' },
      },
    },
  },
  {
    name: 'open_manual_booking',
    description:
      'Show a card that opens the full manual New Booking form PRE-FILLED with everything already ' +
      'collected in this conversation, continuing the same saved request when you pass its requestId. ' +
      'THE fallback when the automated booking pipeline fails (create_booking_request/confirm_booking ' +
      'errors that a retry did not fix): offer it FIRST - "I couldn\'t complete this automatically, but I ' +
      'gathered everything - want me to open the booking form so you can finish it?" - and only reach for ' +
      'contact_support if the user prefers that or the manual path also fails. Pass EVERY detail you ' +
      'already collected so nothing has to be retyped.',
    input_schema: {
      type: 'object',
      properties: {
        requestId: { type: 'string', description: 'The saved requestId from the failed create_booking_request, so the form continues that request instead of starting over.' },
        bookingFormId: { type: 'string', description: 'The service the user picked, pre-selected in the form.' },
        reason: { type: 'string', description: 'Short human reason the automated flow failed, shown on the card (e.g. "the time search failed twice").' },
        contactId: { type: 'string', description: 'Existing contactId, if the customer was matched.' },
        contactAddressId: { type: 'string', description: 'Existing contactAddressId, if known.' },
        firstName: { type: 'string', description: 'Customer first name already collected.' },
        lastName: { type: 'string', description: 'Customer last name already collected.' },
        email: { type: 'string', description: 'Customer email already collected.' },
        phone: { type: 'string', description: 'Customer phone already collected.' },
        address: { type: 'string', description: 'Service address already collected.' },
        preferredDate: { type: 'string', description: 'Preferred day, YYYY-MM-DD, if known.' },
        preferredTimeRange: {
          type: 'string',
          enum: ['Morning', 'Afternoon', 'Evening'],
          description: 'Preferred time of day, if known.',
        },
      },
    },
  },
  {
    name: 'contact_support',
    description:
      'Show the "reach a human" card with Routemize\'s REAL support channels: a button that opens the ' +
      'in-app Support Chat (the same messenger as the sidebar "Help" button) and the official support ' +
      'email. Use whenever the user asks for help from support, the dev team, billing, or a human; ' +
      'reports a bug/technical problem; or asks how to contact the company. NEVER answer those requests ' +
      'from memory - do not invent emails, phone numbers, "help centers", or settings pages, and never ' +
      'claim you escalated something yourself (this card IS the escalation path). After calling it, your ' +
      'text reply is 1-2 warm sentences pointing at the card, then offer to keep helping with scheduling.',
    input_schema: {
      type: 'object',
      properties: {
        topic: {
          type: 'string',
          description: 'Optional 3-8 word summary of what they need help with, shown on the card.',
        },
        contextSummary: {
          type: 'string',
          description:
            'REQUIRED when escalating a failure: a support-ready snapshot prefilled into the Support Chat ' +
            'so the team has full context - what the user was trying to do, the EXACT error text you saw, ' +
            'and the key details (customer name, service, date/time, address). 2-6 plain sentences, no ' +
            'markdown. Written FOR the support team, not the user.',
        },
      },
    },
  },
  {
    name: 'send_reschedule_link',
    description:
      "Send a customer their personal SELF-RESCHEDULE link for a specific appointment, by SMS and/or " +
      "email, using the tenant's real Reschedule Link message templates (tracked in customer " +
      'communications). THE ONLY correct tool when the user wants the customer to pick a new time ' +
      'themselves - "send her the link to reschedule", "text him the rebooking link", or after a ' +
      'cancellation. ONLY works for REAL customer appointments (booked on a booking form) - it REFUSES ' +
      'ad-hoc JOB STOPS and anything with no linked customer, because self-rescheduling does not exist ' +
      'for those (the link would be broken); reschedule those directly via reschedule_appointment / ' +
      'get_reschedule_options instead. Needs the appointmentId (from get_schedule/find_appointments, or ' +
      'the appointment you just acted on in this conversation - a just-cancelled appointment still works). ' +
      'Channel availability is checked automatically (SMS needs a connected texting provider + phone on ' +
      'file; email needs an email on file) and the user confirms via a card before anything sends. NEVER ' +
      'hand-write a reschedule link yourself - only this tool generates the real link.',
    input_schema: {
      type: 'object',
      properties: {
        appointmentId: { type: 'string', description: 'The appointment the customer should reschedule.' },
        channel: {
          type: 'string',
          enum: ['sms', 'email', 'both'],
          description: 'Preferred channel(s). Default "both"; unavailable channels fall back automatically.',
        },
        customerName: { type: 'string', description: "The customer's name, for the confirmation card." },
      },
      required: ['appointmentId'],
    },
  },
  {
    name: 'collect_stops',
    description:
      'Open an interactive multi-stop entry form in the chat (with Google address autocomplete) so the user ' +
      'can type addresses for several stops. This is a LAST RESORT for stops you cannot resolve yourself — ' +
      'use it ONLY when the stops are unnamed/arbitrary (e.g. "I have 5 errands to fit in" with no names) or ' +
      'when the user explicitly asks to type the addresses. ' +
      'DO NOT use this when the user names businesses, chains, or a type of place. If EVERY stop is a named ' +
      'business/chain (e.g. "Walmart", "Ace Hardware", "Home Depot") or a service category (e.g. "haircut", ' +
      '"lunch", "car wash"), you must RESOLVE and SUGGEST them yourself based on the route/starting address ' +
      'instead of asking the user to type anything: pass each named chain to add_multiple_stops as ' +
      'businessQuery (it compares real branch locations against the day\'s gaps and starting point), and use ' +
      'find_service_along_route for each service category (it ranks real nearby places by how little they add ' +
      'to the route). Only fall back to this form for the specific stops that are neither a known ' +
      'person/contact, a named business, nor a category. ' +
      'After calling this tool, STOP and wait - do NOT ask for the addresses in text. The user will fill in ' +
      'the form and their next message will contain the finished stop list, which you then schedule with ' +
      'add_multiple_stops.',
    input_schema: {
      type: 'object',
      properties: {
        count: { type: 'number', description: 'How many blank stop rows to start with (default 3, max 12).' },
        startDate: { type: 'string', description: 'The day the stops are for, YYYY-MM-DD, if known.' },
        endDate: { type: 'string', description: 'Optional last day of a range to consider, YYYY-MM-DD.' },
        defaultDurationMinutes: { type: 'number', description: 'Default minutes per stop (default 30).' },
        stops: {
          type: 'array',
          description: 'Optional rows to pre-fill if the user already named some stops.',
          items: {
            type: 'object',
            properties: {
              title: { type: 'string', description: 'Short label, e.g. "Home Depot".' },
              address: { type: 'string', description: 'Address or business name, if already known.' },
              durationMinutes: { type: 'number', description: 'Minutes for this stop.' },
            },
          },
        },
      },
    },
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export async function geocodeOrThrow(address: string): Promise<{ lat: number; lng: number; formatted: string }> {
  const result = await GeocodingService.geocodeAddress(address);
  if (!result) {
    throw new Error(`Could not find coordinates for "${address}". Ask the user for a more specific address.`);
  }
  return { lat: result.lat, lng: result.lng, formatted: result.address };
}

/**
 * Split a Google-formatted address ("4318 SW 55th Ave, Ocala, FL 34474, USA")
 * into the structured components a ContactAddress row is expected to carry.
 * Routy previously stored the whole string in addressLine1 with empty
 * city/state/zip — requests linked to such an address fail the New Booking
 * modal's "complete service address" guard when reopened from the Requests
 * panel. Unparseable strings fall back to addressLine1-only (same as before).
 */
export function parseFormattedAddress(formatted: string): {
  addressLine1: string;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  country: string | null;
} {
  const fallback = { addressLine1: formatted, city: null, state: null, zipCode: null, country: null };
  const parts = formatted.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length < 3) return fallback;

  let idx = parts.length - 1;
  let country: string | null = null;
  if (isUsOrCanada(parts[idx])) {
    country = normalizeCountryToIso(parts[idx]);
    idx--;
  }

  // "FL 34474" / "ON M5V 2T6" — a valid 2-letter region code, then the postal code.
  const stateZipMatch = (parts[idx] ?? '').match(/^([A-Za-z]{2})\s+(.+)$/);
  if (!stateZipMatch || !isValidRegionCode(stateZipMatch[1], country ?? undefined) || idx < 2) {
    return fallback;
  }
  const state = stateZipMatch[1].toUpperCase();
  const zipCode = stateZipMatch[2].trim();
  const city = parts[idx - 1];
  const addressLine1 = parts.slice(0, idx - 1).join(', ');
  if (!addressLine1 || !city) return fallback;

  return { addressLine1, city, state, zipCode, country };
}

/** Minimum lead time before a proposed slot so Routy never suggests "right now". */
const ROUTY_SLOT_LEAD_MINUTES = 20;

/**
 * Frame-correct "is this proposed slot still in the future?" check. The internal
 * gap finder mixes tenant-shifted and real-UTC Date frames, so to reliably keep
 * Routy from proposing times that have already passed we convert the proposed
 * tenant-local time to its real UTC instant and compare it to the actual now.
 */
function isFutureTenantSlot(
  dateKey: string,
  startTime24: string,
  timezone: string | undefined,
  leadMinutes: number = ROUTY_SLOT_LEAD_MINUTES,
): boolean {
  const realUtc = tenantLocalToUtcDate(dateKey, startTime24, timezone);
  return realUtc.getTime() > Date.now() + leadMinutes * 60_000;
}

function enumerateDates(startDate: string, endDate?: string): Date[] {
  const start = parseLocalDateTime(startDate);
  if (!start) return [];
  const end = endDate ? parseLocalDateTime(endDate) : start;
  if (!end) return [start];
  const dates: Date[] = [];
  const cursor = new Date(start);
  let guard = 0;
  while (cursor.getTime() <= end.getTime() && guard < 31) {
    dates.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
    guard++;
  }
  return dates;
}

/**
 * Deterministic, weekday-correct alternatives for a requested day, as
 * pre-formatted labels the model must quote VERBATIM. LLM date arithmetic
 * mislabels weekdays (QA: suggested "next Sunday, July 13th" when July 13 is
 * a Monday), so whenever a request lands on a non-working day the tool hands
 * back real labels instead of letting the model compute its own.
 */
async function describeNonWorkingDayAlternatives(
  dateKey: string,
  deps: RoutyToolDeps,
  scheduleByDay?: Awaited<ReturnType<typeof fetchWorkScheduleByDay>>,
  opts?: {
    /**
     * Set by JOB-STOP flow callers (find_stop_slots, check_time_availability):
     * add_stop CAN schedule on a non-working day (it writes an availability
     * override for the stop's window), so the model must offer that choice
     * UPFRONT instead of only suggesting the next working day and waiting for
     * the user to ask about overriding (QA 86bavtw0c). Never set for customer
     * BOOKING callers - create_booking_request has no day-off override.
     */
    offerStopOverride?: boolean;
    /**
     * Whose availability decides which days are open. Required whenever
     * `scheduleByDay` is omitted - without it the weekly availability the
     * person set for themselves is ignored and days they are off get offered
     * as alternatives.
     */
    userId?: string;
  },
): Promise<string> {
  const day = parseLocalDateTime(dateKey);
  if (!day) return '';
  const schedule = scheduleByDay ?? (await fetchWorkScheduleByDay(opts?.userId));
  const tenantNow = tenantShiftedNow(deps.env.companyTimezone);
  const isOpen = (d: Date) => {
    const h = getWorkingHoursForDate(d, schedule);
    return h.isWorkDay && isDayAvailable(d, h.end, tenantNow);
  };
  const scanForward = (match: (d: Date) => boolean, maxDays: number): Date | null => {
    const cursor = new Date(day);
    for (let i = 0; i < maxDays; i++) {
      cursor.setDate(cursor.getDate() + 1);
      if (match(cursor)) return new Date(cursor);
    }
    return null;
  };

  const requestedLabel = `${formatDateMedium(day)} (${dateKey})`;
  const weekdayName = formatWeekdayLong(day);
  const nextWorkingDay = scanForward(isOpen, 14);
  const weekdayWorks = getWorkingHoursForDate(day, schedule).isWorkDay;
  const nextSameWeekday = weekdayWorks ? scanForward((d) => d.getDay() === day.getDay() && isOpen(d), 35) : null;

  return (
    ` VERIFIED DATE FACTS - quote these labels VERBATIM and NEVER compute a weekday name yourself: the ` +
    `requested day is ${requestedLabel}. ` +
    (nextWorkingDay ? `The next working day after it is ${formatDateMedium(nextWorkingDay)} (${toDateKey(nextWorkingDay)}). ` : '') +
    (weekdayWorks
      ? nextSameWeekday
        ? `The next working ${weekdayName} after it is ${formatDateMedium(nextSameWeekday)} (${toDateKey(nextSameWeekday)}). `
        : ''
      : `${weekdayName}s are NOT working days in this schedule, so do not offer another ${weekdayName}. `) +
    'Only suggest dates from these facts (or from pick_date/find_open_day results).' +
    (opts?.offerStopOverride && !weekdayWorks
      ? ' A JOB STOP can still be scheduled on the requested non-working day: add_stop supports it and ' +
        'automatically marks that window available (the confirmation card discloses it). Your reply must ' +
        'present BOTH choices upfront and ask which the user wants - move it to the next working day above, ' +
        'or keep the requested day anyway as a day-off override. NEVER suggest only the working day and ' +
        'make the user ask about overriding.'
      : '')
  );
}

function slotToJson(slot: StopSlot) {
  return {
    date: slot.suggestedDate,
    startTime: formatTimeString(slot.startTime),
    endTime: formatTimeString(slot.endTime),
    routeScore: Math.round(slot.routeScore),
    driveFromPreviousMinutes: slot.previousAppointmentTravelTimeMinutes ?? null,
    driveToNextMinutes: slot.nextAppointmentTravelTimeMinutes ?? null,
    previousStop: slot.previousAppointmentLocation || null,
    nextStop: slot.nextAppointmentLocation || null,
  };
}

function appointmentToJson(apt: Appointment, now?: Date) {
  // Notes are not part of the summary (notes consolidation, 86bbkaqna): the
  // staff/customer notes come from the appointment's notes bundle only when
  // asked for (get_schedule includeNotes). An ad-hoc stop's label is its
  // timeline `type`.
  return {
    appointmentId: apt.id || null,
    title: apt.type || 'Appointment',
    contact: apt.customerName || null,
    startTime: apt.startTime,
    endTime: apt.endTime,
    address: apt.address || null,
    latitude: apt.latitude ?? null,
    longitude: apt.longitude ?? null,
    // The REAL status (QA 86bavp7rz): a past appointment still 'scheduled'
    // was never marked done - it must not be described as completed.
    status: apt.status,
    ...(apt.isAdHocStop && apt.type ? { stopLabel: apt.type } : {}),
    isUpcoming: now ? apt.startDateTime.getTime() >= now.getTime() : undefined,
  };
}

/** Keeps a note short in a tool result. */
function truncateNoteForAi(content: string): string {
  return content.length > 220 ? `${content.slice(0, 200)}…` : content;
}

/**
 * The appointment's notes bundle as labelled AI context (notes consolidation,
 * 86bbkaqna): the customer's own note, staff notes left on the request before
 * booking, and staff notes on the appointment. Archive/cancel reasons are
 * included only when `includeReasons` (a cancelled appointment). Empty lists
 * are omitted, so an appointment without any of these fields has no notes.
 */
async function loadAppointmentNotesForAi(
  appointmentId: string,
  includeReasons: boolean,
): Promise<{
  customerNote?: string;
  preBookingNotes?: Array<{ title?: string; content: string }>;
  appointmentNotes?: Array<{ title?: string; content: string }>;
  cancellationReason?: string;
}> {
  const bundle = await notesManager.getAppointmentBundle(appointmentId);
  const { customerNote, preBookingNotes, internalNotes } = briefingNotesFromBundle(bundle);
  const shorten = (list: Array<{ title?: string; content: string }>) =>
    list.map((n) => ({ ...n, content: truncateNoteForAi(n.content) }));
  const reasons = includeReasons ? joinNoteContents(appointmentReasons(bundle)) : '';
  return {
    ...(customerNote ? { customerNote: truncateNoteForAi(customerNote) } : {}),
    ...(preBookingNotes.length > 0 ? { preBookingNotes: shorten(preBookingNotes) } : {}),
    ...(internalNotes.length > 0 ? { appointmentNotes: shorten(internalNotes) } : {}),
    ...(reasons ? { cancellationReason: truncateNoteForAi(reasons) } : {}),
  };
}

/**
 * Fetch an appointment and apply a partial update, preserving every other
 * field. Used by reschedule / delay / status tools.
 */
type NotifyChannel = 'sms' | 'email' | 'both';

/**
 * Downgrade a desired notification channel to what the tenant can actually
 * deliver: texting a customer silently does nothing on the backend unless an
 * SMS provider (Twilio/OpenPhone) is connected in Settings > Integrations, so
 * 'sms'/'both' fall back to 'email' when it isn't - the same guard Create
 * Booking / Edit Event / On The Way already apply via `useSmsProviderStatus`.
 */
function resolveNotifyChannel(deps: RoutyToolDeps, desired: NotifyChannel): { channel: NotifyChannel; smsUnavailable: boolean } {
  if (deps.env.smsProviderConnected || desired === 'email') return { channel: desired, smsUnavailable: false };
  return { channel: 'email', smsUnavailable: true };
}

function notifyChannelLabel(channel: NotifyChannel): string {
  return channel === 'sms' ? 'SMS' : channel === 'both' ? 'SMS & email' : 'email';
}

/** A confirmation-card line warning that SMS isn't available, so texting silently downgraded to email. */
const SMS_UNAVAILABLE_LINE: ConfirmLine = {
  icon: 'bell',
  text: 'No SMS provider connected (Settings > Integrations) — this will notify by email only.',
};

/**
 * Build the customer-notification toggle for a confirmation card, downgrading
 * to email-only (and flagging it) when no SMS provider is connected. Returns
 * the `notify` option for the card, the channel that will ACTUALLY be used if
 * the user opts in, and an optional warning line to surface on the card.
 */
function buildNotifyOption(
  deps: RoutyToolDeps,
  desired: NotifyChannel,
  wantsNotify: boolean,
  onDecision: (notify: boolean) => void,
): { notify: ConfirmNotifyOption; effectiveChannel: NotifyChannel; warningLine?: ConfirmLine } {
  const { channel, smsUnavailable } = resolveNotifyChannel(deps, desired);
  return {
    notify: { channelLabel: notifyChannelLabel(channel), defaultOn: wantsNotify, onDecision },
    effectiveChannel: channel,
    warningLine: smsUnavailable ? SMS_UNAVAILABLE_LINE : undefined,
  };
}

async function applyAppointmentUpdate(
  appointmentId: string,
  opts: {
    startUtc?: Date;
    endUtc?: Date;
    status?: Routemize_Constants_AppointmentStatus;
    /** Send confirmation on both channels (legacy shorthand for notifyChannel 'both'). */
    notify?: boolean;
    /** Explicit confirmation channel; overrides `notify`. */
    notifyChannel?: NotifyChannel;
    /** Toggle the SMS reminder flag (leaves it as-is when undefined). */
    sendSmsReminders?: boolean;
    /** Toggle the email reminder flag (leaves it as-is when undefined). */
    sendEmailReminders?: boolean;
    /** Replace the appointment's location entirely (leaves it as-is when undefined). */
    location?: { formatted: string; lat: number; lng: number };
    /** Set the gate/access code (leaves it as-is when undefined). */
    accessCode?: string;
    /** Reassign to this user (leaves the assignee as-is when undefined). */
    assignedUserId?: string;
  },
  deps: RoutyToolDeps,
) {
  const current = await AppointmentService.getApiAppAppointmentGetById(appointmentId);

  // Rescheduling an appointment that sits in an INACTIVE status (Cancelled /
  // No-Show) restores it to Scheduled (QA 86bavddna): the user is putting it
  // back on the calendar, and keeping the old status would leave a
  // "rescheduled" appointment invisible to the normal dispatch flow. An
  // explicit opts.status from the caller always wins.
  const isTimeChange = !!(opts.startUtc || opts.endUtc);
  const wasInactive =
    current.status === Routemize_Constants_AppointmentStatus.CANCELLED ||
    current.status === Routemize_Constants_AppointmentStatus.NO_SHOW;
  const restoredToScheduled = !opts.status && isTimeChange && wasInactive;

  const channel: NotifyChannel | null = opts.notifyChannel ?? (opts.notify === true ? 'both' : null);
  // Hard safety net: never actually flip sendSmsConfirmation/sendSmsReminders on
  // if no SMS provider is connected, regardless of what an upstream caller
  // requested - callers should already downgrade via resolveNotifyChannel/
  // buildNotifyOption before this point, but this guarantees it either way.
  const smsOk = deps.env.smsProviderConnected;
  const notifyFlags =
    channel !== null
      ? {
          sendSmsConfirmation: smsOk && (channel === 'sms' || channel === 'both'),
          sendEmailConfirmation: channel === 'email' || channel === 'both',
        }
      : {
          sendSmsConfirmation: smsOk ? (current.sendSmsConfirmation ?? null) : false,
          sendEmailConfirmation: current.sendEmailConfirmation ?? null,
        };

  const dto: Routemize_DTOs_UpdateAppointmentDTO = {
    id: appointmentId,
    status:
      opts.status ??
      (restoredToScheduled ? Routemize_Constants_AppointmentStatus.SCHEDULED : current.status) ??
      Routemize_Constants_AppointmentStatus.SCHEDULED,
    startTimeUtc: (opts.startUtc ?? parseBackendDate(current.startTimeUtc) ?? undefined)?.toISOString() ?? null,
    endTimeUtc: (opts.endUtc ?? parseBackendDate(current.endTimeUtc) ?? undefined)?.toISOString() ?? null,
    addressLine1: opts.location ? opts.location.formatted : current.addressLine1 ?? null,
    // A full location replacement supersedes the old line2/city/state/zip - the
    // new formatted address is the source of truth going forward.
    addressLine2: opts.location ? null : current.addressLine2 ?? null,
    city: opts.location ? null : current.city ?? null,
    state: opts.location ? null : current.state ?? null,
    zipCode: opts.location ? null : current.zipCode ?? null,
    latitude: opts.location ? opts.location.lat : current.latitude ?? null,
    longitude: opts.location ? opts.location.lng : current.longitude ?? null,
    // No `notes`: the deprecated shim would re-write the computed text as a
    // note. `stopLabel` is omitted (null/absent = unchanged) - no caller here
    // changes a stop's label.
    accessCode: opts.accessCode ?? current.accessCode ?? null,
    sendSmsReminders: smsOk ? (opts.sendSmsReminders ?? current.sendSmsReminders ?? null) : false,
    sendEmailReminders: opts.sendEmailReminders ?? current.sendEmailReminders ?? null,
    // A new ad-hoc location is no longer the contact's saved address record.
    contactAddressId: opts.location ? null : current.contactAddressId ?? null,
    ...(opts.assignedUserId ? { assignedUserId: opts.assignedUserId } : {}),
    ...notifyFlags,
  };

  await AppointmentService.putApiAppAppointmentUpdate(dto);
  return { restoredToScheduled, previousStatus: current.status ?? null };
}

// ---------------------------------------------------------------------------
// Slot categorization (shared by reschedule + booking flows)
// ---------------------------------------------------------------------------

/** Normalize a slot time ("HH:MM" or "HH:MM:SS") to a 24h "HH:MM" string. */
function normSlotTime(t?: string | null): string {
  if (!t) return '';
  const m = String(t).match(/^(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : String(t);
}

function slotTypeLabel(slot: Routemize_DTOs_SlotSuggestionDTO): string {
  const factor = (slot.matchFactors ?? []).find((f) => (f.key || '').toLowerCase() === 'slot_type');
  return (factor?.label || '').toLowerCase();
}

/**
 * Categorize scheduling-engine slots into the same buckets the reschedule /
 * booking UI shows (Top Choice / Proximity / Customer Preference), best-first,
 * and map them to the RoutySlotOption artifact shape.
 */
function categorizeSlots(slots: Routemize_DTOs_SlotSuggestionDTO[], timezone: string | undefined): RoutySlotOption[] {
  const future = slots.filter(
    (s) =>
      s.suggestedDate &&
      s.startTime &&
      isFutureTenantSlot(s.suggestedDate, normSlotTime(s.startTime), timezone),
  );
  if (future.length === 0) return [];

  const byScore = [...future].sort((a, b) => (b.routeScore ?? 0) - (a.routeScore ?? 0));
  const used = new Set<Routemize_DTOs_SlotSuggestionDTO>();
  const picked: Array<{ slot: Routemize_DTOs_SlotSuggestionDTO; category: string }> = [];

  const take = (slot: Routemize_DTOs_SlotSuggestionDTO | undefined, category: string) => {
    if (!slot || used.has(slot)) return;
    used.add(slot);
    picked.push({ slot, category });
  };

  // Categories come from the engine's slot-type tag, NEVER from score order —
  // a 100% preference-match customer slot can outscore the company slot, but
  // "Top Choice" must always be the COMPANY (route efficiency) slot:
  //   Top Choice = company/route-efficiency slot
  //   Proximity = proximity slot
  //   Customer Preference = customer slot
  take(
    byScore.find((s) => /company|efficien/.test(slotTypeLabel(s))) ??
      byScore.find((s) => !slotTypeLabel(s)) ??
      byScore[0],
    'Top Choice',
  );
  take(byScore.find((s) => !used.has(s) && /proximity/.test(slotTypeLabel(s))), 'Proximity');
  take(byScore.find((s) => !used.has(s) && /customer|preference/.test(slotTypeLabel(s))), 'Customer Preference');
  // The rest by score, for the "more options" expander.
  for (const s of byScore) {
    if (picked.length >= 8) break;
    take(s, 'Option');
  }

  return picked.map(({ slot, category }) => ({
    slotId: slot.id ?? null,
    category,
    // Backend DateOnly serializes as "YYYY-MM-DDT00:00:00" - keep only the
    // date key so cards, follow-up messages, and confirm_booking stay clean.
    date: String(slot.suggestedDate).split('T')[0],
    startTime: normSlotTime(slot.startTime),
    endTime: normSlotTime(slot.endTime),
    routeScore: slot.routeScore ?? null,
    driveInMinutes:
      slot.previousAppointmentTravelTimeMinutes != null
        ? Math.round(slot.previousAppointmentTravelTimeMinutes)
        : null,
    driveOutMinutes:
      slot.nextAppointmentTravelTimeMinutes != null ? Math.round(slot.nextAppointmentTravelTimeMinutes) : null,
    previousLabel: slot.previousAppointmentLocation ?? null,
    nextLabel: slot.nextAppointmentLocation ?? null,
  }));
}

// ---------------------------------------------------------------------------
// Tool executors
// ---------------------------------------------------------------------------

async function execGetSchedule(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const date = parseLocalDateTime(String(input.date));
  if (!date) throw new Error('Invalid date. Use YYYY-MM-DD.');
  const includeNotes = input.includeNotes === true;
  const { userId, isOther } = resolveAssignee(input, deps);

  deps.onActivity?.(`Checking ${isOther ? `${assigneeLabel(input, isOther)}'s` : 'the'} schedule for ${input.date}`);
  const [appointments, scheduleByDay, overridesByDate] = await Promise.all([
    fetchDayAppointments(userId, date, deps.env.companyTimezone),
    fetchWorkScheduleByDay(userId),
    // Date-specific availability overrides (day off / vacation / marked
    // available) for THIS person, so a working day with nothing booked is
    // never confused with a day they're actually off.
    fetchUserOverridesByDate(userId),
  ]);
  const hours = getEffectiveDayAvailability(date, scheduleByDay, overridesByDate);
  const now = tenantShiftedNow(deps.env.companyTimezone);

  // Only Routemize-managed appointments are ever presented as something to
  // act on (reschedule/status-change/etc.) - external calendar copies
  // (Google/Outlook/Zoom) can't be edited through Routemize.
  const sorted = appointments
    .filter(isRoutemizeManaged)
    .sort((a, b) => a.startDateTime.getTime() - b.startDateTime.getTime());

  // Agenda-with-notes (QA 86bavp433): the notes (the appointment's notes
  // bundle - customer, pre-booking and appointment notes) live behind a
  // per-appointment endpoint, so they are only fetched when the user actually
  // asked for notes.
  const notesById = new Map<string, Awaited<ReturnType<typeof loadAppointmentNotesForAi>>>();
  if (includeNotes && sorted.length > 0) {
    deps.onActivity?.('Reading appointment notes');
    await Promise.all(
      sorted
        .filter((a) => !!a.id)
        .slice(0, 15)
        .map(async (a) => {
          try {
            const notes = await loadAppointmentNotesForAi(a.id!, a.status === 'cancelled');
            if (Object.keys(notes).length > 0) notesById.set(a.id!, notes);
          } catch {
            // Best-effort - a failed notes fetch never breaks the agenda.
          }
        }),
    );
  }

  // External calendar events are surfaced SEPARATELY (QA 86bavnm8g): they
  // block availability and the user may ask about them directly ("any Google
  // Calendar conflicts tomorrow?"), so hiding them made Routy claim it can't
  // see them. Listing them apart from `appointments` keeps them out of the
  // actionable flows and the one-appointment heuristics below.
  const externalEvents = appointments
    .filter((a) => !isRoutemizeManaged(a))
    .sort((a, b) => a.startDateTime.getTime() - b.startDateTime.getTime())
    .map((a) => ({
      source: a.source ?? 'External',
      title: a.type || a.customerName || 'Busy',
      startTime: a.startTime,
      endTime: a.endTime,
    }));
  const externalNote =
    externalEvents.length > 0
      ? ` externalEvents lists ${externalEvents.length} EXTERNAL calendar event(s) synced from Google/Outlook/Zoom - ` +
        'they BLOCK that time (slot searches already avoid them) but are READ-ONLY copies. When mentioning one, ' +
        'label it by its source (e.g. "a Google Calendar event"), never as a regular appointment, and never offer ' +
        'to reschedule/cancel/act on it.'
      : '';

  // Counting (QA: "how many appointments today" was always off): the timeline
  // includes cancelled/no-show records, so a bare list length overcounts what
  // a user means by "appointments scheduled today". Report an explicit split
  // so the model answers with the ACTIVE number and can mention the rest.
  const statusBreakdown: Record<string, number> = {};
  for (const a of sorted) statusBreakdown[a.status] = (statusBreakdown[a.status] ?? 0) + 1;
  const activeAppointmentCount = sorted.filter((a) => a.status !== 'cancelled' && a.status !== 'no-show').length;

  // Availability verdict (QA: empty days were being summarized as "Off"): the
  // difference between OFF and OPEN comes from working hours + this person's
  // date overrides - NEVER from whether anything happens to be booked.
  const dayStatus = !hours.isWorkDay
    ? describeDayOff(hours, date, assigneePossessive(input, isOther))
    : activeAppointmentCount === 0
      ? `OPEN working day (${hours.start}-${hours.end}) with NOTHING booked. Describe it as open/free/wide open - ` +
        'NEVER as "off". A day is only "off" when isWorkDay is false.'
      : `Working day, hours ${hours.start}-${hours.end}.`;

  return {
    date: input.date,
    for: isOther ? assigneeLabel(input, isOther) : 'you',
    // `now` is already in the tenant-shifted frame (its local getters hold the tenant's
    // wall-clock digits) — do NOT also pass `companyTimezone` here, which would reinterpret
    // its (meaningless) underlying UTC instant and double-shift the reported time.
    currentTime: formatTimeOfDay24(now),
    isWorkDay: hours.isWorkDay,
    workingHours: hours.isWorkDay ? { start: hours.start, end: hours.end } : null,
    dayStatus,
    ...(hours.blockedWindows && hours.blockedWindows.length > 0
      ? {
          blockedWindows: hours.blockedWindows,
          blockedWindowsNote:
            'blockedWindows are date-specific UNAVAILABLE blocks inside the working day (e.g. lunch, out of ' +
            'office) - mention them when summarizing the day, but they do not make the whole day "off".',
        }
      : {}),
    appointmentCount: sorted.length,
    activeAppointmentCount,
    statusBreakdown,
    countingRule:
      'When the user asks HOW MANY appointments they have, answer with activeAppointmentCount (cancelled ' +
      'and no-show excluded) - mention cancelled/no-show records separately only if relevant. This list is ' +
      `${isOther ? "one team member's" : "the operator's own"} calendar only, NOT the whole company - for a ` +
      'company-wide count or summary use get_daily_summary.',
    appointments: sorted.map((a) => ({
      ...appointmentToJson(a, now),
      ...(includeNotes && a.id ? notesById.get(a.id) : undefined),
    })),
    ...(externalEvents.length > 0 ? { externalEventCount: externalEvents.length, externalEvents } : {}),
    ...(includeNotes
      ? {
          notesIncluded: true,
          noteFieldsExplained:
            'Each appointment carries `customerNote` (what the customer wrote), `preBookingNotes` (staff ' +
            'notes on the request before booking), `appointmentNotes` (staff notes on the appointment) and, ' +
            'for a cancelled one, `cancellationReason` - each only when it exists. An appointment WITHOUT ' +
            'those fields genuinely has no notes on file. `stopLabel` marks an ad-hoc stop (a label, not a note).',
        }
      : {}),
    note:
      ((sorted.length === 1
        ? 'Exactly ONE appointment this day - if the user asked to reschedule/change/cancel "my appointment" ' +
          'or named this day, this IS the one they mean (even if the time they mentioned differs from its ' +
          "current time - they're describing the NEW time, not identifying which appointment). Act on it " +
          'directly; do NOT ask which appointment or call present_appointments.'
        : sorted.length > 1
          ? 'Multiple appointments this day - if it is not obvious which one the user means, call ' +
            'present_appointments with one card per match so they tap the right one instead of asking in prose.'
          : '') + externalNote) || undefined,
  };
}

/**
 * Answer "why can't I book this day?" — the question the slot picker's "Don't
 * see the time you need?" button asks on the operator's behalf.
 *
 * The order of investigation is the whole point. The AVAILABILITY SCHEDULE is
 * resolved before anything on the calendar, because a weekday switched off (or
 * a date marked as time off) closes the day outright: no appointment is the
 * cause and clearing calendar events would change nothing. Only once the day
 * is genuinely open do the appointments and synced calendar events sitting on
 * it become the real explanation — and the address-less synced events get
 * flagged, since those are the ones that are usually personal blocks rather
 * than field work, and clearing them is what actually frees the day up.
 */
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
const WEEKDAY_ALLOW_FLAGS = [
  'allowSunday',
  'allowMonday',
  'allowTuesday',
  'allowWednesday',
  'allowThursday',
  'allowFriday',
  'allowSaturday',
] as const;

/** Raw booking-form DTOs (with eventNature etc.), cached per session. */
let rawBookingFormsCache: Routemize_DTOs_BookingFormDto[] | null = null;
const formSettingsCache = new Map<string, Routemize_DTOs_BookingFormSettingDto | null>();

/** Drop every cached form, setting and question list so the next read sees setup changes. */
export function invalidateBookingFormCaches(): void {
  rawBookingFormsCache = null;
  publishedFormsCache = null;
  formSettingsCache.clear();
  formQuestionsCache.clear();
}

export async function fetchRawBookingForms(): Promise<Routemize_DTOs_BookingFormDto[]> {
  if (!rawBookingFormsCache) {
    const res = await BookingFormService.getApiAppBookingFormGetList(undefined, 0, 100);
    rawBookingFormsCache = ((res as { items?: Routemize_DTOs_BookingFormDto[] })?.items ?? []) as Routemize_DTOs_BookingFormDto[];
  }
  return rawBookingFormsCache;
}

async function fetchFormSettings(formId: string): Promise<Routemize_DTOs_BookingFormSettingDto | null> {
  if (formSettingsCache.has(formId)) return formSettingsCache.get(formId) ?? null;
  try {
    const settings = await BookingFormSettingService.getApiAppBookingFormSettingGetByFormId(formId);
    formSettingsCache.set(formId, settings ?? null);
    return settings ?? null;
  } catch {
    formSettingsCache.set(formId, null);
    return null;
  }
}

function isPublishedInternalForm(form: Routemize_DTOs_BookingFormDto): boolean {
  return !!form.id && form.status === Routemize_Constants_BookingFormStatus.PUBLISHED && !form.leadSource;
}

/**
 * Resolve the booking form the operator is actually booking — by id first,
 * then by the service name they said. Without this the audit can only read
 * the calendar, and form settings (same-day off, min notice) stay invisible.
 */
async function resolveBookingForm(input: Record<string, unknown>): Promise<Routemize_DTOs_BookingFormDto | null> {
  const forms = await fetchRawBookingForms();
  const formId = input.bookingFormId ? String(input.bookingFormId).trim() : '';
  if (formId) {
    const byId = forms.find((f) => f.id === formId);
    if (byId) return byId;
  }

  const hint = input.serviceHint ? String(input.serviceHint).trim().toLowerCase() : '';
  if (!hint) return null;
  const published = forms.filter(isPublishedInternalForm);
  const exact = published.filter((f) => (f.formName || '').toLowerCase() === hint);
  if (exact.length > 0) return exact[0];
  const matches = published.filter((f) => formNameMatchesHint(f.formName || '', hint));
  return matches[0] ?? null;
}

function allowedWeekdaysFromSettings(settings: Routemize_DTOs_BookingFormSettingDto | null): string[] {
  if (!settings) return [...WEEKDAY_NAMES];
  const allowed = WEEKDAY_NAMES.filter((_, i) => settings[WEEKDAY_ALLOW_FLAGS[i]] !== false);
  // Same fallback as the New Booking modal: every day switched off is a
  // misconfiguration, not "this form never books".
  return allowed.length === 0 ? [...WEEKDAY_NAMES] : allowed;
}

/**
 * Form settings that skip a day the calendar would still take. Ranked the same
 * way the engine skips: weekday, booking window, same-day off, cutoff, then
 * minimum notice covering the whole day.
 */
function evaluateFormRule(
  form: Routemize_DTOs_BookingFormDto,
  settings: Routemize_DTOs_BookingFormSettingDto | null,
  dateKey: string,
  todayKey: string,
  nowTime: string,
  dayEndTime: string | null,
): AvailabilityFormRule {
  const name = form.formName || 'This form';
  const allowSameDayBooking = form.allowSameDayBooking !== false;
  const cutoff = settings?.sameDayBookingCutoffTime ?? null;
  const minimumNoticeHours = settings?.minimumNoticeHours ?? null;
  const maxDays = settings?.maxSchedulingDaysFromToday ?? null;
  const allowedWeekdays = allowedWeekdaysFromSettings(settings);
  const firstAppointmentTime = settings?.firstAppointmentTime ?? null;
  const lastAppointmentTime = settings?.lastAppointmentTime ?? null;
  const daysOut = daysBetweenDateKeys(todayKey, dateKey);
  const isToday = daysOut === 0;
  const weekday = WEEKDAY_NAMES[getWeekdayFromDateKey(dateKey)] ?? 'Sunday';

  const hold = (reason: AvailabilityFormRuleReason, summary: string): AvailabilityFormRule => ({
    bookingFormId: form.id || '',
    name,
    holds: true,
    reason,
    summary,
    allowSameDayBooking,
    sameDayBookingCutoffTime: cutoff,
    minimumNoticeHours,
    maxSchedulingDaysFromToday: maxDays,
    allowedWeekdays,
    firstAppointmentTime,
    lastAppointmentTime,
  });

  if (!allowedWeekdays.includes(weekday)) {
    return hold(
      'weekdayOff',
      `${name} does not offer ${weekday}s — that's a form setting, not the calendar`,
    );
  }

  if (maxDays != null && daysOut > maxDays) {
    return hold(
      'tooFarOut',
      `${name} only books ${maxDays} day${maxDays === 1 ? '' : 's'} ahead`,
    );
  }

  if (isToday && !allowSameDayBooking) {
    return hold(
      'sameDayOff',
      `Same-day booking is turned off on ${name}`,
    );
  }

  if (isToday && allowSameDayBooking && cutoff) {
    const nowMinutes = timeStringToMinutes(nowTime);
    const cutoffMinutes = timeStringToMinutes(cutoff);
    if (nowMinutes != null && cutoffMinutes != null && nowMinutes >= cutoffMinutes) {
      return hold(
        'sameDayCutoff',
        `${name} stops offering same-day after ${formatTimeString(cutoff)}`,
      );
    }
  }

  if (minimumNoticeHours != null && minimumNoticeHours > 0) {
    const nowDt = parseLocalDateTime(todayKey, nowTime);
    const dayEnd = parseLocalDateTime(dateKey, lastAppointmentTime || dayEndTime || '23:59');
    if (nowDt && dayEnd) {
      const earliest = addMinutes(nowDt, Math.round(minimumNoticeHours * 60));
      if (earliest.getTime() >= dayEnd.getTime()) {
        return hold(
          'minimumNotice',
          `${name} needs ${minimumNoticeHours} hour${minimumNoticeHours === 1 ? '' : 's'} of notice, which skips this day`,
        );
      }
    }
  }

  return {
    bookingFormId: form.id || '',
    name,
    holds: false,
    reason: null,
    summary: null,
    allowSameDayBooking,
    sameDayBookingCutoffTime: cutoff,
    minimumNoticeHours,
    maxSchedulingDaysFromToday: maxDays,
    allowedWeekdays,
    firstAppointmentTime,
    lastAppointmentTime,
  };
}

/**
 * Resolve what is actually being booked into the day.
 *
 * A gap length on its own explains nothing — "there is a 60-minute opening" and
 * "the job needs 60 minutes before anyone drives anywhere" are the same day
 * described two ways, and only the second one tells the operator why the
 * booking engine keeps skipping it. Prefers the booking form (authoritative
 * duration AND buffers) and falls back to whatever the caller could supply.
 */
async function resolveServiceFit(
  input: Record<string, unknown>,
  form?: Routemize_DTOs_BookingFormDto | null,
): Promise<AvailabilityServiceFit | null> {
  const resolved = form === undefined ? await resolveBookingForm(input) : form;
  if (resolved) {
    const durationMinutes = resolved.timeAtLocationMinutes ?? (Number(input.serviceDurationMinutes) || 0);
    const bufferMinutes =
      (resolved.defaultPreAppointmentBufferMinutes ?? 0) + (resolved.defaultPostAppointmentBufferMinutes ?? 0);
    if (durationMinutes > 0) {
      return {
        name: resolved.formName || null,
        durationMinutes,
        bufferMinutes,
        totalMinutes: durationMinutes + bufferMinutes,
      };
    }
  }

  const durationMinutes = Number(input.serviceDurationMinutes) || 0;
  if (durationMinutes <= 0) return null;
  const bufferMinutes = Number(input.serviceBufferMinutes) || 0;
  return {
    name: input.serviceName ? String(input.serviceName) : null,
    durationMinutes,
    bufferMinutes,
    totalMinutes: durationMinutes + bufferMinutes,
  };
}

/** Best-effort driving leg. Never throws — a missing route degrades to null. */
async function driveLegBetween(
  from: { lat: number; lng: number } | null,
  to: { lat: number; lng: number } | null,
): Promise<{ minutes: number; miles: number } | null> {
  if (!from || !to) return null;
  const r = await googleMapsDistanceService.calculateDrivingDistance(from, to).catch(() => null);
  return r ? { minutes: Math.round(r.duration), miles: Number(r.distance.toFixed(1)) } : null;
}

/**
 * "2h for an Estimate" rather than "2h of work". The length only means
 * something next to what it is being held for — the operator thinks in
 * services, not in abstract blocks of time.
 */
function serviceFitLabel(fit: AvailabilityServiceFit): string {
  const total = formatDurationMinutes(fit.totalMinutes);
  if (!fit.name) return `${total} of work`;
  const article = /^[aeiou]/i.test(fit.name) ? 'an' : 'a';
  return `${total} for ${article} ${fit.name}`;
}

/** Flatten a window into the shape the model reads back. */
function describeSlot(s: AvailabilityOpenSlot) {
  return {
    window: `${s.startLabel} – ${s.endLabel}`,
    startTime: s.startTime,
    endTime: s.endTime,
    minutes: s.durationMinutes,
    after: s.afterTitle,
    before: s.beforeTitle,
    driveBetweenMinutes: s.bracketDriveMinutes,
    usableMinutes: s.usableMinutes,
    fits: s.fits,
    shortByMinutes: s.shortByMinutes,
  };
}

/** Strip the leading time off a gap label ("11:30 AM Karen Sell" -> "Karen Sell"). */
function gapLabelTitle(label: string | null): string | null {
  if (!label) return null;
  return label.replace(/^\d{1,2}:\d{2}\s*(?:AM|PM)\s*/i, '').trim() || label;
}

/**
 * Turn the day's raw gaps into windows an operator can reason about: how long
 * each one is, what pins either end, how much of it the surrounding drive
 * already eats, and whether the job clears what's left.
 *
 * Gaps below the timeline's own threshold are dropped so this card and the
 * timeline never disagree about what is on the day — if the timeline does not
 * show a gap there, neither does the audit.
 *
 * The drive figure is deliberately the direct hop between the bracketing stops.
 * Dropping a job in the middle can only ever add to that, so it is a floor —
 * which makes "this window is already 18 minutes of driving" a claim that can
 * never overstate the problem.
 */
async function buildOpenSlots(
  gaps: ReturnType<typeof findOpenGaps>,
  fit: AvailabilityServiceFit | null,
): Promise<AvailabilityOpenSlot[]> {
  return Promise.all(
    gaps
      .filter((g) => g.durationMinutes >= MIN_GAP_MINUTES)
      .map(async (g) => {
        const drive = await driveLegBetween(g.prevCoords, g.nextCoords);
        const usableMinutes = Math.max(0, g.durationMinutes - (drive?.minutes ?? 0));
        const startTime = formatTimeOfDay24(g.startTime);
        const endTime = formatTimeOfDay24(g.endTime);
        return {
          startTime,
          endTime,
          startLabel: formatTimeString(startTime),
          endLabel: formatTimeString(endTime),
          durationMinutes: g.durationMinutes,
          afterTitle: gapLabelTitle(g.prevLabel),
          beforeTitle: gapLabelTitle(g.nextLabel),
          bracketDriveMinutes: drive?.minutes ?? null,
          bracketDriveMiles: drive?.miles ?? null,
          usableMinutes,
          fits: fit ? usableMinutes >= fit.totalMinutes : null,
          shortByMinutes: fit ? Math.max(0, fit.totalMinutes - usableMinutes) : null,
        };
      }),
  );
}

async function execAuditAvailability(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const date = parseLocalDateTime(String(input.date));
  if (!date) throw new Error('Invalid date. Use YYYY-MM-DD.');
  // A requestId means this audit is about a booking actually in flight. The
  // request already knows the service and the day the customer asked for, so
  // the audit measures the right form without anyone re-typing it — and when
  // the answer turns out to be "this day was never asked for", the fix can be
  // re-run against this same request instead of starting the booking over.
  const requestId = input.requestId ? String(input.requestId) : '';
  const request = requestId ? await loadRequestContext(requestId).catch(() => null) : null;
  if (request?.bookingFormId && !input.bookingFormId) {
    input = { ...input, bookingFormId: request.bookingFormId };
  }
  const { userId, isOther } = resolveAssignee(input, deps);
  const dateKey = toDateKey(date);
  const dateLabel = formatDateMedium(date);
  const now = tenantShiftedNow(deps.env.companyTimezone);
  const todayKey = toDateKey(now);
  const nowTime = formatTimeOfDay24(now);

  deps.onActivity?.(`Checking what's blocking ${dateLabel}`);
  const [appointments, scheduleByDay, overridesByDate, form] = await Promise.all([
    fetchDayAppointments(userId, date, deps.env.companyTimezone),
    fetchWorkScheduleByDay(userId),
    fetchUserOverridesByDate(userId),
    resolveBookingForm(input),
  ]);
  const hours = getEffectiveDayAvailability(date, scheduleByDay, overridesByDate);
  const settings = form?.id ? await fetchFormSettings(form.id) : null;
  const formRule = form
    ? evaluateFormRule(form, settings, dateKey, todayKey, nowTime, hours.isWorkDay ? hours.end : null)
    : null;

  // Cancelled/no-show records still ride along on the timeline but hold no
  // time, so counting them would blame the wrong thing for a "full" day.
  const active = appointments.filter((a) => a.status !== 'cancelled' && a.status !== 'no-show');

  // Only things that actually overlap the working window can be the reason a
  // day won't book. A 5 AM "Wake up" or a 6:30 AM "Family time" sits entirely
  // outside a 9–5 and costs no bookable minute, so listing it just buries the
  // real blockers and invites the operator to clear events for nothing.
  const workStart = hours.isWorkDay ? parseLocalDateTime(dateKey, hours.start) : null;
  const workEnd = hours.isWorkDay ? parseLocalDateTime(dateKey, hours.end) : null;
  const onDay = active.filter(makeWorkingHoursFilter(date, hours));
  const outsideWorkingHoursCount = active.length - onDay.length;
  const byStart = (a: Appointment, b: Appointment) => a.startDateTime.getTime() - b.startDateTime.getTime();
  const managed = onDay.filter(isRoutemizeManaged).sort(byStart);
  const external = onDay.filter((a) => !isRoutemizeManaged(a)).sort(byStart);
  const externalAvailability = external.length > 0 ? await googleCalendarAvailabilityMode() : 'unknown';

  // Where the day's route actually starts, so "how far away is this?" has an
  // origin. Only meaningful when auditing the operator — a teammate's own
  // endpoints aren't in env, and guessing one would produce confident nonsense.
  const base = isOther ? null : deps.env.startingCoordinates ?? null;
  // A 0,0 pair is the geocoder's way of saying it found nothing, so treating
  // it as a real point would put the stop off the coast of Africa and blow up
  // every distance on the day.
  const isLocated = (a: Appointment): boolean =>
    a.latitude != null && a.longitude != null && !(a.latitude === 0 && a.longitude === 0);
  const milesFromBase = (a: Appointment): number | null =>
    base && isLocated(a)
      ? Math.round(calculateDistance(base.lat, base.lng, a.latitude!, a.longitude!))
      : null;

  const toBlocker = (a: Appointment, kind: 'appointment' | 'external'): AvailabilityBlocker => {
    const address = (a.address || '').trim();
    const unlocatedAddress = !!address && !isLocated(a);
    return {
      id: a.id || null,
      kind,
      ...(kind === 'external' ? { source: a.source ?? 'External' } : {}),
      title: a.type || a.customerName || (kind === 'external' ? 'Busy' : 'Appointment'),
      startTime: a.startTime,
      endTime: a.endTime,
      durationMinutes: Math.max(0, getTimeDifferenceInMinutes(a.startDateTime, a.endDateTime)),
      address: address || null,
      milesFromBase: milesFromBase(a),
      ...(kind === 'appointment' ? { status: a.status } : {}),
      missingAddress: !address,
      unlocatedAddress,
      ...(unlocatedAddress ? { addressGaps: describeAddressGaps(address) } : {}),
      removable: kind === 'external' && !!a.id,
    };
  };
  const blockers: AvailabilityBlocker[] = [
    ...managed.map((a) => toBlocker(a, 'appointment')),
    ...external.map((a) => toBlocker(a, 'external')),
  ];

  // An all-day "Busy" block is the single most common reason a day looks full
  // for no visible reason: nobody thinks of a birthday or a weekly note as
  // scheduling, but Routemize treats every synced event as busy time.
  const longBlocks: AvailabilityLongBlock[] = external
    .filter(isBlockingExternalEvent)
    .map((a) => ({
      id: a.id || null,
      title: a.type || a.customerName || 'Busy',
      source: a.source ?? 'External',
      isAllDay: isAllDayExternalEvent(a),
      durationMinutes: Math.max(0, getTimeDifferenceInMinutes(a.startDateTime, a.endDateTime)),
      removable: !!a.id,
    }));

  // Geography check. A day pinned by an airport run at 8am and a hotel in
  // another state at 1pm has plenty of clock gaps and zero bookable minutes,
  // and clock-only reasoning will confidently call it open.
  const travel = ((): AvailabilityTravelLoad | null => {
    // Deliberately the WHOLE day, not just the in-window set: the 8am airport
    // run that ends right as the workday starts is the clearest evidence the
    // operator is leaving town, and dropping it hides the reason entirely.
    const located = active.filter(isLocated).sort(byStart);
    // Counted so the card and the model can say the geography is only partly
    // known, instead of quoting a reassuring mileage that simply skipped stops.
    const unmeasuredStops = active.filter((a) => !isLocated(a) && !!(a.address || '').trim()).length;
    if (located.length < 2 || !workStart || !workEnd) return null;

    let totalMiles = 0;
    let longestLeg: AvailabilityTravelLoad['longestLeg'] = null;
    const label = (a: Appointment) => a.type || a.customerName || 'Busy';
    for (let i = 1; i < located.length; i++) {
      const from = located[i - 1];
      const to = located[i];
      const miles = calculateDistance(from.latitude!, from.longitude!, to.latitude!, to.longitude!);
      totalMiles += miles;
      if (!longestLeg || miles > longestLeg.miles) {
        longestLeg = { fromTitle: label(from), toTitle: label(to), miles: Math.round(miles) };
      }
    }

    // Straight-line miles at a generous highway average. Deliberately optimistic
    // so the "you cannot physically do this" call is never a false alarm.
    const estimatedDriveMinutes = Math.round((totalMiles / 55) * 60);
    const workingMinutes = Math.max(0, getTimeDifferenceInMinutes(workStart, workEnd));
    return {
      totalMiles: Math.round(totalMiles),
      estimatedDriveMinutes,
      workingMinutes,
      longestLeg,
      exceedsWorkday: estimatedDriveMinutes > workingMinutes,
      unmeasuredStops,
    };
  })();

  // How much room is actually left inside the working window once everything
  // on the day is subtracted — the difference between "packed" and "there IS
  // room, something narrower is in the way".
  // Fed the in-window set for the same reason: an early-morning block sitting
  // before the day starts would otherwise anchor a "gap" that runs from 5:30 AM
  // to the first real job and overstate how much bookable room is left.
  const openGaps = hours.isWorkDay
    ? findOpenGaps(onDay, date, hours.start, hours.end, tenantShiftedNow(deps.env.companyTimezone))
    : [];
  const largestGapMinutes = openGaps.reduce((max, g) => Math.max(max, g.durationMinutes), 0);

  // What the openings are being measured against. Without it the card can only
  // report gap lengths, which is the version of this answer that reads as "the
  // day looks open to me too".
  const serviceFit = await resolveServiceFit(input, form);
  const windows = await buildOpenSlots(openGaps, serviceFit);
  // Everything booked through Routemize goes to an address, so a window the
  // job cannot physically fit is not an opening — calling it one is what made
  // a day with nothing bookable report "5 openings". Only real ones ship; the
  // nearest miss rides along so the answer can still be specific.
  const openSlots = windows.filter((s) => s.fits !== false);
  const closestMiss =
    openSlots.length === 0
      ? windows.reduce<AvailabilityOpenSlot | null>(
          (best, s) => (!best || s.usableMinutes > best.usableMinutes ? s : best),
          null,
        )
      : null;

  const removableNoAddress = blockers.filter((b) => b.removable && b.missingAddress);
  // Events specific enough to name a street that still never resolved to a
  // point. They cost bookable time while being invisible to every distance
  // check, so the engine's answer looks arbitrary — which is precisely the
  // complaint that brings someone to ask why a day won't book.
  const unlocatedBlockers = blockers.filter((b) => b.unlocatedAddress);
  // A form setting that skips the day outranks the calendar: openings can
  // still be real, but the engine will not offer them. A closed availability
  // day still wins, because nobody is working.
  const formHolds = !!formRule?.holds && hours.isWorkDay;
  const outcome: 'dayOff' | 'formRule' | 'full' | 'partial' = !hours.isWorkDay
    ? 'dayOff'
    : formHolds
      ? 'formRule'
      : largestGapMinutes < 30 || travel?.exceedsWorkday
        ? 'full'
        : 'partial';

  const allDayBlock = longBlocks.find((b) => b.isAllDay);
  const possessive = assigneePossessive(input, isOther);
  const verdict = !hours.isWorkDay
    ? hours.dayOffReason
      ? `Marked off — "${hours.dayOffReason}"`
      : hours.isPersonalSchedule
        ? `${formatWeekdayLong(date)}s are switched off on ${possessive} availability`
        : `${formatWeekdayLong(date)}s are not working days on the company schedule`
    : formHolds
      ? (formRule?.summary as string)
      : // Travel and all-day blocks outrank the item count: they are the reason,
        // and burying them under "4 items on the day" is what made the last
        // answer useless.
        travel?.exceedsWorkday
        ? `Travelling — about ${travel.totalMiles} mi between stops, more driving than the day holds`
        : allDayBlock
          ? `An all-day "${allDayBlock.title}" block is holding the whole day`
          : blockers.length === 0
            ? `Open ${formatTimeString(hours.start)}–${formatTimeString(hours.end)} with nothing booked`
            : outcome === 'full'
              ? `Booked solid — ${blockers.length} item${blockers.length === 1 ? '' : 's'} fill the working day`
              : // With a service to measure against, the openings ARE the answer:
                // "there is a 1h gap" and "nothing here holds a 1h job once you
                // account for the drive" are very different sentences.
                serviceFit && openSlots.length === 0
                ? `Nothing on the day holds ${serviceFitLabel(serviceFit)}` +
                  (closestMiss
                    ? ` — the closest is ${formatDurationMinutes(closestMiss.usableMinutes, { short: true })} at ` +
                      closestMiss.startLabel
                    : '')
                : serviceFit
                  ? `${openSlots.length} opening${openSlots.length === 1 ? '' : 's'} can take ` +
                    serviceFitLabel(serviceFit)
                  : openSlots.length > 0
                    ? `${blockers.length} item${blockers.length === 1 ? '' : 's'} inside working hours, ` +
                      `${openSlots.length} open window${openSlots.length === 1 ? '' : 's'}`
                    : `${blockers.length} item${blockers.length === 1 ? '' : 's'} inside working hours, ` +
                      `largest opening ${formatDurationMinutes(largestGapMinutes)}`;

  const requestedTimeRaw = input.requestedTime ? String(input.requestedTime).trim() : '';
  const requestedTimeLabel = /^\d{1,2}:\d{2}$/.test(requestedTimeRaw)
    ? formatTimeString(requestedTimeRaw)
    : requestedTimeRaw || null;
  const earliestOffered = input.earliestOfferedDate ? parseLocalDateTime(String(input.earliestOfferedDate)) : null;

  // Nothing is holding the day: it is open, the job fits, and the form allows
  // it. That leaves exactly one explanation — GetSlots only ever offers the
  // Top Choice, a Proximity option, and windows inside the customer's
  // PREFERRED dates, so a day the request never asked for is never offered.
  // Without this, the audit ends at "the calendar looks fine to me too", which
  // is where the answer used to call the engine's behavior unusual.
  const requestPreferredDate = request?.preferredDate && request.preferredDate !== dateKey ? request.preferredDate : '';
  const requestPreferredLabel = requestPreferredDate
    ? formatDateMedium(parseLocalDateTime(requestPreferredDate) ?? date)
    : null;
  const slotPreference: AvailabilitySlotPreference | null =
    hours.isWorkDay && !formHolds && !travel?.exceedsWorkday && openSlots.length > 0 && !!serviceFit
      ? {
          offeredLabel: earliestOffered ? formatDateMedium(earliestOffered) : null,
          requestId: requestId || null,
          requestedDateLabel: requestPreferredLabel,
          contactName: request?.contactName || null,
          summary:
            `Nothing is blocking ${dateLabel} — it just was not asked for. The scheduler offers the Top ` +
            'Choice, a Proximity option, and times inside the dates the request lists as preferred, so ' +
            'an open day that is not one of those never shows up.',
        }
      : null;

  deps.onArtifact?.({
    type: 'availability_audit',
    date: dateKey,
    dateLabel,
    requestedTimeLabel,
    forLabel: assigneeLabel(input, isOther),
    assignedUserId: isOther ? userId : null,
    verdict,
    outcome,
    bookingFormId: form?.id ?? null,
    formRule,
    slotPreference,
    workingHoursLabel: hours.isWorkDay
      ? `${formatTimeString(hours.start)} – ${formatTimeString(hours.end)}`
      : null,
    ...(hours.blockedWindows?.length
      ? {
          blockedWindows: hours.blockedWindows.map((w) => ({
            label: `${formatTimeString(w.start)} – ${formatTimeString(w.end)}`,
            reason: w.reason,
          })),
        }
      : {}),
    blockers,
    serviceFit,
    ...(openSlots.length ? { openSlots } : {}),
    ...(closestMiss ? { closestMiss } : {}),
    ...(longBlocks.length ? { longBlocks } : {}),
    travel,
    externalAvailability,
    earliestOfferedLabel: earliestOffered ? formatDateMedium(earliestOffered) : null,
  });

  return {
    date: dateKey,
    dateLabel,
    for: assigneeLabel(input, isOther),
    isWorkDay: hours.isWorkDay,
    dayStatus: hours.isWorkDay
      ? `Working day, hours ${hours.start}-${hours.end}.`
      : describeDayOff(hours, date, possessive),
    workingHours: hours.isWorkDay ? { start: hours.start, end: hours.end } : null,
    ...(hours.blockedWindows?.length ? { blockedWindows: hours.blockedWindows } : {}),
    appointmentCount: managed.length,
    externalEventCount: external.length,
    blockers,
    /** Events on the day that fall entirely outside working hours — excluded on purpose. */
    ignoredOutsideWorkingHours: outsideWorkingHoursCount,
    longBlocks,
    travel,
    largestOpeningMinutes: hours.isWorkDay ? largestGapMinutes : 0,
    serviceFit,
    /**
     * REAL openings only — windows the job actually fits once the unavoidable
     * driving comes out. Gaps too small to take the work are NOT openings and
     * are deliberately absent; never describe the day as having more openings
     * than are listed here.
     */
    openSlots: openSlots.map(describeSlot),
    /** The nearest thing to an opening when there are none, for a specific answer. */
    closestMiss: closestMiss ? describeSlot(closestMiss) : null,
    /** Gaps at or above the timeline's threshold, before the fit test. */
    windowsConsidered: windows.length,
    formRule,
    bookingFormId: form?.id ?? null,
    /**
     * Present when the day is open, fits, and the form allows it — i.e. the
     * engine's silence is about the request's preferred dates, not this day.
     */
    slotPreference,
    externalAvailability,
    removableWithoutAddress: removableNoAddress.map((b) => ({ id: b.id, title: b.title, time: `${b.startTime} – ${b.endTime}` })),
    /**
     * Events holding time that Routemize could not place on the map. NOT
     * candidates for removal — they are real work with a sloppy address, and
     * the fix is the address, not the event.
     */
    unlocatedAddressEvents: unlocatedBlockers.map((b) => ({
      title: b.title,
      time: `${b.startTime} – ${b.endTime}`,
      address: b.address,
      source: b.source ?? (b.kind === 'external' ? 'External' : 'Routemize'),
      missing: b.addressGaps,
    })),
    cardRendered: true,
    note:
      (!hours.isWorkDay
        ? 'THE ANSWER IS THE AVAILABILITY SCHEDULE, not the calendar. Lead with it, say it closes EVERY ' +
          'date on this weekday (or just this date, for a date-specific block), and point at the ' +
          'Availability page in the sidebar as where to open it back up. Do NOT blame appointments or ' +
          'synced events, and do NOT offer to clear anything — none of that would make the day bookable. '
        : formHolds
          ? `THE ANSWER IS THE BOOKING FORM SETTING, not the calendar. ${formRule?.summary}. ` +
            'The card may still show openings that would physically fit — those are real calendar gaps, ' +
            'but the engine skips this day because of that setting. Lead with the setting. Booking Forms ' +
            'in the sidebar is where to change it. Do NOT tell them to audit slots or clear calendar ' +
            'events; none of that changes a form rule. If they mentioned a later day the engine offered, ' +
            'that is the first day the form allows. '
        : travel?.exceedsWorkday
          ? `THE OPERATOR IS TRAVELLING, and that is the whole answer — LEAD WITH IT. The day's events are ` +
            `about ${travel.totalMiles} mi apart end to end (roughly ` +
            `${formatDurationMinutes(travel.estimatedDriveMinutes)} of driving against a ` +
            `${formatDurationMinutes(travel.workingMinutes)} workday)` +
            (travel.longestLeg
              ? `, the worst hop being ${travel.longestLeg.miles} mi from "${travel.longestLeg.fromTitle}" to ` +
                `"${travel.longestLeg.toTitle}"`
              : '') +
            '. Name where they are actually going (use the addresses on the blockers - say the city/state, ' +
            'e.g. "you are in Colorado that afternoon"), and be blunt that this is not a scheduling ' +
            'quirk: nobody can be at a job here and there on the same day, so the day is genuinely ' +
            'unbookable. Do NOT offer to clear events to make room, and do NOT suggest booking into a gap ' +
            'between two stops hundreds of miles apart. Steer them to another day instead. '
          : allDayBlock
            ? `An ALL-DAY synced "${allDayBlock.title}" block covers the day, and that alone makes it look ` +
              'full - Routemize treats every synced event as busy time. Lead with that. The real fix is to ' +
              'open the event in the source calendar and switch it from Busy to Free (then it stops eating ' +
              'availability everywhere, permanently); clearing it off Routemize from this card also works ' +
              'but a future re-sync can bring it back. Offer both, shortest version. '
            : blockers.length === 0
              ? 'The working window is OPEN with nothing holding it, so something else is narrowing it (the ' +
                'service length vs. the remaining hours, buffers, drive time from the surrounding route, or ' +
                'the service area). Say that plainly instead of inventing a blocker. '
              : `The day is open ${hours.start}-${hours.end} and the card lists everything holding it. ` +
                (largestGapMinutes < 30
                  ? 'There is no meaningful opening left, so the calendar IS the reason. '
                  : !serviceFit
                    ? `There ${openSlots.length === 1 ? 'is 1 open window' : `are ${openSlots.length} open windows`} ` +
                      'at or above the threshold the timeline uses, but you do NOT know the service length ' +
                      '(no bookingFormId was passed), so do NOT claim the job fits any of them — say what ' +
                      'the windows are and offer to check against the real service. '
                    : !formRule && openSlots.length > 0
                      ? `The calendar has room (${openSlots.length} opening${openSlots.length === 1 ? '' : 's'}), ` +
                        'but you did NOT resolve a booking form, so you cannot see the settings that usually ' +
                        'explain why the engine skipped an otherwise-open day (same-day booking, min notice, ' +
                        'allowed weekdays). Re-call with bookingFormId or serviceHint — the service name the ' +
                        'user said — and do NOT claim the engine should have offered these times. '
                    : openSlots.length === 0
                      ? // The single most important correction: a gap the job
                        // cannot fit is not an opening. Everything here is
                        // booked to an address, so the drive is never optional.
                        'There are NO openings on this day — not "some tight ones", NONE. ' +
                        `${serviceFit.name ? `A ${serviceFit.name}` : 'The job'} needs ` +
                        `${formatDurationMinutes(serviceFit.totalMinutes, { short: true })} ` +
                        `(${formatDurationMinutes(serviceFit.durationMinutes, { short: true })}` +
                        (serviceFit.bufferMinutes > 0
                          ? ` + ${formatDurationMinutes(serviceFit.bufferMinutes, { short: true })} buffer`
                          : '') +
                        ') before anyone drives anywhere, and every gap on the day is smaller than that ' +
                        'once the driving between the surrounding stops comes out. ' +
                        (closestMiss
                          ? `The closest it gets is ${closestMiss.startLabel}–${closestMiss.endLabel} with ` +
                            `${formatDurationMinutes(closestMiss.usableMinutes, { short: true })} usable. ` +
                            'Name that ONE window and what it is short by, and stop there. '
                          : '') +
                        'Do NOT list the gaps as if they were options, do NOT re-list the day\'s events, ' +
                        'and do NOT suggest squeezing the job in. The honest answer is that the day is ' +
                        'full for work this length — offer another day or moving a stop. '
                      : // Open day, job fits, form allows it. There is nothing
                        // left to blame, and guessing at "something narrower"
                        // is how this answer went wrong: the engine simply
                        // never offers days the request did not ask for.
                        `${openSlots.length} window${openSlots.length === 1 ? '' : 's'} genuinely take the ` +
                        `job (${formatDurationMinutes(serviceFit.totalMinutes, { short: true })} including ` +
                        'buffer, before driving), and form settings do NOT skip this day. ' +
                        'THE ANSWER IS THE REQUEST\'S DATE PREFERENCES — nothing is blocking this day, it ' +
                        'was never asked for. Scheduling does not return every bookable window: it returns ' +
                        'the Top Choice, a Proximity option, and times inside the dates the request lists ' +
                        'as preferred' +
                        (requestPreferredLabel ? `, and this request asks for ${requestPreferredLabel}` : '') +
                        (earliestOffered
                          ? `, which is why ${formatDateMedium(earliestOffered)} came back instead`
                          : '') +
                        '. Lead with that, and do NOT call the engine\'s behavior unusual, a bug, or ' +
                        'unexplained, do NOT tell them to audit a slot to find the cause (the windows are ' +
                        'fine — that is the point), and do NOT offer to clear calendar events. ' +
                        // The fix is one tool call away, so ending on "go change
                        // the request and try again" is the wrong answer: it
                        // makes the operator redo work Routy can do for them.
                        (requestId
                          ? 'YOU CAN FIX THIS YOURSELF, so OFFER it in the same breath instead of sending ' +
                            'them off to redo the booking: ask whether to re-run the times for ' +
                            `${dateLabel} (the card carries the same button). When they say yes, call ` +
                            `create_booking_request with existingRequestId "${requestId}" and preferredDate ` +
                            `"${dateKey}" — that re-runs the search on the SAME request with this day ` +
                            'preferred and shows fresh time cards. Pass nothing else: the customer, ' +
                            'service and address all come off the request, and asking for them again is ' +
                            'the exact failure this path exists to avoid. '
                          : 'The fix is re-running the search with this day preferred, and you can do that ' +
                            'yourself — but you were not given a requestId, so resolve the request first ' +
                            '(the create_booking_request result earlier in this conversation, or ' +
                            'find_pending_requests for a lead still sitting there) and OFFER the re-run: ' +
                            'create_booking_request with existingRequestId set to it and preferredDate ' +
                            `"${dateKey}". Pass requestId to this audit next time so the offer is one ` +
                            'step. Only when no request exists at all is setting the preferred date THEIR ' +
                            'job — and then it is a booking to start, not homework to fix. '))) +
      (longBlocks.length > 0 && !travel?.exceedsWorkday && !allDayBlock && !formHolds
        ? `${longBlocks.length} synced event(s) run 4+ hours and swallow most of the window. Mention that ` +
          'switching them from Busy to Free in the source calendar is the permanent fix. '
        : '') +
      (unlocatedBlockers.length > 0 && !formHolds
        ? `${unlocatedBlockers.length} event(s) here have an address Routemize could NOT place on the map` +
          (unlocatedBlockers[0].addressGaps
            ? ` (e.g. "${unlocatedBlockers[0].address}" — no ${unlocatedBlockers[0].addressGaps})`
            : '') +
          '. These are NOT the address-less personal blocks: an event specific enough to name a street is ' +
          'real work, so do NOT offer to clear them and NEVER fold them into removeUnlocated. What they do ' +
          'break is the geography — they hold their time while sitting outside every drive-time ' +
          'calculation, so the openings around them get measured from the last stop that DID resolve, and ' +
          'the day can behave differently than the numbers here suggest. If the operator is asking why the ' +
          'engine offered something that looks wrong, THIS is a likely cause and worth naming in one ' +
          'clause. The fix is the address, not the schedule: open the event in the source calendar, ' +
          'complete the location (street, city, state, ZIP), and let it re-sync. '
        : '') +
      (travel && travel.unmeasuredStops > 0
        ? `The ${travel.totalMiles} mi travel figure SKIPS ${travel.unmeasuredStops} stop(s) that never ` +
          'geocoded, so it is a floor and the real driving is worse. Do not use it to argue the day is ' +
          'comfortable, and do not call the day travel-free on the strength of it. '
        : '') +
      (removableNoAddress.length > 0 && !formHolds
        ? `${removableNoAddress.length} synced calendar event(s) have NO address, which usually means a ` +
          'personal block rather than real field work. Ask whether to keep or remove them — the card has ' +
          'the buttons, so just ask in ONE sentence and wait. Once they choose, call remove_calendar_events ' +
          'with removeUnlocated=true and this date (NOT a hand-copied id list — dropping one leaves the day ' +
          'blocked and the booking still fails). '
        : '') +
      (externalAvailability === 'displayOnly' && external.length > 0
        ? "The tenant's Google calendars are set to display-only, so those synced events do NOT block " +
          'availability — say that, because clearing them would not open anything up. '
        : '') +
      (outsideWorkingHoursCount > 0
        ? `${outsideWorkingHoursCount} other event(s) on the day sit entirely outside working hours and cost ` +
          'no bookable time, so they are deliberately left off the card. Do not mention or offer to clear ' +
          'them unless the user asks about them directly. '
        : '') +
      'FORMAT: at most TWO short sentences, plain prose, no lists and no headings. Do not re-narrate the ' +
      "day's events, times, or addresses — every one of them is already on the card, and repeating them " +
      'as a paragraph is the single worst version of this answer. Lead with the REASON, not the inventory.',
  };
}

/**
 * Spend one open window minute by minute: drive in, buffer, the visit, buffer,
 * drive out. "The day is tight" is an opinion; "this window is 60 minutes and
 * the job needs 78" is a number the operator can act on — move a stop, trim the
 * buffer, or take the next day.
 */
async function execAuditSlot(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const date = parseLocalDateTime(String(input.date));
  if (!date) throw new Error('Invalid date. Use YYYY-MM-DD.');
  const startTime = String(input.startTime || '');
  const endTime = String(input.endTime || '');
  if (!/^\d{1,2}:\d{2}$/.test(startTime) || !/^\d{1,2}:\d{2}$/.test(endTime)) {
    throw new Error('startTime and endTime must be 24h HH:MM, exactly as the audit card gave them.');
  }
  const { userId, isOther } = resolveAssignee(input, deps);
  const dateKey = toDateKey(date);
  const dateLabel = formatDateMedium(date);
  const slotLabel = `${formatTimeString(startTime)} – ${formatTimeString(endTime)}`;

  deps.onActivity?.(`Auditing the ${formatTimeString(startTime)} window`);
  const windowStart = parseLocalDateTime(dateKey, startTime);
  const windowEnd = parseLocalDateTime(dateKey, endTime);
  if (!windowStart || !windowEnd) throw new Error('Could not resolve that window.');
  const windowMinutes = Math.max(0, getTimeDifferenceInMinutes(windowStart, windowEnd));

  const [appointments, fit] = await Promise.all([
    fetchDayAppointments(userId, date, deps.env.companyTimezone),
    resolveServiceFit(input),
  ]);
  if (!fit) {
    throw new Error(
      'No service length available. Pass bookingFormId (preferred) or serviceDurationMinutes so the ' +
        'window can be measured against something.',
    );
  }

  // Whatever actually brackets the window, which is what the drive legs run
  // to and from. Cancelled records hold no time and would misattribute a leg.
  const active = appointments
    .filter((a) => a.status !== 'cancelled' && a.status !== 'no-show')
    .sort((a, b) => a.startDateTime.getTime() - b.startDateTime.getTime());
  const label = (a: Appointment) => a.customerName || a.type || 'your stop';
  const coords = (a: Appointment | undefined) =>
    a && a.latitude != null && a.longitude != null ? { lat: a.latitude, lng: a.longitude } : null;
  const prev = [...active].reverse().find((a) => a.endDateTime.getTime() <= windowStart.getTime() + 60_000);
  const next = active.find((a) => a.startDateTime.getTime() >= windowEnd.getTime() - 60_000);

  // A bracketing stop with an address we never geocoded is the quiet way this
  // math goes wrong: `coords` returns null, the drive in falls back to the
  // day's starting point, and the shortfall gets reported to the minute as if
  // it had been measured from the real previous stop.
  const unmeasuredNeighbors = [prev, next]
    .filter((a): a is Appointment => !!a && !!(a.address || '').trim() && !coords(a))
    .map(label);

  // With the job's real address the legs are measured to and from where the
  // tech is actually going. Without it, the direct hop between the bracketing
  // stops is the honest floor — flagged as estimated so the card can say so.
  const jobCoords = input.serviceAddress
    ? await GeocodingService.geocodeAddress(String(input.serviceAddress))
        .then((r) => (r ? { lat: r.lat, lng: r.lng } : null))
        .catch(() => null)
    : null;
  const fromCoords = coords(prev) ?? (isOther ? null : deps.env.startingCoordinates ?? null);
  const toCoords = coords(next);

  const [driveIn, driveOut, bracketDrive] = await Promise.all([
    jobCoords ? driveLegBetween(fromCoords, jobCoords) : Promise.resolve(null),
    jobCoords ? driveLegBetween(jobCoords, toCoords) : Promise.resolve(null),
    jobCoords ? Promise.resolve(null) : driveLegBetween(fromCoords, toCoords),
  ]);

  const lines: SlotAuditLine[] = [
    { kind: 'window', label: 'The open window', detail: slotLabel, minutes: windowMinutes },
  ];
  if (jobCoords) {
    if (driveIn) {
      lines.push({
        kind: 'drive',
        label: prev ? `Drive from ${label(prev)}` : 'Drive from your starting point',
        detail: prev?.address || deps.env.startingLabel || null,
        minutes: driveIn.minutes,
      });
    }
  } else if (bracketDrive) {
    lines.push({
      kind: 'drive',
      label:
        prev && next
          ? `Driving between ${label(prev)} and ${label(next)}`
          : 'Driving around this window',
      detail: 'A floor — routing through the new job can only add to it',
      minutes: bracketDrive.minutes,
      estimated: true,
    });
  }
  if (fit.bufferMinutes > 0) {
    lines.push({ kind: 'buffer', label: 'Booking buffer', detail: 'From the service settings', minutes: fit.bufferMinutes });
  }
  lines.push({
    kind: 'visit',
    label: fit.name ? `${fit.name} on site` : 'Time on site',
    minutes: fit.durationMinutes,
  });
  if (jobCoords && driveOut && next) {
    lines.push({
      kind: 'drive',
      label: `Drive to ${label(next)}`,
      detail: next.address || null,
      minutes: driveOut.minutes,
    });
  }

  const requiredMinutes = lines.filter((l) => l.kind !== 'window').reduce((sum, l) => sum + l.minutes, 0);
  const fits = requiredMinutes <= windowMinutes;
  const shortByMinutes = fits ? 0 : requiredMinutes - windowMinutes;
  const spareMinutes = fits ? windowMinutes - requiredMinutes : 0;
  const verdict = fits
    ? `Fits with ${formatDurationMinutes(spareMinutes, { short: true })} to spare`
    : `${formatDurationMinutes(shortByMinutes, { short: true })} short`;

  // Only remedies that are actually available on THIS window, so the operator
  // is never sent to try something the numbers already rule out.
  const remedies: string[] = [];
  if (!fits) {
    if (fit.bufferMinutes > 0 && shortByMinutes <= fit.bufferMinutes) {
      remedies.push(
        `Trim the ${formatDurationMinutes(fit.bufferMinutes, { short: true })} buffer on ` +
          `${fit.name || 'this service'} — that alone covers the gap.`,
      );
    }
    if (next) {
      remedies.push(
        `Push ${label(next)} back ${formatDurationMinutes(shortByMinutes, { short: true })} to widen the window.`,
      );
    }
    if (prev) {
      remedies.push(
        `Pull ${label(prev)} earlier by ${formatDurationMinutes(shortByMinutes, { short: true })}.`,
      );
    }
    remedies.push('Book it on a day with a longer opening.');
  }

  deps.onArtifact?.({
    type: 'slot_audit',
    date: dateKey,
    dateLabel,
    slotLabel,
    windowMinutes,
    serviceName: fit.name,
    afterTitle: prev ? label(prev) : null,
    beforeTitle: next ? label(next) : null,
    lines,
    requiredMinutes,
    fits,
    shortByMinutes,
    spareMinutes,
    verdict,
    ...(remedies.length ? { remedies } : {}),
    ...(unmeasuredNeighbors.length ? { unmeasuredNeighbors } : {}),
  });

  return {
    date: dateKey,
    window: slotLabel,
    windowMinutes,
    requiredMinutes,
    fits,
    shortByMinutes,
    spareMinutes,
    breakdown: lines.filter((l) => l.kind !== 'window').map((l) => `${l.label}: ${l.minutes}m`),
    drivesMeasuredAgainstJobAddress: !!jobCoords,
    cardRendered: true,
    note:
      (fits
        ? `The job DOES fit this window with ${formatDurationMinutes(spareMinutes, { short: true })} to spare. ` +
          'Say so in one sentence and offer to book it — if the engine is still refusing this day, say ' +
          'that plainly rather than implying the calendar is full. '
        : `The job does NOT fit: the window holds ${windowMinutes}m and ` +
          `${fit.name ? `a ${fit.name}` : 'the work'} needs ${requiredMinutes}m, ` +
          `so it is ${shortByMinutes}m short. Lead with that number. ` +
          (remedies.length ? `Offer the single best fix (${remedies[0]}) — one option, not a menu. ` : '')) +
      (jobCoords
        ? ''
        : 'The drive figures are the floor between the surrounding stops, not routes to the actual job ' +
          '(no address was passed), so do not present them as exact. ') +
      (unmeasuredNeighbors.length
        ? `WARNING: ${unmeasuredNeighbors.join(' and ')} ${unmeasuredNeighbors.length === 1 ? 'has' : 'have'} ` +
          'an address Routemize could not locate, so the drive legs here were measured from the day\'s ' +
          'starting point instead of from that stop. Treat the shortfall as approximate, say which stop ' +
          'is unlocatable, and note that completing its address in the source calendar is what makes this ' +
          'window measurable. Do NOT present the minutes as exact and do NOT offer to clear that stop. '
        : '') +
      'FORMAT: ONE or TWO short sentences, no lists — the card already shows every line of the math.',
  };
}

/**
 * Tell the rest of the app the schedule changed underneath it.
 *
 * Routy writes while other surfaces are already on screen — the Timeline is
 * usually open behind the chat panel — and those views fetch once and hold.
 * Without a nudge, events Routy just cleared stay visible, which the operator
 * quite reasonably reads as the removal having failed.
 */
function notifyScheduleChanged(): void {
  try {
    // Each platform backs this differently (a window event on the web, an
    // in-memory emitter on mobile), so going through it keeps the nudge
    // working on both rather than silently doing nothing off the web.
    notifyAppointmentsChanged();
  } catch {
    // Never let a UI refresh hint break a completed write.
  }
}

/**
 * Predicate for "this event actually costs bookable time on that day".
 *
 * Anything sitting entirely outside the working window — a 6:30am alarm, a
 * 6pm personal block against 9-5 hours — holds no bookable minute, so it must
 * never be listed as a blocker and must never be offered up for removal. The
 * audit card and the removal both resolve their sets through this so they can
 * never disagree about which events are in play. Touching the boundary (a
 * drive that ends at 9:00) is not an overlap.
 */
function makeWorkingHoursFilter(
  date: Date,
  hours: EffectiveDayAvailability,
): (a: Appointment) => boolean {
  const key = toDateKey(date);
  const start = hours.isWorkDay ? parseLocalDateTime(key, hours.start) : null;
  const end = hours.isWorkDay ? parseLocalDateTime(key, hours.end) : null;
  return (a: Appointment) => !start || !end || (a.startDateTime < end && a.endDateTime > start);
}

/**
 * Clear synced calendar copies off Routemize so they stop holding availability.
 *
 * Deliberately non-destructive: the backend removes only Routemize's copy, so
 * the original Google/Outlook/Zoom event survives untouched (and a later
 * re-sync can bring it back). That is what makes this safe to offer inside a
 * chat — the operator is freeing their Routemize availability, not deleting
 * someone's meeting.
 */
async function execRemoveCalendarEvents(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const day = input.date ? parseLocalDateTime(String(input.date)) : null;
  const dayLabel = day ? formatDateMedium(day) : null;
  const removeUnlocated = input.removeUnlocated === true;

  if (!day && removeUnlocated) {
    throw new Error('removeUnlocated needs the date (YYYY-MM-DD) of the day to clear.');
  }

  deps.onActivity?.('Looking up those calendar events');
  // Re-read the day so the confirmation card names what is actually being
  // cleared instead of echoing raw ids back at the user, and so the working
  // window is known before anything is proposed for deletion.
  const { userId } = resolveAssignee(input, deps);
  const [dayAppointments, scheduleByDay, overridesByDate] = day
    ? await Promise.all([
        fetchDayAppointments(userId, day, deps.env.companyTimezone).catch(() => [] as Appointment[]),
        fetchWorkScheduleByDay(userId).catch(() => null),
        fetchUserOverridesByDate(userId).catch(() => null),
      ])
    : [[] as Appointment[], null, null];
  const hours =
    day && scheduleByDay && overridesByDate
      ? getEffectiveDayAvailability(day, scheduleByDay, overridesByDate)
      : null;
  const known = dayAppointments.filter((a) => !isRoutemizeManaged(a) && a.id);

  // Resolving the set HERE rather than trusting a list of UUIDs echoed through
  // the model is the difference between clearing all seven blocks and clearing
  // one: transcribing ids into prose is exactly the step that silently drops
  // items, and the user only finds out when the day still won't book.
  let eventIds: string[];
  let skippedOutsideHours = 0;
  if (removeUnlocated) {
    // Scoped to the working window on purpose. "Clear the blocks that are in
    // the way" must never reach into someone's 6:30am routine or their evening
    // — those cost no bookable time, so deleting them is pure collateral damage.
    const inWindow = hours && day ? makeWorkingHoursFilter(day, hours) : () => true;
    const unlocated = known.filter((a) => !(a.address || '').trim());
    const target = unlocated.filter(inWindow);
    skippedOutsideHours = unlocated.length - target.length;
    eventIds = target.map((a) => a.id as string);
    if (eventIds.length === 0) {
      return {
        removed: 0,
        skippedOutsideWorkingHours: skippedOutsideHours,
        note:
          `No synced events without an address are left inside working hours on ${dayLabel ?? 'that day'} — ` +
          'nothing to clear. ' +
          (skippedOutsideHours > 0
            ? `${skippedOutsideHours} address-less event(s) sit outside working hours; they hold no bookable ` +
              'time, so removing them would not help and they were left alone. '
            : ''),
      };
    }
  } else {
    eventIds = Array.isArray(input.eventIds)
      ? [...new Set(input.eventIds.map((id) => String(id).trim()).filter(Boolean))]
      : [];
    if (eventIds.length === 0) {
      throw new Error(
        'Provide eventIds to remove, or pass removeUnlocated=true with the date to clear every ' +
          'address-less synced event on that day.',
      );
    }
    // A dropped id has to fail loudly. Silently removing 1 of 7 and reporting
    // success is what makes the day still refuse to book afterwards.
    const expected = input.expectedCount != null ? Number(input.expectedCount) : null;
    if (expected != null && Number.isFinite(expected) && eventIds.length !== expected) {
      throw new Error(
        `Expected ${expected} event ids but received ${eventIds.length}. Re-send the call with the COMPLETE ` +
          'list of ids from the audit, or pass removeUnlocated=true to clear every address-less event on the day.',
      );
    }
  }

  const labelFor = (id: string): string => {
    const match = known.find((a) => a.id === id);
    return match ? `${match.type || match.customerName || 'Busy'} · ${match.startTime} – ${match.endTime}` : id;
  };

  const windowLabel =
    hours?.isWorkDay ? `${formatTimeString(hours.start)} – ${formatTimeString(hours.end)}` : null;
  const approved = await deps.requestConfirmation({
    title: `Clear ${eventIds.length} calendar event${eventIds.length === 1 ? '' : 's'} off Routemize?`,
    subtitle: [dayLabel ? `On ${dayLabel}` : null, windowLabel ? `working hours ${windowLabel}` : null]
      .filter(Boolean)
      .join(' · ') || undefined,
    confirmLabel: 'Remove and free the time',
    tone: 'danger',
    lines: [
      ...eventIds.map((id) => ({ icon: 'calendar' as const, text: labelFor(id) })),
      ...(skippedOutsideHours > 0
        ? [
            {
              icon: 'clock' as const,
              text: `${skippedOutsideHours} event${skippedOutsideHours === 1 ? '' : 's'} outside working hours left alone`,
            },
          ]
        : []),
      {
        icon: 'bell' as const,
        text: 'Removes the Routemize copy only — the original calendar event is untouched.',
      },
    ],
  });
  if (!approved) return { removed: 0, note: 'The user cancelled — nothing was removed.' };

  // ONE AT A TIME, not Promise.all. Firing these concurrently made the backend
  // drop all but the first — clearing 4 events reported 1 removed — because the
  // deletes contend on the same calendar-sync record. Sequential is slower by a
  // few hundred milliseconds and is the difference between working and not.
  const removedIds: string[] = [];
  const failures = new Map<string, string>();

  const removeOne = async (id: string): Promise<boolean> => {
    try {
      await GoogleCalendarService.deleteApiAppGoogleCalendarDeleteExternalEvent(id);
      failures.delete(id);
      return true;
    } catch (err) {
      failures.set(id, err instanceof Error ? err.message : 'Removal failed.');
      return false;
    }
  };

  for (const [i, id] of eventIds.entries()) {
    deps.onActivity?.(
      eventIds.length > 1 ? `Clearing event ${i + 1} of ${eventIds.length}` : 'Freeing up that time',
    );
    if (await removeOne(id)) removedIds.push(id);
  }

  // Trust the calendar, not the responses. A delete can report failure and
  // still have landed (or the reverse), so re-read the day and retry only what
  // is genuinely still there — that way "removed 4" means four are gone.
  if (day && failures.size > 0) {
    deps.onActivity?.('Double-checking what is left');
    try {
      const recheck = await fetchDayAppointments(userId, day, deps.env.companyTimezone);
      const stillPresent = new Set(recheck.map((a) => a.id).filter(Boolean) as string[]);
      for (const id of [...failures.keys()]) {
        if (!stillPresent.has(id)) {
          // It actually went through; the error was noise.
          failures.delete(id);
          if (!removedIds.includes(id)) removedIds.push(id);
        } else if (await removeOne(id)) {
          removedIds.push(id);
        }
      }
    } catch {
      // Re-read failed — fall through and report what the calls told us.
    }
  }

  const removed = removedIds;
  const failed = [...failures.entries()].map(([id, error]) => ({ id, removed: false, error }));
  if (removed.length > 0) notifyScheduleChanged();

  // Re-read the day and say what ACTUALLY opened up. Claiming the time is free
  // and then watching the booking engine offer the same distant date is the
  // worst version of this flow — measure it instead of assuming.
  let opening: { largestOpeningMinutes: number; remainingEvents: number } | null = null;
  if (day && hours && removed.length > 0) {
    deps.onActivity?.('Checking what opened up');
    try {
      const after = await fetchDayAppointments(userId, day, deps.env.companyTimezone);
      const inWindow = makeWorkingHoursFilter(day, hours);
      const stillOn = after
        .filter((a) => a.status !== 'cancelled' && a.status !== 'no-show')
        .filter(inWindow);
      const gaps = hours.isWorkDay
        ? findOpenGaps(stillOn, day, hours.start, hours.end, tenantShiftedNow(deps.env.companyTimezone))
        : [];
      opening = {
        largestOpeningMinutes: gaps.reduce((max, g) => Math.max(max, g.durationMinutes), 0),
        remainingEvents: stillOn.filter((a) => !isRoutemizeManaged(a)).length,
      };
    } catch {
      // Non-fatal — the removals already happened.
    }
  }

  return {
    requested: eventIds.length,
    removed: removed.length,
    failed: failed.length > 0 ? failed : undefined,
    skippedOutsideWorkingHours: skippedOutsideHours || undefined,
    ...(opening ? { dayAfterRemoval: opening } : {}),
    note:
      `Asked to clear ${eventIds.length}; ${removed.length} actually removed` +
      `${dayLabel ? ` on ${dayLabel}` : ''}. ` +
      (failed.length > 0
        ? `${failed.length} FAILED and are still on the day. You MUST say so explicitly — "I cleared X of Y, ` +
          'these are still there" — and name them. Never report a partial clear as if it worked, and never ' +
          'blame the remaining events for a tight day without first admitting you could not remove them. ' +
          `Offer to try those ${failed.length} again. `
        : '') +
      (skippedOutsideHours > 0
        ? `${skippedOutsideHours} address-less event(s) outside working hours were deliberately LEFT ALONE — ` +
          'they hold no bookable time, so clearing them would take away personal blocks for nothing. Do not ' +
          'offer to remove them. '
        : '') +
      (opening
        ? `The day now has a largest opening of ${formatDurationMinutes(opening.largestOpeningMinutes)} with ` +
          `${opening.remainingEvents} synced event(s) left. Use these NUMBERS — never guess whether the day ` +
          'opened up. ' +
          (opening.largestOpeningMinutes < 60
            ? 'That is still tight, so if the booking engine keeps offering a later date, say plainly that ' +
              'what is left on the day (not a system quirk) is still filling it, and offer to clear more or ' +
              'take a different day. '
            : 'That is real room, so the booking should now be possible on this day — if the engine still ' +
              'refuses it, say so honestly and name what is left rather than repeating that it is "packed". ')
        : '') +
      'They were removed from Routemize only; the originals are still in the source calendar, and a future ' +
      're-sync can bring them back. ' +
      'NOW FINISH THE BOOKING — do not stop at "done" and do not wait to be asked. In the SAME turn, lead ' +
      `with one short line confirming you freed the time ("I freed up${dayLabel ? ` ${dayLabel}` : ' the time'} — ` +
      'let\'s get this booked"), then immediately call create_booking_request for the request you were ' +
      `booking, with existingRequestId set and preferredDate = ${dayLabel ? `the day you just cleared` : 'that day'}. ` +
      'You do NOT need to ask for the customer, service, or address: passing existingRequestId pulls all of ' +
      'that off the request, so asking for an address that is already on the request is a hard mistake. ' +
      'The time cards that come back ARE how you ask which time they want — if the customer only gave a ' +
      'vague preference like "Morning", let the cards do the asking instead of asking in prose, and never ' +
      'pick a time on their behalf.',
  };
}

async function searchContactsByText(text: string, maxResultCount = 5): Promise<Routemize_DTOs_ContactDTO[]> {
  const res = await ContactService.getApiAppContactGetList(
    text,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    0,
    maxResultCount,
  );
  return ((res?.items ?? []) as Routemize_DTOs_ContactDTO[]).filter(Boolean);
}

function contactDisplayName(c: Routemize_DTOs_ContactDTO): string {
  return (c.fullName || [c.firstName, c.lastName].filter(Boolean).join(' ') || c.businessName || c.displayName || '').trim();
}

/**
 * Last-resort fallback when the server-side SearchText match comes up empty:
 * page through the operator's own contacts (bounded) and match client-side.
 * Guards against backend search quirks (e.g. not matching a combined "First
 * Last" query, or fuzzy-matching only a single field) hiding a contact that
 * genuinely exists.
 */
async function scanContactsClientSide(needle: string): Promise<Routemize_DTOs_ContactDTO[]> {
  const lower = needle.toLowerCase();
  const pageSize = 100;
  const maxPages = 5; // up to 500 contacts scanned
  const matches: Routemize_DTOs_ContactDTO[] = [];
  for (let page = 0; page < maxPages; page++) {
    const res = await ContactService.getApiAppContactGetList(
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      'firstName',
      page * pageSize,
      pageSize,
    ).catch(() => null);
    const items = ((res?.items ?? []) as Routemize_DTOs_ContactDTO[]).filter(Boolean);
    if (items.length === 0) break;
    for (const c of items) {
      const haystack = `${contactDisplayName(c)} ${c.businessName ?? ''}`.toLowerCase();
      if (haystack.includes(lower)) matches.push(c);
    }
    if (matches.length > 0) break;
    const total = res?.totalCount ?? 0;
    if ((page + 1) * pageSize >= total) break;
  }
  return matches;
}

async function execFindContact(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const name = String(input.name || '').trim();
  if (!name) throw new Error('Provide a name to search for.');

  deps.onActivity?.(`Looking up ${name} in contacts`);
  let items = await searchContactsByText(name);
  let via = 'full-name search';

  // A full "First Last" search can come up empty if the backend's SearchText
  // only matches a single field - retry with individual name tokens (e.g.
  // just the surname) before concluding the contact doesn't exist.
  if (items.length === 0) {
    const tokens = name.split(/\s+/).filter((t) => t.length > 1 && t.toLowerCase() !== name.toLowerCase());
    for (const token of tokens) {
      deps.onActivity?.(`Trying "${token}" in contacts`);
      const retry = await searchContactsByText(token);
      if (retry.length > 0) {
        items = retry;
        via = `word-part search ("${token}")`;
        break;
      }
    }
  }

  // Still nothing? Server-side search may just not match this query shape -
  // page through the operator's own contacts and match client-side before
  // concluding the contact doesn't exist.
  if (items.length === 0) {
    deps.onActivity?.(`Double-checking your full contact list for ${name}`);
    items = await scanContactsClientSide(name).catch(() => []);
    via = 'full contact list scan';
  }

  // eslint-disable-next-line no-console
  console.debug(`[Routy find_contact] "${name}" -> ${items.length} match(es) via ${via}`);

  if (items.length === 0) {
    return {
      found: 0,
      note:
        `No contact matching "${name}" after searching the full name, each word separately, and scanning ` +
        'the full contact list. This is a REAL person, not a business - before asking the user, double-check ' +
        'you have the right spelling. If still nothing, call request_missing_address instead of asking for ' +
        'the address in plain text.',
    };
  }

  return {
    found: items.length,
    contacts: items.map((c) => ({
      contactId: c.id ?? null,
      name: c.fullName || [c.firstName, c.lastName].filter(Boolean).join(' ') || c.displayName || 'Unknown',
      businessName: c.businessName || null,
      phone: c.phoneNumber || null,
      addresses: (c.addresses ?? [])
        .filter((a) => a.addressLine1 || a.fullAddress)
        .map((a) => ({
          full: a.fullAddress || [a.addressLine1, a.city, a.state, a.zipCode].filter(Boolean).join(', '),
          isPrimary: a.isActive ?? false,
        })),
    })),
    note:
      items.length > 1
        ? 'Multiple contacts matched - call present_contacts with one card per match (name, businessName, phone, ' +
          "primary address) so the user taps the right one instead of asking in prose. Then use the confirmed " +
          "contact's address with find_stop_slots / check_time_availability, and pass contactId to add_stop."
        : "Use the contact's address with find_stop_slots / check_time_availability, and pass contactId to add_stop. If this contact has multiple addresses, ask which one.",
  };
}

/**
 * Resolve who a tool should act on behalf of. Defaults to the operator
 * (`currentUserId`). Acting on anyone else requires `canManageAllBookings` -
 * without it, this throws, which surfaces to the model as an `is_error` tool
 * result so it can tell the user plainly instead of guessing or proceeding.
 */
function resolveAssignee(
  input: Record<string, unknown>,
  deps: RoutyToolDeps,
): { userId: string; isOther: boolean } {
  const requested = input.assignedUserId ? String(input.assignedUserId).trim() : '';
  if (!requested || requested === deps.env.currentUserId) {
    return { userId: deps.env.currentUserId, isOther: false };
  }
  if (!deps.env.canManageAllBookings) {
    throw new Error(
      "You don't have permission to manage other team members' schedules - only your own. " +
        'Tell the user this plainly and offer to handle it on their own calendar instead.',
    );
  }
  return { userId: requested, isOther: true };
}

/** Display label for confirm cards / copy: "you" or the resolved teammate's name. */
function assigneeLabel(input: Record<string, unknown>, isOther: boolean): string {
  if (!isOther) return 'you';
  const name = input.assignedUserName ? String(input.assignedUserName).trim() : '';
  return name || 'the selected team member';
}

/** Possessive form of {@link assigneeLabel}: "your" or "Chris's". */
function assigneePossessive(input: Record<string, unknown>, isOther: boolean): string {
  return isOther ? `${assigneeLabel(input, isOther)}'s` : 'your';
}

/**
 * Per-session cache of which team members can RECEIVE appointments/stops -
 * the same role-based rule the app's assignment pickers use
 * (UserManager.getAssignableTeamMembers): Scheduling Assistant-only users
 * book FOR others and are never assignable to jobs themselves.
 */
let assignabilityCache: Map<string, { name: string; assignable: boolean }> | null = null;

async function getAssignability(userId: string): Promise<{ name: string; assignable: boolean } | null> {
  if (!assignabilityCache) {
    const members = ((await UserService.getApiAppUserGetTeamMembers()) ?? []) as Routemize_DTOs_TeamMemberListDto[];
    assignabilityCache = new Map(
      members
        .filter((m) => !!m.id)
        .map((m) => [
          m.id as string,
          {
            name: m.name || 'this team member',
            assignable: m.isActive !== false && UserRoleConfigManager.isAssignableFromRoleNames(m.roleNames),
          },
        ]),
    );
  }
  return assignabilityCache.get(userId) ?? null;
}

/**
 * Guard for tools that CREATE work on someone's calendar (add_stop,
 * add_multiple_stops, plan_route, repeat_stop, create_booking_request) or
 * search for where work would fit on it (find_stop_slots,
 * check_time_availability, find_service_along_route): returns a structured
 * "not eligible" result when the target teammate cannot receive appointments,
 * or null when it's fine to proceed. The operator's own calendar is never
 * blocked, and an unknown id is left to the backend to judge.
 */
async function assigneeNotEligibleResult(
  userId: string,
  isOther: boolean,
  input: Record<string, unknown>,
): Promise<{ created: false; assigneeNotEligible: true; note: string } | null> {
  if (!isOther) return null;
  const info = await getAssignability(userId);
  if (!info || info.assignable) return null;
  const name = input.assignedUserName ? String(input.assignedUserName).trim() || info.name : info.name;
  return {
    created: false,
    assigneeNotEligible: true,
    note:
      `${name} cannot receive appointments - their role (e.g. Scheduling Assistant) schedules FOR others ` +
      'but is not assignable to jobs/stops, or their account is inactive. NOTHING was scheduled. Tell the ' +
      'user this plainly and do NOT keep offering to book for this person. Offer to put it on the ' +
      "user's own calendar instead, or on an assignable teammate (find_team_member shows who can " +
      'receive appointments via canReceiveAppointments).',
  };
}

/**
 * Resolve the effective route START/END coordinates for whoever a tool is
 * acting on, respecting the company's Route Preferences (home vs. office -
 * see `fetchRouteEndpoints`). Reuses the operator's already-resolved env
 * values when acting for yourself (no extra call); fetches fresh per-person
 * data when acting for a teammate, since their home address (and therefore
 * their correct starting point) differs from the operator's.
 */
async function resolveRouteEndpointsFor(
  userId: string,
  deps: RoutyToolDeps,
): Promise<{ start?: { lat: number; lng: number }; end?: { lat: number; lng: number } }> {
  if (userId === deps.env.currentUserId) {
    return {
      start: deps.env.startingCoordinates,
      end: deps.env.endingCoordinates ?? deps.env.startingCoordinates,
    };
  }
  const endpoints = await fetchRouteEndpoints(userId);
  return {
    start: endpoints.start.coordinates,
    end: endpoints.end.coordinates ?? endpoints.start.coordinates,
  };
}

async function execFindTeamMember(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const name = String(input.name || '').trim();
  if (!name) throw new Error('Provide a name to search for.');

  if (!deps.env.canManageAllBookings) {
    return {
      found: 0,
      permissionDenied: true,
      note:
        "The operator doesn't have permission to view or manage other team members' schedules. Tell them " +
        'plainly and offer to handle this on their own calendar instead - do not attempt to act on anyone ' +
        'else\'s calendar.',
    };
  }

  deps.onActivity?.(`Looking up ${name} on the team`);
  const members = await UserService.getApiAppUserGetTeamMembers();
  const needle = name.toLowerCase();
  const matches = ((members ?? []) as Routemize_DTOs_TeamMemberListDto[]).filter(
    (m) => m.isActive !== false && (m.name ?? '').toLowerCase().includes(needle),
  );

  if (matches.length === 0) {
    return { found: 0, note: `No team member matching "${name}". Ask the user to double-check the name.` };
  }

  // Resolve each match's REAL starting/ending address (home vs. office, per the
  // company's Route Preferences) so questions like "what's Chris's starting
  // location?" are answered from actual data, never guessed. Also flag whether
  // each match can RECEIVE appointments (same role rule assignment pickers
  // use) so a Scheduling Assistant is never silently offered as an assignee.
  const withLocations = await Promise.all(
    matches.slice(0, 5).map(async (m) => {
      const endpoints = m.id ? await fetchRouteEndpoints(m.id).catch(() => null) : null;
      return {
        userId: m.id ?? null,
        name: m.name || 'Unknown',
        roles: m.roleNames ?? [],
        canReceiveAppointments: UserRoleConfigManager.isAssignableFromRoleNames(m.roleNames),
        startingAddress: endpoints?.start.address ?? null,
        endingAddress: endpoints?.end.address ?? null,
      };
    }),
  );

  const someNotAssignable = withLocations.some((m) => !m.canReceiveAppointments);
  return {
    found: matches.length,
    teamMembers: withLocations,
    note:
      (matches.length > 1
        ? 'Multiple team members matched - ask the user which one before proceeding. '
        : "Pass this userId as assignedUserId (and the name as assignedUserName for display) to the scheduling tool you're about to call. startingAddress/endingAddress are their REAL route endpoints (home or office, per company settings) - use these verbatim if asked; never guess or infer a location from appointment addresses. ") +
      (someNotAssignable
        ? 'IMPORTANT: canReceiveAppointments=false means that person schedules FOR others (e.g. Scheduling ' +
          'Assistant) and CANNOT be assigned appointments, bookings, or stops - if the user wants to book ' +
          'work for them, say that plainly UP FRONT and offer their own calendar or an assignable teammate ' +
          'instead. Viewing such a person\'s schedule or blocking their days off is still fine.'
        : ''),
  };
}

async function execRequestMissingAddress(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const subject = String(input.subject || '').trim();
  if (!subject) throw new Error('Provide a subject (whose/what address is needed).');

  deps.onArtifact?.({
    type: 'missing_address',
    subject,
    context: input.context ? String(input.context) : undefined,
    purpose: input.purpose ? String(input.purpose) : undefined,
  });

  return {
    shown: true,
    note:
      'An address input card is now shown to the user for this one item - do not also ask for it in text. ' +
      'If other stops in this request already have real addresses, schedule THOSE now (add_multiple_stops/ ' +
      'add_stop) in this same turn rather than waiting. Your text reply must plainly state what you already ' +
      'scheduled (if anything) and that you are waiting on this one address - never reply with just "Done". ' +
      'The user\'s next message will contain the address they entered; use it to add this one remaining stop.',
  };
}

async function execSearchPlaces(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const query = String(input.query);
  const maxResults = input.maxResults ? Number(input.maxResults) : 5;
  const bias =
    input.nearLat != null && input.nearLng != null
      ? { lat: Number(input.nearLat), lng: Number(input.nearLng) }
      : deps.env.startingCoordinates ?? null;

  deps.onActivity?.(`Searching for "${query}"`);
  const results = await searchPlaces(query, bias, maxResults);

  // "Closest" must mean closest to DRIVE to, not by straight line — a place
  // across a river/highway can be nearest as the crow flies but a longer
  // drive. Measure the real driving leg from the bias point to each candidate
  // (cached; a handful of matrix calls) and re-rank by drive time.
  let ranked: Array<(typeof results)[number] & { driveMiles?: number | null; driveMinutes?: number | null }> = results;
  if (bias && results.length > 1) {
    const legs = await Promise.all(
      results.map((p) =>
        googleMapsDistanceService.calculateDrivingDistance(bias, { lat: p.lat, lng: p.lng }).catch(() => null),
      ),
    );
    ranked = results
      .map((p, i) => ({
        ...p,
        driveMiles: legs[i] ? Number(legs[i]!.distance.toFixed(1)) : null,
        driveMinutes: legs[i] ? Math.round(legs[i]!.duration) : null,
      }))
      .sort((a, b) => (a.driveMinutes ?? Infinity) - (b.driveMinutes ?? Infinity) || (a.distanceMiles ?? Infinity) - (b.distanceMiles ?? Infinity));
  }

  const biasLabel =
    input.nearLat != null
      ? 'the point you provided'
      : bias
        ? deps.env.startingLabel || 'your starting address'
        : 'none';

  return {
    biasedFrom: biasLabel,
    resultCount: ranked.length,
    results: ranked,
    note:
      ranked.length === 0
        ? 'No matching places found. Try a broader query or a city/area.'
        : bias && ranked.length > 1
          ? `Results are ranked by REAL DRIVING TIME from ${biasLabel} (driveMinutes/driveMiles) - the FIRST ` +
            'result is the closest to drive to. If the user asked for the closest to a SPECIFIC stop/appointment, ' +
            'make sure the bias point you passed was THAT stop\'s coordinates, not the route start.'
          : 'Distances are straight-line miles from the bias point.',
  };
}

function stopLabel(apt: Appointment): string {
  const who = apt.customerName || apt.type || 'stop';
  return `your ${apt.startTime} ${who}`.trim();
}

async function execPresentPlaces(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const placeIds = Array.isArray(input.placeIds)
    ? (input.placeIds as unknown[]).map(String).filter(Boolean).slice(0, 3)
    : [];
  if (placeIds.length === 0) throw new Error('Provide at least one placeId from search_places.');

  const durationMinutes = input.durationMinutes ? Number(input.durationMinutes) : 15;
  const date = input.date ? String(input.date) : undefined;
  const startTime = input.startTime ? String(input.startTime) : undefined;

  // Guard against proposing a time that has already passed.
  if (date && startTime && !isFutureTenantSlot(date, startTime, deps.env.companyTimezone)) {
    return {
      presented: 0,
      note:
        'That proposed time has already passed (or is too soon). Call find_stop_slots or ' +
        'find_service_along_route to get the next available future slot, then call present_places again ' +
        'with that date/time. Do not present a past time.',
    };
  }

  deps.onActivity?.('Pulling place details');

  // Determine the appointments bracketing the proposed slot, for drive legs.
  let prev: Appointment | null = null;
  let next: Appointment | null = null;
  if (date && startTime) {
    const requested = parseLocalDateTime(date, startTime);
    const day = parseLocalDateTime(date);
    if (requested && day) {
      const appts = await fetchDayAppointments(deps.env.currentUserId, day, deps.env.companyTimezone);
      const before = appts
        .filter((a) => a.startDateTime.getTime() < requested.getTime())
        .sort((a, b) => a.startDateTime.getTime() - b.startDateTime.getTime());
      const after = appts
        .filter((a) => a.startDateTime.getTime() >= requested.getTime())
        .sort((a, b) => a.startDateTime.getTime() - b.startDateTime.getTime());
      prev = before[before.length - 1] ?? null;
      next = after[0] ?? null;
    }
  }

  const cards: RoutyPlaceCard[] = [];
  for (const placeId of placeIds) {
    let details: PlaceDetails;
    try {
      details = await getPlaceDetails(placeId);
    } catch {
      const c = getCachedPlace(placeId);
      if (!c) continue;
      details = {
        placeId,
        name: c.name,
        address: c.address,
        lat: c.lat,
        lng: c.lng,
        rating: c.rating,
        reviewCount: c.reviewCount,
        openNow: c.openNow,
        phone: null,
        website: null,
        mapsUrl: null,
        photoUrl: c.photoUrl,
        topReview: null,
      };
    }

    const cached = getCachedPlace(placeId);
    const placeCoords = { lat: details.lat || cached?.lat || 0, lng: details.lng || cached?.lng || 0 };

    let driveIn: DriveLeg | undefined;
    let driveOut: DriveLeg | undefined;
    if (date && startTime && placeCoords.lat) {
      const fromCoords =
        prev?.latitude != null && prev?.longitude != null
          ? { lat: prev.latitude, lng: prev.longitude }
          : deps.env.startingCoordinates;
      if (fromCoords) {
        const r = await googleMapsDistanceService.calculateDrivingDistance(fromCoords, placeCoords).catch(() => null);
        driveIn = {
          miles: r ? Number(r.distance.toFixed(1)) : null,
          minutes: r ? Math.round(r.duration) : null,
          label: prev ? stopLabel(prev) : (deps.env.startingLabel || 'your starting location'),
        };
      }
      if (next?.latitude != null && next?.longitude != null) {
        const toCoords = { lat: next.latitude, lng: next.longitude };
        const r = await googleMapsDistanceService.calculateDrivingDistance(placeCoords, toCoords).catch(() => null);
        driveOut = {
          miles: r ? Number(r.distance.toFixed(1)) : null,
          minutes: r ? Math.round(r.duration) : null,
          label: stopLabel(next),
        };
      }
    }

    cards.push({
      placeId: details.placeId,
      name: details.name,
      address: details.address,
      lat: placeCoords.lat,
      lng: placeCoords.lng,
      rating: details.rating,
      reviewCount: details.reviewCount,
      distanceMiles: cached?.distanceMiles ?? null,
      openNow: details.openNow,
      photoUrl: details.photoUrl,
      phone: details.phone,
      website: details.website,
      mapsUrl: details.mapsUrl,
      topReview: details.topReview,
      driveIn,
      driveOut,
      date,
      startTime,
    });
  }

  if (cards.length === 0) {
    throw new Error('Could not load details for those places. Re-run search_places and retry.');
  }

  deps.onArtifact?.({ type: 'places', durationMinutes, cards });

  return {
    presented: cards.length,
    durationMinutes,
    places: cards.map((c) => ({
      name: c.name,
      // Exact resolved location — carry address + latitude + longitude into
      // add_stop/add_multiple_stops when scheduling, never the bare name.
      address: c.address,
      latitude: c.lat,
      longitude: c.lng,
      rating: c.rating,
      reviews: c.reviewCount,
      driveInMiles: c.driveIn?.miles ?? null,
      driveInMinutes: c.driveIn?.minutes ?? null,
      driveOutMiles: c.driveOut?.miles ?? null,
      driveOutMinutes: c.driveOut?.minutes ?? null,
    })),
    note:
      'Cards are shown to the user. Briefly summarize the added driving and ask if you should schedule it. ' +
      'When scheduling one, pass its EXACT address + latitude + longitude (above) to add_stop/' +
      'add_multiple_stops — never the bare place name, or it re-geocodes to the wrong branch.',
  };
}

const TIME_WINDOWS: Record<string, { start: number; end: number }> = {
  Morning: { start: 6, end: 12 },
  Afternoon: { start: 12, end: 17 },
  Evening: { start: 17, end: 21 },
  Flexible: { start: 0, end: 24 },
};

async function execFindServiceAlongRoute(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const category = String(input.category);
  const startDate = String(input.startDate);
  const endDate = input.endDate ? String(input.endDate) : undefined;
  const timePreference = (input.timePreference as string) || 'Flexible';
  const durationMinutes = input.durationMinutes ? Number(input.durationMinutes) : 30;
  const win = TIME_WINDOWS[timePreference] ?? TIME_WINDOWS.Flexible;
  const { userId, isOther } = resolveAssignee(input, deps);
  // Finding stops to slot into a teammate's route implies assigning them work.
  const notEligible = await assigneeNotEligibleResult(userId, isOther, input);
  if (notEligible) return notEligible;
  const { start: routeStart } = await resolveRouteEndpointsFor(userId, deps);

  deps.onActivity?.(`Finding ${category} options that fit ${isOther ? `${assigneeLabel(input, isOther)}'s` : 'your'} route`);
  const scheduleByDay = await fetchWorkScheduleByDay(userId);
  const tenantNow = tenantShiftedNow(deps.env.companyTimezone);

  // Collect open gaps long enough for the visit across the workdays in range.
  const dates = enumerateDates(startDate, endDate).filter((d) => {
    const h = getWorkingHoursForDate(d, scheduleByDay);
    return h.isWorkDay && isDayAvailable(d, h.end, tenantNow);
  });

  type Gap = ReturnType<typeof findOpenGaps>[number];
  const gaps: Gap[] = [];
  for (const d of dates) {
    const h = getWorkingHoursForDate(d, scheduleByDay);
    const appts = await fetchDayAppointments(userId, d, deps.env.companyTimezone);
    for (const g of findOpenGaps(appts, d, h.start, h.end, tenantNow)) {
      if (g.durationMinutes >= durationMinutes + 10) gaps.push(g);
    }
  }
  if (gaps.length === 0) {
    return { found: 0, note: 'No open gaps long enough in that range. Suggest a different week or a shorter visit.' };
  }

  // Bound the number of place searches to the soonest few gaps.
  gaps.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
  const topGaps = gaps.slice(0, 4);

  interface Candidate {
    placeId: string;
    name: string;
    address: string;
    lat: number;
    lng: number;
    rating: number | null;
    reviewCount: number | null;
    openNow: boolean | null;
    photoUrl: string | null;
    driveIn: DriveLeg;
    driveOut?: DriveLeg;
    date: string;
    startTime: string;
    totalDrive: number;
  }

  const candidates: Candidate[] = [];
  for (const gap of topGaps) {
    const bias = gap.prevCoords ?? gap.nextCoords ?? routeStart ?? null;
    const places = await searchPlaces(category, bias, 3);
    for (const place of places) {
      const placeCoords = { lat: place.lat, lng: place.lng };
      const fromCoords = gap.prevCoords ?? routeStart;
      const driveInRes = fromCoords
        ? await googleMapsDistanceService.calculateDrivingDistance(fromCoords, placeCoords).catch(() => null)
        : null;
      const driveOutRes = gap.nextCoords
        ? await googleMapsDistanceService.calculateDrivingDistance(placeCoords, gap.nextCoords).catch(() => null)
        : null;
      const driveInMin = driveInRes?.duration ?? 0;
      const driveOutMin = driveOutRes?.duration ?? 0;

      const slotStart = roundToStepMinutes(addMinutes(gap.startTime, driveInMin), 5, 'up');
      const slotEnd = addMinutes(slotStart, durationMinutes);
      if (slotEnd.getTime() + driveOutMin * 60_000 > gap.endTime.getTime()) continue;
      const hr = slotStart.getHours();
      if (timePreference !== 'Flexible' && (hr < win.start || hr >= win.end)) continue;
      // Never offer a slot that has already passed (or is too soon).
      if (!isFutureTenantSlot(toDateKey(gap.date), formatTimeOfDay24(slotStart), deps.env.companyTimezone)) continue;

      candidates.push({
        placeId: place.placeId,
        name: place.name,
        address: place.address,
        lat: place.lat,
        lng: place.lng,
        rating: place.rating,
        reviewCount: place.reviewCount,
        openNow: place.openNow,
        photoUrl: place.photoUrl,
        driveIn: {
          miles: driveInRes ? Number(driveInRes.distance.toFixed(1)) : null,
          minutes: driveInRes ? Math.round(driveInRes.duration) : null,
          label: gap.prevLabel ?? (deps.env.startingLabel || 'your starting location'),
        },
        driveOut: gap.nextCoords
          ? {
              miles: driveOutRes ? Number(driveOutRes.distance.toFixed(1)) : null,
              minutes: driveOutRes ? Math.round(driveOutRes.duration) : null,
              label: gap.nextLabel ?? 'your next stop',
            }
          : undefined,
        date: toDateKey(gap.date),
        startTime: formatTimeOfDay24(slotStart),
        totalDrive: driveInMin + driveOutMin,
      });
    }
  }

  if (candidates.length === 0) {
    return { found: 0, note: `No ${category} fit cleanly into your gaps. Try a wider range or different time preference.` };
  }

  // Keep the best slot per place, then the top 3 by least added driving.
  const bestByPlace = new Map<string, Candidate>();
  for (const c of candidates) {
    const ex = bestByPlace.get(c.placeId);
    if (!ex || c.totalDrive < ex.totalDrive) bestByPlace.set(c.placeId, c);
  }
  const top = [...bestByPlace.values()].sort((a, b) => a.totalDrive - b.totalDrive).slice(0, 3);

  // Enrich the finalists with full Google details (photo/reviews/phone).
  const cards: RoutyPlaceCard[] = [];
  for (const c of top) {
    let card: RoutyPlaceCard = {
      placeId: c.placeId,
      name: c.name,
      address: c.address,
      lat: c.lat,
      lng: c.lng,
      rating: c.rating,
      reviewCount: c.reviewCount,
      distanceMiles: null,
      openNow: c.openNow,
      photoUrl: c.photoUrl,
      phone: null,
      website: null,
      mapsUrl: null,
      topReview: null,
      driveIn: c.driveIn,
      driveOut: c.driveOut,
      date: c.date,
      startTime: c.startTime,
    };
    try {
      const d = await getPlaceDetails(c.placeId);
      card = {
        ...card,
        address: d.address || card.address,
        rating: d.rating ?? card.rating,
        reviewCount: d.reviewCount ?? card.reviewCount,
        openNow: d.openNow ?? card.openNow,
        photoUrl: d.photoUrl ?? card.photoUrl,
        phone: d.phone,
        website: d.website,
        mapsUrl: d.mapsUrl,
        topReview: d.topReview,
      };
    } catch {
      // Keep the search-level card if details fail.
    }
    cards.push(card);
  }

  deps.onArtifact?.({ type: 'places', durationMinutes, cards });
  return {
    found: cards.length,
    durationMinutes,
    options: cards.map((c) => ({
      name: c.name,
      // Exact resolved location — MUST be carried into add_stop/add_multiple_stops
      // (address + latitude + longitude) when scheduling this place, so the
      // stop lands at THIS branch instead of a name that re-geocodes elsewhere.
      address: c.address,
      latitude: c.lat,
      longitude: c.lng,
      rating: c.rating,
      reviews: c.reviewCount,
      date: c.date,
      startTime: formatTimeString(c.startTime),
      driveInMinutes: c.driveIn?.minutes ?? null,
      driveOutMinutes: c.driveOut?.minutes ?? null,
    })),
    note:
      'Cards are shown to the user, ranked by least added driving. Recommend the top one and ask if you ' +
      'should schedule it. When you DO schedule one of these, pass its EXACT address + latitude + longitude ' +
      '(above) to add_stop/add_multiple_stops — never pass the bare place name as the address, or it will ' +
      're-geocode to the wrong branch.',
  };
}

async function execFindStopSlots(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const address = String(input.address);
  const durationMinutes = Number(input.durationMinutes);
  const startDate = String(input.startDate);
  const endDate = input.endDate ? String(input.endDate) : undefined;
  const timePreference = (input.timePreference as TimePreference) || 'Flexible';
  const { userId, isOther } = resolveAssignee(input, deps);
  // Refuse at the ENTRY of the job-stop flow, not just at the final write -
  // proposing slots for someone who can't receive work strings the user along.
  const notEligible = await assigneeNotEligibleResult(userId, isOther, input);
  if (notEligible) return notEligible;
  const { start: routeStart } = await resolveRouteEndpointsFor(userId, deps);

  deps.onActivity?.(`Finding the best slots near ${address}${isOther ? ` for ${assigneeLabel(input, isOther)}` : ''}`);
  const coords = await geocodeOrThrow(address);
  const scheduleByDay = await fetchWorkScheduleByDay(userId);
  const tenantNow = tenantShiftedNow(deps.env.companyTimezone);

  const allDates = enumerateDates(startDate, endDate);
  const workdays = allDates.filter((d) => {
    const hours = getWorkingHoursForDate(d, scheduleByDay);
    return hours.isWorkDay && isDayAvailable(d, hours.end, tenantNow);
  });

  if (workdays.length === 0) {
    // Hand back verified date labels plus (when the requested day is a
    // non-working day) the add_stop day-off override option, so the first
    // reply offers BOTH "next working day" and "book it anyway" instead of
    // making the user ask for the override (QA 86bavtw0c).
    const dateFacts = await describeNonWorkingDayAlternatives(startDate, deps, scheduleByDay, {
      offerStopOverride: true,
    });
    return { slots: [], note: 'No workdays with remaining availability in that range.' + dateFacts };
  }

  let slots: StopSlot[];
  if (workdays.length === 1) {
    const d = workdays[0];
    const hours = getWorkingHoursForDate(d, scheduleByDay);
    const appointments = await fetchDayAppointments(userId, d, deps.env.companyTimezone);
    slots = await findStopSlotsForDay({
      appointments,
      stopCoordinates: { lat: coords.lat, lng: coords.lng },
      stopDurationMinutes: durationMinutes,
      date: d,
      timePreference,
      workingHoursStart: hours.start,
      workingHoursEnd: hours.end,
      startingCoordinates: routeStart,
      slotIntervalMinutes: 30,
      nowOverride: tenantNow,
    });
  } else {
    const daySchedules = await Promise.all(
      workdays.map(async (d) => {
        const hours = getWorkingHoursForDate(d, scheduleByDay);
        const appointments = await fetchDayAppointments(userId, d, deps.env.companyTimezone);
        return { date: d, appointments, workingHoursStart: hours.start, workingHoursEnd: hours.end };
      }),
    );
    slots = await findStopSlotsMultiDay({
      daySchedules,
      stopCoordinates: { lat: coords.lat, lng: coords.lng },
      stopDurationMinutes: durationMinutes,
      timePreference,
      startingCoordinates: routeStart,
      slotIntervalMinutes: 30,
      nowOverride: tenantNow,
    });
  }

  // Drop any slot that has already passed (frame-correct, see isFutureTenantSlot).
  // StopSlot.startTime is already a 24h "HH:MM" string.
  slots = slots.filter(
    (s) =>
      s.suggestedDate &&
      s.startTime &&
      isFutureTenantSlot(s.suggestedDate, s.startTime, deps.env.companyTimezone),
  );

  return {
    resolvedAddress: coords.formatted,
    durationMinutes,
    slotCount: slots.length,
    slots: slots.slice(0, 6).map(slotToJson),
    note:
      slots.length === 0
        ? 'No future slots fit that duration in the searched range. Suggest a shorter duration or wider date range.'
        : 'Slots are ranked best-first by route efficiency (least extra driving).',
  };
}

async function execCheckTimeAvailability(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const address = String(input.address);
  const date = String(input.date);
  const startTime = String(input.startTime);
  const durationMinutes = Number(input.durationMinutes);
  const { userId, isOther } = resolveAssignee(input, deps);
  // Checking "can X be at this address at 1pm" implies scheduling work for X -
  // refuse up front for a teammate who can't receive appointments.
  const notEligible = await assigneeNotEligibleResult(userId, isOther, input);
  if (notEligible) return notEligible;

  const day = parseLocalDateTime(date);
  const requestedStart = parseLocalDateTime(date, startTime);
  if (!day || !requestedStart) throw new Error('Invalid date/time. Use YYYY-MM-DD and HH:MM.');
  const requestedEnd = addMinutes(requestedStart, durationMinutes);

  deps.onActivity?.(`Checking if ${startTime} works on ${date}${isOther ? ` for ${assigneeLabel(input, isOther)}` : ''}`);
  const coords = await geocodeOrThrow(address);
  const scheduleByDay = await fetchWorkScheduleByDay(userId);
  const hours = getWorkingHoursForDate(day, scheduleByDay);
  const appointments = await fetchDayAppointments(userId, day, deps.env.companyTimezone);

  const dayStart = parseLocalDateTime(date, hours.start)!;
  const dayEnd = parseLocalDateTime(date, hours.end)!;
  const withinHours =
    hours.isWorkDay &&
    requestedStart.getTime() >= dayStart.getTime() &&
    requestedEnd.getTime() <= dayEnd.getTime();

  const conflicts = appointments.filter(
    (apt) =>
      requestedStart.getTime() < apt.endDateTime.getTime() &&
      requestedEnd.getTime() > apt.startDateTime.getTime(),
  );

  const available = withinHours && conflicts.length === 0;

  let alternatives: ReturnType<typeof slotToJson>[] = [];
  if (hours.isWorkDay) {
    const tenantNow = tenantShiftedNow(deps.env.companyTimezone);
    const { start: routeStart } = await resolveRouteEndpointsFor(userId, deps);
    const slots = await findStopSlotsForDay({
      appointments,
      stopCoordinates: { lat: coords.lat, lng: coords.lng },
      stopDurationMinutes: durationMinutes,
      date: day,
      timePreference: 'Flexible',
      workingHoursStart: hours.start,
      workingHoursEnd: hours.end,
      startingCoordinates: routeStart,
      slotIntervalMinutes: 15,
      nowOverride: tenantNow,
    });
    alternatives = slots.slice(0, 5).map(slotToJson);
  }

  return {
    available,
    resolvedAddress: coords.formatted,
    reason: available
      ? 'The requested time is within working hours and has no conflicts.'
      : !hours.isWorkDay
        ? `${formatDateMedium(day)} is not a working day. ` +
          describeDayOff(hours, day, assigneePossessive(input, isOther)) +
          // Job-stop flow: surface the add_stop day-off override as an upfront
          // choice, not something the user must ask for (QA 86bavtw0c).
          (await describeNonWorkingDayAlternatives(date, deps, scheduleByDay, { offerStopOverride: true }))
        : !withinHours
          ? `The requested time falls outside working hours (${formatTimeString(hours.start)}-${formatTimeString(hours.end)}).`
          : `Conflicts with ${conflicts.length} existing appointment(s).`,
    conflicts: conflicts.map((a) => appointmentToJson(a)),
    alternatives,
  };
}

async function execGetDayBriefing(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const tenantNow = tenantShiftedNow(deps.env.companyTimezone);
  const date = input.date ? String(input.date) : toDateKey(tenantNow);
  const day = parseLocalDateTime(date);
  if (!day) throw new Error('Invalid date.');
  const { userId, isOther } = resolveAssignee(input, deps);

  deps.onActivity?.(`Reviewing ${date}${isOther ? ` for ${assigneeLabel(input, isOther)}` : ''}`);
  const [response, scheduleByDay, overridesByDate] = await Promise.all([
    AppointmentService.getApiAppAppointmentGetUserTimeline(userId, undefined, undefined, undefined, date),
    fetchWorkScheduleByDay(userId),
    fetchUserOverridesByDate(userId),
  ]);
  const hours = getEffectiveDayAvailability(day, scheduleByDay, overridesByDate);
  const items = response?.items ?? [];

  const appts = items.filter((i) => i.type === Routemize_DTOs_TimelineItemType.APPOINTMENT && i.appointment);
  const travels = items.filter((i) => i.type === Routemize_DTOs_TimelineItemType.TRAVEL && i.travelInfo);
  const gaps = items.filter((i) => i.type === Routemize_DTOs_TimelineItemType.GAP);

  const KM_TO_MI = 0.621371;
  let totalMiles = 0;
  let totalDriveMin = 0;
  let longestLeg = { miles: 0, minutes: 0, to: '' as string | null };
  for (const t of travels) {
    const mi = (t.travelInfo!.distanceKm ?? 0) * KM_TO_MI;
    const min = t.travelInfo!.travelTimeMinutes ?? 0;
    totalMiles += mi;
    totalDriveMin += min;
    if (min > longestLeg.minutes) longestLeg = { miles: Number(mi.toFixed(1)), minutes: Math.round(min), to: t.travelInfo!.toAddress ?? null };
  }

  // Split synced external (Google/Outlook/Zoom) events out of the Routemize
  // stops so the briefing gives a COMPLETE picture of the day without
  // mislabeling an external meeting as a regular appointment (QA 86bavp5v8).
  const isExternalItem = (a: (typeof appts)[number]) =>
    !!a.appointment!.source && EXTERNAL_APPOINTMENT_SOURCES.has(String(a.appointment!.source));
  const routemizeAppts = appts.filter((a) => !isExternalItem(a));
  const externalItems = appts.filter(isExternalItem);

  const apptSummaries = routemizeAppts.map((a) => ({
    title: a.appointment!.bookingFormName || a.appointment!.title || 'Appointment',
    contact: a.appointment!.contactName || null,
    start: a.startTime,
    // Real status (QA 86bavp7rz) - a past appointment still "Scheduled" was
    // never marked done and must not be summarized as completed.
    status: String(a.appointment!.status ?? 'Scheduled'),
  }));
  const externalEvents = externalItems.map((a) => ({
    source: String(a.appointment!.source ?? 'External'),
    title: a.appointment!.title || 'Busy',
    start: a.startTime,
    end: a.endTime,
  }));

  return {
    date,
    isWorkDay: hours.isWorkDay,
    workingHours: hours.isWorkDay ? { start: hours.start, end: hours.end } : null,
    // OFF vs OPEN comes from working hours + this person's date overrides,
    // never from whether anything happens to be booked (QA: empty working
    // days were being summarized as "Off").
    dayStatus: !hours.isWorkDay
      ? describeDayOff(hours, day, assigneePossessive(input, isOther))
      : routemizeAppts.length === 0
        ? `OPEN working day (${hours.start}-${hours.end}) with NOTHING booked - describe it as open/free, NEVER as "off".`
        : `Working day, hours ${hours.start}-${hours.end}.`,
    stopCount: routemizeAppts.length,
    first: apptSummaries[0]?.start ?? null,
    last: apptSummaries[apptSummaries.length - 1]?.start ?? null,
    totalDriveMiles: Number(totalMiles.toFixed(1)),
    totalDriveMinutes: Math.round(totalDriveMin),
    openGapCount: gaps.length,
    longestLeg: longestLeg.minutes > 0 ? longestLeg : null,
    appointments: apptSummaries,
    ...(externalEvents.length > 0
      ? {
          externalEventCount: externalEvents.length,
          externalEvents,
          note:
            `The day also has ${externalEvents.length} synced EXTERNAL calendar event(s) (see externalEvents) - ` +
            'include them in any day summary, labeled by source (e.g. "plus your Google Calendar event at 2 PM"), ' +
            'so the user gets a complete view. They are read-only: never offer to move/cancel them.',
        }
      : {}),
  };
}

/**
 * Role-aware daily summary card: company-wide (one row per team member plus
 * the day's top lead sources) for operators with manage-all-bookings,
 * otherwise the operator's own day. Rendered as a `day_summary` artifact so
 * the numbers read at a glance instead of as a paragraph.
 */
async function execGetDailySummary(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const tenantNow = tenantShiftedNow(deps.env.companyTimezone);
  const date = input.date ? String(input.date) : toDateKey(tenantNow);
  const day = parseLocalDateTime(date);
  if (!day) throw new Error('Invalid date. Use YYYY-MM-DD.');
  const companyScope = deps.env.canManageAllBookings;

  deps.onActivity?.(companyScope ? "Summarizing the team's day" : 'Summarizing your day');

  // Who to include: every active assignable team member for a company-wide
  // summary; just the operator otherwise.
  let people: Array<{ id: string; name: string }>;
  if (companyScope) {
    const team = (await UserService.getApiAppUserGetTeamMembers().catch(() => [])) as Routemize_DTOs_TeamMemberListDto[];
    people = (team ?? [])
      .filter((m) => !!m.id && m.isActive !== false && UserRoleConfigManager.isAssignableFromRoleNames(m.roleNames))
      .map((m) => ({ id: m.id as string, name: m.name || 'Team member' }));
    if (people.length === 0) people = [{ id: deps.env.currentUserId, name: deps.env.currentUserName || 'You' }];
  } else {
    people = [{ id: deps.env.currentUserId, name: deps.env.currentUserName || 'You' }];
  }

  const memberRows = await Promise.all(
    people.slice(0, 15).map(async (p) => {
      const appts = (await fetchDayAppointments(p.id, day, deps.env.companyTimezone).catch(() => []))
        .filter(isRoutemizeManaged);
      const active = appts.filter((a) => a.status !== 'cancelled' && a.status !== 'no-show');
      const completed = active.filter((a) => a.status === 'complete');
      const cancelled = appts.length - active.length;
      const byService: Record<string, number> = {};
      for (const c of completed) {
        const key = c.type || 'Appointment';
        byService[key] = (byService[key] ?? 0) + 1;
      }
      const completedLabel =
        Object.entries(byService)
          .sort((a, b) => b[1] - a[1])
          .map(([svc, n]) => `${n}× ${svc}`)
          .join(', ') || null;
      return {
        name: p.name,
        total: active.length,
        completed: completed.length,
        remaining: active.length - completed.length,
        cancelled,
        completedLabel,
      };
    }),
  );
  // Drop empty rows so a big team doesn't produce a wall of zeros.
  const members = memberRows.filter((r) => r.total > 0 || r.cancelled > 0);

  const totals = members.reduce(
    (acc, r) => ({
      appointments: acc.appointments + r.total,
      completed: acc.completed + r.completed,
      remaining: acc.remaining + r.remaining,
      cancelled: acc.cancelled + r.cancelled,
    }),
    { appointments: 0, completed: 0, remaining: 0, cancelled: 0 },
  );

  // Bookings CREATED today + their top lead sources - company scope + today
  // only (same data the dashboard's Booked Today widget shows). These are a
  // DIFFERENT axis from `totals` (appointments happening on the day): a
  // booking that came in today for next Wednesday counts here but not there.
  // Best-effort: a failure here never breaks the summary.
  let leadSources: Array<{ source: string; count: number }> | undefined;
  let bookedToday: RoutyDaySummaryArtifact['bookedToday'];
  const isToday = date === toDateKey(tenantNow);
  if (companyScope && isToday) {
    try {
      const manager = new AppointmentManager(deps.env.companyTimezone);
      const result = await manager.searchAppointments({ maxResultCount: 300, dateFilter: 'all', sorting: 'creationTime desc' });
      const items = (result.items as Routemize_DTOs_AppointmentListItem[] | undefined) ?? [];
      const windowStart = creationWindowStartUtc('today', deps.env.companyTimezone);
      const createdToday = items.filter((apt) => {
        if (apt.isExternal) return false;
        const status = apt.status?.toString();
        if (status === Routemize_Constants_AppointmentStatus.CANCELLED || status === Routemize_Constants_AppointmentStatus.ARCHIVED) return false;
        const created = parseBackendDate(apt.creationTime);
        return !!created && created.getTime() >= windowStart.getTime();
      });
      bookedToday = { count: createdToday.length };
      if (createdToday.length > 0) {
        const [details, leadSourceMasterData] = await Promise.all([
          Promise.all(createdToday.slice(0, 20).map((apt) => (apt.id ? AppointmentService.getApiAppAppointmentGetAppointmentDetail(apt.id).catch(() => null) : null))),
          MasterDataService.getApiAppMasterDataGet('ReferralType').catch(() => []),
        ]);
        bookedToday.items = details
          .filter((d): d is NonNullable<typeof d> => !!d)
          .slice(0, 5)
          .map((d) => {
            const customer =
              [d.contact?.firstName, d.contact?.lastName].filter(Boolean).join(' ').trim() ||
              d.contact?.businessName ||
              'Customer';
            const start = parseBackendDate(d.startTimeUtc || d.appointment?.startTimeUtc);
            return {
              customer,
              service: d.request?.eventDetail?.eventName || d.title || null,
              whenLabel: start ? formatDateMedium(start) : null,
            };
          });
        const codeToName = new Map<string, string>();
        for (const item of leadSourceMasterData?.[0]?.items ?? []) {
          if (item.code && item.text) codeToName.set(item.code, item.text);
        }
        const counts: Record<string, number> = {};
        for (const d of details) {
          const code = d?.request?.leadSource ?? null;
          const label = code ? codeToName.get(code) || code : 'Unknown';
          counts[label] = (counts[label] ?? 0) + 1;
        }
        leadSources = Object.entries(counts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([source, count]) => ({ source, count }));
      }
    } catch {
      // Booked-today/lead sources are a bonus - skip on failure.
    }
  }

  deps.onArtifact?.({
    type: 'day_summary',
    date,
    dateLabel: formatDateMedium(day),
    scope: companyScope ? 'company' : 'self',
    totals,
    ...(companyScope ? { members } : {}),
    ...(bookedToday ? { bookedToday } : {}),
    ...(leadSources && leadSources.length > 0 ? { leadSources } : {}),
  });

  return {
    date,
    scope: companyScope ? 'the whole company' : 'your own day',
    totals,
    ...(companyScope ? { members } : {}),
    ...(bookedToday ? { bookedToday } : {}),
    ...(leadSources ? { leadSources } : {}),
    note:
      'The daily summary CARD is now shown - it has every number. Your reply must be ONE short sentence of ' +
      'framing (e.g. "Here\'s how the day looks - Chris is leading with 4 completed.") and must NOT restate ' +
      'the counts, member rows, or lead sources as prose or a list. Counts exclude cancelled/no-show. ' +
      'IMPORTANT distinction: totals.appointments = appointments HAPPENING on this date; bookedToday.count = ' +
      'NEW bookings CREATED today (possibly scheduled for future dates). "How many did we book today?" is ' +
      'answered by bookedToday.count, never totals.appointments.',
  };
}

/**
 * Comprehensive company/team report over a date range (typically a week),
 * rendered as a `week_report` card - the "CEO view": company totals, one row
 * per team member (appointment counts, service mix, lead sources, drive
 * miles), busiest day, and who is driving the most. Built on the same
 * Analytics Dashboard the reporting page uses (company-wide call for totals +
 * per-member calls for each member's lead sources and service mix), so the
 * numbers always match reporting. Manage-all-bookings only.
 */
async function execGetWeekReport(input: Record<string, unknown>, deps: RoutyToolDeps) {
  if (!deps.env.canManageAllBookings) {
    return {
      shown: false,
      note:
        'REFUSED - the operator does not have manage-all-bookings permission, so no company report is ' +
        'available. Summarize their OWN week instead by calling get_schedule for each day of the range.',
    };
  }

  const startDate = String(input.startDate);
  const endDate = String(input.endDate);
  const startDay = parseLocalDateTime(startDate);
  const endDay = parseLocalDateTime(endDate);
  if (!startDay || !endDay) throw new Error('Invalid date range. Use YYYY-MM-DD for startDate and endDate.');
  if (endDay.getTime() < startDay.getTime()) throw new Error('endDate must not be before startDate.');
  const rangeDays = Math.round((endDay.getTime() - startDay.getTime()) / 86400000) + 1;
  if (rangeDays > 31) {
    throw new Error('The range is too long for a team report (max 31 days). Narrow it and try again.');
  }

  deps.onActivity?.("Building the team's report");
  const dash = await AnalyticsDashboardService.getApiAppAnalyticsDashboardGetDashboard(
    startDate,
    endDate,
    undefined,
    false,
  );

  const perf = (dash.teamPerformance ?? []).filter((t) => (t.totalAppointments ?? 0) > 0);

  // Per-member drilldown (lead sources + service mix) via the same dashboard
  // filtered to each member. Best-effort: a failed drilldown only loses that
  // member's breakdown, never the report.
  const drilldowns = await Promise.all(
    perf.slice(0, 10).map(async (t) => {
      if (!t.userId) return null;
      return AnalyticsDashboardService.getApiAppAnalyticsDashboardGetDashboard(
        startDate,
        endDate,
        t.userId,
        false,
      ).catch(() => null);
    }),
  );

  const members: WeekReportMemberRow[] = perf
    .slice(0, 10)
    .map((t, i) => {
      const drill = drilldowns[i];
      const servicesLabel =
        (drill?.bookingForms ?? [])
          .filter((f) => (f.appointmentCount ?? 0) > 0)
          .sort((a, b) => (b.appointmentCount ?? 0) - (a.appointmentCount ?? 0))
          .slice(0, 4)
          .map((f) => `${f.appointmentCount}× ${f.formName || 'Service'}`)
          .join(', ') || null;
      const leadSources = (drill?.leadSources ?? [])
        .filter((l) => (l.count ?? 0) > 0)
        .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
        .slice(0, 3)
        .map((l) => ({ source: l.label || 'Unknown', count: l.count ?? 0 }));
      return {
        name: t.name || 'Team member',
        total: t.totalAppointments ?? 0,
        completed: t.completedCount ?? 0,
        scheduled: t.scheduledCount ?? 0,
        cancelled: t.cancelledCount ?? 0,
        noShow: t.noShowCount ?? 0,
        driveMiles: t.totalDistanceMiles != null ? Number(t.totalDistanceMiles.toFixed(1)) : null,
        driveMinutes: t.totalTravelTimeMinutes != null ? Math.round(t.totalTravelTimeMinutes) : null,
        ...(servicesLabel ? { servicesLabel } : {}),
        ...(leadSources.length > 0 ? { leadSources } : {}),
      };
    })
    .sort((a, b) => b.total - a.total);

  const busiestVolume = [...(dash.volumeByDay ?? [])]
    .filter((d) => (d.appointmentCount ?? 0) > 0)
    .sort((a, b) => (b.appointmentCount ?? 0) - (a.appointmentCount ?? 0))[0];
  const busiestDayDate = busiestVolume?.date ? parseLocalDateTime(String(busiestVolume.date).slice(0, 10)) : null;
  const busiestDay =
    busiestVolume && busiestDayDate
      ? { label: formatDateMedium(busiestDayDate), count: busiestVolume.appointmentCount ?? 0 }
      : null;

  const topByCount = members[0];
  const topByMiles = [...members].sort((a, b) => (b.driveMiles ?? 0) - (a.driveMiles ?? 0))[0];
  const highlights = {
    mostAppointments: topByCount && topByCount.total > 0 ? { name: topByCount.name, count: topByCount.total } : null,
    mostDriving:
      topByMiles && (topByMiles.driveMiles ?? 0) > 0
        ? { name: topByMiles.name, miles: topByMiles.driveMiles as number }
        : null,
  };

  const leadSources = (dash.leadSources ?? [])
    .filter((l) => (l.count ?? 0) > 0)
    .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
    .slice(0, 5)
    .map((l) => ({ source: l.label || 'Unknown', count: l.count ?? 0 }));

  const totals = {
    appointments: dash.totalAppointments ?? 0,
    completed: dash.completedCount ?? 0,
    scheduled: dash.scheduledCount ?? 0,
    cancelled: dash.cancelledCount ?? 0,
    noShow: dash.noShowCount ?? 0,
    driveMiles:
      dash.routeInsights?.totalDistanceMiles != null
        ? Number(dash.routeInsights.totalDistanceMiles.toFixed(1))
        : null,
    driveMinutes:
      dash.routeInsights?.totalTravelTimeMinutes != null
        ? Math.round(dash.routeInsights.totalTravelTimeMinutes)
        : null,
  };

  deps.onArtifact?.({
    type: 'week_report',
    startDate,
    endDate,
    rangeLabel: `${formatDateMedium(startDay)} – ${formatDateMedium(endDay)}`,
    totals,
    members,
    ...(leadSources.length > 0 ? { leadSources } : {}),
    busiestDay,
    highlights,
  });

  return {
    startDate,
    endDate,
    totals,
    members,
    leadSources,
    busiestDay,
    highlights,
    note:
      'The company report CARD is now shown - it has every number (per-member rows, service mix, lead ' +
      'sources, drive miles, busiest day). Your reply must be ONE short sentence of framing that may name ' +
      'the single most notable fact (e.g. who has the heaviest week or drives the most) and must NOT ' +
      'restate the rows, counts, or lead sources as prose or a list. Counts cover appointments OCCURRING ' +
      'in the range regardless of when they were booked. For an upcoming range, "completed" is naturally 0 ' +
      'and "scheduled" is the workload ahead.',
  };
}

/**
 * The Routy AI Summary briefing for one appointment, rendered as an
 * `appointment_brief` card in the chat - the same generation pipeline (and
 * session cache) the appointment details view uses.
 */
async function execGetAppointmentBrief(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const appointmentId = String(input.appointmentId);
  deps.onActivity?.('Preparing the appointment brief');

  const detail = await AppointmentService.getApiAppAppointmentGetAppointmentDetail(appointmentId);
  const contactName = [detail.contact?.firstName, detail.contact?.lastName].filter(Boolean).join(' ').trim();
  const businessName = detail.contact?.businessName || '';
  const serviceType = detail.request?.eventDetail?.eventName || detail.title || 'Appointment';

  const start = parseBackendDate(detail.startTimeUtc || detail.appointment?.startTimeUtc);
  const whenLabel = start
    ? `${formatDateMedium(start)} · ${formatTimeString(formatTimeOfDay24(start, deps.env.companyTimezone))}`
    : null;

  // Question labels so form answers read as "How many rooms?: 4" instead of
  // exposing raw question ids (same resolution the details view applies).
  const questionLabels = new Map<string, string>();
  if (detail.request?.bookingFormId) {
    try {
      const qs = await fetchFormQuestions(detail.request.bookingFormId);
      for (const q of qs) if (q.id && q.label) questionLabels.set(q.id, q.label);
    } catch {
      // Best-effort labels only.
    }
  }
  const formResponses = (detail.request?.answers ?? [])
    .filter((a) => a.answer && a.answer.trim() !== '')
    .map((a) => ({
      question: (a.questionId && questionLabels.get(a.questionId)) || a.question || 'Question',
      answer: a.answer || '',
    }));

  // Notes from the appointment's notes bundle (same source as the details
  // view): customer note, pre-booking and appointment staff notes, and the
  // cancel reason only for a cancelled appointment.
  let briefingNotes: ReturnType<typeof briefingNotesFromBundle> = {
    customerNote: undefined,
    preBookingNotes: [],
    internalNotes: [],
  };
  let cancellationReason: string | undefined;
  try {
    const bundle = await notesManager.getAppointmentBundle(appointmentId);
    briefingNotes = briefingNotesFromBundle(bundle);
    if (detail.status === Routemize_Constants_AppointmentStatus.CANCELLED) {
      cancellationReason = joinNoteContents(appointmentReasons(bundle)) || undefined;
    }
  } catch {
    // Best-effort - the brief still works without notes.
  }

  const data: AppointmentSummaryData = {
    serviceType,
    customerName: contactName || businessName || undefined,
    businessName: businessName || undefined,
    address:
      [detail.addressLine1, detail.addressLine2, [detail.city, detail.state].filter(Boolean).join(', '), detail.zipCode]
        .filter(Boolean)
        .join(', ') || undefined,
    scheduledDateTime: detail.startTimeUtc || detail.appointment?.startTimeUtc,
    scheduledDateTimeDisplay: whenLabel || undefined,
    durationMinutes:
      start && parseBackendDate(detail.endTimeUtc || detail.appointment?.endTimeUtc)
        ? getTimeDifferenceInMinutes(start, parseBackendDate(detail.endTimeUtc || detail.appointment?.endTimeUtc)!)
        : undefined,
    status: detail.status ? String(detail.status) : undefined,
    customerNote: briefingNotes.customerNote,
    preBookingNotes: briefingNotes.preBookingNotes,
    internalNotes: briefingNotes.internalNotes,
    cancellationReason,
    projectDetail: detail.request?.projectDetail || undefined,
    leadSource: detail.contact?.leadSource || detail.request?.leadSource || undefined,
    origin: detail.request?.origin != null ? String(detail.request.origin) : undefined,
    isAsap: detail.request?.isAsap || false,
    formResponses,
    hasAccessCode: !!detail.accessCode,
  };

  deps.onActivity?.('Writing the brief');
  const briefing = await AISummaryService.generateAppointmentBriefing(data, appointmentId, false, 'salesPrep');

  const title = [serviceType, contactName || businessName].filter(Boolean).join(' — ') || null;
  deps.onArtifact?.({
    type: 'appointment_brief',
    appointmentId,
    title,
    whenLabel,
    headline: briefing.headline,
    keyPoints: briefing.keyPoints,
    beforeArrival: briefing.beforeArrival,
    salesNotes: briefing.salesNotes,
    insight: briefing.insight,
  });

  return {
    shown: true,
    appointmentId,
    isSalesOpportunity: !!briefing.insight?.isSalesOpportunity,
    note:
      'The appointment brief CARD is now shown with the full briefing (key points, before-arrival, sales ' +
      'prep when applicable). Your reply must be ONE short sentence pointing at it (e.g. "Here\'s your prep ' +
      `for the ${serviceType.toLowerCase()}${contactName ? ` with ${contactName}` : ''}.") - do NOT restate ` +
      'its content.',
  };
}

async function execRescheduleToBestSlot(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const appointmentId = String(input.appointmentId);
  const preferredDate = input.preferredDate ? String(input.preferredDate) : undefined;

  deps.onActivity?.('Finding a more route-efficient slot');
  const resp = await RoutemizationService.getApiAppRoutemizationGetRescheduleSlots(
    appointmentId,
    preferredDate,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    true,
  );
  const slots = resp?.slots ?? [];
  if (slots.length === 0) {
    return { rescheduled: false, note: 'The engine found no better/open slots to move this to.' };
  }

  const best = [...slots].sort((a, b) => (b.routeScore ?? 0) - (a.routeScore ?? 0))[0];
  const dateStr = best.suggestedDate;
  const startT = best.startTime;
  const endT = best.endTime;
  if (!dateStr || !startT || !endT) {
    return { rescheduled: false, note: 'The suggested slot was incomplete; ask the user for a specific time instead.' };
  }

  const current = await AppointmentService.getApiAppAppointmentGetById(appointmentId);
  const hasCustomer = !!current.contactId;
  const wantsNotify = input.notifyCustomer === true;
  let willNotify = false;
  const startLocal = parseLocalDateTime(dateStr, startT);

  // The scheduling engine ignores display-only synced calendars by design, so
  // its "best" slot can land on top of an external (Google/Outlook/Zoom) event
  // - disclose that on the card instead of booking over it silently
  // (QA 86bavnu8k).
  let externalOverlaps: Appointment[] = [];
  if (startLocal) {
    try {
      const slotEndLocal = parseLocalDateTime(dateStr, endT) ?? addMinutes(startLocal, 60);
      const dayAppts = await fetchDayAppointments(deps.env.currentUserId, startLocal, deps.env.companyTimezone);
      externalOverlaps = findOverlappingAppointments(dayAppts, startLocal, slotEndLocal, appointmentId).filter(
        (a) => !isRoutemizeManaged(a),
      );
    } catch {
      // Best-effort disclosure only - never block the reschedule on this fetch.
    }
  }
  const extMode = externalOverlaps.length > 0 ? await googleCalendarAvailabilityMode() : undefined;

  const { notify, effectiveChannel, warningLine } = buildNotifyOption(deps, 'both', wantsNotify, (v) => { willNotify = v; });
  const approved = await deps.requestConfirmation({
    title: 'Move to a more efficient slot?',
    confirmLabel: 'Reschedule',
    lines: [
      { icon: 'user', text: current.title || 'this appointment' },
      { icon: 'calendar', text: startLocal ? formatDateMedium(startLocal) : dateStr },
      { icon: 'clock', text: `${formatTimeString(startT)} – ${formatTimeString(endT)}` },
      ...(hasCustomer ? [] : [{ icon: 'bell' as const, text: 'Ad-hoc stop — no customer to notify' }]),
      ...(externalOverlaps.length > 0
        ? [{ icon: 'bell' as const, text: `Overlaps your ${describeExternalOverlaps(externalOverlaps, extMode)} — that event lives outside Routemize and won't move` }]
        : []),
      ...(hasCustomer && warningLine ? [warningLine] : []),
    ],
    notify: hasCustomer ? notify : undefined,
  });
  if (!approved) {
    return { rescheduled: false, note: 'The user declined. Do not retry without new instructions.' };
  }

  const startUtc = tenantLocalToUtcDate(dateStr, startT, deps.env.companyTimezone);
  const endUtc = tenantLocalToUtcDate(dateStr, endT, deps.env.companyTimezone);
  deps.onActivity?.(willNotify ? 'Updating and notifying the customer' : 'Updating the appointment');
  const { restoredToScheduled } = await applyAppointmentUpdate(
    appointmentId,
    { startUtc, endUtc, notifyChannel: willNotify ? effectiveChannel : undefined },
    deps,
  );

  return {
    rescheduled: true,
    appointmentId,
    newDate: dateStr,
    newStartTime: formatTimeString(startT),
    routeScore: best.routeScore ?? null,
    notified: willNotify,
    ...(externalOverlaps.length > 0 ? { overlapsExternalEvents: describeExternalOverlaps(externalOverlaps, extMode) } : {}),
    note:
      ((restoredToScheduled
        ? 'This appointment was Cancelled/No-Show and rescheduling RESTORED it to Scheduled - tell the user it is back on the calendar as Scheduled. '
        : '') +
        (externalOverlaps.length > 0
          ? `The new time overlaps the user's ${describeExternalOverlaps(externalOverlaps, extMode)} - they saw and accepted that on the card; mention it once in past tense.`
          : '')) || undefined,
    ...(restoredToScheduled ? { restoredToScheduled: true } : {}),
  };
}

async function execOptimizeRoute(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const tenantNow = tenantShiftedNow(deps.env.companyTimezone);
  const date = input.date ? String(input.date) : toDateKey(tenantNow);
  const day = parseLocalDateTime(date);
  if (!day) throw new Error('Invalid date.');
  const { userId, isOther } = resolveAssignee(input, deps);

  deps.onActivity?.(`Analyzing the route for ${date}${isOther ? ` for ${assigneeLabel(input, isOther)}` : ''}`);
  const appts = (await fetchDayAppointments(userId, day, deps.env.companyTimezone))
    .filter((a) => a.id && a.latitude != null && a.longitude != null && isRoutemizeManaged(a))
    .sort((a, b) => a.startDateTime.getTime() - b.startDateTime.getTime());

  if (appts.length < 3) {
    return { optimized: false, note: 'Need at least 3 located stops to reorder. Nothing to optimize.' };
  }
  // Use the TARGET's own route start/end (home vs. office, per Route
  // Preferences) when acting for a teammate, not the operator's - otherwise
  // the "optimal" order would be biased toward the wrong starting point.
  const { start: routeStart, end: routeEnd } = await resolveRouteEndpointsFor(userId, deps);
  if (!routeStart) {
    return { optimized: false, note: 'No starting location on file, so route order cannot be optimized reliably.' };
  }

  const waypoints = appts.map((a) => ({ lat: a.latitude!, lng: a.longitude! }));
  const result = await googleMapsDistanceService.calculateOptimizedRoute(routeStart, routeEnd ?? routeStart, waypoints);
  if (!result) {
    return { optimized: false, note: 'Could not compute an optimized route right now.' };
  }

  const savedMiles = Number((result.currentRoute.distance - result.optimizedRoute.distance).toFixed(1));
  const savedMinutes = Math.round(result.currentRoute.duration - result.optimizedRoute.duration);
  const order = result.waypointOrder;
  const suggestedOrder = order.map((idx) => appts[idx]?.customerName || appts[idx]?.type || `Stop ${idx + 1}`);

  // Already optimal (no reorder) or negligible savings.
  const isReordered = order.some((idx, i) => idx !== i);
  if (!isReordered || savedMinutes <= 1) {
    return {
      optimized: false,
      alreadyEfficient: true,
      currentMiles: Number(result.currentRoute.distance.toFixed(1)),
      currentDriveMinutes: Math.round(result.currentRoute.duration),
      note: 'The day is already in a route-efficient order.',
    };
  }

  // If the optimal order is exactly a swap of two stops, offer to apply it.
  const diffPositions = order.map((idx, i) => ({ idx, i })).filter((p) => p.idx !== p.i);
  const isSingleSwap =
    diffPositions.length === 2 &&
    diffPositions[0].idx === diffPositions[1].i &&
    diffPositions[1].idx === diffPositions[0].i;

  if (!isSingleSwap) {
    return {
      optimized: false,
      savedMiles,
      savedMinutes,
      suggestedOrder,
      note: 'Reordering would help, but it needs more than a single swap. Tell the user the recommended order and the potential savings; they can reschedule stops individually if they want.',
    };
  }

  const aAppt = appts[diffPositions[0].i];
  const bAppt = appts[diffPositions[1].i];
  const approved = await deps.requestConfirmation({
    title: `Swap two stops to save ~${savedMinutes} min?`,
    confirmLabel: 'Apply swap',
    lines: [
      ...(isOther ? [{ icon: 'user' as const, text: `For: ${assigneeLabel(input, isOther)}` }] : []),
      { icon: 'repeat', text: `Swap ${aAppt.customerName || aAppt.type || 'stop'} and ${bAppt.customerName || bAppt.type || 'stop'}` },
      { icon: 'clock', text: `Saves ~${savedMiles} mi · ${savedMinutes} min of driving` },
    ],
  });
  if (!approved) {
    return { optimized: false, note: 'The user declined the swap.' };
  }

  const aFull = await AppointmentService.getApiAppAppointmentGetById(aAppt.id);
  const bFull = await AppointmentService.getApiAppAppointmentGetById(bAppt.id);
  // Each appointment takes the other's time slot.
  const aNewStart = bFull.startTimeUtc;
  const aNewEnd = bFull.endTimeUtc;
  const bNewStart = aFull.startTimeUtc;
  const bNewEnd = aFull.endTimeUtc;
  if (!aNewStart || !aNewEnd || !bNewStart || !bNewEnd) {
    return { optimized: false, note: 'Could not read both appointments\' times to swap.' };
  }
  deps.onActivity?.('Applying the swap');
  await AppointmentService.postApiAppAppointmentSwap({
    appointmentAId: aAppt.id,
    appointmentBId: bAppt.id,
    aStartTimeUtc: aNewStart,
    aEndTimeUtc: aNewEnd,
    bStartTimeUtc: bNewStart,
    bEndTimeUtc: bNewEnd,
  });

  return { optimized: true, applied: 'swap', savedMiles, savedMinutes };
}

async function execGetBusinessStats(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const startDate = String(input.startDate);
  const endDate = String(input.endDate);

  deps.onActivity?.('Pulling your business stats');
  const dash = await AnalyticsDashboardService.getApiAppAnalyticsDashboardGetDashboard(
    startDate,
    endDate,
    undefined,
    false,
  );

  const busiestDay = [...(dash.dayOfWeek ?? [])].sort(
    (a, b) => (b.scheduledCount ?? 0) - (a.scheduledCount ?? 0),
  )[0];

  return {
    range: { startDate, endDate },
    semantics:
      'appointmentsBooked = bookings CREATED during the range. totalAppointments (and every breakdown below - ' +
      'statusBreakdown, leadSources, channels, rates) covers ALL appointments the range touches, including ones ' +
      'booked earlier that occur in the range - so these breakdowns will NOT sum to appointmentsBooked. To report ' +
      'lead sources / service types for what was JUST BOOKED, call find_recently_booked instead of using these.',
    appointmentsBooked: dash.appointmentsCreated ?? 0,
    totalAppointments: dash.totalAppointments ?? 0,
    newContacts: dash.newContacts ?? 0,
    statusBreakdown: {
      scheduled: dash.scheduledCount ?? 0,
      completed: dash.completedCount ?? 0,
      cancelled: dash.cancelledCount ?? 0,
      noShow: dash.noShowCount ?? 0,
      inProgress: dash.inProgressCount ?? 0,
    },
    rates: {
      completionRate: dash.completionRate ?? null,
      cancellationRate: dash.cancellationRate ?? null,
      noShowRate: dash.noShowRate ?? null,
      instantBookingRate: dash.instantBookingRate ?? null,
    },
    leadSources: (dash.leadSources ?? []).map((l) => ({ source: l.label, count: l.count })),
    channels: dash.channels
      ? {
          web: dash.channels.webCount ?? 0,
          chatbot: dash.channels.chatbotCount ?? 0,
          voiceBot: dash.channels.voiceBotCount ?? 0,
          internal: dash.channels.internalCount ?? 0,
        }
      : null,
    routeEfficiency: dash.routeInsights
      ? {
          totalMiles: dash.routeInsights.totalDistanceMiles ?? null,
          totalDriveMinutes: dash.routeInsights.totalTravelTimeMinutes ?? null,
          avgMilesPerLeg: dash.routeInsights.avgDistancePerLegMiles ?? null,
          avgMinutesPerLeg: dash.routeInsights.avgTravelTimePerLegMinutes ?? null,
        }
      : null,
    busiestDay: busiestDay ? { day: busiestDay.day, scheduled: busiestDay.scheduledCount ?? 0 } : null,
    teamPerformance: (dash.teamPerformance ?? []).slice(0, 5).map((t) => ({
      name: t.name,
      total: t.totalAppointments ?? 0,
      completed: t.completedCount ?? 0,
      completionRate: t.completionRate ?? null,
      miles: t.totalDistanceMiles ?? null,
    })),
    topZipCodes: (dash.zipCodes ?? []).slice(0, 5).map((z) => ({
      zip: z.zipCode,
      bookings: z.bookingCount ?? 0,
      closeRate: z.closeRate ?? null,
    })),
    comparison: dash.comparison ?? null,
  };
}

/**
 * Session cache of published booking forms, used to detect a "job stop" that
 * is really a service booking in disguise (its title names a real form).
 */
let publishedFormsCache: BookingFormOption[] | null = null;

async function findFormMatchingTitle(title: string): Promise<BookingFormOption | null> {
  try {
    if (!publishedFormsCache) publishedFormsCache = await fetchPublishedForms();
  } catch {
    return null; // Best-effort guard - never block stops on a forms fetch failure.
  }
  const t = title.toLowerCase();
  // Length floor avoids false positives from very short/generic form names.
  return publishedFormsCache.find((f) => f.name.length >= 4 && t.includes(f.name.toLowerCase())) ?? null;
}

/**
 * Backstop for the QA-reported "book a form -> job stop" bug: a stop titled
 * after a published booking form is a customer BOOKING being recreated as an
 * ad-hoc stop (no customer link, form, or notifications). Returns a structured
 * refusal redirecting to the booking pipeline, or null when the title is fine.
 */
async function stopIsServiceBookingResult(
  title: string,
  input: Record<string, unknown>,
): Promise<Record<string, unknown> | null> {
  if (input.allowServiceTitle) return null;
  const form = await findFormMatchingTitle(title);
  if (!form) return null;
  return {
    created: false,
    isServiceBooking: true,
    matchedBookingForm: { bookingFormId: form.id, name: form.name },
    note:
      `"${title}" names your published service "${form.name}" - that is a real customer BOOKING, not an ` +
      'ad-hoc job stop, so NOTHING was created. A job stop would have no customer link, no booking form, ' +
      'and no confirmations/reminders. Run the booking pipeline instead: collect_booking (with ' +
      `bookingFormId ${form.id}) -> create_booking_request -> confirm_booking. Tell the user you are ` +
      'setting it up as a proper booking. Only if they EXPLICITLY say they want a bare stop with no ' +
      'customer/booking attached, re-call this tool with the same details plus allowServiceTitle=true.',
  };
}

/**
 * Warning metadata when a write targets a start time that has ALREADY PASSED
 * (tenant wall clock). Past scheduling stays allowed — e.g. documenting a
 * visit that already happened — but never silently (QA 86bavtzkv): the
 * confirmation card carries `cardLine` so the user explicitly sees and accepts
 * the past date before anything is written, and `resultNote` keeps the model's
 * summary honest afterwards. Returns null when the time is now/future.
 *
 * `startLocal` must be tenant wall-clock (parseLocalDateTime of the tool's
 * date/time inputs), matching tenantShiftedNow's frame.
 */
function pastTimeWarning(
  startLocal: Date,
  deps: RoutyToolDeps,
): { cardLine: ConfirmLine; resultNote: string } | null {
  const tenantNow = tenantShiftedNow(deps.env.companyTimezone);
  if (startLocal.getTime() >= tenantNow.getTime()) return null;
  const earlierToday = toDateKey(startLocal) === toDateKey(tenantNow);
  const startLabel = `${formatDateMedium(startLocal)} at ${formatTimeString(formatTimeOfDay24(startLocal))}`;
  return {
    cardLine: {
      icon: 'bell',
      text: earlierToday
        ? `Heads up: ${formatTimeString(formatTimeOfDay24(startLocal))} today has already passed — this books it in the PAST (e.g. to document a visit that already happened)`
        : `Heads up: ${formatDateMedium(startLocal)} has already passed (today is ${formatDateMedium(tenantNow)}) — this books it in the PAST (e.g. to document a visit that already happened)`,
    },
    resultNote:
      `${startLabel} was already in the past; the user saw that warning on the card and confirmed anyway. ` +
      'Refer to it as a past/backdated entry (for their records), never as an upcoming stop or appointment. ',
  };
}

async function execAddStop(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const title = String(input.title);
  const address = String(input.address);
  const date = String(input.date);
  const startTime = String(input.startTime);
  const durationMinutes = Number(input.durationMinutes);
  // Gate/access code + extra note the user dictated in chat (QA 86bavpj1r:
  // these used to be silently dropped - the tool had no way to receive them).
  const accessCode = input.accessCode ? String(input.accessCode).trim() : '';
  const stopNotes = input.notes ? String(input.notes).trim() : '';
  const { userId, isOther } = resolveAssignee(input, deps);
  const forLabel = assigneeLabel(input, isOther);
  const notEligible = await assigneeNotEligibleResult(userId, isOther, input);
  if (notEligible) return notEligible;
  const bookingRedirect = await stopIsServiceBookingResult(title, input);
  if (bookingRedirect) return bookingRedirect;

  const requestedStart = parseLocalDateTime(date, startTime);
  if (!requestedStart) throw new Error('Invalid date/time. Use YYYY-MM-DD and HH:MM.');
  const requestedEnd = addMinutes(requestedStart, durationMinutes);
  // Booking into the past is allowed (documenting a visit that already
  // happened) but must be flagged on the card first (QA 86bavtzkv).
  const pastWarning = pastTimeWarning(requestedStart, deps);

  deps.onActivity?.(`Preparing to add "${title}"${isOther ? ` for ${forLabel}` : ''}`);
  // When the caller supplies exact coordinates (a place the user picked from a
  // find_service_along_route / search_places card), use them verbatim instead
  // of geocoding the address string. Re-geocoding a business name would resolve
  // to an arbitrary branch of a chain and book the wrong location.
  const lat = typeof input.latitude === 'number' ? input.latitude : Number(input.latitude);
  const lng = typeof input.longitude === 'number' ? input.longitude : Number(input.longitude);
  const hasExactCoords = Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
  const coords = hasExactCoords
    ? { lat, lng, formatted: address }
    : await geocodeOrThrow(address);
  const dateObj = parseLocalDateTime(date)!;

  // Overlaps with ROUTEMIZE appointments are a HARD block (QA 86bave3w9): the
  // rest of the product (the New Booking / Edit Event exact-time pickers)
  // refuses to double-book, so Routy must too - there is no "book it anyway"
  // override. Overlaps with EXTERNAL (Google/Outlook/Zoom-synced) events never
  // hard-block - a synced calendar can be display-only and the event can't be
  // moved from Routemize anyway - but they MUST be disclosed on the card and
  // in the result (QA 86bavnu8k).
  const dayAppts = await fetchDayAppointments(userId, dateObj, deps.env.companyTimezone);
  const allOverlaps = findOverlappingAppointments(dayAppts, requestedStart, requestedEnd);
  const overlaps = allOverlaps.filter(isRoutemizeManaged);
  const externalOverlaps = allOverlaps.filter((a) => !isRoutemizeManaged(a));
  if (overlaps.length > 0) {
    return {
      created: false,
      conflicts: remainingApptSummaries(overlaps),
      note:
        `STOP - this time overlaps ${overlaps.length} existing appointment(s), and double-booking is not ` +
        'allowed, so NOTHING was created. There is NO way to force it. Tell the user exactly what it ' +
        'conflicts with and offer the real options: a different time (find_stop_slots or ' +
        'check_time_availability finds open ones), or first move/cancel the conflicting appointment ' +
        '(reschedule_appointment / update_appointment_status) and then add this stop.',
    };
  }
  const extMode = externalOverlaps.length > 0 ? await googleCalendarAvailabilityMode() : undefined;

  // A stop on a non-working day silently leaves Availability showing "Off"
  // with a stray appointment on it (QA 86baunvz6). Detect it up front so the
  // confirm card discloses that the day will be marked available, and the
  // override is actually written after creation below.
  const endTime = formatTimeOfDay24(requestedEnd);
  const isDayOff = !getWorkingHoursForDate(dateObj, await fetchWorkScheduleByDay(userId)).isWorkDay;
  const stopWindowLabel = `${formatTimeString(startTime)} – ${formatTimeString(endTime)}`;

  const approved = await deps.requestConfirmation({
    title: 'Add this stop?',
    confirmLabel: 'Add stop',
    lines: [
      { icon: 'user', text: title },
      ...(isOther ? [{ icon: 'user' as const, text: `For: ${forLabel}` }] : []),
      { icon: 'map', text: coords.formatted },
      { icon: 'calendar', text: formatDateMedium(dateObj) },
      {
        icon: 'clock',
        text: `${stopWindowLabel} · ${formatDurationMinutes(durationMinutes)}`,
      },
      ...(accessCode ? [{ icon: 'map' as const, text: `Gate/access code: ${accessCode}` }] : []),
      ...(stopNotes ? [{ icon: 'bell' as const, text: `Note: ${stopNotes}` }] : []),
      ...(pastWarning ? [pastWarning.cardLine] : []),
      ...(isDayOff
        ? [{
            icon: 'bell' as const,
            text: `${formatDateMedium(dateObj)} is a day off — this also marks ${isOther ? forLabel : 'you'} available ${stopWindowLabel} so Availability reflects it.`,
          }]
        : []),
      ...(externalOverlaps.length > 0
        ? [{ icon: 'bell' as const, text: `Overlaps ${isOther ? `${forLabel}'s` : 'your'} ${describeExternalOverlaps(externalOverlaps, extMode)} — that event lives outside Routemize and won't move` }]
        : []),
    ],
  });

  if (!approved) {
    return { created: false, note: 'The user declined. Do not retry without new instructions.' };
  }

  const startUtc = tenantLocalToUtcDate(date, startTime, deps.env.companyTimezone);
  const endUtc = tenantLocalToUtcDate(date, endTime, deps.env.companyTimezone);

  deps.onActivity?.(`Adding "${title}"`);
  const appointment = await AppointmentService.postApiAppAppointmentCreateStop({
    title: title || 'Stop',
    // The stop's label (timeline/dashboard detection). The user's real note is
    // saved as an internal note below - the create-stop DTO has no note field.
    stopLabel: title || undefined,
    startTimeUtc: startUtc.toISOString(),
    endTimeUtc: endUtc.toISOString(),
    assignedUserId: userId,
    contactId: input.contactId ? String(input.contactId) : undefined,
    addressLine1: coords.formatted,
    latitude: coords.lat,
    longitude: coords.lng,
  });

  // Persist the dictated extras (QA 86bavpj1r). The create-stop DTO has no
  // accessCode field, so the code lands via a follow-up update onto the
  // appointment's dedicated Gate/Access Code field; the note becomes an
  // internal note (CreateStopAppointmentDTO has no internalNote). Both best-effort - the
  // stop itself is already created.
  let accessCodeSaved = false;
  let noteSaved = false;
  if (appointment?.id) {
    if (accessCode) {
      try {
        await applyAppointmentUpdate(appointment.id, { accessCode }, deps);
        accessCodeSaved = true;
      } catch {
        // Reported via the result note below.
      }
    }
    if (stopNotes) {
      try {
        await notesManager.add('Appointment', appointment.id, stopNotes);
        noteSaved = true;
      } catch {
        // Reported via the result note below.
      }
    }
  }

  // Reflect the day-off exception in Availability: write (or widen) the same
  // AVAILABLE "Work Hours" override the Route Planner's availability toggle
  // creates, scoped to just the stop's window so the rest of the day stays
  // closed to new bookings. Best-effort - the stop itself is already created.
  let availabilityOverride: string | null = null;
  let availabilityOverrideFailed = false;
  if (isDayOff) {
    try {
      const mgr = new AvailabilityOverrideManager(deps.env.companyTimezone);
      // Anchor at NOON so the company-timezone date can't shift a day when the
      // browser and tenant are in different timezones.
      const anchor = parseLocalDateTime(date, '12:00')!;
      const existing = (await mgr.getOverridesForDate(userId, anchor)).workDayOverride;
      // Widen an existing override rather than shrinking it (HH:MM compares
      // lexicographically), so multiple stops on the same day-off all fit.
      const windowStart = existing && existing.startTime < startTime ? existing.startTime : startTime;
      const windowEnd = existing && existing.endTime > endTime ? existing.endTime : endTime;
      await mgr.setWorkDayAvailable(userId, anchor, true, { startTime: windowStart, endTime: windowEnd }, existing?.id);
      availabilityOverride = `${formatTimeString(windowStart)} – ${formatTimeString(windowEnd)}`;
    } catch {
      availabilityOverrideFailed = true;
    }
  }

  return {
    created: true,
    appointmentId: appointment.id ?? null,
    date,
    startTime: formatTimeString(startTime),
    title,
    ...(accessCode ? { accessCodeSaved } : {}),
    ...(stopNotes ? { noteSaved } : {}),
    ...(availabilityOverride ? { availabilityMarkedAvailable: availabilityOverride } : {}),
    ...(externalOverlaps.length > 0 ? { overlapsExternalEvents: describeExternalOverlaps(externalOverlaps, extMode) } : {}),
    ...(pastWarning ? { scheduledInPast: true } : {}),
    note:
      ((pastWarning ? pastWarning.resultNote : '') +
        (accessCode && !accessCodeSaved
        ? 'The stop was created but saving the gate/access code FAILED - tell the user to add it to the stop manually. '
        : '') +
        (stopNotes && !noteSaved
          ? 'The stop was created but saving the note FAILED - tell the user to add it to the stop manually. '
          : '') +
        (externalOverlaps.length > 0
          ? `This stop overlaps the ${describeExternalOverlaps(externalOverlaps, extMode)} - the user saw and accepted that ` +
            'on the card; mention it once in past tense (the external event lives outside Routemize and was not moved). '
          : '') +
        (availabilityOverride
          ? `${formatDateMedium(dateObj)} was a day off, so ${isOther ? `${forLabel} was` : 'you were'} also marked ` +
            `available ${availabilityOverride} that day (visible in Availability). Mention this.`
          : availabilityOverrideFailed
            ? `The stop was created, but marking the day-off as available in the Availability calendar failed - ` +
              'tell the user the day may still show as "Off" there and they can toggle it from the day view.'
            : '')) || undefined,
  };
}

async function execRescheduleAppointment(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const appointmentId = String(input.appointmentId);
  const hasNewTime =
    input.newDate != null &&
    input.newStartTime != null &&
    String(input.newDate).trim() !== '' &&
    String(input.newStartTime).trim() !== '';

  deps.onActivity?.('Preparing the reschedule');
  const current = await AppointmentService.getApiAppAppointmentGetById(appointmentId);
  const hasCustomer = !!current.contactId;
  // Rescheduling a Cancelled / No-Show appointment restores it to Scheduled
  // (QA 86bavddna) - flag it on the confirm card and in the result so both
  // the user and the model know the status is coming back to life.
  const wasInactive =
    current.status === Routemize_Constants_AppointmentStatus.CANCELLED ||
    current.status === Routemize_Constants_AppointmentStatus.NO_SHOW;
  const inactiveLabel = current.status === Routemize_Constants_AppointmentStatus.CANCELLED ? 'Cancelled' : 'No-Show';

  const curStart = parseBackendDate(current.startTimeUtc);
  const curEnd = parseBackendDate(current.endTimeUtc);
  const currentDuration = curStart && curEnd ? getTimeDifferenceInMinutes(curStart, curEnd) : 60;
  const duration = input.durationMinutes ? Number(input.durationMinutes) : currentDuration;

  // Resolve the new start. When only the duration changes (extend/shorten), KEEP
  // the existing start instant so the time is never shifted or double-converted.
  let startUtc: Date;
  let dateLabel: Date;
  // Set when the TARGET time has already passed - moving into the past is
  // allowed (backdating for documentation) but must be flagged on the card
  // first (QA 86bavtzkv).
  let pastWarning: ReturnType<typeof pastTimeWarning> = null;
  if (hasNewTime) {
    const newDate = String(input.newDate);
    const newStartTime = String(input.newStartTime);
    const newStartLocal = parseLocalDateTime(newDate, newStartTime);
    if (!newStartLocal) throw new Error('Invalid newDate/newStartTime. Use YYYY-MM-DD and HH:MM.');
    startUtc = tenantLocalToUtcDate(newDate, newStartTime, deps.env.companyTimezone);
    dateLabel = newStartLocal;
    pastWarning = pastTimeWarning(newStartLocal, deps);
  } else {
    if (!curStart) throw new Error('Could not read the current appointment start time.');
    startUtc = curStart;
    dateLabel = curStart;
  }
  const endUtc = addMinutes(startUtc, duration);
  const startStr = formatTimeString(formatTimeOfDay24(startUtc, deps.env.companyTimezone));
  const endStr = formatTimeString(formatTimeOfDay24(endUtc, deps.env.companyTimezone));

  // Check for a new conflict with the target window - relevant both when moving
  // to a new time AND when only extending/shortening (a longer duration can newly
  // overlap the next appointment even though the start didn't move). Overlaps
  // with ROUTEMIZE appointments are a HARD block (QA 86bave3w9). Overlaps with
  // EXTERNAL (Google/Outlook/Zoom-synced) events never hard-block - a synced
  // calendar can be display-only and the event can't be moved from Routemize
  // anyway - but they MUST be disclosed on the card and in the result
  // (QA 86bavnu8k) instead of silently booking over someone's real meeting.
  //
  // Check the calendar of the person the appointment is ASSIGNED to, not the
  // operator's (86bcefj6k) - a teammate's appointments, events and day off are
  // what decide whether the move can happen.
  const assigneeIds = Array.from(
    new Set((current.attendees ?? []).map((a) => a.userId).filter((id): id is string => !!id)),
  );
  if (assigneeIds.length === 0) assigneeIds.push(deps.env.currentUserId);
  const assigneeId = assigneeIds[0];
  const dayAppts = await fetchDayAppointments(assigneeId, dateLabel, deps.env.companyTimezone);
  const allOverlaps = findOverlappingAppointments(dayAppts, startUtc, endUtc, appointmentId);
  const overlaps = allOverlaps.filter(isRoutemizeManaged);
  const externalOverlaps = allOverlaps.filter((a) => !isRoutemizeManaged(a));

  // Validate the target against the SAME server-side checks the save enforces
  // (busy slots incl. availability-blocking calendar events and buffers, work
  // hours, daily cap) BEFORE the confirmation card (86bcefj6k), so the user is
  // never offered a Reschedule that is already known to fail or land on a day off.
  const precheck = await precheckRescheduleTarget({
    appointmentId,
    assigneeIds,
    startUtc,
    endUtc,
    targetDate: dateLabel,
    checkWorkingHours: hasNewTime,
    deps,
  });
  const blockers = precheck?.blockers ?? [];
  // The server check covers Routemize overlaps too (buffers included) and
  // answers with alternatives below, and reports conflicts even on a day off.
  // If it ran but did not report an overlap the client sees, add it so the
  // overlap stays a hard block; the client-only stop below is the fallback
  // when the server check failed.
  if (precheck && overlaps.length > 0 && !blockers.some((b) => b.kind === 'appointment')) {
    blockers.push({
      kind: 'appointment',
      message: `This time overlaps ${overlaps.map((a) => `${a.customerName || a.type || 'an appointment'} (${formatTimeString(a.startTime)})`).join(', ')}.`,
    });
  }
  if (!precheck && overlaps.length > 0) {
    return {
      rescheduled: false,
      conflicts: remainingApptSummaries(overlaps),
      note:
        `STOP - that window overlaps ${overlaps.length} existing appointment(s), and double-booking is not ` +
        'allowed, so NOTHING was changed. There is NO way to force it. Tell the user exactly what it ' +
        'conflicts with and offer the real options: a different time/length (get_reschedule_options finds ' +
        'open ones), or first move/cancel the conflicting appointment and then redo this change.',
    };
  }

  const dayOffOverridden =
    blockers.length > 0 && input.acknowledgeUnavailableDay === true && onlySoftRescheduleBlockers(blockers);
  if (blockers.length > 0 && !dayOffOverridden) {
    let alternativesShown = 0;
    let alternativesFailed = false;
    try {
      const alternatives = await loadRescheduleOptions(
        appointmentId,
        hasNewTime ? String(input.newDate) : undefined,
        undefined,
        deps,
      );
      alternativesShown = alternatives.found;
    } catch {
      alternativesFailed = true;
    }
    const onlySoft = onlySoftRescheduleBlockers(blockers);
    return {
      rescheduled: false,
      unavailable: true,
      requested: { date: toDateKey(dateLabel), startTime: startStr, endTime: endStr },
      reasons: blockers.map((b) => b.message),
      ...(precheck && precheck.dayOffDetails.length > 0 ? { dayOffDetail: precheck.dayOffDetails.join(' ') } : {}),
      alternativesShown,
      note:
        'NOTHING was changed and NO confirmation card was shown, because the requested time is not available. ' +
        'Lead with the exact reason(s) in `reasons`, in plain words (e.g. "Jose is off on Sat, Oct 10, and 10-11 AM ' +
        'is blocked by the Google Calendar event “Drop off kids at school”"). ' +
        (alternativesFailed
          ? 'Loading alternative times failed - offer to find open times with get_reschedule_options. '
          : alternativesShown > 0
            ? 'Open alternative times are already on screen as cards - briefly recommend the Top Choice and let the user pick. '
            : 'The engine found no open alternatives near that date - ask for another day or a wider range. ') +
        (onlySoft
          ? 'The ONLY problem is the day off / working hours: if the user explicitly insists on this exact time, call ' +
            'reschedule_appointment again with acknowledgeUnavailableDay=true.'
          : 'Do NOT call reschedule_appointment again for this time - overlapping appointments, blocking calendar ' +
            'events and a reached daily cap cannot be overridden.'),
    };
  }

  const extMode = externalOverlaps.length > 0 ? await googleCalendarAvailabilityMode() : undefined;
  const assigneePossessive = precheck?.possessiveByUser.get(assigneeId) ?? 'your';

  const wantsNotify = input.notifyCustomer === true;
  let willNotify = false;

  const label = current.title || 'this appointment';
  // Resolve the customer's name for the card subtitle so the operator sees
  // WHO this sensitive change affects at a glance ("Found appointment: Amy
  // Wright") instead of just the generic appointment type.
  let contactDisplayName: string | null = null;
  if (current.contactId) {
    try {
      const contact = await ContactService.getApiAppContactGetById(current.contactId);
      contactDisplayName =
        contact.fullName || [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.displayName || contact.businessName || null;
    } catch {
      // Non-critical - the card falls back to the appointment title below.
    }
  }

  // Only show the "was -> now" visual when the date/time is actually moving;
  // a duration-only change (extend/shorten) keeps the plain calendar/clock line.
  const timeChange =
    hasNewTime && curStart && curEnd
      ? {
          oldDateLabel: formatDateMedium(curStart),
          oldTimeLabel: `${formatTimeString(formatTimeOfDay24(curStart, deps.env.companyTimezone))} – ${formatTimeString(formatTimeOfDay24(curEnd, deps.env.companyTimezone))}`,
          newDateLabel: formatDateMedium(dateLabel),
          newTimeLabel: `${startStr} – ${endStr} · ${formatDurationMinutes(duration)}`,
        }
      : undefined;

  const { notify, effectiveChannel, warningLine } = buildNotifyOption(deps, 'both', wantsNotify, (v) => { willNotify = v; });

  const approved = await deps.requestConfirmation({
    title: 'Found appointment',
    subtitle: contactDisplayName || label,
    confirmLabel: hasNewTime ? 'Reschedule' : 'Update',
    timeChange,
    lines: [
      ...(contactDisplayName ? [{ icon: 'user' as const, text: label }] : []),
      ...(timeChange
        ? []
        : [
            { icon: 'calendar' as const, text: formatDateMedium(dateLabel) },
            { icon: 'clock' as const, text: `${startStr} – ${endStr} · ${formatDurationMinutes(duration)}` },
          ]),
      ...(hasCustomer ? [] : [{ icon: 'bell' as const, text: 'Ad-hoc stop — no customer to notify' }]),
      ...(pastWarning ? [pastWarning.cardLine] : []),
      ...(wasInactive
        ? [{ icon: 'bell' as const, text: `Currently ${inactiveLabel} — rescheduling restores it to Scheduled` }]
        : []),
      ...(dayOffOverridden ? [dayOffOverrideCardLine(blockers, dateLabel, precheck)] : []),
      ...(externalOverlaps.length > 0
        ? [{ icon: 'bell' as const, text: `Overlaps ${assigneePossessive} ${describeExternalOverlaps(externalOverlaps, extMode)} — that event lives outside Routemize and won't move` }]
        : []),
      ...(hasCustomer && warningLine ? [warningLine] : []),
    ],
    notify: hasCustomer ? notify : undefined,
  });

  if (!approved) {
    return { rescheduled: false, note: 'The user declined. Do not retry without new instructions.' };
  }

  deps.onActivity?.(willNotify ? 'Updating and notifying the customer' : 'Updating the appointment');
  let restoredToScheduled: boolean;
  try {
    ({ restoredToScheduled } = await applyAppointmentUpdate(
      appointmentId,
      { startUtc, endUtc, notifyChannel: willNotify ? effectiveChannel : undefined },
      deps,
    ));
  } catch (err) {
    // The slot was free at the pre-check but the save's own guard rejected it
    // (taken in between, or a check the pre-check could not run). Return a
    // structured "unavailable" result instead of a raw error.
    const code = err instanceof ApiError ? err.body?.error?.code : undefined;
    if (code === 'Appointment:SchedulingConflict' || code === 'Appointment:UserDailyCapReached') {
      return {
        rescheduled: false,
        unavailable: true,
        reasons: [resolveSchedulingErrorMessage(err, 'This time is not available.')],
        note:
          'NOTHING was changed: the save was rejected because the slot was taken between the check and the save ' +
          '(or the daily appointment limit was reached). Tell the user that plainly, then offer open times with ' +
          'get_reschedule_options.',
      };
    }
    throw err;
  }

  return {
    rescheduled: true,
    appointmentId,
    changedTime: hasNewTime,
    durationMinutes: duration,
    notified: willNotify,
    notifiedVia: willNotify ? effectiveChannel : null,
    hadCustomer: hasCustomer,
    ...(restoredToScheduled ? { restoredToScheduled: true, previousStatus: inactiveLabel } : {}),
    ...(externalOverlaps.length > 0 ? { overlapsExternalEvents: describeExternalOverlaps(externalOverlaps, extMode) } : {}),
    ...(pastWarning ? { scheduledInPast: true } : {}),
    ...(dayOffOverridden ? { movedOntoUnavailableDay: blockers.map((b) => b.message) } : {}),
    note:
      (pastWarning ? pastWarning.resultNote : '') +
      (dayOffOverridden
        ? 'The user knowingly moved it onto a day off / outside working hours (shown on the card) - mention it once. '
        : '') +
      (restoredToScheduled
        ? `This appointment was ${inactiveLabel} and rescheduling RESTORED it to Scheduled - state that plainly (e.g. "it's back on the calendar as Scheduled"). `
        : '') +
        (externalOverlaps.length > 0
          ? `The new time overlaps the user's ${describeExternalOverlaps(externalOverlaps, extMode)} - they saw and accepted that on the card; mention it once in past tense (the external event lives outside Routemize and was not moved). `
          : '') +
        (hasCustomer
          ? `Done - the user already decided ${willNotify ? `to notify the customer via ${notifyChannelLabel(effectiveChannel)}` : 'not to notify the customer'} on the card. ` +
            (willNotify && warningLine
              ? 'No SMS provider is connected for this tenant, so it was sent by email instead of text - mention that substitution once. '
              : '') +
            'State that as a completed fact - do NOT mention the notify toggle/card again or ask if they want to notify.'
          : '') || undefined,
  };
}

interface RescheduleTargetPrecheck {
  blockers: RescheduleBlocker[];
  /** describeDayOff(...) for each assignee who is off that day, for the model. */
  dayOffDetails: string[];
  /** Display label ("You" for the operator, otherwise their name) of each assignee who is off that day. */
  dayOffLabels: string[];
  /** Possessive per assignee: "your" for the operator, otherwise "Name's". */
  possessiveByUser: Map<string, string>;
}

/**
 * Server-side availability check for a reschedule target, per assignee, turned
 * into plain-language blockers (86bcefj6k). Uses GetTeamMemberAvailability -
 * the read-only mirror of the create/update guards - so Routy does not keep its
 * own copy of the rules; the client schedule only picks the wording for a
 * NotWorking result (day off vs blocked window vs outside hours). Returns null
 * when the check itself fails: the save's own guard remains the final word.
 */
async function precheckRescheduleTarget(args: {
  appointmentId: string;
  assigneeIds: string[];
  startUtc: Date;
  endUtc: Date;
  targetDate: Date;
  checkWorkingHours: boolean;
  deps: RoutyToolDeps;
}): Promise<RescheduleTargetPrecheck | null> {
  const { appointmentId, assigneeIds, startUtc, endUtc, targetDate, checkWorkingHours, deps } = args;
  const tz = deps.env.companyTimezone;
  try {
    const members = await teamMemberAvailabilityManager.fetch({
      userIds: assigneeIds,
      startUtc,
      endUtc,
      // The booking form (for the daily cap) is resolved server-side from this appointment.
      excludeAppointmentId: appointmentId,
    });

    const windowLocal = { start: formatTimeOfDay24(startUtc, tz), end: formatTimeOfDay24(endUtc, tz) };
    const dateText = formatDateMedium(targetDate);
    const formatConflictWindow = (s: string, e: string) => {
      const start = parseBackendDate(s);
      const end = parseBackendDate(e);
      if (!start || !end) return 'that time';
      return `${formatTimeString(formatTimeOfDay24(start, tz))} – ${formatTimeString(formatTimeOfDay24(end, tz))}`;
    };

    const blockers: RescheduleBlocker[] = [];
    const dayOffDetails: string[] = [];
    const dayOffLabels: string[] = [];
    const possessiveByUser = new Map<string, string>();
    for (const member of members) {
      const isSelf = member.userId === deps.env.currentUserId;
      const memberLabel = isSelf ? 'You' : member.name;
      const possessive = isSelf ? 'your' : `${member.name}'s`;
      possessiveByUser.set(member.userId, possessive);

      // Only a NotWorking result needs the schedule, to word it.
      let effectiveDay: EffectiveDayAvailability = { start: '00:00', end: '23:59', isWorkDay: true };
      if (member.status === 'NotWorking' && checkWorkingHours) {
        const [scheduleByDay, overridesByDate] = await Promise.all([
          fetchWorkScheduleByDay(member.userId),
          fetchUserOverridesByDate(member.userId),
        ]);
        effectiveDay = getEffectiveDayAvailability(targetDate, scheduleByDay, overridesByDate);
        if (!effectiveDay.isWorkDay) {
          dayOffDetails.push(`${memberLabel}: ${describeDayOff(effectiveDay, targetDate, possessive)}`);
          dayOffLabels.push(memberLabel);
        }
      }

      blockers.push(
        ...describeRescheduleBlockers({
          member,
          effectiveDay,
          memberLabel,
          dateLabel: dateText,
          windowLocal,
          checkWorkingHours,
          formatConflictWindow,
        }),
      );
    }

    // Day-level reasons first, across all assignees.
    const dayLevel = blockers.filter((b) => onlySoftRescheduleBlockers([b]));
    const rest = blockers.filter((b) => !onlySoftRescheduleBlockers([b]));
    return { blockers: [...dayLevel, ...rest], dayOffDetails, dayOffLabels, possessiveByUser };
  } catch {
    return null;
  }
}

/** Card line when the user knowingly moves an appointment onto a day off / outside working hours. */
function dayOffOverrideCardLine(
  blockers: RescheduleBlocker[],
  targetDate: Date,
  precheck: RescheduleTargetPrecheck | null,
): { icon: 'bell'; text: string } {
  return {
    icon: 'bell',
    text: describeDayOffOverride(blockers, formatDateMedium(targetDate), precheck?.dayOffLabels ?? []),
  };
}

async function execUpdateAppointmentLocation(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const appointmentId = String(input.appointmentId);
  const address = String(input.address || '').trim();
  if (!address) throw new Error('Provide the new address.');

  deps.onActivity?.('Looking up the new address');
  const current = await AppointmentService.getApiAppAppointmentGetById(appointmentId);
  const coords = await geocodeOrThrow(address);
  const oldAddress = current.addressLine1 || 'the current address';

  const approved = await deps.requestConfirmation({
    title: 'Change this appointment\'s location?',
    confirmLabel: 'Update location',
    lines: [
      { icon: 'user', text: current.title || 'this appointment' },
      { icon: 'map', text: `${oldAddress} → ${coords.formatted}` },
    ],
  });
  if (!approved) return { updated: false, note: 'The user declined. Do not retry without new instructions.' };

  deps.onActivity?.('Updating the location');
  await applyAppointmentUpdate(appointmentId, { location: coords }, deps);

  return { updated: true, appointmentId, newAddress: coords.formatted };
}

/**
 * Change ONLY who an appointment is assigned to (86bcptebv) - the Routy side of
 * the internal off-form override from 86bcb2p37. Any active assignable teammate
 * may be chosen, even one not on the appointment's booking form; the card and
 * result say so. The time is unchanged; the backend validates the new
 * assignee's conflicts and daily cap on save.
 */
async function execReassignAppointment(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const appointmentId = String(input.appointmentId || '').trim();
  const newUserId = String(input.assignedUserId || '').trim();
  if (!appointmentId) throw new Error('Provide the appointmentId.');
  if (!newUserId) throw new Error('Provide the new assignee\'s userId (from find_team_member) as assignedUserId.');

  if (!deps.env.canManageAllBookings) {
    return {
      reassigned: false,
      permissionDenied: true,
      note:
        "The user doesn't have permission to reassign appointments (manage all bookings). NOTHING was changed. " +
        'Tell them plainly and suggest asking an admin or manager to do it.',
    };
  }

  deps.onActivity?.('Checking the appointment');
  const detail = await AppointmentService.getApiAppAppointmentGetAppointmentDetail(appointmentId);
  const currentAssigneeIds = Array.from(
    new Set(
      ((detail.appointmentAttendees?.length ? detail.appointmentAttendees : detail.attendees) ?? [])
        .map((a) => a.userId)
        .filter((id): id is string => !!id),
    ),
  );

  const newInfo = await getAssignability(newUserId);
  const inputName = input.assignedUserName ? String(input.assignedUserName).trim() : '';
  const newName = inputName || newInfo?.name || 'the selected team member';

  const refusal = checkReassignment({ currentAssigneeIds, newUserId, assignability: newInfo });
  if (refusal?.kind === 'multipleAssignees') {
    return {
      reassigned: false,
      multipleAssignees: true,
      note:
        'This appointment has several assignees, so NOTHING was changed - Routy can only reassign an appointment ' +
        'with a single assignee. Tell the user to reassign it from the appointment screen.',
    };
  }
  if (refusal?.kind === 'alreadyAssigned') {
    return {
      reassigned: false,
      alreadyAssigned: true,
      note: `${newName} is already assigned to this appointment - nothing to change. Tell the user so.`,
    };
  }
  if (refusal?.kind === 'notAssignable') {
    const name = inputName || refusal.name;
    return {
      reassigned: false,
      assigneeNotEligible: true,
      note:
        `${name} cannot receive appointments - their role (e.g. Scheduling Assistant) schedules FOR others ` +
        'but is not assignable to jobs/stops, or their account is inactive. NOTHING was changed. Tell the ' +
        'user this plainly and offer an assignable teammate instead (find_team_member shows who can ' +
        'receive appointments via canReceiveAppointments).',
    };
  }

  const oldId = currentAssigneeIds[0];
  const oldName = oldId ? (await getAssignability(oldId))?.name ?? 'the current assignee' : 'Unassigned';

  // Booking-form membership: the backend allows an off-form assignee (internal
  // override) - flag it on the card. Ad-hoc stops have no form; an unknown
  // roster (fetch failed) is not flagged.
  const bookingFormId = detail.request?.bookingFormId ?? null;
  const formName = detail.request?.eventDetail?.eventName || 'this booking form';
  let formUserIds: string[] | null = null;
  if (bookingFormId) {
    try {
      const formUsers = await BookingFormUserService.getApiAppBookingFormUserGetFormUsers(bookingFormId);
      formUserIds = (formUsers ?? []).map((u) => u.id).filter((id): id is string => !!id);
    } catch {
      formUserIds = null;
    }
  }
  const offForm = isOffForm(bookingFormId, formUserIds, newUserId);

  const curStart = parseBackendDate(detail.startTimeUtc || detail.appointment?.startTimeUtc);
  const curEnd = parseBackendDate(detail.endTimeUtc || detail.appointment?.endTimeUtc);
  if (!curStart || !curEnd) throw new Error('Could not read the appointment\'s current time.');
  const tz = deps.env.companyTimezone;
  const startStr = formatTimeString(formatTimeOfDay24(curStart, tz));
  const endStr = formatTimeString(formatTimeOfDay24(curEnd, tz));

  // Check the NEW person's calendar at the unchanged time with the same
  // server-side mirror reschedule_appointment uses, before the card.
  deps.onActivity?.(`Checking ${newName}'s availability`);
  const precheck = await precheckRescheduleTarget({
    appointmentId,
    assigneeIds: [newUserId],
    startUtc: curStart,
    endUtc: curEnd,
    targetDate: curStart,
    checkWorkingHours: true,
    deps,
  });
  const blockers = precheck?.blockers ?? [];
  const dayOffOverridden =
    blockers.length > 0 && input.acknowledgeUnavailableDay === true && onlySoftRescheduleBlockers(blockers);
  if (blockers.length > 0 && !dayOffOverridden) {
    const onlySoft = onlySoftRescheduleBlockers(blockers);
    return {
      reassigned: false,
      unavailable: true,
      reasons: blockers.map((b) => b.message),
      ...(precheck && precheck.dayOffDetails.length > 0 ? { dayOffDetail: precheck.dayOffDetails.join(' ') } : {}),
      note:
        `NOTHING was changed and NO confirmation card was shown, because ${newName} is not available at this ` +
        "appointment's time. Lead with the exact reason(s) in `reasons`, in plain words. " +
        (onlySoft
          ? `The ONLY problem is the day off / working hours: if the user explicitly insists on giving it to ${newName} ` +
            'anyway, call reassign_appointment again with acknowledgeUnavailableDay=true.'
          : 'This cannot be forced (overlapping appointments, blocking calendar events and a reached daily cap ' +
            'cannot be overridden) - offer another teammate, or to reschedule the appointment first.'),
    };
  }

  const label = detail.title || 'this appointment';
  const contact = detail.contact;
  const contactDisplayName = contact
    ? contact.fullName || [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.displayName || contact.businessName || null
    : null;

  const approved = await deps.requestConfirmation({
    title: 'Reassign this appointment?',
    subtitle: contactDisplayName || label,
    confirmLabel: 'Reassign',
    changes: [{ label: 'Assigned to', from: oldName, to: newName }],
    lines: [
      ...(contactDisplayName ? [{ icon: 'user' as const, text: label }] : []),
      { icon: 'calendar' as const, text: formatDateMedium(curStart) },
      { icon: 'clock' as const, text: `${startStr} – ${endStr} · time unchanged` },
      ...(offForm ? [{ icon: 'bell' as const, text: offFormCardText(newName, formName) }] : []),
      ...(dayOffOverridden ? [dayOffOverrideCardLine(blockers, curStart, precheck)] : []),
    ],
  });
  if (!approved) {
    return { reassigned: false, note: 'The user declined. Do not retry without new instructions.' };
  }

  deps.onActivity?.('Reassigning the appointment');
  try {
    await applyAppointmentUpdate(appointmentId, { assignedUserId: newUserId }, deps);
  } catch (err) {
    // Free at the pre-check but rejected by the save's own guard (taken in
    // between, or a check the pre-check could not run).
    const code = err instanceof ApiError ? err.body?.error?.code : undefined;
    if (code === 'Appointment:SchedulingConflict' || code === 'Appointment:UserDailyCapReached') {
      return {
        reassigned: false,
        unavailable: true,
        reasons: [resolveSchedulingErrorMessage(err, `${newName} is not available at this time.`)],
        note:
          `NOTHING was changed: the save was rejected because ${newName}'s calendar is no longer free at this time ` +
          '(or their daily appointment limit was reached). Tell the user that plainly, then offer another teammate ' +
          'or a reschedule.',
      };
    }
    throw err;
  }
  notifyAppointmentsChanged();

  return {
    reassigned: true,
    appointmentId,
    from: oldName,
    to: newName,
    notOnBookingForm: offForm,
    ...(offForm ? { bookingFormName: formName } : {}),
    ...(dayOffOverridden ? { assignedOnUnavailableDay: blockers.map((b) => b.message) } : {}),
    note:
      `Done - the appointment is now assigned to ${newName} at the same time; state that as a completed fact. ` +
      (offForm
        ? `${newName} is not on the "${formName}" booking form - say that once, and that the form's team is unchanged. `
        : '') +
      (dayOffOverridden
        ? `The user knowingly gave it to ${newName} on a day off / outside working hours (shown on the card) - mention it once.`
        : ''),
  };
}

async function execDelayRemaining(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const delayMinutes = Number(input.delayMinutes);
  if (!delayMinutes || delayMinutes <= 0) throw new Error('delayMinutes must be a positive number.');
  const tenantNow = tenantShiftedNow(deps.env.companyTimezone);
  const date = input.date ? String(input.date) : `${tenantNow.getFullYear()}-${String(tenantNow.getMonth() + 1).padStart(2, '0')}-${String(tenantNow.getDate()).padStart(2, '0')}`;
  const wantsNotify = input.notifyCustomers === true;
  let willNotify = false;
  const { userId, isOther } = resolveAssignee(input, deps);

  const day = parseLocalDateTime(date);
  if (!day) throw new Error('Invalid date.');
  const cutoff = input.fromTime ? parseLocalDateTime(date, String(input.fromTime)) ?? tenantNow : tenantNow;

  deps.onActivity?.('Finding appointments to move');
  const appointments = await fetchDayAppointments(userId, day, deps.env.companyTimezone);
  const candidates = appointments
    .filter((a) => a.id && isRoutemizeManaged(a) && a.startDateTime.getTime() >= cutoff.getTime())
    .sort((a, b) => a.startDateTime.getTime() - b.startDateTime.getTime());

  if (candidates.length === 0) {
    return { moved: 0, note: 'No upcoming appointments to move on that day.' };
  }

  const { notify, effectiveChannel, warningLine } = buildNotifyOption(deps, 'both', wantsNotify, (v) => { willNotify = v; });
  const approved = await deps.requestConfirmation({
    title: `Push ${candidates.length} appointment${candidates.length > 1 ? 's' : ''} back?`,
    confirmLabel: `Delay ${formatDurationMinutes(delayMinutes)}`,
    lines: [
      ...(isOther ? [{ icon: 'user' as const, text: `For: ${assigneeLabel(input, isOther)}` }] : []),
      { icon: 'repeat', text: `${candidates.length} appointment${candidates.length > 1 ? 's' : ''} shifted by ${formatDurationMinutes(delayMinutes)}` },
      { icon: 'clock', text: `Earliest moved: ${candidates[0].startTime}` },
      ...(warningLine ? [warningLine] : []),
    ],
    notify,
  });

  if (!approved) {
    return { moved: 0, note: 'The user declined. Do not retry without new instructions.' };
  }

  deps.onActivity?.(willNotify ? `Shifting and notifying ${candidates.length} customer(s)` : `Shifting ${candidates.length} appointment(s)`);
  let moved = 0;
  for (const apt of candidates) {
    const full = await AppointmentService.getApiAppAppointmentGetById(apt.id);
    const curStart = parseBackendDate(full.startTimeUtc);
    const curEnd = parseBackendDate(full.endTimeUtc);
    if (!curStart || !curEnd) continue;
    await applyAppointmentUpdate(apt.id, {
      startUtc: addMinutes(curStart, delayMinutes),
      endUtc: addMinutes(curEnd, delayMinutes),
      notifyChannel: willNotify ? effectiveChannel : undefined,
    }, deps);
    moved++;
  }

  return { moved, delayMinutes, notified: willNotify, notifiedVia: willNotify ? effectiveChannel : null };
}

async function execMoveDayAppointments(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const sourceDate = String(input.sourceDate);
  const targetDate = String(input.targetDate);
  const dupeMode = String(input.mode) === 'duplicate';
  const wantsNotify = input.notifyCustomers === true;
  let willNotify = false;
  const { userId, isOther } = resolveAssignee(input, deps);

  const sourceDay = parseLocalDateTime(sourceDate);
  const targetDay = parseLocalDateTime(targetDate);
  if (!sourceDay || !targetDay) throw new Error('Invalid sourceDate/targetDate. Use YYYY-MM-DD.');

  deps.onActivity?.(`Checking ${formatDateMedium(targetDay)}${isOther ? ` for ${assigneeLabel(input, isOther)}` : ''}`);
  const scheduleByDay = await fetchWorkScheduleByDay(userId);
  const targetHours = getWorkingHoursForDate(targetDay, scheduleByDay);
  if (!targetHours.isWorkDay) {
    return {
      moved: 0,
      note:
        `${formatDateMedium(targetDay)} is not a working day. Use find_open_day or pick_date to choose a working day instead.` +
        (await describeNonWorkingDayAlternatives(targetDate, deps, scheduleByDay)),
    };
  }

  const sourceAppts = (await fetchDayAppointments(userId, sourceDay, deps.env.companyTimezone))
    .filter((a): a is Appointment & { id: string } => !!a.id && isRoutemizeManaged(a))
    .sort((a, b) => a.startDateTime.getTime() - b.startDateTime.getTime());
  if (sourceAppts.length === 0) {
    return { moved: 0, note: `No appointments on ${formatDateMedium(sourceDay)} to ${dupeMode ? 'duplicate' : 'move'}.` };
  }

  deps.onActivity?.('Checking for conflicts on the target day');
  const targetExisting = await fetchDayAppointments(userId, targetDay, deps.env.companyTimezone);

  interface PlannedMove {
    appointmentId: string;
    label: string;
    hasCustomer: boolean;
    oldTimeLabel: string;
    newTimeLabel: string;
    startUtc: Date;
    endUtc: Date;
    full: Routemize_DTOs_AppointmentDTO;
  }
  const planned: PlannedMove[] = [];
  const conflicts: string[] = [];
  const occupied = [...targetExisting];

  for (const a of sourceAppts) {
    const full = await AppointmentService.getApiAppAppointmentGetById(a.id);
    const hasCustomer = !!full.contactId;
    const label = a.customerName || a.type || 'appointment';
    if (dupeMode && hasCustomer) {
      conflicts.push(`${label} is linked to a customer — can only be moved, not duplicated`);
      continue;
    }

    // `a.startDateTime`/`endDateTime` are already tenant-shifted local wall-clock
    // values (see fetchDayAppointments) — reapply the same time-of-day on the
    // target date rather than shifting the UTC instant, so DST/day-length
    // differences never leak in.
    const timeOfDay = formatTimeOfDay24(a.startDateTime);
    const durationMinutes = getTimeDifferenceInMinutes(a.startDateTime, a.endDateTime);
    const newStartUtc = tenantLocalToUtcDate(targetDate, timeOfDay, deps.env.companyTimezone);
    const newEndUtc = addMinutes(newStartUtc, durationMinutes);

    const overlaps = occupied.some(
      (o) => newStartUtc.getTime() < o.endDateTime.getTime() && newEndUtc.getTime() > o.startDateTime.getTime(),
    );
    if (overlaps) {
      conflicts.push(`${label} at ${formatTimeString(timeOfDay)} conflicts with something already on ${formatDateMedium(targetDay)}`);
      continue;
    }

    planned.push({
      appointmentId: a.id,
      label,
      hasCustomer,
      oldTimeLabel: a.startTime,
      newTimeLabel: formatTimeString(timeOfDay),
      startUtc: newStartUtc,
      endUtc: newEndUtc,
      full,
    });
    // Reserve this window so later source appointments don't collide with it.
    occupied.push({ ...a, startDateTime: newStartUtc, endDateTime: newEndUtc });
  }

  if (planned.length === 0) {
    return {
      moved: 0,
      conflicts,
      note: conflicts.length > 0 ? 'Every appointment conflicted on the target day. Suggest a different day.' : 'Nothing to move.',
    };
  }

  const anyCustomer = planned.some((p) => p.hasCustomer);
  const { notify, effectiveChannel, warningLine } = buildNotifyOption(deps, 'both', wantsNotify, (v) => { willNotify = v; });
  const approved = await deps.requestConfirmation({
    title: `${dupeMode ? 'Duplicate' : 'Move'} ${planned.length} appointment${planned.length > 1 ? 's' : ''} to ${formatDateMedium(targetDay)}?`,
    confirmLabel: dupeMode ? `Duplicate ${planned.length}` : `Move ${planned.length}`,
    tone: dupeMode ? 'default' : 'danger',
    lines: [
      ...(isOther ? [{ icon: 'user' as const, text: `For: ${assigneeLabel(input, isOther)}` }] : []),
      ...planned.map((p) => ({ icon: 'repeat' as const, text: `${p.label}: ${p.oldTimeLabel} → ${p.newTimeLabel}` })),
      ...(conflicts.length ? [{ icon: 'bell' as const, text: `Skipped (conflict): ${conflicts.join('; ')}` }] : []),
      ...(!dupeMode ? [{ icon: 'calendar' as const, text: `${formatDateMedium(sourceDay)} will be cleared` }] : []),
      ...(anyCustomer && warningLine ? [warningLine] : []),
    ],
    notify: anyCustomer ? notify : undefined,
  });
  if (!approved) return { moved: 0, note: 'The user declined. Do not retry without new instructions.' };

  deps.onActivity?.(
    dupeMode
      ? `Duplicating ${planned.length} stop${planned.length > 1 ? 's' : ''}`
      : `Moving ${planned.length} appointment${planned.length > 1 ? 's' : ''}`,
  );
  let count = 0;
  for (const p of planned) {
    if (dupeMode) {
      const copy = await AppointmentService.postApiAppAppointmentCreateStop({
        title: p.full.title || p.label,
        stopLabel: p.full.stopLabel ?? undefined,
        startTimeUtc: p.startUtc.toISOString(),
        endTimeUtc: p.endUtc.toISOString(),
        assignedUserId: userId,
        addressLine1: p.full.addressLine1 || '',
        addressLine2: p.full.addressLine2 ?? undefined,
        city: p.full.city ?? undefined,
        state: p.full.state ?? undefined,
        zipCode: p.full.zipCode ?? undefined,
        latitude: p.full.latitude ?? undefined,
        longitude: p.full.longitude ?? undefined,
      });
      // Copy the source's own staff notes onto the copy (CreateStopAppointmentDTO
      // has no note field). Blank notes are skipped. Best-effort - the copy
      // itself is already created.
      if (copy?.id) {
        try {
          const bundle = await notesManager.getAppointmentBundle(p.appointmentId);
          for (const note of bundle.appointment ?? []) {
            if (!note.content?.trim()) continue;
            await notesManager.add('Appointment', copy.id, note.content, note.title ? { title: note.title } : undefined);
          }
        } catch {
          // A failed note copy never undoes the duplicated stop.
        }
      }
    } else {
      await applyAppointmentUpdate(p.appointmentId, {
        startUtc: p.startUtc,
        endUtc: p.endUtc,
        notifyChannel: p.hasCustomer && willNotify ? effectiveChannel : undefined,
      }, deps);
    }
    count++;
  }

  return {
    count,
    mode: dupeMode ? 'duplicate' : 'move',
    sourceDate,
    targetDate,
    conflicts,
    notified: willNotify,
    notifiedVia: willNotify ? effectiveChannel : null,
  };
}

async function execUpdateStatus(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const appointmentId = String(input.appointmentId);
  const statusInput = String(input.status);
  const channelInput = input.notifyChannel ? (String(input.notifyChannel) as NotifyChannel) : undefined;
  const wantsNotify = channelInput != null || Boolean(input.notifyCustomer);

  const statusMap: Record<string, Routemize_Constants_AppointmentStatus> = {
    OnTheWay: Routemize_Constants_AppointmentStatus.ON_THE_WAY,
    InProgress: Routemize_Constants_AppointmentStatus.IN_PROGRESS,
    Completed: Routemize_Constants_AppointmentStatus.COMPLETED,
    Cancelled: Routemize_Constants_AppointmentStatus.CANCELLED,
    NoShow: Routemize_Constants_AppointmentStatus.NO_SHOW,
  };
  const status = statusMap[statusInput];
  if (!status) throw new Error(`Unknown status "${statusInput}".`);

  // Load the appointment up front: needed for the confirm card, the ETA, and to
  // know whether there is a real customer to notify. Ad-hoc stops (job stops
  // with no contact) can't be notified, so we never claim/attempt it.
  const appt = await AppointmentService.getApiAppAppointmentGetById(appointmentId);
  const hasCustomer = !!appt.contactId;

  const isDestructive = status === Routemize_Constants_AppointmentStatus.CANCELLED || status === Routemize_Constants_AppointmentStatus.NO_SHOW;
  const desiredChannel: NotifyChannel =
    channelInput ?? (status === Routemize_Constants_AppointmentStatus.ON_THE_WAY ? 'sms' : 'both');

  // Confirm whenever it's destructive or a customer notification is on the table
  // (e.g. the "on the way" text). Notifications are opt-in via the card toggle.
  const needsConfirm = isDestructive || (hasCustomer && wantsNotify);
  let willNotify = false;
  let effectiveChannel: NotifyChannel = desiredChannel;
  let smsWasRequested = false;

  if (needsConfirm) {
    const title = isDestructive
      ? statusInput === 'Cancelled'
        ? 'Cancel this appointment?'
        : 'Mark as no-show?'
      : status === Routemize_Constants_AppointmentStatus.ON_THE_WAY
        ? 'Mark on the way?'
        : `Mark ${statusInput}?`;
    const confirmLabel = isDestructive
      ? statusInput === 'Cancelled'
        ? 'Cancel appointment'
        : 'Mark no-show'
      : status === Routemize_Constants_AppointmentStatus.ON_THE_WAY
        ? 'Mark on the way'
        : `Mark ${statusInput}`;
    const built = buildNotifyOption(deps, desiredChannel, wantsNotify, (v) => { willNotify = v; });
    effectiveChannel = built.effectiveChannel;
    smsWasRequested = !!built.warningLine;
    const approved = await deps.requestConfirmation({
      title,
      confirmLabel,
      tone: isDestructive ? 'danger' : 'default',
      lines: [
        { icon: 'user', text: appt.title || 'this appointment' },
        ...(hasCustomer ? [] : [{ icon: 'bell' as const, text: 'Ad-hoc stop — no customer to notify' }]),
        ...(hasCustomer && built.warningLine ? [built.warningLine] : []),
      ],
      notify: hasCustomer ? built.notify : undefined,
    });
    if (!approved) {
      return { updated: false, note: 'The user declined. Do not retry without new instructions.' };
    }
  }

  // For "on the way", estimate ETA = drive time from the prior stop (or the
  // operator's starting location) to this appointment, so Routy can tell the
  // user / customer roughly when they'll arrive.
  let etaMinutes: number | null = null;
  if (status === Routemize_Constants_AppointmentStatus.ON_THE_WAY) {
    try {
      const target = appt;
      if (target.latitude != null && target.longitude != null) {
        const targetCoords = { lat: target.latitude, lng: target.longitude };
        const targetStart = parseBackendDate(target.startTimeUtc);
        let fromCoords = deps.env.startingCoordinates;
        if (targetStart) {
          const day = new Date(targetStart);
          const dayAppts = await fetchDayAppointments(deps.env.currentUserId, day, deps.env.companyTimezone);
          const prior = dayAppts
            .filter((a) => a.id !== appointmentId && a.latitude != null && a.longitude != null && a.startDateTime.getTime() < targetStart.getTime())
            .sort((a, b) => a.startDateTime.getTime() - b.startDateTime.getTime())
            .pop();
          if (prior?.latitude != null && prior?.longitude != null) {
            fromCoords = { lat: prior.latitude, lng: prior.longitude };
          }
        }
        if (fromCoords) {
          const r = await googleMapsDistanceService.calculateDrivingDistance(fromCoords, targetCoords).catch(() => null);
          if (r) etaMinutes = Math.round(r.duration);
        }
      }
    } catch {
      // ETA is best-effort.
    }
  }

  // Only notify if the user opted in on the confirmation toggle.
  const notifyChannel: NotifyChannel | undefined = willNotify ? effectiveChannel : undefined;

  if (status === Routemize_Constants_AppointmentStatus.ON_THE_WAY) {
    deps.onActivity?.('Changing status to On the way');
    if (notifyChannel) {
      deps.onActivity?.(
        notifyChannel === 'sms'
          ? 'Sending SMS: On the way'
          : notifyChannel === 'email'
            ? 'Sending email: On the way'
            : 'Sending SMS + email: On the way',
      );
    }
  } else {
    deps.onActivity?.(`Updating status to ${statusInput}`);
  }
  await applyAppointmentUpdate(appointmentId, { status, notifyChannel }, deps);

  return {
    updated: true,
    appointmentId,
    status: statusInput,
    notified: willNotify,
    notifyChannel: notifyChannel ?? null,
    etaMinutes,
    note:
      wantsNotify && !hasCustomer
        ? 'This is an ad-hoc stop with no customer on file, so no message was sent. Tell the user that.'
        : hasCustomer && needsConfirm
          ? `Done - the user already decided ${willNotify ? `to notify the customer via ${notifyChannelLabel(effectiveChannel)}` : 'NOT to notify the customer'} on the card. ` +
            (willNotify && smsWasRequested
              ? 'No SMS provider is connected for this tenant, so this was sent by email instead of text - mention that substitution once, do not claim a text was sent. '
              : '') +
            (willNotify
              ? 'State that as a completed fact - do NOT mention the notify toggle/card again or ask if they want to notify.'
              : 'NO message was sent - NEVER say the customer was emailed, texted, or notified. If you mention it at all, say the customer was NOT notified.')
          : undefined,
  };
}

function synthAppointment(
  dateStr: string,
  startTime: string,
  endTime: string,
  lat: number,
  lng: number,
  title: string,
): Appointment {
  const startDateTime = parseLocalDateTime(dateStr, startTime)!;
  const endDateTime = parseLocalDateTime(dateStr, endTime)!;
  return {
    id: `routy-temp-${Math.random().toString(36).slice(2)}`,
    startTime: formatTimeString(startTime),
    endTime: formatTimeString(endTime),
    startDateTime,
    endDateTime,
    date: startDateTime,
    customerName: title,
    address: '',
    distance: '',
    status: 'scheduled',
    initials: '',
    type: title,
    latitude: lat,
    longitude: lng,
  } as Appointment;
}

/**
 * Mile radius used when comparing chain-branch candidates against a whole
 * day's gaps - wide enough to cover a multi-city service area (e.g. Ocala to
 * Gainesville/The Villages/Belleview) rather than just a single-city bias.
 */
const CHAIN_SEARCH_RADIUS_MILES = 40;
const CHAIN_SEARCH_MAX_CANDIDATES = 8;

/** How far (in minutes) to try extending the workday to fit an otherwise-unplaceable
 * stop, and the increments to check - smallest sufficient extension wins. */
const HOURS_EXTENSION_TRIES_MINUTES = [15, 30, 45, 60];

type DaySchedule = { date: Date; appointments: Appointment[]; workingHoursStart: string; workingHoursEnd: string };

/** Push every day's working-hours END later by `extraMinutes` (start/appointments untouched). */
function withExtendedEndTimes(daySchedules: DaySchedule[], extraMinutes: number): DaySchedule[] {
  return daySchedules.map((d) => ({ ...d, workingHoursEnd: addMinutesToTimeString(d.workingHoursEnd, extraMinutes) }));
}

async function searchChainCandidates(
  query: string,
  routeStart: { lat: number; lng: number } | null | undefined,
): Promise<PlaceSearchResult[]> {
  return searchPlaces(query, routeStart ?? null, CHAIN_SEARCH_MAX_CANDIDATES, CHAIN_SEARCH_RADIUS_MILES).catch(() => []);
}

/**
 * Score a set of already-found chain-branch candidates against a day's
 * schedule and return whichever specific (branch, slot) combination adds the
 * least driving overall - split out from the candidate search itself so the
 * same real branches can be re-scored against an hours-extended schedule
 * without re-querying Google every time.
 */
async function scoreChainCandidates(
  candidates: PlaceSearchResult[],
  daySchedules: DaySchedule[],
  stopDurationMinutes: number,
  routeStart: { lat: number; lng: number } | null | undefined,
  deps: RoutyToolDeps,
  deadline?: { date: string; time?: string },
): Promise<{ coords: { lat: number; lng: number; formatted: string }; slot: StopSlot } | null> {
  const tenantNow = tenantShiftedNow(deps.env.companyTimezone);
  let best: { coords: { lat: number; lng: number; formatted: string }; slot: StopSlot } | null = null;

  for (const candidate of candidates) {
    const slots = await findStopSlotsMultiDay({
      daySchedules,
      stopCoordinates: { lat: candidate.lat, lng: candidate.lng },
      stopDurationMinutes,
      timePreference: 'Flexible',
      startingCoordinates: routeStart ?? undefined,
      slotIntervalMinutes: 30,
      nowOverride: tenantNow,
    }).catch(() => [] as StopSlot[]);

    let usable = slots.filter(
      (s) => s.suggestedDate && s.startTime && s.endTime && isFutureTenantSlot(s.suggestedDate, s.startTime, deps.env.companyTimezone),
    );
    if (deadline) {
      usable = usable.filter((s) => {
        if (s.suggestedDate! < deadline.date) return true;
        if (s.suggestedDate! > deadline.date) return false;
        return !deadline.time || s.endTime! <= deadline.time;
      });
    }

    const top = usable[0];
    if (top && (!best || top.routeScore > best.slot.routeScore)) {
      best = {
        coords: { lat: candidate.lat, lng: candidate.lng, formatted: candidate.address || candidate.name },
        slot: top,
      };
    }
  }

  return best;
}

/**
 * Last resort when a stop genuinely does not fit within normal working
 * hours: re-check the SAME candidates (or same single address) against a
 * workday whose end time has been pushed later, trying the smallest
 * extension first. Returns the minimal extension that works, if any -
 * surfaced to the user as an explicit offer, never applied silently.
 */
async function findMinimalHoursExtension(
  daySchedules: DaySchedule[],
  scoreFn: (schedules: DaySchedule[]) => Promise<{ coords: { lat: number; lng: number; formatted: string }; slot: StopSlot } | null>,
): Promise<{ extraMinutes: number; coords: { lat: number; lng: number; formatted: string }; slot: StopSlot } | null> {
  for (const extraMinutes of HOURS_EXTENSION_TRIES_MINUTES) {
    const result = await scoreFn(withExtendedEndTimes(daySchedules, extraMinutes));
    if (result) return { extraMinutes, ...result };
  }
  return null;
}

async function execAddMultipleStops(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const rawStops = Array.isArray(input.stops) ? (input.stops as Array<Record<string, unknown>>) : [];
  const stops = rawStops.slice(0, 6).map((s) => {
    const lat = typeof s.latitude === 'number' ? s.latitude : Number(s.latitude);
    const lng = typeof s.longitude === 'number' ? s.longitude : Number(s.longitude);
    const hasExactCoords = Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
    return {
      title: String(s.title),
      address: s.address ? String(s.address) : undefined,
      exactCoords: hasExactCoords ? { lat, lng } : undefined,
      businessQuery: s.businessQuery ? String(s.businessQuery) : undefined,
      durationMinutes: s.durationMinutes ? Number(s.durationMinutes) : 30,
      fixedDate: s.fixedDate ? String(s.fixedDate) : undefined,
      fixedStartTime: s.fixedStartTime ? String(s.fixedStartTime) : undefined,
      deadlineDate: s.deadlineDate ? String(s.deadlineDate) : undefined,
      deadlineTime: s.deadlineTime ? String(s.deadlineTime) : undefined,
      extendEndTimeMinutes: s.extendEndTimeMinutes ? Number(s.extendEndTimeMinutes) : undefined,
    };
  });
  if (stops.length === 0) throw new Error('Provide at least one stop.');
  for (const s of stops) {
    if (!s.address && !s.businessQuery) throw new Error(`"${s.title}" needs either an address or a businessQuery.`);
    if (s.fixedDate && s.fixedStartTime && !s.address) {
      throw new Error(`"${s.title}" has a fixed time so it needs a resolved address, not a businessQuery.`);
    }
  }
  const startDate = String(input.startDate);
  const endDate = input.endDate ? String(input.endDate) : undefined;
  const { userId, isOther } = resolveAssignee(input, deps);
  const notEligible = await assigneeNotEligibleResult(userId, isOther, input);
  if (notEligible) return notEligible;
  // Same backstop as add_stop: a stop titled after a published service is a
  // customer booking in disguise - route it through the booking pipeline.
  if (!input.allowServiceTitle) {
    for (const s of stops) {
      const bookingRedirect = await stopIsServiceBookingResult(s.title, input);
      if (bookingRedirect) {
        return {
          ...bookingRedirect,
          note:
            `${bookingRedirect.note} The OTHER stops in this batch were NOT scheduled either - once the ` +
            'booking is set up properly, re-call add_multiple_stops with just the remaining ad-hoc stops.',
        };
      }
    }
  }
  const { start: routeStart } = await resolveRouteEndpointsFor(userId, deps);

  const fixedStops = stops.filter((s) => s.fixedDate && s.fixedStartTime);
  const flexibleStops = stops.filter((s) => !(s.fixedDate && s.fixedStartTime));

  deps.onActivity?.(`Planning ${stops.length} stop${stops.length > 1 ? 's' : ''}${isOther ? ` for ${assigneeLabel(input, isOther)}` : ''}`);
  const scheduleByDay = await fetchWorkScheduleByDay(userId);
  const tenantNow = tenantShiftedNow(deps.env.companyTimezone);
  const workdays = enumerateDates(startDate, endDate).filter((d) => {
    const h = getWorkingHoursForDate(d, scheduleByDay);
    return h.isWorkDay && isDayAvailable(d, h.end, tenantNow);
  });
  if (workdays.length === 0 && flexibleStops.length > 0) {
    return { planned: 0, note: 'No available workdays in that range for the flexible stops.' };
  }

  const baseByDate = new Map<string, Appointment[]>();
  for (const d of workdays) {
    baseByDate.set(toDateKey(d), await fetchDayAppointments(userId, d, deps.env.companyTimezone));
  }
  const accByDate = new Map<string, Appointment[]>();

  interface Planned {
    title: string;
    coords: { lat: number; lng: number; formatted: string };
    date: string;
    startTime: string;
    endTime: string;
    fixed: boolean;
    /** Set when this slot was chosen to meet a deadline (e.g. "before 12pm"), for the confirm card badge. */
    deadlineTime?: string;
    /** Set when this stop only fits because the user approved extending the workday, for the confirm card badge. */
    extendedByMinutes?: number;
  }
  const planned: Planned[] = [];
  const skipped: string[] = [];
  const conflicts: string[] = [];

  // Place the FIXED (locked-time) stops first so the flexible slot search
  // below already treats their windows as occupied.
  for (const stop of fixedStops) {
    const fixedDate = stop.fixedDate as string;
    const fixedStartTime = stop.fixedStartTime as string;
    const requestedStart = parseLocalDateTime(fixedDate, fixedStartTime);
    const day = parseLocalDateTime(fixedDate);
    if (!requestedStart || !day) {
      skipped.push(stop.title);
      continue;
    }
    if (!isFutureTenantSlot(fixedDate, fixedStartTime, deps.env.companyTimezone)) {
      skipped.push(stop.title);
      continue;
    }
    let coords: { lat: number; lng: number; formatted: string };
    if (stop.exactCoords) {
      // Place the user already picked from a card — use its exact coordinates
      // rather than re-geocoding the address/name (which can hit a wrong branch).
      coords = { lat: stop.exactCoords.lat, lng: stop.exactCoords.lng, formatted: stop.address! };
    } else {
      try {
        coords = await geocodeOrThrow(stop.address!);
      } catch {
        skipped.push(stop.title);
        continue;
      }
    }
    const requestedEnd = addMinutes(requestedStart, stop.durationMinutes);
    const dayKey = toDateKey(day);
    const existing = baseByDate.get(dayKey) ?? (await fetchDayAppointments(userId, day, deps.env.companyTimezone));
    baseByDate.set(dayKey, existing);
    const occupied = [...existing, ...(accByDate.get(dayKey) || [])];
    const overlaps = occupied.some(
      (a) => requestedStart.getTime() < a.endDateTime.getTime() && requestedEnd.getTime() > a.startDateTime.getTime(),
    );
    if (overlaps) {
      conflicts.push(`${stop.title} at ${formatTimeString(fixedStartTime)} on ${formatDateMedium(day)}`);
      continue;
    }
    const endTimeStr = formatTimeOfDay24(requestedEnd);
    planned.push({ title: stop.title, coords, date: fixedDate, startTime: fixedStartTime, endTime: endTimeStr, fixed: true });
    const list = accByDate.get(dayKey) || [];
    list.push(synthAppointment(fixedDate, fixedStartTime, endTimeStr, coords.lat, coords.lng, stop.title));
    accByDate.set(dayKey, list);
  }

  for (const stop of flexibleStops) {
    const baseDaySchedules: DaySchedule[] = workdays.map((d) => {
      const k = toDateKey(d);
      const h = getWorkingHoursForDate(d, scheduleByDay);
      return {
        date: d,
        appointments: [...(baseByDate.get(k) || []), ...(accByDate.get(k) || [])],
        workingHoursStart: h.start,
        workingHoursEnd: h.end,
      };
    });
    // If the user already approved extending the day for THIS stop (a
    // follow-up call after being offered the option below), apply it up
    // front instead of re-running the normal-hours search first.
    const daySchedules = stop.extendEndTimeMinutes
      ? withExtendedEndTimes(baseDaySchedules, stop.extendEndTimeMinutes)
      : baseDaySchedules;
    const deadline = stop.deadlineDate ? { date: stop.deadlineDate, time: stop.deadlineTime } : undefined;

    let coords: { lat: number; lng: number; formatted: string };
    let best: StopSlot;

    if (stop.businessQuery) {
      // Compare SEVERAL real branch locations against every gap in the day(s)
      // instead of committing to a single address biased from the start
      // point - the right branch may sit right next to a later appointment
      // instead of near home.
      const candidates = await searchChainCandidates(stop.businessQuery, routeStart);
      const resolved = candidates.length
        ? await scoreChainCandidates(candidates, daySchedules, stop.durationMinutes, routeStart, deps, deadline)
        : null;

      if (!resolved) {
        // eslint-disable-next-line no-console
        console.debug(`[Routy add_multiple_stops] no branch of "${stop.businessQuery}" fit any gap in the day(s) considered.`);

        // Not asked to extend yet - check whether a small extension would
        // actually fix it, so we can offer that instead of a dead end.
        const extension = !stop.extendEndTimeMinutes && candidates.length
          ? await findMinimalHoursExtension(baseDaySchedules, (ext) =>
              scoreChainCandidates(candidates, ext, stop.durationMinutes, routeStart, deps, deadline),
            )
          : null;

        skipped.push(
          extension
            ? `${stop.title} at ${extension.coords.formatted} (would fit on ${formatDateMedium(parseLocalDateTime(extension.slot.suggestedDate!) ?? workdays[0])} with ${extension.extraMinutes} extra minutes past the usual end time - ask to extend if you want this)`
            : `${stop.title} (checked nearby ${stop.businessQuery} locations, none fit without too much extra driving)`,
        );
        continue;
      }
      coords = resolved.coords;
      best = resolved.slot;
    } else {
      if (stop.exactCoords) {
        // Place the user already picked from a card — use its exact coordinates
        // rather than re-geocoding the address/name (which can hit a wrong branch).
        coords = { lat: stop.exactCoords.lat, lng: stop.exactCoords.lng, formatted: stop.address! };
      } else {
        try {
          coords = await geocodeOrThrow(stop.address!);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.debug(`[Routy add_multiple_stops] geocode failed for "${stop.title}" ("${stop.address}"):`, err);
          skipped.push(`${stop.title} (could not resolve "${stop.address}" to a real address)`);
          continue;
        }
      }

      const scoreAddress = async (schedules: DaySchedule[]) => {
        const slots = await findStopSlotsMultiDay({
          daySchedules: schedules,
          stopCoordinates: { lat: coords.lat, lng: coords.lng },
          stopDurationMinutes: stop.durationMinutes,
          timePreference: 'Flexible',
          startingCoordinates: routeStart,
          slotIntervalMinutes: 30,
          nowOverride: tenantNow,
        });
        // A deadline stays route-optimized (unlike fixedStartTime) - just narrow
        // the ranked candidates to ones that finish by the deadline, then take
        // the best-scoring one among those, same as any other flexible stop.
        let candidates = slots;
        if (deadline) {
          candidates = slots.filter((s) => {
            if (!s.suggestedDate || !s.endTime) return false;
            if (s.suggestedDate < deadline.date) return true;
            if (s.suggestedDate > deadline.date) return false;
            return !deadline.time || s.endTime <= deadline.time;
          });
        }
        const top = candidates[0];
        return top?.suggestedDate && top.startTime && top.endTime ? { coords, slot: top } : null;
      };

      const direct = await scoreAddress(daySchedules);
      if (!direct) {
        // eslint-disable-next-line no-console
        console.debug(`[Routy add_multiple_stops] no viable slot for "${stop.title}" at ${coords.formatted} (${coords.lat}, ${coords.lng}).`);

        const extension = !stop.extendEndTimeMinutes ? await findMinimalHoursExtension(baseDaySchedules, scoreAddress) : null;

        skipped.push(
          extension
            ? `${stop.title} at ${coords.formatted} (would fit on ${formatDateMedium(parseLocalDateTime(extension.slot.suggestedDate!) ?? workdays[0])} with ${extension.extraMinutes} extra minutes past the usual end time - ask to extend if you want this)`
            : deadline
              ? `${stop.title} at ${coords.formatted} (no route-efficient slot before ${deadline.time ? formatTimeString(deadline.time) : 'the deadline'})`
              : `${stop.title} at ${coords.formatted} (too far/no time available on the day(s) considered)`,
        );
        continue;
      }
      best = direct.slot;
    }

    if (!isFutureTenantSlot(best.suggestedDate, best.startTime, deps.env.companyTimezone)) {
      skipped.push(`${stop.title} at ${coords.formatted}`);
      continue;
    }

    planned.push({
      title: stop.title,
      coords,
      date: best.suggestedDate,
      startTime: best.startTime,
      endTime: best.endTime,
      fixed: false,
      deadlineTime: deadline?.time,
      extendedByMinutes: stop.extendEndTimeMinutes,
    });
    const list = accByDate.get(best.suggestedDate) || [];
    list.push(synthAppointment(best.suggestedDate, best.startTime, best.endTime, coords.lat, coords.lng, stop.title));
    accByDate.set(best.suggestedDate, list);
  }

  if (planned.length === 0) {
    return {
      planned: 0,
      skipped,
      conflicts,
      note:
        conflicts.length > 0
          ? 'Every fixed-time stop conflicted with an existing appointment. Tell the user and ask for a different time.'
          : 'Could not fit any of the stops in that range.',
    };
  }

  // Sort for a readable confirm card: by date, then by time.
  planned.sort((a, b) => (a.date === b.date ? a.startTime.localeCompare(b.startTime) : a.date.localeCompare(b.date)));

  // Compute the drive leg INTO each stop (from the previous same-day stop, or
  // the route start otherwise) so the confirm card can show how tight the route
  // is between stops. Best-effort — a failed lookup just omits that leg.
  const driveLegs: Array<{ label: string; minutes: number | null; miles: number | null }> = [];
  for (let i = 0; i < planned.length; i++) {
    const p = planned[i];
    const prev = i > 0 ? planned[i - 1] : null;
    const sameDayAsPrev = !!prev && prev.date === p.date;
    const fromCoords = sameDayAsPrev ? { lat: prev!.coords.lat, lng: prev!.coords.lng } : routeStart;
    const label = sameDayAsPrev ? prev!.title : 'start';
    let minutes: number | null = null;
    let miles: number | null = null;
    if (fromCoords) {
      const r = await googleMapsDistanceService
        .calculateDrivingDistance(fromCoords, { lat: p.coords.lat, lng: p.coords.lng })
        .catch(() => null);
      if (r) {
        minutes = Math.round(r.duration);
        miles = Number(r.distance.toFixed(1));
      }
    }
    driveLegs.push({ label, minutes, miles });
  }

  const approved = await deps.requestConfirmation({
    title: `Add ${planned.length} stop${planned.length > 1 ? 's' : ''}?`,
    confirmLabel: `Add ${planned.length} stop${planned.length > 1 ? 's' : ''}`,
    stops: planned.map((p, i) => {
      const d = parseLocalDateTime(p.date);
      const startMin = timeStringToMinutes(p.startTime);
      const endMin = timeStringToMinutes(p.endTime);
      const durationMinutes = startMin != null && endMin != null ? endMin - startMin : 0;
      const leg = driveLegs[i];
      return {
        title: p.title,
        address: p.coords.formatted,
        dateLabel: d ? formatDateMedium(d) : p.date,
        timeLabel: `${formatTimeString(p.startTime)} – ${formatTimeString(p.endTime)}`,
        durationMinutes,
        latitude: p.coords.lat,
        longitude: p.coords.lng,
        driveFromLabel: leg?.label,
        driveFromMinutes: leg?.minutes ?? null,
        driveFromMiles: leg?.miles ?? null,
        badge: p.fixed
          ? 'fixed time'
          : p.extendedByMinutes
            ? `+${p.extendedByMinutes} min past end time`
            : p.deadlineTime
              ? `by ${formatTimeString(p.deadlineTime)}`
              : undefined,
      };
    }),
    lines: [
      ...(isOther ? [{ icon: 'user' as const, text: `For: ${assigneeLabel(input, isOther)}` }] : []),
      ...(conflicts.length ? [{ icon: 'bell' as const, text: `Skipped (conflict): ${conflicts.join('; ')}` }] : []),
      ...(skipped.length ? [{ icon: 'bell' as const, text: `Skipped (couldn't place): ${skipped.join(', ')}` }] : []),
    ],
  });
  if (!approved) return { planned: 0, note: 'The user declined. Do not retry without new instructions.' };

  deps.onActivity?.(`Adding ${planned.length} stop${planned.length > 1 ? 's' : ''}`);
  let created = 0;
  for (const p of planned) {
    const startUtc = tenantLocalToUtcDate(p.date, p.startTime, deps.env.companyTimezone);
    const endUtc = tenantLocalToUtcDate(p.date, p.endTime, deps.env.companyTimezone);
    await AppointmentService.postApiAppAppointmentCreateStop({
      title: p.title,
      stopLabel: p.title,
      startTimeUtc: startUtc.toISOString(),
      endTimeUtc: endUtc.toISOString(),
      assignedUserId: userId,
      addressLine1: p.coords.formatted,
      latitude: p.coords.lat,
      longitude: p.coords.lng,
    });
    created++;
  }

  return {
    planned: planned.length,
    created,
    skipped,
    conflicts,
    // Include each stop's resolved address + exact coordinates so a later
    // swap/remove re-plan can carry the KEPT stops forward verbatim (pass their
    // address+latitude+longitude back into add_multiple_stops) instead of
    // re-resolving a name to a possibly-different branch.
    stops: planned.map((p) => ({
      title: p.title,
      address: p.coords.formatted,
      latitude: p.coords.lat,
      longitude: p.coords.lng,
      date: p.date,
      startTime: formatTimeString(p.startTime),
      fixed: p.fixed,
    })),
    note:
      'When the user later asks to SWAP or REMOVE one of these stops, re-plan by passing the KEPT stops ' +
      'back into add_multiple_stops using their exact address + latitude + longitude above (never the bare ' +
      'name), plus the new/replacement stop, so the kept ones stay at the exact same places.',
  };
}

async function execPlanRoute(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const date = String(input.date);
  const day = parseLocalDateTime(date);
  if (!day) throw new Error('Invalid date. Use YYYY-MM-DD.');

  const rawStops = Array.isArray(input.stops) ? (input.stops as Array<Record<string, unknown>>) : [];
  if (rawStops.length === 0) throw new Error('Provide at least one stop.');

  const { userId, isOther } = resolveAssignee(input, deps);
  const notEligible = await assigneeNotEligibleResult(userId, isOther, input);
  if (notEligible) return notEligible;
  const { start, end } = await resolveRouteEndpointsFor(userId, deps);
  if (!start) {
    return {
      planned: 0,
      note:
        "No starting location is set, so the route can't be optimized. Ask the user to set a start address " +
        'in Route Preferences, or give a specific first-stop address.',
    };
  }

  deps.onActivity?.(
    `Planning a route for ${rawStops.length} stop${rawStops.length > 1 ? 's' : ''}${isOther ? ` for ${assigneeLabel(input, isOther)}` : ''}`,
  );

  // Resolve coordinates for each stop — prefer the exact coords the model
  // passed (from find_contact/search_places), geocode the address only as a
  // fallback. The optimizer requires real coordinates for every stop.
  const drafts: BulkStopDraft[] = [];
  for (let i = 0; i < rawStops.length; i++) {
    const s = rawStops[i];
    const title = String(s.title || `Stop ${i + 1}`);
    const address = s.address ? String(s.address) : '';
    const lat = typeof s.latitude === 'number' ? s.latitude : Number(s.latitude);
    const lng = typeof s.longitude === 'number' ? s.longitude : Number(s.longitude);
    const hasExact = Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
    let coordLat = hasExact ? lat : null;
    let coordLng = hasExact ? lng : null;
    let formatted = address;
    if (coordLat == null || coordLng == null) {
      if (!address) return { planned: 0, note: `"${title}" has no address or coordinates — resolve it first.` };
      try {
        const g = await geocodeOrThrow(address);
        coordLat = g.lat;
        coordLng = g.lng;
        formatted = g.formatted;
      } catch {
        return { planned: 0, note: `Could not resolve an address for "${title}". Resolve it with find_contact/search_places first.` };
      }
    }
    drafts.push({
      id: `routy-${i}`,
      title,
      addressLine1: formatted,
      latitude: coordLat,
      longitude: coordLng,
      durationMinutes: s.durationMinutes ? Number(s.durationMinutes) : 30,
      bufferMinutes: 0,
      fixedStartTime: s.fixedStartTime ? String(s.fixedStartTime) : null,
      contactId: s.contactId ? String(s.contactId) : undefined,
    });
  }

  const scheduleByDay = await fetchWorkScheduleByDay(userId);
  const hours = getWorkingHoursForDate(day, scheduleByDay);
  const existingAppointments = await fetchDayAppointments(userId, day, deps.env.companyTimezone);

  const ctx: BulkRouteContext = {
    date: day,
    dateString: date,
    companyTimezone: deps.env.companyTimezone || 'UTC',
    workingHoursStart: hours.start,
    workingHoursEnd: hours.end,
    startingCoords: start,
    startingAddress: deps.env.startingAddress || 'Start',
    endingCoords: end ?? start,
    endingAddress: deps.env.endingAddress || deps.env.startingAddress || 'End',
    existingAppointments,
    selectedTeamMemberId: userId,
    nowOverride: tenantShiftedNow(deps.env.companyTimezone),
  };

  const { result, conflicts } = await validateAndOptimize(drafts, ctx);
  if (!result) {
    const reasons = conflicts.map((c) => c.message).filter(Boolean).join('; ');
    return {
      planned: 0,
      conflicts,
      note:
        `Couldn't fit these stops on ${formatDateMedium(day)}${reasons ? `: ${reasons}` : ''}. Tell the user ` +
        'what conflicts and offer a different day, fewer stops, or adjusting a fixed time.',
    };
  }

  const ordered = result.orderedStops;
  const approved = await deps.requestConfirmation({
    title: `Add ${ordered.length} stop${ordered.length > 1 ? 's' : ''}?`,
    subtitle: `Optimized route · ~${formatDurationMinutes(Math.round(result.totalDriveMinutes))} driving · ${result.totalMiles.toFixed(1)} mi`,
    confirmLabel: `Add ${ordered.length} stop${ordered.length > 1 ? 's' : ''}`,
    stops: ordered.map((p, i) => ({
      title: p.draft.title,
      address: p.draft.addressLine1,
      dateLabel: formatDateMedium(day),
      timeLabel: `${formatTimeString(p.startTenantHHMM)} – ${formatTimeString(p.endTenantHHMM)}`,
      durationMinutes: p.draft.durationMinutes,
      latitude: p.draft.latitude ?? undefined,
      longitude: p.draft.longitude ?? undefined,
      driveFromLabel: i === 0 ? 'start' : ordered[i - 1].draft.title,
      driveFromMinutes: Math.round(p.driveMinutesIn),
      driveFromMiles: Number(p.driveMilesIn.toFixed(1)),
      badge: p.isFixed ? 'fixed time' : undefined,
    })),
    lines: [
      ...(isOther ? [{ icon: 'user' as const, text: `For: ${assigneeLabel(input, isOther)}` }] : []),
      { icon: 'clock' as const, text: `Wrapped up by ${formatTimeString(result.endingTimeHHMM)} (incl. drive home)` },
    ],
  });
  if (!approved) return { planned: 0, note: 'The user declined. Do not retry without new instructions.' };

  deps.onActivity?.(`Adding ${ordered.length} stop${ordered.length > 1 ? 's' : ''}`);
  let created = 0;
  for (const p of ordered) {
    await AppointmentService.postApiAppAppointmentCreateStop({
      title: p.draft.title,
      stopLabel: p.draft.title,
      startTimeUtc: p.startUtc.toISOString(),
      endTimeUtc: p.endUtc.toISOString(),
      assignedUserId: userId,
      contactId: p.draft.contactId,
      addressLine1: p.draft.addressLine1,
      latitude: p.draft.latitude ?? undefined,
      longitude: p.draft.longitude ?? undefined,
    });
    created++;
  }

  return {
    planned: ordered.length,
    created,
    totalDriveMinutes: Math.round(result.totalDriveMinutes),
    totalMiles: Number(result.totalMiles.toFixed(1)),
    endingTime: formatTimeString(result.endingTimeHHMM),
    // Carry each placed stop's exact address+coords so a later swap/remove can
    // re-plan keeping the others verbatim.
    stops: ordered.map((p) => ({
      title: p.draft.title,
      address: p.draft.addressLine1,
      latitude: p.draft.latitude,
      longitude: p.draft.longitude,
      startTime: formatTimeString(p.startTenantHHMM),
      fixed: p.isFixed,
    })),
    note:
      'Presented an optimized route and created the stops. If the user swaps/removes one, re-plan with ' +
      'plan_route passing the kept stops by their exact address+latitude+longitude (never bare names).',
  };
}

async function execRepeatStop(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const title = String(input.title);
  const address = String(input.address);
  const durationMinutes = input.durationMinutes ? Number(input.durationMinutes) : 30;
  const startDate = String(input.startDate);
  const startTime = String(input.startTime);
  const occurrences = Math.min(12, Math.max(1, Math.round(Number(input.occurrences) || 1)));
  const cadenceDays = Math.max(1, Math.round(Number(input.cadenceDays) || 7));
  const contactId = input.contactId ? String(input.contactId) : undefined;
  const { userId, isOther } = resolveAssignee(input, deps);
  const notEligible = await assigneeNotEligibleResult(userId, isOther, input);
  if (notEligible) return notEligible;

  const firstDay = parseLocalDateTime(startDate);
  if (!firstDay) throw new Error('Invalid startDate. Use YYYY-MM-DD.');

  deps.onActivity?.(`Planning ${occurrences} occurrence${occurrences > 1 ? 's' : ''}${isOther ? ` for ${assigneeLabel(input, isOther)}` : ''}`);
  const coords = await geocodeOrThrow(address);
  const scheduleByDay = await fetchWorkScheduleByDay(userId);

  interface PlannedOccurrence {
    date: string;
    startTime: string;
    endTime: string;
  }
  const planned: PlannedOccurrence[] = [];
  const skipped: string[] = [];

  for (let i = 0; i < occurrences; i++) {
    const day = new Date(firstDay);
    day.setDate(day.getDate() + i * cadenceDays);
    const dateKey = toDateKey(day);
    const hours = getWorkingHoursForDate(day, scheduleByDay);

    if (!hours.isWorkDay) {
      skipped.push(`${formatDateMedium(day)} (not a working day)`);
      continue;
    }
    const requestedStart = parseLocalDateTime(dateKey, startTime);
    if (!requestedStart || !isFutureTenantSlot(dateKey, startTime, deps.env.companyTimezone)) {
      skipped.push(`${formatDateMedium(day)} (in the past)`);
      continue;
    }
    const requestedEnd = addMinutes(requestedStart, durationMinutes);

    const dayAppts = await fetchDayAppointments(userId, day, deps.env.companyTimezone);
    const overlaps = findOverlappingAppointments(dayAppts, requestedStart, requestedEnd);
    if (overlaps.length > 0) {
      skipped.push(`${formatDateMedium(day)} (conflicts with ${overlaps[0].customerName || overlaps[0].type || 'an appointment'})`);
      continue;
    }

    planned.push({ date: dateKey, startTime, endTime: formatTimeOfDay24(requestedEnd) });
  }

  if (planned.length === 0) {
    return { planned: 0, skipped, note: 'Every occurrence landed on a non-working day, a past time, or a conflict. Try a different start day/time.' };
  }

  const approved = await deps.requestConfirmation({
    title: `Add "${title}" ${planned.length} time${planned.length > 1 ? 's' : ''}?`,
    confirmLabel: `Add ${planned.length}`,
    lines: [
      ...(isOther ? [{ icon: 'user' as const, text: `For: ${assigneeLabel(input, isOther)}` }] : []),
      { icon: 'map', text: coords.formatted },
      { icon: 'repeat', text: `Every ${cadenceDays === 7 ? 'week' : `${cadenceDays} days`} at ${formatTimeString(startTime)}, ${formatDurationMinutes(durationMinutes)} each` },
      ...planned.slice(0, 6).map((p) => ({ icon: 'calendar' as const, text: formatDateMedium(parseLocalDateTime(p.date)!) })),
      ...(planned.length > 6 ? [{ icon: 'calendar' as const, text: `+${planned.length - 6} more` }] : []),
      ...(skipped.length ? [{ icon: 'bell' as const, text: `Skipped: ${skipped.join(', ')}` }] : []),
      { icon: 'bell', text: 'These are independent stops, not a linked series — cancelling one will not affect the others.' },
    ],
  });
  if (!approved) return { planned: 0, note: 'The user declined. Do not retry without new instructions.' };

  deps.onActivity?.(`Adding ${planned.length} occurrence${planned.length > 1 ? 's' : ''}`);
  let created = 0;
  for (const p of planned) {
    const startUtc = tenantLocalToUtcDate(p.date, p.startTime, deps.env.companyTimezone);
    const endUtc = tenantLocalToUtcDate(p.date, p.endTime, deps.env.companyTimezone);
    await AppointmentService.postApiAppAppointmentCreateStop({
      title: title || 'Stop',
      stopLabel: title || undefined,
      startTimeUtc: startUtc.toISOString(),
      endTimeUtc: endUtc.toISOString(),
      assignedUserId: userId,
      contactId,
      addressLine1: coords.formatted,
      latitude: coords.lat,
      longitude: coords.lng,
    });
    created++;
  }

  return {
    created,
    skipped,
    occurrences: planned.map((p) => ({ date: p.date, startTime: formatTimeString(p.startTime) })),
    note: 'These were created as independent stops (Routemize has no linked recurring-series concept yet).',
  };
}

/** Summarize the appointments still scheduled in a window, for block guardrails. */
function remainingApptSummaries(appts: Appointment[]): Array<{ appointmentId: string | null; time: string; who: string }> {
  return appts.map((a) => ({
    appointmentId: a.id || null,
    time: a.startTime,
    who: a.customerName || a.type || 'appointment',
  }));
}

/** Appointments (excluding `excludeId`) whose window overlaps [start, end). */
/**
 * External calendar sources (Google/Outlook/Zoom) are read-only copies
 * synced into the timeline - Routemize has no ability to edit, reschedule,
 * or otherwise write to them. They ARE still fetched (and must stay in every
 * conflict/gap-finding calculation below) so Routy never double-books over
 * someone's real external meeting, but they must never be offered up as an
 * appointment the user can pick, reschedule, or bulk-move - that would either
 * fail outright or silently do nothing.
 */
const EXTERNAL_APPOINTMENT_SOURCES = new Set(['Google', 'Outlook', 'Zoom']);

function isRoutemizeManaged(a: Appointment): boolean {
  return !a.source || !EXTERNAL_APPOINTMENT_SOURCES.has(a.source);
}

function findOverlappingAppointments(appts: Appointment[], start: Date, end: Date, excludeId?: string): Appointment[] {
  return appts.filter(
    (a) => a.id !== excludeId && start.getTime() < a.endDateTime.getTime() && end.getTime() > a.startDateTime.getTime(),
  );
}

/**
 * Session cache of the tenant's connected/active Google calendars (with each
 * one's affects-availability setting), used to explain WHY an overlapped
 * external event does or doesn't block scheduling (QA 86bavnvhm).
 */
let connectedGoogleCalendarsCache: Routemize_DTOs_CalendarSyncStatusDto[] | null = null;

/**
 * How the tenant's connected Google calendars treat availability:
 * - 'displayOnly': every active calendar is set to NOT block availability
 * - 'blocking':    every active calendar blocks availability
 * - 'mixed':       some do, some don't (per-event attribution isn't possible
 *                  from the timeline data, so say nothing rather than guess)
 * - 'unknown':     none connected / lookup failed
 */
type ExternalAvailabilityMode = 'displayOnly' | 'blocking' | 'mixed' | 'unknown';

async function googleCalendarAvailabilityMode(): Promise<ExternalAvailabilityMode> {
  if (!connectedGoogleCalendarsCache) {
    try {
      const info = await GoogleCalendarService.getApiAppGoogleCalendarGetCalendarIntegrationInfo();
      connectedGoogleCalendarsCache = (info?.connectedCalendars ?? []).filter((c) => c.isActive !== false);
    } catch {
      connectedGoogleCalendarsCache = [];
    }
  }
  const cals = connectedGoogleCalendarsCache;
  if (cals.length === 0) return 'unknown';
  const blocking = cals.filter((c) => c.affectsAvailability !== false).length;
  if (blocking === 0) return 'displayOnly';
  if (blocking === cals.length) return 'blocking';
  return 'mixed';
}

/**
 * One-line human description of the external (Google/Outlook/Zoom-synced)
 * events a window overlaps. External events never hard-block (a calendar can
 * be configured display-only, and none of them can be moved/cancelled from
 * Routemize), but overlapping one must be DISCLOSED on the confirm card and
 * in the result so the user decides knowingly (QA 86bavnu8k). When the
 * calendar's availability setting is knowable, say it outright (QA 86bavnvhm)
 * so the user understands why the booking can still proceed.
 */
function describeExternalOverlaps(events: Appointment[], availabilityMode?: ExternalAvailabilityMode): string {
  return events
    .map((e) => {
      const base = `${e.source ?? 'synced'} calendar event "${e.type || e.customerName || 'Busy'}" (${e.startTime} – ${e.endTime})`;
      if (e.source === 'Google' && availabilityMode === 'displayOnly') {
        return `${base}, which is set to display-only (it does not block availability)`;
      }
      if (e.source === 'Google' && availabilityMode === 'blocking') {
        return `${base}, which is set to block availability`;
      }
      return base;
    })
    .join(', ');
}

async function execBlockTime(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const type = String(input.type);
  const tenantNow = tenantShiftedNow(deps.env.companyTimezone);
  const date = input.date ? String(input.date) : toDateKey(tenantNow);
  const day = parseLocalDateTime(date);
  if (!day) throw new Error('Invalid date.');
  const dateLabel = formatDateMedium(day);
  const mgr = new AvailabilityOverrideManager(deps.env.companyTimezone);
  const acknowledgeAppointments = Boolean(input.acknowledgeAppointments);
  const { userId, isOther } = resolveAssignee(input, deps);
  const forLine = isOther ? [{ icon: 'user' as const, text: `For: ${assigneeLabel(input, isOther)}` }] : [];

  if (type === 'restOfDay') {
    const scheduleByDay = await fetchWorkScheduleByDay(userId);
    const hours = getWorkingHoursForDate(day, scheduleByDay);
    if (!hours.isWorkDay) return { blocked: false, note: `${dateLabel} is not a working day — nothing to block.` };

    // `tenantNow` is already tenant-shifted (see comment in execGetSchedule) — passing
    // `companyTimezone` here would double-shift.
    const nowHHMM = formatTimeOfDay24(tenantNow);
    const startTime = nowHHMM > hours.start ? nowHHMM : hours.start;
    if (startTime >= hours.end) {
      return { blocked: false, note: `The workday on ${dateLabel} is already over — nothing left to block.` };
    }
    const reason = input.reason ? String(input.reason) : 'Out of office';

    deps.onActivity?.(`Checking the rest of ${isOther ? `${assigneeLabel(input, isOther)}'s` : 'your'} day`);
    const appts = (await fetchDayAppointments(userId, day, deps.env.companyTimezone))
      .filter((a) => a.startDateTime.getTime() >= tenantNow.getTime())
      .sort((a, b) => a.startDateTime.getTime() - b.startDateTime.getTime());

    if (appts.length > 0 && !acknowledgeAppointments) {
      return {
        blocked: false,
        needsAcknowledgement: true,
        remainingAppointments: remainingApptSummaries(appts),
        note:
          `There ${appts.length === 1 ? 'is' : 'are'} still ${appts.length} appointment` +
          `${appts.length === 1 ? '' : 's'} scheduled for the rest of ${dateLabel}. Tell the user and ask ` +
          'whether to cancel/notify those (use update_appointment_status per appointment) or keep them. ' +
          'Once they decide, call block_time again with acknowledgeAppointments=true to block the time.',
      };
    }

    const approved = await deps.requestConfirmation({
      title: 'Turn off the rest of today?',
      confirmLabel: 'Block rest of day',
      tone: 'danger',
      lines: [
        ...forLine,
        { icon: 'calendar', text: dateLabel },
        { icon: 'clock', text: `${formatTimeString(startTime)} - ${formatTimeString(hours.end)}` },
        { icon: 'bell', text: reason },
        ...(appts.length ? [{ icon: 'user' as const, text: `${appts.length} appointment(s) still on the books` }] : []),
      ],
    });
    if (!approved) return { blocked: false, note: 'The user declined.' };

    await UserService.postApiAppUserCreateUserWorkHourOverrideAysnc([
      {
        userId,
        date,
        startTime,
        endTime: hours.end,
        status: Routemize_Constants_WorkingHourStatus.UNAVAILABLE,
        windowName: reason,
        isEnabled: true,
      },
    ]);
    return { blocked: true, type: 'restOfDay', date, startTime, endTime: hours.end, reason };
  }

  if (type === 'dayOff') {
    const approved = await deps.requestConfirmation({
      title: 'Take the whole day off?',
      confirmLabel: 'Block day off',
      tone: 'danger',
      lines: [
        ...forLine,
        { icon: 'calendar', text: dateLabel },
        { icon: 'bell', text: isOther ? `${assigneeLabel(input, isOther)} will show as unavailable all day` : 'You will show as unavailable all day' },
      ],
    });
    if (!approved) return { blocked: false, note: 'The user declined.' };
    await mgr.setDayOff(userId, day, true);
    return { blocked: true, type, date };
  }

  // Resolve the day range for lunch/personal (single day unless endDate given).
  const rangeEndDate = input.endDate ? String(input.endDate) : undefined;
  const scheduleByDayForRange = await fetchWorkScheduleByDay(userId);
  const rangeDays = (rangeEndDate ? enumerateDates(date, rangeEndDate) : [day]).filter((d) => {
    const h = getWorkingHoursForDate(d, scheduleByDayForRange);
    return h.isWorkDay && isDayAvailable(d, h.end, tenantNow);
  });
  if (rangeDays.length === 0) return { blocked: false, note: 'No available working days in that range.' };
  const rangeLabel =
    rangeDays.length > 1 ? `${formatDateMedium(rangeDays[0])} – ${formatDateMedium(rangeDays[rangeDays.length - 1])}` : dateLabel;

  if (type === 'lunch') {
    const startTime = input.startTime ? String(input.startTime) : '12:00';
    const endTime = input.endTime ? String(input.endTime) : '13:00';

    deps.onActivity?.('Checking for conflicts');
    const conflicts: string[] = [];
    for (const d of rangeDays) {
      const winStart = parseLocalDateTime(toDateKey(d), startTime);
      const winEnd = parseLocalDateTime(toDateKey(d), endTime);
      if (!winStart || !winEnd) continue;
      const appts = await fetchDayAppointments(userId, d, deps.env.companyTimezone);
      const overlaps = findOverlappingAppointments(appts, winStart, winEnd);
      if (overlaps.length > 0) {
        conflicts.push(`${formatDateMedium(d)}: ${remainingApptSummaries(overlaps).map((o) => `${o.time} ${o.who}`).join(', ')}`);
      }
    }
    if (conflicts.length > 0 && !acknowledgeAppointments) {
      return {
        blocked: false,
        needsAcknowledgement: true,
        conflicts,
        note:
          'Some days already have appointments during that window. Tell the user and ask whether to ' +
          'keep them (skip blocking those days), pick a different window, or proceed anyway. Once they ' +
          'decide, call block_time again with acknowledgeAppointments=true.',
      };
    }

    const approved = await deps.requestConfirmation({
      title: rangeDays.length > 1 ? `Block lunch on ${rangeDays.length} days?` : 'Block lunch?',
      confirmLabel: 'Block lunch',
      lines: [
        ...forLine,
        { icon: 'calendar', text: rangeLabel },
        { icon: 'clock', text: `${formatTimeString(startTime)} - ${formatTimeString(endTime)}` },
        ...(conflicts.length ? [{ icon: 'bell' as const, text: `Overlaps: ${conflicts.join('; ')}` }] : []),
      ],
    });
    if (!approved) return { blocked: false, note: 'The user declined.' };
    for (const d of rangeDays) {
      await mgr.setLunchBreak(userId, d, true, startTime, endTime);
    }
    return { blocked: true, type, days: rangeDays.map((d) => toDateKey(d)) };
  }

  // personal block (arbitrary window)
  const startTime = input.startTime ? String(input.startTime) : '';
  const endTime = input.endTime ? String(input.endTime) : '';
  if (!startTime || !endTime) throw new Error('Personal blocks need a startTime and endTime (HH:MM).');
  const reason = input.reason ? String(input.reason) : 'Personal';

  deps.onActivity?.('Checking for conflicts');
  const personalConflicts: string[] = [];
  for (const d of rangeDays) {
    const winStart = parseLocalDateTime(toDateKey(d), startTime);
    const winEnd = parseLocalDateTime(toDateKey(d), endTime);
    if (!winStart || !winEnd) continue;
    const appts = await fetchDayAppointments(userId, d, deps.env.companyTimezone);
    const overlaps = findOverlappingAppointments(appts, winStart, winEnd);
    if (overlaps.length > 0) {
      personalConflicts.push(`${formatDateMedium(d)}: ${remainingApptSummaries(overlaps).map((o) => `${o.time} ${o.who}`).join(', ')}`);
    }
  }
  if (personalConflicts.length > 0 && !acknowledgeAppointments) {
    return {
      blocked: false,
      needsAcknowledgement: true,
      conflicts: personalConflicts,
      note:
        'Some days already have appointments during that window. Tell the user and ask whether to keep ' +
        'them, pick a different window, or proceed anyway. Once they decide, call block_time again with ' +
        'acknowledgeAppointments=true.',
    };
  }

  const approved = await deps.requestConfirmation({
    title: rangeDays.length > 1 ? `Block time for ${reason} on ${rangeDays.length} days?` : `Block time for ${reason}?`,
    confirmLabel: 'Block time',
    lines: [
      ...forLine,
      { icon: 'calendar', text: rangeLabel },
      { icon: 'clock', text: `${formatTimeString(startTime)} - ${formatTimeString(endTime)}` },
      ...(personalConflicts.length ? [{ icon: 'bell' as const, text: `Overlaps: ${personalConflicts.join('; ')}` }] : []),
    ],
  });
  if (!approved) return { blocked: false, note: 'The user declined.' };
  await UserService.postApiAppUserCreateUserWorkHourOverrideAysnc(
    rangeDays.map((d) => ({
      userId,
      date: toDateKey(d),
      startTime,
      endTime,
      status: Routemize_Constants_WorkingHourStatus.UNAVAILABLE,
      windowName: reason,
      isEnabled: true,
    })),
  );
  return { blocked: true, type: 'personal', days: rangeDays.map((d) => toDateKey(d)), reason };
}

async function execGetContactHistory(input: Record<string, unknown>, deps: RoutyToolDeps) {
  let contactId = input.contactId ? String(input.contactId) : undefined;
  let contact: Routemize_DTOs_ContactDTO | undefined;

  deps.onActivity?.('Looking up contact history');
  if (!contactId) {
    const name = input.name ? String(input.name) : '';
    if (!name) throw new Error('Provide a name or contactId.');
    const res = await ContactService.getApiAppContactGetList(name, undefined, undefined, undefined, undefined, undefined, undefined, undefined, 0, 1);
    const first = ((res?.items ?? []) as Routemize_DTOs_ContactDTO[])[0];
    if (!first?.id) return { found: false, note: `No contact matching "${name}".` };
    contact = first;
    contactId = first.id;
  } else {
    contact = await ContactService.getApiAppContactGetById(contactId);
  }

  // The generated search proxy mis-types `filter`; the backend accepts an
  // AppointmentSearchFilter with contactId (see ContactDetailPage usage).
  const req = {
    maxResultCount: 100,
    skipCount: 0,
    filter: { contactId },
  } as unknown as Routemize_DTOs_SearchParameters_PagedSortedAndFilteredRequestDto_1;
  const res = await AppointmentService.postApiAppAppointmentSearch(req);
  const items = (res as unknown as { items?: Routemize_DTOs_AppointmentListItem[] })?.items ?? [];

  // Real UTC instants; use `new Date()` (not tenantShiftedNow) so past/upcoming
  // splits compare like with like.
  const now = Date.now();
  const dated = items
    .filter((i) => !i.isExternal)
    .map((i) => ({ i, t: (parseBackendDate(i.meetingStartTimeUtc) ?? parseBackendDate(i.meetingStartTime))?.getTime() ?? 0 }))
    .filter((x) => x.t > 0)
    .sort((a, b) => a.t - b.t);
  const past = dated.filter((x) => x.t < now);
  const upcoming = dated.filter((x) => x.t >= now);

  const fmt = (it: Routemize_DTOs_AppointmentListItem) => {
    const d = parseBackendDate(it.meetingStartTimeUtc) ?? parseBackendDate(it.meetingStartTime);
    const dateKey = d ? dateKeyInTenantTz(d, deps.env.companyTimezone) : null;
    const assignees = (it.attendees ?? [])
      .map((u) => u.name || [u.firstName, u.lastName].filter(Boolean).join(' '))
      .filter(Boolean);
    return {
      // Both a machine key (for matching "yesterday") and a display label,
      // rendered in the TENANT timezone (browser-local formatting could land
      // an appointment on the wrong calendar day).
      date: dateKey,
      // Weekday-bearing label from the TENANT-local date key (e.g. "Wed, Sep 23, 2026"):
      // the model must quote this, never derive a weekday itself (QA 86bcd7ja3).
      dateLabel: dateKey ? `${formatDateKeyMedium(dateKey)}, ${dateKey.slice(0, 4)}` : null,
      time: d ? formatTimeInTenantTz(d, deps.env.companyTimezone) : null,
      type: it.bookingFormName || it.eventName || 'Appointment',
      status: it.status ?? null,
      // Which team member(s) the appointment was assigned to.
      assignedTo: assignees.join(', ') || null,
    };
  };

  return {
    found: true,
    name: contact?.fullName || [contact?.firstName, contact?.lastName].filter(Boolean).join(' ') || 'Contact',
    phone: contact?.phoneNumber || null,
    totalAppointments: contact?.totalAppointments ?? dated.length,
    lastVisit: past.length ? fmt(past[past.length - 1].i) : null,
    pastCount: past.length,
    // Most recent first, capped. Covers the WHOLE company (every team
    // member's appointments with this contact), with per-appointment date,
    // time, status, and assignedTo — use this to answer "verify the
    // appointments I had for X yesterday and who had them".
    recentPast: past.slice(-10).reverse().map((x) => fmt(x.i)),
    upcomingCount: upcoming.length,
    upcoming: upcoming.slice(0, 5).map((x) => fmt(x.i)),
    note:
      'recentPast lists this contact\'s PAST appointments (most recent first, across ALL team members) with ' +
      'each one\'s date, time, status, and assignedTo. Report statuses verbatim - a past appointment is only ' +
      '"completed" if its status says so. dateLabel includes the weekday - quote it verbatim; never compute a ' +
      'weekday yourself.',
  };
}

/** The official Routemize support email (also used by the billing screen). */
export const ROUTEMIZE_SUPPORT_EMAIL = 'support@routemize.com';

/**
 * Show the "reach a human" support card. UI-only: emits a support artifact the
 * panel renders with an "Open Support Chat" button (Intercom) + the official
 * support email, and hands the model the exact real channels to speak to.
 */
async function execOpenManualBooking(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const prefill: BookingCollectorPrefill = {
    ...(input.contactId ? { contactId: String(input.contactId) } : {}),
    ...(input.contactAddressId ? { contactAddressId: String(input.contactAddressId) } : {}),
    ...(input.firstName ? { firstName: String(input.firstName) } : {}),
    ...(input.lastName ? { lastName: String(input.lastName) } : {}),
    ...(input.email ? { email: String(input.email) } : {}),
    ...(input.phone ? { phone: String(input.phone) } : {}),
    ...(input.address ? { address: String(input.address) } : {}),
  };
  deps.onArtifact?.({
    type: 'open_manual_booking',
    requestId: input.requestId ? String(input.requestId) : undefined,
    bookingFormId: input.bookingFormId ? String(input.bookingFormId) : undefined,
    reason: input.reason ? String(input.reason) : undefined,
    prefill: Object.keys(prefill).length > 0 ? prefill : undefined,
    preferredDate: input.preferredDate ? String(input.preferredDate) : undefined,
    preferredTimeRange: input.preferredTimeRange ? String(input.preferredTimeRange) : undefined,
  });
  return {
    shown: true,
    note:
      'The hand-off card is shown - tapping it opens the full New Booking form with everything already ' +
      'collected pre-filled, so the user can finish scheduling manually. Your reply: ONE short sentence ' +
      'pointing at the card (e.g. "I couldn\'t finish this automatically, but everything you gave me is ' +
      'pre-filled - tap below to complete the booking."). Do NOT also offer contact_support unless the ' +
      'user asks for support or this manual path fails too.',
  };
}

async function execContactSupport(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const topic = input.topic ? String(input.topic).trim() : undefined;
  const contextSummary = input.contextSummary ? String(input.contextSummary).trim() : undefined;
  deps.onArtifact?.({ type: 'support', topic, contextSummary });
  return {
    shown: true,
    supportEmail: ROUTEMIZE_SUPPORT_EMAIL,
    note:
      'A support card is now shown with the REAL channels: the in-app Support Chat (its button opens the ' +
      `messenger directly; it is also behind the sidebar "Help" button) and ${ROUTEMIZE_SUPPORT_EMAIL}. ` +
      (contextSummary
        ? 'Opening the chat COPIES your context snapshot to their clipboard (and attempts to pre-fill the ' +
          'composer, though the support bot may ignore that) - tell the user to PASTE the copied details ' +
          'into the chat so the team has the full picture. Never claim you already opened the chat or sent ' +
          'anything - the user taps the button. '
        : '') +
      'Your reply: 1-2 warm sentences pointing at the card - e.g. that the Support Chat is the fastest way ' +
      'to get the team reviewing their account. Do NOT list any other emails/phones/channels, and do not ' +
      'claim anything was escalated automatically. Then offer to keep helping with their schedule.',
  };
}

/**
 * Send a customer their self-reschedule link for an appointment, over SMS
 * and/or email, using the tenant's Reschedule Link message templates - the
 * same backend flow as the appointment view's "Send Reschedule Link" modal.
 */
async function execSendRescheduleLink(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const appointmentId = String(input.appointmentId);
  const requested = input.channel ? String(input.channel) : 'both';
  const customerName = input.customerName ? String(input.customerName).trim() : 'the customer';

  deps.onActivity?.('Preparing the reschedule link');

  // Self-reschedule links only exist for REAL customer appointments - the app
  // hides "Send Reschedule Link" for ad-hoc job stops and personal events
  // (see AppointmentMenu.showSendRescheduleLinkItem), and a generated link for
  // one lands on an error page. Refuse up front instead of handing the user a
  // broken link (QA 86bavtc81).
  const appt = await AppointmentService.getApiAppAppointmentGetById(appointmentId);
  const isAdHocStop =
    appt.eventTypeId === INTERNAL_STOP_EVENT_TYPE_ID || !!appt.stopLabel?.trim();
  if (isAdHocStop || !appt.contactId) {
    return {
      sent: false,
      link: null,
      note:
        `This is ${isAdHocStop ? 'an ad-hoc JOB STOP' : 'an appointment with no linked customer'} - ` +
        'self-reschedule links are NOT available for it (they only work for real customer appointments ' +
        'booked on a booking form; the link would open a broken page). Tell the user plainly that ' +
        'reschedule links don\'t apply to job stops, and NEVER share or promise a link for this one. ' +
        'Offer to move the stop yourself instead: reschedule_appointment with a new time, or ' +
        'get_reschedule_options / find_stop_slots to find a route-efficient slot.',
    };
  }

  const info = await AppointmentService.getApiAppAppointmentGetRescheduleLinkInfo(appointmentId);
  const canSms = !!info.smsAvailable && !!info.contactPhone;
  const canEmail = !!info.contactEmail;

  if (!canSms && !canEmail) {
    return {
      sent: false,
      link: info.link ?? null,
      note:
        `Neither channel is available - ${
          info.smsAvailable ? 'no phone number on file' : 'no texting provider is connected'
        } and no email on file for this customer. Share the link itself with the user (it is in this result) ` +
        'so they can pass it along however they like.',
    };
  }

  // Honor the requested channel where possible; fall back to whatever IS
  // available instead of failing (and say so on the card).
  let sendSms = canSms && requested !== 'email';
  let sendEmail = canEmail && requested !== 'sms';
  if (!sendSms && !sendEmail) {
    sendSms = canSms;
    sendEmail = canEmail;
  }
  const smsDowngraded = (requested === 'sms' || requested === 'both') && !canSms && sendEmail;

  const approved = await deps.requestConfirmation({
    title: `Send ${customerName} their reschedule link?`,
    confirmLabel: 'Send link',
    lines: [
      ...(sendSms ? [{ icon: 'user' as const, text: `Text: ${info.contactPhone}` }] : []),
      ...(sendEmail ? [{ icon: 'user' as const, text: `Email: ${info.contactEmail}` }] : []),
      { icon: 'bell' as const, text: 'Sends your Reschedule Link message template with their personal link.' },
      ...(smsDowngraded
        ? [{ icon: 'bell' as const, text: !info.smsAvailable ? 'No texting provider connected - sending by email instead.' : 'No phone on file - sending by email instead.' }]
        : []),
    ],
  });
  if (!approved) return { sent: false, note: 'The user declined. Do not retry without new instructions.' };

  deps.onActivity?.('Sending the reschedule link');
  const res = await AppointmentService.postApiAppAppointmentSendRescheduleLink({ appointmentId, sendSms, sendEmail });
  const sentChannels = [
    ...(sendSms && res.smsSent ? ['SMS'] : []),
    ...(sendEmail && res.emailSent ? ['email'] : []),
  ];
  const failures = [
    ...(sendSms && !res.smsSent ? [res.smsError || 'SMS failed to send.'] : []),
    ...(sendEmail && !res.emailSent ? [res.emailError || 'Email failed to send.'] : []),
  ];

  return {
    sent: sentChannels.length > 0,
    sentVia: sentChannels,
    failures: failures.length ? failures : undefined,
    link: info.link ?? null,
    note:
      sentChannels.length === 0
        ? 'Nothing went out - relay the failure reason(s) plainly and share the link itself so the user can pass it along manually.'
        : failures.length
          ? `Sent by ${sentChannels.join(' and ')}, but one channel failed - relay the failure plainly.`
          : `The reschedule link was sent by ${sentChannels.join(' and ')}. Report exactly that (do not claim a channel that is not listed).`,
  };
}

/**
 * Open the interactive multi-stop entry form in the chat. This is a UI-only
 * action: it emits a stop_collector artifact and returns a short note telling
 * the model to wait for the user's filled-in stops (which arrive as the next
 * user message for add_multiple_stops).
 */
async function execCollectStops(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const rawCount = typeof input.count === 'number' ? input.count : Number(input.count);
  const seedRows = Array.isArray(input.stops) ? (input.stops as Record<string, unknown>[]) : [];
  const seeds = seedRows
    .map((s) => ({
      title: typeof s.title === 'string' ? s.title : undefined,
      address: typeof s.address === 'string' ? s.address : undefined,
      durationMinutes: typeof s.durationMinutes === 'number' ? s.durationMinutes : undefined,
    }))
    .filter((s) => s.title || s.address || s.durationMinutes != null);

  const requested = Number.isFinite(rawCount) ? Math.round(rawCount) : 0;
  const count = Math.min(12, Math.max(1, requested || seeds.length || 3));

  const rawDuration =
    typeof input.defaultDurationMinutes === 'number'
      ? input.defaultDurationMinutes
      : Number(input.defaultDurationMinutes);
  const defaultDurationMinutes = Number.isFinite(rawDuration) && rawDuration > 0 ? Math.round(rawDuration) : 30;

  const startDate = typeof input.startDate === 'string' ? input.startDate : undefined;
  const endDate = typeof input.endDate === 'string' ? input.endDate : undefined;

  deps.onArtifact?.({
    type: 'stop_collector',
    count,
    defaultDurationMinutes,
    startDate,
    endDate,
    seeds: seeds.length ? seeds : undefined,
  });

  return {
    shown: true,
    rows: count,
    note:
      'An interactive stop-entry form (with Google address autocomplete) is now displayed to the user. Your ' +
      'text reply should briefly say what it is for - never just "Done". Do NOT ask for the addresses in ' +
      'text. Wait for the user to submit the form - their next message will contain the finished stop list ' +
      'for you to schedule with add_multiple_stops.',
  };
}

async function execBlockDaysOff(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const startDate = String(input.startDate);
  const endDate = String(input.endDate);
  const reason = input.reason ? String(input.reason) : 'Vacation';
  const acknowledge = Boolean(input.acknowledgeAppointments);
  const { userId, isOther } = resolveAssignee(input, deps);

  const days = enumerateDates(startDate, endDate);
  if (days.length === 0) throw new Error('Invalid date range. Use YYYY-MM-DD.');

  const scheduleByDay = await fetchWorkScheduleByDay(userId);
  const tenantNow = tenantShiftedNow(deps.env.companyTimezone);
  const workdays = days.filter((d) => {
    const h = getWorkingHoursForDate(d, scheduleByDay);
    return h.isWorkDay && isDayAvailable(d, h.end, tenantNow);
  });
  if (workdays.length === 0) {
    return { blocked: false, note: 'No upcoming working days in that range to block off.' };
  }

  deps.onActivity?.(`Checking ${isOther ? `${assigneeLabel(input, isOther)}'s` : 'your'} appointments in that range`);
  const remaining: Array<{ appointmentId: string | null; date: string; time: string; who: string }> = [];
  for (const d of workdays) {
    const appts = (await fetchDayAppointments(userId, d, deps.env.companyTimezone)).filter(
      (a) => a.startDateTime.getTime() >= tenantNow.getTime(),
    );
    for (const a of appts) {
      remaining.push({
        appointmentId: a.id || null,
        date: toDateKey(d),
        time: a.startTime,
        who: a.customerName || a.type || 'appointment',
      });
    }
  }

  if (remaining.length > 0 && !acknowledge) {
    return {
      blocked: false,
      needsAcknowledgement: true,
      remainingAppointments: remaining,
      note:
        `There ${remaining.length === 1 ? 'is' : 'are'} still ${remaining.length} appointment` +
        `${remaining.length === 1 ? '' : 's'} on those days. Tell the user and ask whether to cancel/notify ` +
        'them (use update_appointment_status per appointment) or keep them. Once they decide, call ' +
        'block_days_off again with acknowledgeAppointments=true.',
    };
  }

  const mgr = new AvailabilityOverrideManager(deps.env.companyTimezone);
  const approved = await deps.requestConfirmation({
    title: `Block ${workdays.length} day${workdays.length > 1 ? 's' : ''} off?`,
    confirmLabel: `Block ${workdays.length} day${workdays.length > 1 ? 's' : ''}`,
    tone: 'danger',
    lines: [
      ...(isOther ? [{ icon: 'user' as const, text: `For: ${assigneeLabel(input, isOther)}` }] : []),
      { icon: 'calendar', text: `${formatDateMedium(workdays[0])} – ${formatDateMedium(workdays[workdays.length - 1])}` },
      { icon: 'bell', text: reason },
      ...(remaining.length
        ? [{ icon: 'user' as const, text: `${remaining.length} appointment(s) still on the books` }]
        : []),
    ],
  });
  if (!approved) return { blocked: false, note: 'The user declined.' };

  deps.onActivity?.(`Blocking ${workdays.length} day(s) off`);
  let blocked = 0;
  for (const d of workdays) {
    await mgr.setDayOff(userId, d, true);
    blocked++;
  }
  return { blocked: true, days: blocked, startDate, endDate, reason };
}

async function execGetRescheduleOptions(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const appointmentId = String(input.appointmentId);
  const preferredDate = input.preferredDate ? String(input.preferredDate) : undefined;
  const timePreference = input.timePreference ? String(input.timePreference) : undefined;
  return loadRescheduleOptions(appointmentId, preferredDate, timePreference, deps);
}

/**
 * Ranked reschedule slots for an appointment: emits the slot_options artifact
 * (the alternative-time cards) and returns the summary for the model. Shared by
 * get_reschedule_options and reschedule_appointment's unavailable path.
 */
async function loadRescheduleOptions(
  appointmentId: string,
  preferredDate: string | undefined,
  timePreference: string | undefined,
  deps: RoutyToolDeps,
) {
  deps.onActivity?.('Finding the best times to move it');
  const resp = await RoutemizationService.getApiAppRoutemizationGetRescheduleSlots(
    appointmentId,
    preferredDate,
    undefined,
    timePreference,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    true,
  );
  const options = categorizeSlots(resp?.slots ?? [], deps.env.companyTimezone);
  if (options.length === 0) {
    return {
      found: 0,
      note:
        'The engine found no open future slots to move this to. Ask the user for a specific day/time or a ' +
        'wider date range.',
    };
  }

  let label = 'your appointment';
  try {
    const current = await AppointmentService.getApiAppAppointmentGetById(appointmentId);
    label = current.title || label;
  } catch {
    // Best-effort label.
  }

  deps.onArtifact?.({ type: 'slot_options', kind: 'reschedule', appointmentId, appointmentLabel: label, options });
  return {
    found: options.length,
    appointmentLabel: label,
    options: options.map((o) => ({
      category: o.category,
      date: o.date,
      startTime: formatTimeString(o.startTime),
      driveInMinutes: o.driveInMinutes,
      driveOutMinutes: o.driveOutMinutes,
    })),
    note:
      'Ranked cards (Top Choice / Proximity / Customer Preference) are shown to the user. Briefly recommend ' +
      'the top one; when the user picks a card the reschedule is confirmed via reschedule_appointment.',
  };
}

async function fetchPublishedForms(): Promise<BookingFormOption[]> {
  const items = await fetchRawBookingForms();
  return items
    .filter(
      (f) =>
        f.id &&
        f.status === Routemize_Constants_BookingFormStatus.PUBLISHED &&
        // Same rule as the New Booking modal's service list: forms with a
        // leadSource assigned are channel-specific (auto-created per source)
        // and are never offered for internal booking.
        !f.leadSource,
    )
    .map((f) => ({
      id: f.id as string,
      name: f.formName || 'Service',
      durationMinutes: f.timeAtLocationMinutes ?? null,
      description: f.formDescription || null,
      allowSameDayBooking: f.allowSameDayBooking !== false,
    }));
}

/**
 * Resolve a Morning/Afternoon/Evening preference (or "any time" when absent)
 * to the SAME representation the New Booking modal writes onto a request: the
 * master-data option's CODE (e.g. "MRN") plus its explicit start/end window.
 * Routy previously stored the literal word "Morning" with no start/end times,
 * which the scheduling engine can't map to a window - GetSlots then returned
 * zero slots for days that were wide open (QA report).
 */
async function resolveRequestTimeWindow(
  preference: string | undefined,
): Promise<{ code: string | null; start: string | null; end: string | null }> {
  // Same fallback list the New Booking modal uses when master data is missing.
  const fallback: PreferredTimeRangeOption[] = [
    { code: 'ANY', text: 'Anytime', helpText: 'Flexible with timing', start: '', end: '' },
    { code: 'MRN', text: 'Morning', helpText: '8AM - 12PM', start: '08:00', end: '12:00' },
    { code: 'AFT', text: 'Afternoon', helpText: '12PM - 5PM', start: '12:00', end: '17:00' },
    { code: 'EVE', text: 'Evening', helpText: '5PM - 8PM', start: '17:00', end: '20:00' },
  ];
  let options = fallback;
  try {
    const response = await MasterDataService.getApiAppMasterDataGet('PreferredTimeRanges', false);
    // ABP serializes each item's per-option window into `extraProperties`,
    // which the generated MasterData DTO doesn't declare (the modal reads the
    // same fields) - widen the item type structurally rather than assuming.
    const items = (response?.[0]?.items ?? []) as Array<
      Routemize_DTOs_MasterData & { extraProperties?: Record<string, unknown> }
    >;
    if (items.length > 0) {
      options = items.map((item) => ({
        code: item.code || '',
        text: item.text || '',
        helpText: item.helpText || '',
        start: typeof item.extraProperties?.start === 'string' ? item.extraProperties.start : '',
        end: typeof item.extraProperties?.end === 'string' ? item.extraProperties.end : '',
      }));
    }
  } catch {
    // Master data unavailable - the standard fallback matches the modal's.
  }
  const value = preference || 'anytime';
  const option = resolvePreferredTimeOption(value, options) ?? resolvePreferredTimeOption(value, fallback);
  if (!option) return { code: preference ?? null, start: null, end: null };
  return { code: option.code || option.text || null, start: option.start || null, end: option.end || null };
}

async function execListBookingForms(input: Record<string, unknown>, deps: RoutyToolDeps) {
  deps.onActivity?.('Loading your services');
  if (input.includeDrafts === true) {
    const all = (await fetchRawBookingForms()).filter(
      (f) => f.id && f.status !== Routemize_Constants_BookingFormStatus.ARCHIVED,
    );
    return {
      count: all.length,
      forms: all.map((f) => ({
        id: f.id,
        name: f.formName || 'Untitled form',
        status: f.status ?? null,
        durationMinutes: f.timeAtLocationMinutes ?? null,
        leadSource: f.leadSource || null,
      })),
      note:
        'Every form that is not archived. Only Published forms without a leadSource can be booked; use ' +
        'these ids with get_booking_form, update_booking_form and edit_form_questions.',
    };
  }
  const forms = await fetchPublishedForms();
  return {
    count: forms.length,
    forms,
    note: forms.length
      ? 'These are the bookable services. allowSameDayBooking false means the engine will not offer today ' +
        'for that form — pass that bookingFormId to audit_availability when asking why a day will not book.'
      : 'No published booking forms found.',
  };
}

/**
 * Request statuses that mean "captured but not yet turned into an appointment".
 * Excludes CONFIRMED (booked), HISTORY (already booked/closed out), ARCHIVED and
 * DECLINED (dead leads) - those are not "pending" in the actionable sense.
 */
const PENDING_REQUEST_STATUSES = [
  Routemize_Constants_RequestStatus.SCHEDULING,
  Routemize_Constants_RequestStatus.ACTIVE,
  Routemize_Constants_RequestStatus.CONTACT_PROVIDED,
  Routemize_Constants_RequestStatus.ADDRESS_PROVIDED,
  Routemize_Constants_RequestStatus.SERVICE_DETAILS_COMPLETED,
  Routemize_Constants_RequestStatus.SLOTS_PRESENTED,
  Routemize_Constants_RequestStatus.PREVIEW_SHOWN,
];

async function execFindPendingRequests(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const text = input.text ? String(input.text).trim() : '';
  const bookingFormId = input.bookingFormId ? String(input.bookingFormId) : undefined;
  const maxResults = input.maxResults ? Number(input.maxResults) : 10;

  deps.onActivity?.('Checking your pending leads');

  // The search filter only accepts a single status per call, so query each
  // "still pending" status in parallel and merge/dedupe the results.
  const pages = await Promise.all(
    PENDING_REQUEST_STATUSES.map((status) =>
      RequestsService.postApiAppRequestsSearch({
        maxResultCount: 50,
        skipCount: 0,
        sorting: 'creationTime desc',
        filter: { status, bookingFormId, searchText: text || undefined },
      }).catch(() => null),
    ),
  );

  const byId = new Map<string, Routemize_DTOs_RequestSearchResponse>();
  for (const page of pages) {
    const items = (page as unknown as { items?: Routemize_DTOs_RequestSearchResponse[] } | null)?.items ?? [];
    for (const item of items) {
      if (item.id && !byId.has(item.id)) byId.set(item.id, item);
    }
  }

  const requests = [...byId.values()]
    .sort((a, b) => (b.creationTime ?? '').localeCompare(a.creationTime ?? ''))
    .slice(0, Math.max(1, Math.min(20, maxResults)))
    .map((r) => ({
      requestId: r.id ?? null,
      contactName: [r.contactFirstName, r.contactLastName].filter(Boolean).join(' ') || null,
      service: r.formName || null,
      bookingFormId: r.bookingFormId || null,
      status: r.status ?? null,
      address: r.addressLine1 || null,
      leadSource: r.leadSource || null,
      createdOn: r.creationTime ? formatDateMedium(parseBackendDate(r.creationTime) ?? new Date()) : null,
      notes: r.notes || null,
    }));

  return {
    count: requests.length,
    requests,
    note:
      requests.length === 0
        ? 'No pending leads matched. Try without a filter, or the customer may already be scheduled.'
        : 'Tell the user what you found (contact, service, how long it has been pending). To book one, call ' +
          'create_booking_request with existingRequestId set to its requestId (skip the intake form if the ' +
          'contact/address are already on the request).',
  };
}

async function execCollectBooking(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const mode = String(input.mode) === 'existing' ? 'existing' : 'new';
  let bookingFormId = input.bookingFormId ? String(input.bookingFormId) : undefined;

  deps.onActivity?.('Opening the booking form');
  const forms = await fetchPublishedForms();

  // Resolve a named service TYPE ("estimate") to a real form so it arrives
  // preselected instead of making the user pick (or the model guess in prose).
  // With an assignee, a form that teammate is actually on wins ties - "jonny
  // smith estimate for chris" should land on Chris's estimate form.
  const serviceHint = input.serviceHint ? String(input.serviceHint).trim().toLowerCase() : '';
  const hintAssigneeId = input.assignedUserId ? String(input.assignedUserId).trim() : '';
  if (!bookingFormId && serviceHint) {
    const matches = forms.filter((f) => {
      const name = f.name.toLowerCase();
      return name.includes(serviceHint) || serviceHint.split(/\s+/).some((w) => w.length >= 4 && name.includes(w));
    });
    if (matches.length === 1) {
      bookingFormId = matches[0].id;
    } else if (matches.length > 1) {
      if (hintAssigneeId) {
        for (const m of matches) {
          try {
            const users = await BookingFormUserService.getApiAppBookingFormUserGetFormUsers(m.id);
            if ((users ?? []).some((u) => u.id === hintAssigneeId)) {
              bookingFormId = m.id;
              break;
            }
          } catch {
            // Skip - fall through to the first match below.
          }
        }
      }
      if (!bookingFormId) bookingFormId = matches[0].id;
    }
  }

  let prefill: BookingCollectorPrefill | undefined;
  if (mode === 'existing' && input.contactId) {
    try {
      const c = await ContactService.getApiAppContactGetById(String(input.contactId));
      const addr = (c.addresses ?? []).find((a) => a.isActive) ?? (c.addresses ?? [])[0];
      prefill = {
        contactId: c.id,
        firstName: c.firstName ?? undefined,
        lastName: c.lastName ?? undefined,
        email: c.email ?? undefined,
        phone: c.phoneNumber ?? undefined,
        address: addr?.fullAddress ?? undefined,
        lat: addr?.latitude ?? null,
        lng: addr?.longitude ?? null,
        contactAddressId: addr?.id,
      };
    } catch {
      // Proceed with an empty form if the contact can't be loaded.
    }
  }

  // Backstop for the "should have checked itself" rule: a NEW-customer form
  // for a name that already exists in contacts is probably a duplicate in the
  // making. Non-blocking - the form still opens - but the model is told so it
  // can flag it ("you already have a Jonny Smith saved - same person?").
  let existingMatchNote = '';
  if (mode === 'new' && input.firstName && input.lastName) {
    const fullName = `${String(input.firstName).trim()} ${String(input.lastName).trim()}`.trim();
    try {
      const hits = await searchContactsByText(fullName);
      const exact = hits.find(
        (c) => (c.fullName || [c.firstName, c.lastName].filter(Boolean).join(' ')).toLowerCase() === fullName.toLowerCase(),
      );
      if (exact?.id) {
        existingMatchNote =
          ` HEADS UP: a contact named "${fullName}" already exists (contactId ${exact.id}). The new-customer ` +
          'form is open, but mention this to the user - if it is the same person, re-open with mode ' +
          '"existing" and that contactId instead of creating a duplicate.';
      }
    } catch {
      // Best-effort duplicate check only.
    }
  }

  // Fold in whatever the user already dictated in chat so the form opens
  // pre-filled instead of making them retype it (QA 86bauq5wy). Existing-
  // contact data wins for identity fields; a dictated address wins over the
  // saved one (it may be a different job site).
  const dictated = {
    firstName: input.firstName ? String(input.firstName).trim() : '',
    lastName: input.lastName ? String(input.lastName).trim() : '',
    email: input.email ? String(input.email).trim() : '',
    phone: input.phone ? String(input.phone).trim() : '',
    address: input.address ? String(input.address).trim() : '',
  };
  if (Object.values(dictated).some(Boolean)) {
    prefill = {
      ...(prefill ?? {}),
      firstName: prefill?.firstName || dictated.firstName || undefined,
      lastName: prefill?.lastName || dictated.lastName || undefined,
      email: prefill?.email || dictated.email || undefined,
      phone: prefill?.phone || dictated.phone || undefined,
      ...(dictated.address
        ? { address: dictated.address, lat: null, lng: null, contactAddressId: undefined }
        : {}),
    };
    // Best-effort geocode of a dictated address so the form shows it verified
    // with real coordinates (the user can still edit it).
    if (dictated.address) {
      try {
        const coords = await geocodeOrThrow(dictated.address);
        prefill = { ...prefill, address: coords.formatted, lat: coords.lat, lng: coords.lng };
      } catch {
        // Keep the raw text; create_booking_request geocodes again later.
      }
    }
  }

  // Sensible starting point when the user gave no date: tomorrow (tenant
  // frame), with time left as "Any time" - one less thing to fill in.
  const preferredDate = input.preferredDate
    ? String(input.preferredDate)
    : (() => {
        const t = tenantShiftedNow(deps.env.companyTimezone);
        t.setDate(t.getDate() + 1);
        return toDateKey(t);
      })();
  const rawRange = input.preferredTimeRange ? String(input.preferredTimeRange) : undefined;
  const preferredTimeRange = rawRange && ['Morning', 'Afternoon', 'Evening'].includes(rawRange) ? rawRange : undefined;

  // Surface the selected service's REQUIRED intake questions right away so the
  // model can collect the answers in chat while the user fills the form -
  // create_booking_request refuses to run without them (QA 86bav5cnw). Only
  // base-visible questions are listed; conditional follow-ups surface later if
  // an answer reveals them.
  let requiredQuestions: ReturnType<typeof describeFormQuestion>[] = [];
  let formIsRequestMethod = false;
  if (bookingFormId) {
    try {
      const qs = await fetchFormQuestions(bookingFormId);
      requiredQuestions = qs
        .filter((q) => q.isRequired === true && isQuestionVisible(q, qs, {}))
        .map(describeFormQuestion);
    } catch {
      // Best-effort - create_booking_request re-checks either way.
    }
    try {
      const raw = (await fetchRawBookingForms()).find((f) => f.id === bookingFormId);
      formIsRequestMethod = !!raw && raw.allowInstantBooking !== true;
    } catch {
      // Best-effort - create_booking_request re-checks either way.
    }
  }

  deps.onArtifact?.({ type: 'booking_collector', mode, forms, bookingFormId, prefill, preferredDate, preferredTimeRange });
  return {
    shown: true,
    mode,
    formCount: forms.length,
    prefilled: {
      name: !!(prefill?.firstName || prefill?.lastName),
      address: !!prefill?.address,
      date: !!preferredDate,
      service: bookingFormId ? forms.find((f) => f.id === bookingFormId)?.name ?? true : false,
    },
    ...(requiredQuestions.length > 0 ? { requiredQuestions } : {}),
    ...(formIsRequestMethod ? { requestModeForm: true } : {}),
    note:
      'The in-chat booking form is displayed with everything you passed already filled in. Your text reply ' +
      'should briefly say what it is for and what (if anything) is still missing - never just "Done", and ' +
      'never list the pre-filled values telling the user to type them; they are already in the form. Wait ' +
      'for the user to submit it — their next message will contain the booking details for ' +
      'create_booking_request.' +
      (requiredQuestions.length > 0
        ? ` This service also has ${requiredQuestions.length} REQUIRED intake question(s) (see requiredQuestions) ` +
          'that the form does NOT collect - ask the user for those answers in this same reply (offer the exact ' +
          'options for choice questions) and pass them as questionAnswers to create_booking_request.'
        : '') +
      (formIsRequestMethod
        ? ' NOTE: this service is set to REQUEST mode (no instant booking) - after submission the booking is ' +
          'saved as a pending request for the team to schedule manually, with no time cards. Mention that up ' +
          'front so the user is not surprised.'
        : '') +
      existingMatchNote,
  };
}

/**
 * Booking-form intake question helpers. These mirror the optionsJson-based
 * logic in `react/src/utils/questionConditionalLogic.ts` but live locally
 * because this file is synced verbatim to mobile, whose copy of that util has
 * a different (pre-parsed) shape - a shared import would break the sync.
 */

/** Parse the options array from a question's `optionsJson` (legacy array or object). */
function parseQuestionOptions(optionsJson: string | null | undefined): string[] {
  if (!optionsJson) return [];
  try {
    const parsed = JSON.parse(optionsJson);
    if (Array.isArray(parsed)) return parsed as string[];
    if (parsed && Array.isArray(parsed.options)) return parsed.options as string[];
    return [];
  } catch {
    return [];
  }
}

/** Parse the per-option reveal-target map (option index -> target branchKey). */
function parseOptionTargets(optionsJson: string | null | undefined): Record<number, string> {
  if (!optionsJson) return {};
  try {
    const parsed = JSON.parse(optionsJson);
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') return {};
    const raw = parsed.optionTargets;
    if (!raw || typeof raw !== 'object') return {};
    const result: Record<number, string> = {};
    Object.entries(raw as Record<string, unknown>).forEach(([k, v]) => {
      const idx = Number(k);
      if (Number.isFinite(idx) && idx >= 0 && typeof v === 'string' && v.length > 0) result[idx] = v;
    });
    return result;
  } catch {
    return {};
  }
}

/** Parse this question's stable branch-target identifier, if any. */
function parseBranchKey(optionsJson: string | null | undefined): string | undefined {
  if (!optionsJson) return undefined;
  try {
    const parsed = JSON.parse(optionsJson);
    if (parsed && !Array.isArray(parsed) && typeof parsed === 'object' && typeof parsed.branchKey === 'string') {
      return parsed.branchKey;
    }
  } catch {
    /* ignore */
  }
  return undefined;
}

/**
 * Whether a question is currently shown under conditional (show/hide) logic: a
 * question targeted by some option's reveal is hidden until a visible
 * controller's selected answer targets it. Same semantics as the booking
 * forms' `isQuestionVisible`.
 */
function isQuestionVisible(
  question: Routemize_DTOs_BookingFormQuestionDto,
  questions: Routemize_DTOs_BookingFormQuestionDto[],
  answers: Record<string, string | undefined>,
  visiting: Set<string> = new Set(),
): boolean {
  const myBranchKey = parseBranchKey(question.optionsJson);
  if (!myBranchKey) return true;

  const conditionalKeys = new Set<string>();
  for (const q of questions) {
    for (const key of Object.values(parseOptionTargets(q.optionsJson))) conditionalKeys.add(key);
  }
  if (!conditionalKeys.has(myBranchKey)) return true;

  // Cycle guard for (mis)configured conditional graphs.
  if (question.id) {
    if (visiting.has(question.id)) return false;
    visiting.add(question.id);
  }

  for (const controller of questions) {
    if (!controller.id || controller.id === question.id) continue;
    const targets = parseOptionTargets(controller.optionsJson);
    if (Object.keys(targets).length === 0) continue;
    const answer = answers[controller.id];
    if (answer == null || answer === '') continue;
    // Multi-select answers are '||'-joined; any selected option that targets
    // this branchKey reveals it (single-select splits to one element).
    const options = parseQuestionOptions(controller.optionsJson);
    const controllerTargetsMe = answer.split('||').some((value) => {
      const selectedIndex = options.indexOf(value);
      return selectedIndex >= 0 && targets[selectedIndex] === myBranchKey;
    });
    if (!controllerTargetsMe) continue;
    if (isQuestionVisible(controller, questions, answers, visiting)) return true;
  }
  return false;
}

/**
 * Session cache of each booking form's custom intake questions, keyed by form
 * id. Attachments-type (photo upload) questions are stripped - they are a
 * public-form-only feature with no in-chat equivalent (the New Booking modal
 * strips them for the same reason).
 */
const formQuestionsCache = new Map<string, Routemize_DTOs_BookingFormQuestionDto[]>();

async function fetchFormQuestions(bookingFormId: string): Promise<Routemize_DTOs_BookingFormQuestionDto[]> {
  const cached = formQuestionsCache.get(bookingFormId);
  if (cached) return cached;
  const fetched = await BookingFormQuestionService.getApiAppBookingFormQuestionGetFormQuestion(bookingFormId);
  const questions = (fetched ?? [])
    .filter((q) => q.type !== Routemize_Constants_FieldType.ATTACHMENTS)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  formQuestionsCache.set(bookingFormId, questions);
  return questions;
}

/** Model-facing description of one intake question (the id to answer by, the options to offer). */
function describeFormQuestion(q: Routemize_DTOs_BookingFormQuestionDto) {
  const options = parseQuestionOptions(q.optionsJson);
  return {
    questionId: q.id ?? '',
    question: q.label ?? '',
    required: q.isRequired === true,
    ...(options.length > 0
      ? { options, multiSelect: q.type === Routemize_Constants_FieldType.CHECKBOX }
      : {}),
  };
}

/**
 * Validate the model-supplied questionAnswers against the form's intake
 * questions and find any REQUIRED (currently visible) questions still
 * unanswered (QA 86bav5cnw: booking a form with unanswered required questions
 * used to blow up mid-pipeline AFTER the request row existed, stranding a
 * half-made request and tempting the model into a job-stop "fallback").
 * Answers already stored on a continued pending request count as given.
 * Returns either a blocking structured result (ask the user first) or the
 * normalized questionId->answer map to persist.
 */
async function resolveQuestionAnswers(
  formQuestions: Routemize_DTOs_BookingFormQuestionDto[],
  input: Record<string, unknown>,
  existingRequestId: string,
): Promise<{ blocked?: Record<string, unknown>; answers: Record<string, string> }> {
  const answers: Record<string, string> = {};

  // A continued pending lead may already carry answers (e.g. captured by the
  // public booking form) - honor those so the user is never re-asked.
  if (existingRequestId) {
    try {
      const stored = await RequestsService.getApiAppRequestsGetAnswers(existingRequestId);
      for (const a of stored ?? []) {
        const qid = a.questionId || (a.question && formQuestions.some((q) => q.id === a.question) ? a.question : '');
        if (qid && a.answer?.trim()) answers[qid] = a.answer.trim();
      }
    } catch {
      // Best-effort - fall through to asking the user.
    }
  }

  const byLabel = new Map(formQuestions.map((q) => [String(q.label ?? '').trim().toLowerCase(), q] as const));
  const invalid: string[] = [];
  const raw =
    input.questionAnswers && typeof input.questionAnswers === 'object'
      ? (input.questionAnswers as Record<string, unknown>)
      : {};
  for (const [key, value] of Object.entries(raw)) {
    const text = value == null ? '' : String(value).trim();
    if (!text) continue;
    // Keyed by questionId; tolerate the question's exact label as a fallback.
    const q = formQuestions.find((f) => f.id === key) ?? byLabel.get(key.trim().toLowerCase());
    if (!q?.id) continue; // Unknown key - the missing check below still governs.
    const options = parseQuestionOptions(q.optionsJson);
    if (options.length === 0) {
      answers[q.id] = text;
      continue;
    }
    // Choice questions must be answered with the real options ('||'-joined for
    // multi-select), matched case-insensitively so the user's phrasing works.
    const picks = text.split('||').map((p) => p.trim()).filter(Boolean);
    const matched = picks.map((p) => options.find((o) => o.trim().toLowerCase() === p.toLowerCase()));
    if (picks.length === 0 || matched.some((m) => !m)) {
      invalid.push(`"${q.label}" must be answered with: ${options.join(' / ')}`);
      continue;
    }
    answers[q.id] =
      q.type === Routemize_Constants_FieldType.CHECKBOX ? (matched as string[]).join('||') : (matched[0] as string);
  }

  // Only questions currently visible under conditional logic can be required -
  // a hidden follow-up only becomes required once an answer reveals it (the
  // re-call after new answers arrive re-runs this gate and catches those).
  const missing = formQuestions.filter(
    (q) => q.isRequired === true && !!q.id && !answers[q.id] && isQuestionVisible(q, formQuestions, answers),
  );
  if (missing.length === 0 && invalid.length === 0) return { answers };
  return {
    answers,
    blocked: {
      created: false,
      needsQuestionAnswers: true,
      ...(missing.length > 0 ? { missingRequiredQuestions: missing.map(describeFormQuestion) } : {}),
      ...(invalid.length > 0 ? { invalidAnswers: invalid } : {}),
      note:
        'STOP - NOTHING was created. This service\'s booking form has REQUIRED intake questions that are ' +
        (invalid.length > 0 ? 'invalid or ' : '') +
        'unanswered, and the booking cannot be completed without them. Ask the user ALL the listed questions ' +
        'in ONE short message (offer the exact options for choice questions), then re-call ' +
        'create_booking_request with the SAME details plus questionAnswers mapping each questionId to the ' +
        'user\'s answer (join multi-select picks with "||"). NEVER invent or skip answers, and NEVER reroute ' +
        'this booking to add_stop/a job stop because of this.',
    },
  };
}

/**
 * Whether any (currently visible) answered choice question hit a
 * "request-only" trigger option (`requestOnlyIndexes` in optionsJson): the
 * tenant flagged those answers as needing MANUAL review, so the booking must
 * become a pending request instead of an instant booking - the same
 * forceRequest behavior the public booking form applies (QA 86bavq4b3).
 */
function answersTriggerRequestOnly(
  formQuestions: Routemize_DTOs_BookingFormQuestionDto[],
  answers: Record<string, string>,
): { triggered: boolean; answer?: string; question?: string } {
  for (const q of formQuestions) {
    if (!q.id || !q.optionsJson) continue;
    const answer = answers[q.id];
    if (!answer) continue;
    if (!isQuestionVisible(q, formQuestions, answers)) continue;
    let requestOnlyOptions: string[] = [];
    try {
      const parsed = JSON.parse(q.optionsJson);
      if (parsed && !Array.isArray(parsed) && Array.isArray(parsed.options) && Array.isArray(parsed.requestOnlyIndexes)) {
        requestOnlyOptions = (parsed.requestOnlyIndexes as number[]).map((i) => parsed.options[i]).filter(Boolean);
      }
    } catch {
      /* ignore malformed optionsJson */
    }
    if (requestOnlyOptions.length === 0) continue;
    const hit = answer.split('||').find((v) => requestOnlyOptions.includes(v));
    if (hit) return { triggered: true, answer: hit, question: q.label ?? '' };
  }
  return { triggered: false };
}

/**
 * Everything a pending request already knows about the booking it represents.
 *
 * A request captured from a form or a lead carries the contact, the service,
 * the address and the day the customer asked for. Asking the operator to
 * re-type any of that — especially the address, which is sitting right there on
 * the request card they are looking at — reads as the assistant not having
 * looked, so continuing a request always starts here.
 */
async function loadRequestContext(requestId: string) {
  const req = await RequestsService.getApiAppRequestsGetById(requestId);
  const contactAddressId = req.contactAddressId || '';
  const pickAddress = (list: Routemize_DTOs_ContactAddressDTO[]): Routemize_DTOs_ContactAddressDTO | undefined =>
    list.find((a) => a.id && a.id === contactAddressId) ?? list.find((a) => a.isActive) ?? list[0];

  // The nested contact does not reliably arrive with its addresses, and the one
  // the request points at is not always among the few that do. When that
  // happens the address has to be fetched by id — otherwise continuing a
  // request asks the operator to re-type an address Routemize is already
  // holding, which is the exact busywork loading the request exists to avoid.
  let addr = pickAddress(req.contact?.addresses ?? []);
  if (!addr && contactAddressId) {
    addr = (await ContactAddressService.getApiAppContactAddressGetById(contactAddressId).catch(() => null)) ?? undefined;
  }
  if (!addr && req.contactId) {
    const list = await ContactAddressService.getApiAppContactAddressGetContactAddresses(req.contactId).catch(() => []);
    addr = pickAddress(list ?? []);
  }
  const address =
    addr?.fullAddress || [addr?.addressLine1, addr?.city, addr?.state, addr?.zipCode].filter(Boolean).join(', ');

  return {
    contactId: req.contactId || '',
    contactAddressId: contactAddressId || addr?.id || '',
    contactName:
      req.contact?.fullName ||
      [req.contact?.firstName, req.contact?.lastName].filter(Boolean).join(' ') ||
      '',
    bookingFormId: req.bookingFormId || '',
    serviceName: req.eventDetail?.eventName || '',
    address: address || '',
    latitude: addr?.latitude ?? null,
    longitude: addr?.longitude ?? null,
    // Stored as a master-data CODE ("MRN"); resolveRequestTimeWindow matches on
    // code or text, so it round-trips without translating here.
    preferredTimeRange: req.preferredTimeRange || '',
    preferredDate: req.preferredDate ? String(req.preferredDate).split('T')[0] : '',
    assignedUserId: req.assignedUserId || '',
  };
}

async function execCreateBookingRequest(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const existingRequestId = input.existingRequestId ? String(input.existingRequestId) : '';
  let mode = String(input.mode) === 'existing' ? 'existing' : 'new';
  let bookingFormId = input.bookingFormId ? String(input.bookingFormId) : '';
  let address = input.address ? String(input.address) : '';
  let preferredDate = input.preferredDate ? String(input.preferredDate) : '';
  let preferredTimeRange = input.preferredTimeRange ? String(input.preferredTimeRange) : undefined;

  // Continuing a pending request: fill anything the model didn't pass from the
  // request itself. Whatever the caller DID pass wins — re-booking onto a day
  // the operator just freed up has to be able to override the original
  // preferred date without the stored one clobbering it.
  if (existingRequestId) {
    try {
      deps.onActivity?.('Pulling up the request');
      const ctx = await loadRequestContext(existingRequestId);
      if (ctx.contactId) {
        mode = 'existing';
        input = { ...input, contactId: input.contactId || ctx.contactId };
      }
      if (ctx.contactAddressId && !input.contactAddressId) {
        input = { ...input, contactAddressId: ctx.contactAddressId };
      }
      if (!bookingFormId) bookingFormId = ctx.bookingFormId;
      if (!address && ctx.address) {
        address = ctx.address;
        if (ctx.latitude != null && ctx.longitude != null) {
          input = { ...input, latitude: ctx.latitude, longitude: ctx.longitude };
        }
      }
      if (!preferredDate) preferredDate = ctx.preferredDate;
      if (!preferredTimeRange && ctx.preferredTimeRange) preferredTimeRange = ctx.preferredTimeRange;
      // Deliberately NOT hydrating assignedUserId: routing a stored assignee
      // through resolveAssignee would put a manage-all-bookings permission gate
      // in front of continuing someone else's request, where today it simply
      // falls through to the normal auto-assignment.
    } catch {
      // Fall through to the normal validation below, which asks for whatever
      // is genuinely missing rather than failing the whole booking.
    }
  }

  const bypassServiceArea = input.bypassServiceArea === true;
  // Optional explicit assignee ("book it for Chris"). Undefined = leave it to
  // the backend's own smart-assignment/rotation, exactly like the New Booking
  // modal's "Auto" default. resolveAssignee enforces the manage-all-bookings
  // permission when the assignee is someone other than the operator.
  let assignedUserId: string | undefined;
  let assigneeName = '';
  if (input.assignedUserId) {
    const { userId, isOther } = resolveAssignee(input, deps);
    const notEligible = await assigneeNotEligibleResult(userId, isOther, input);
    if (notEligible) return notEligible;
    assignedUserId = userId;
    // Only label a real teammate; "assigned to you" is just the normal case.
    assigneeName = isOther ? assigneeLabel(input, isOther) : '';
  }

  if (!bookingFormId) {
    return { created: false, note: 'Missing the service (bookingFormId). Call list_booking_forms and confirm which service.' };
  }
  // A request Routemize already holds carries the customer's address BY ID, and
  // every downstream step (the request update, GetSlots) works off that id — so
  // asking for the address here would block a re-run on data we are already
  // sitting on, which is precisely what "continue this request" must never do.
  const hasStoredAddress = !!existingRequestId && !!input.contactId && !!input.contactAddressId;
  if (!address && !hasStoredAddress) {
    return {
      created: false,
      note: existingRequestId
        ? 'This request has no service address saved on it, so there is nothing to read - ask the user ' +
          'for the address (and say it is missing from the request, not that you lost it).'
        : 'Missing the service address. Ask the user for it.',
    };
  }
  if (!preferredDate) return { created: false, note: 'Missing the preferred date. Ask the user which day they want.' };

  // Required intake questions gate: collect/validate the form's question
  // answers BEFORE creating anything, so an unanswerable booking never leaves
  // a stranded contact/request behind (QA 86bav5cnw).
  let formQuestions: Routemize_DTOs_BookingFormQuestionDto[] = [];
  try {
    formQuestions = await fetchFormQuestions(bookingFormId);
  } catch {
    // Best-effort - never block a booking on a questions fetch failure.
  }
  let questionAnswers: Record<string, string> = {};
  if (formQuestions.length > 0) {
    const gate = await resolveQuestionAnswers(formQuestions, input, existingRequestId);
    if (gate.blocked) return gate.blocked;
    questionAnswers = gate.answers;
  }

  // Request-mode forms and request-only trigger answers cannot be instant-
  // booked (QA 86bavq4b3): the booking becomes a PENDING REQUEST for manual
  // review - exactly like the public form's forceRequest path - instead of
  // running GetSlots and offering time cards.
  const rawForm = (await fetchRawBookingForms()).find((f) => f.id === bookingFormId);
  const formIsRequestMethod = !!rawForm && rawForm.allowInstantBooking !== true;
  const requestOnlyTrigger = answersTriggerRequestOnly(formQuestions, questionAnswers);
  const isRequestOnly = formIsRequestMethod || requestOnlyTrigger.triggered;

  deps.onActivity?.('Setting up the booking');

  let lat = input.latitude != null ? Number(input.latitude) : null;
  let lng = input.longitude != null ? Number(input.longitude) : null;
  let formatted = address;
  if (address && (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng))) {
    const coords = await geocodeOrThrow(address);
    lat = coords.lat;
    lng = coords.lng;
    formatted = coords.formatted;
  }

  let contactId = input.contactId ? String(input.contactId) : '';
  let contactAddressId = input.contactAddressId ? String(input.contactAddressId) : '';

  if (mode === 'existing') {
    if (!contactId) {
      return { created: false, note: 'Missing contactId for an existing customer. Call find_contact first.' };
    }
    if (!contactAddressId) {
      const c = await ContactService.getApiAppContactGetById(contactId);
      const addr = (c.addresses ?? []).find((a) => a.isActive) ?? (c.addresses ?? [])[0];
      if (addr?.id) {
        contactAddressId = addr.id;
      } else {
        const parts = parseFormattedAddress(formatted);
        const created = await ContactAddressService.postApiAppContactAddressCreate({
          contactId,
          addressLine1: parts.addressLine1,
          city: parts.city,
          state: parts.state,
          zipCode: parts.zipCode,
          country: parts.country,
          latitude: lat,
          longitude: lng,
          isActive: true,
        });
        contactAddressId = created.id ?? '';
      }
    }
  } else {
    const firstName = input.firstName ? String(input.firstName).trim() : '';
    const lastName = input.lastName ? String(input.lastName).trim() : '';
    const email = input.email ? String(input.email).trim() : '';
    const phone = input.phone ? String(input.phone).trim() : '';
    if (!firstName || !lastName || !email || !phone) {
      return {
        created: false,
        note:
          'A new customer needs a first name, last name, email, and phone. Ask for whatever is missing ' +
          '(or reopen the booking form with collect_booking).',
      };
    }
    // Enforce the same format rules as the New Booking modal so a malformed
    // email/phone/name never reaches the contact record (where the backend can
    // silently drop it and the appointment ends up missing contact details).
    const invalid: string[] = [];
    if (!isValidName(firstName) || !isValidNameLength(firstName)) invalid.push(`first name "${firstName}" (letters, spaces, hyphens, apostrophes only; max ${MAX_NAME_LENGTH} chars)`);
    if (!isValidName(lastName) || !isValidNameLength(lastName)) invalid.push(`last name "${lastName}" (letters, spaces, hyphens, apostrophes only; max ${MAX_NAME_LENGTH} chars)`);
    if (!isValidEmail(email)) invalid.push(`email "${email}" (not a valid address)`);
    if (!isTenDigitPhone(phone)) invalid.push(`phone "${phone}" (needs a 10-digit number)`);
    if (invalid.length > 0) {
      return {
        created: false,
        invalidFields: invalid,
        note:
          `These customer details are not valid: ${invalid.join('; ')}. NOTHING was created. Tell the user ` +
          'exactly which field(s) need fixing and reopen the booking form with collect_booking (pre-fill ' +
          'the valid fields) - do not guess corrections yourself.',
      };
    }
    const contactDto: Routemize_DTOs_CreateContactDTO = {
      firstName,
      lastName,
      email,
      phoneNumber: phone,
      isFavorite: false,
      addresses: [],
    };
    const contact = await ContactService.postApiAppContactCreate(contactDto);
    contactId = contact.id ?? '';
    if (!contactId) return { created: false, note: 'Failed to create the new contact. Ask the user to re-check the details.' };
    const parts = parseFormattedAddress(formatted);
    const createdAddr = await ContactAddressService.postApiAppContactAddressCreate({
      contactId,
      addressLine1: parts.addressLine1,
      city: parts.city,
      state: parts.state,
      zipCode: parts.zipCode,
      country: parts.country,
      latitude: lat,
      longitude: lng,
      isActive: true,
    });
    contactAddressId = createdAddr.id ?? '';
  }

  if (!contactId || !contactAddressId) {
    return { created: false, note: 'Could not resolve the customer or their address. Ask the user to re-enter the details.' };
  }

  // Mirror the New Booking modal's pre-GetSlots setup exactly:
  // 1. A solo (non-round-robin) form must carry a concrete assignedUserId (the
  //    form's first user) on the request - without one the engine has no
  //    calendar to search and returns zero slots even on a wide-open day.
  //    Round-robin forms stay unassigned so backend rotation picks (unless the
  //    user explicitly chose a teammate, which always wins).
  if (!assignedUserId && !isRequestOnly) {
    const isRoundRobin = rawForm?.eventNature === Routemize_Constants_EventNature.ROUND_ROBIN;
    if (!isRoundRobin) {
      try {
        const formUsers = await BookingFormUserService.getApiAppBookingFormUserGetFormUsers(bookingFormId);
        assignedUserId = formUsers?.[0]?.id || undefined;
      } catch {
        // Leave unset; the backend may still resolve it.
      }
    }
  }
  // 2. The preferred time is stored as the master-data option CODE with an
  //    explicit start/end window, exactly like the modal - never the bare word.
  const timeWindow = await resolveRequestTimeWindow(preferredTimeRange);

  let requestId: string | undefined;
  if (existingRequestId) {
    // Continue a pending lead (from find_pending_requests) instead of starting a
    // fresh request - update it in place so its history/origin is preserved.
    const updateDto: Routemize_DTOs_UpdateRequestsDTO = {
      id: existingRequestId,
      contactId,
      bookingFormId,
      contactAddressId,
      preferredDate,
      preferredTimeRange: timeWindow.code,
      preferredStartTime: timeWindow.start,
      preferredEndTime: timeWindow.end,
      scheduleMethod: isRequestOnly ? Routemize_Constants_ScheduleMethod.MANUAL : Routemize_Constants_ScheduleMethod.ROUTEMIZED,
      status: isRequestOnly ? Routemize_Constants_RequestStatus.ACTIVE : Routemize_Constants_RequestStatus.SCHEDULING,
      ...(assignedUserId ? { assignedUserId } : {}),
    };
    const updated = await RequestsService.putApiAppRequestsUpdate(updateDto);
    requestId = updated?.id ?? existingRequestId;
  } else {
    const requestDto: Routemize_DTOs_CreateRequestsDTO = {
      contactId,
      bookingFormId,
      contactAddressId,
      preferredDate,
      preferredTimeRange: timeWindow.code,
      preferredStartTime: timeWindow.start,
      preferredEndTime: timeWindow.end,
      scheduleMethod: isRequestOnly ? Routemize_Constants_ScheduleMethod.MANUAL : Routemize_Constants_ScheduleMethod.ROUTEMIZED,
      status: isRequestOnly ? Routemize_Constants_RequestStatus.ACTIVE : Routemize_Constants_RequestStatus.SCHEDULING,
      origin: Routemize_Constants_RequestOrigin.INTERNAL,
      ...(assignedUserId ? { assignedUserId } : {}),
    };
    const request = await RequestsService.postApiAppRequestsCreate(requestDto);
    requestId = request?.id;
  }
  if (!requestId) return { created: false, note: 'Failed to create/update the booking request.' };

  // Persist the intake answers with the SAME endpoint the public form and New
  // Booking modal use (upsert keyed by requestId+questionId), so they render
  // under "Form Responses" on the request/appointment. Only currently-visible
  // answers are written - stale answers on hidden conditional branches are
  // dropped, mirroring the modal.
  const visibleAnswers: Record<string, string> = {};
  for (const q of formQuestions) {
    if (q.id && questionAnswers[q.id] && isQuestionVisible(q, formQuestions, questionAnswers)) {
      visibleAnswers[q.id] = questionAnswers[q.id];
    }
  }
  if (Object.keys(visibleAnswers).length > 0) {
    try {
      await PublicBookingFormService.postApiAppPublicBookingFormSaveServiceDetails({
        requestId,
        dynamicAnswers: visibleAnswers,
      });
    } catch (err) {
      // Non-fatal, same as the New Booking modal - the booking still proceeds.
      // eslint-disable-next-line no-console
      console.error('[Routy booking] saving question answers failed', { requestId, err });
    }
  }

  // Request-only path (QA 86bavq4b3): the request is saved for MANUAL review
  // and scheduling - no GetSlots, no time cards, no confirm_booking. This is
  // the same outcome the public form and New Booking modal produce for
  // request-method forms and request-only trigger answers.
  if (isRequestOnly) {
    const reason = requestOnlyTrigger.triggered
      ? `the answer "${requestOnlyTrigger.answer}"${requestOnlyTrigger.question ? ` to "${requestOnlyTrigger.question}"` : ''} is flagged for manual review before scheduling`
      : 'this service is set to Request mode (no instant booking)';
    return {
      created: true,
      requestId,
      requestOnly: true,
      reason,
      note:
        `SUBMITTED AS A PENDING REQUEST, NOT AN INSTANT BOOKING: ${reason}, so no time slots are offered ` +
        'and confirm_booking must NOT be called. The request is saved with all the details (visible on the ' +
        'Requests page) for the team to review and schedule. Tell the user that plainly - their request is ' +
        'in, why it needs manual scheduling, and that the team will follow up with a time. Do NOT retry ' +
        'for slots or reroute this to a job stop.',
    };
  }

  // Normalize the request EXACTLY like the Requests-tab -> Routemize path
  // does right before GetSlots (the path verified working in the field): a
  // PUT carrying the INLINE address fields (UpdateRequestsDTO has them,
  // CreateRequestsDTO does not - the backend's DataGatheringPhase backfills
  // from them), the resolved assignee (Guid.Empty on round-robin so Smart
  // Assignment runs fresh, mirroring the modal), the resolved time window,
  // and the ROUTEMIZED schedule method.
  const addressParts = parseFormattedAddress(formatted);
  const isRoundRobinForm = rawForm?.eventNature === Routemize_Constants_EventNature.ROUND_ROBIN;
  const normalizedAssignee = assignedUserId ?? (isRoundRobinForm ? '00000000-0000-0000-0000-000000000000' : undefined);
  try {
    // eslint-disable-next-line no-console
    console.debug('[Routy booking] PUT requests/update before GetSlots', {
      requestId,
      assignedUserId: normalizedAssignee,
      bookingFormId,
      timeWindow,
    });
    await RequestsService.putApiAppRequestsUpdate({
      id: requestId,
      preferredDate,
      preferredTimeRange: timeWindow.code,
      preferredStartTime: timeWindow.start,
      preferredEndTime: timeWindow.end,
      scheduleMethod: Routemize_Constants_ScheduleMethod.ROUTEMIZED,
      contactAddressId,
      // Only ever ASSERT an address, never blank one: when we are continuing a
      // request whose address we only have by id, sending empty inline fields
      // would wipe the very address the backend backfills the booking from.
      ...(formatted
        ? {
            addressLine1: addressParts.addressLine1,
            city: addressParts.city,
            state: addressParts.state,
            zipCode: addressParts.zipCode,
          }
        : {}),
      bookingFormId,
      assignedUserId: normalizedAssignee,
      // Re-assert the Internal origin on requests Routy itself created so this
      // broad PUT can never reset it - the origin is what labels the request
      // (and the appointment booked from it) as "Internal" across the app
      // (QA 86baummet). Pending leads picked up via existingRequestId keep
      // their original channel (Web/Chatbot/VoiceBot), so origin is omitted.
      ...(existingRequestId ? {} : { origin: Routemize_Constants_RequestOrigin.INTERNAL }),
    });
  } catch (err) {
    // Non-fatal - GetSlots may still succeed from the created row alone.
    // eslint-disable-next-line no-console
    console.error('[Routy booking] pre-GetSlots normalize PUT failed', { requestId, err });
  }

  deps.onActivity?.('Finding available times');
  // GetSlots runs the full scheduling engine and can fail transiently right
  // after the request row is created (the SAME request then works from the
  // Requests page). Retry once, and on repeat failure return a STRUCTURED
  // result carrying the real error + the requestId so the model retries with
  // existingRequestId instead of erroring out or duplicating the request.
  let resp: Routemize_DTOs_SchedulingResponse;
  try {
    resp = await RoutemizationService.getApiAppRoutemizationGetSlotsWithResponse(requestId, bypassServiceArea);
  } catch {
    await new Promise((r) => setTimeout(r, 1500));
    deps.onActivity?.('Retrying the time search');
    try {
      resp = await RoutemizationService.getApiAppRoutemizationGetSlotsWithResponse(requestId, bypassServiceArea);
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'GetSlots failed.';
      // The real backend reason lives in the error BODY (raw is just the HTTP
      // status text) - resolve it like the New Booking modal does.
      const message = resolveSchedulingErrorMessage(err, raw);
      const status = err instanceof ApiError ? err.status : null;
      // eslint-disable-next-line no-console
      console.error('[Routy create_booking_request] GetSlots failed twice', { requestId, status, err });
      return {
        created: true,
        requestId,
        slotsFetchFailed: true,
        error: message,
        ...(status != null ? { httpStatus: status } : {}),
        note:
          `The booking request WAS created and saved (requestId ${requestId}), but fetching the available ` +
          `times failed twice (${status ?? 'no status'}: ${message}). Do NOT create another request. Tell the ` +
          'user the ACTUAL reason above (not a vague "technical issue"), that the request is saved, and offer ' +
          'to try the time search again - if they agree, re-call create_booking_request with the SAME details ' +
          `plus existingRequestId "${requestId}". If it fails AGAIN, offer to open the manual booking form ` +
          `instead (open_manual_booking with requestId "${requestId}" and every detail already collected) so ` +
          'the user can finish scheduling themselves - do NOT route them to support for this; contact_support ' +
          'is a last resort only if the user prefers it or the manual form also fails. NEVER "capture" this ' +
          'failed booking as a job stop/ad-hoc stop (add_stop) - the saved request already holds the lead.',
      };
    }
  }

  // Outside-service-area handling — same policy as the New Booking modal:
  // never silently continue on the first pass. Persist the "Outside Area"
  // flag on the request (so the dashboard badge stays in sync) and make the
  // model warn the user; only an explicit go-ahead re-runs with the bypass.
  const outsideServiceArea = resp?.travelDistanceExceededAction !== undefined && resp?.travelDistanceExceededAction !== null;
  if (outsideServiceArea) {
    try {
      await RequestsService.putApiAppRequestsUpdate({
        id: requestId,
        rejectionReason: Routemize_Constants_SlotRejectionReason.OUTSIDE_SERVICE_AREA,
      });
    } catch {
      // Best-effort, same as the modal - the badge just may lag.
    }
  }
  if (outsideServiceArea && !bypassServiceArea) {
    // The response's error/summary text here is the CUSTOMER-facing message the
    // tenant configured in Route Preferences ("Hi, this appointment is outside
    // of our service radius, please submit your request..."). Quoting it back
    // to the OPERATOR reads awkward and buries the actual choices (QA
    // 86bavtp3c), so it is deliberately NOT passed to the model - the reply
    // goes straight to the options.
    return {
      created: true,
      requestId,
      outsideServiceArea: true,
      note:
        'STOP - this address is OUTSIDE the configured service area/territory, so no times were offered. ' +
        'Reply with ONE short sentence stating that, then go STRAIGHT to the choices - e.g. "This address ' +
        'is outside your service area. Want me to book it anyway as an override, use a different address, ' +
        'or skip it?". Do NOT quote or paraphrase any system/customer-facing message. ONLY if ' +
        `they explicitly say yes to the override, call create_booking_request again with existingRequestId ` +
        `"${requestId}", the same details, and bypassServiceArea=true. ` +
        'Never book an out-of-area address without this explicit go-ahead.',
    };
  }

  const options = categorizeSlots(resp?.slots ?? [], deps.env.companyTimezone);

  const forms = await fetchPublishedForms();
  const form = forms.find((f) => f.id === bookingFormId);
  const formName = form?.name || 'Service';

  if (options.length === 0) {
    // The engine can report WHY inside a 200 (daily cap reached, scheduling
    // errors) - surface that real reason instead of pretending the day is
    // simply full. Falls through to plain no-availability when there are no
    // meaningful errors.
    const engineError = (resp?.errors ?? [])
      .map((e) => e.message || '')
      .find((m) => m && !isOpaqueBackendMessage(m) && !isRawTechnicalError(m));
    // "No slots" most often means the assignee simply doesn't work that
    // weekday, which the engine reports the same way as a genuinely full day.
    // Resolve the requested date against their availability so the reply names
    // the real cause instead of blaming whatever else sits on the calendar.
    const scheduleByDay = await fetchWorkScheduleByDay(assignedUserId);
    const preferredDay = parseLocalDateTime(preferredDate);
    const preferredHours = preferredDay ? getWorkingHoursForDate(preferredDay, scheduleByDay) : null;
    // Hand the model verified weekday+date labels so its "did you mean
    // [weekday], [date]?" follow-up can never mislabel a weekday.
    const dateFacts = await describeNonWorkingDayAlternatives(preferredDate, deps, scheduleByDay);
    return {
      created: true,
      requestId,
      found: 0,
      ...(assigneeName ? { assignedTo: assigneeName } : {}),
      ...(engineError ? { engineError } : {}),
      ...(preferredHours ? { requestedDateIsWorkDay: preferredHours.isWorkDay } : {}),
      note:
        (preferredHours && !preferredHours.isWorkDay && preferredDay
          ? 'The request was created but the requested date is NOT a working day, which is why there were no ' +
            'times to offer - do not blame appointments or synced calendar events. ' +
            describeDayOff(preferredHours, preferredDay, assigneeName ? `${assigneeName}'s` : 'your') +
            ' Offer a working day instead, then call create_booking_request again.'
          : engineError
            ? `The request was created but the scheduling engine reported: "${engineError}". Tell the user this ` +
              'ACTUAL reason. If it is fixable by a different day/preference, offer that and re-call ' +
              `create_booking_request with existingRequestId "${requestId}".`
            : `The request was created but the engine found no open slots${assigneeName ? ` on ${assigneeName}'s schedule` : ''} ` +
              'for that date/preference. Suggest a different day or a wider time preference, then call ' +
              'create_booking_request again.') +
        ' NEVER reroute this booking to a job stop/ad-hoc stop - the saved request already holds the lead.' +
        dateFacts,
    };
  }

  deps.onArtifact?.({
    type: 'slot_options',
    kind: 'booking',
    requestId,
    bookingSummary:
      (assigneeName ? `${formName} · ${assigneeName}` : formName) +
      (outsideServiceArea ? ' · Outside service area' : ''),
    durationMinutes: form?.durationMinutes ?? undefined,
    options,
  });
  return {
    created: true,
    requestId,
    found: options.length,
    service: formName,
    ...(assigneeName ? { assignedTo: assigneeName } : {}),
    ...(outsideServiceArea ? { outsideServiceArea: true } : {}),
    options: options.map((o) => ({
      slotSuggestionId: o.slotId,
      category: o.category,
      date: o.date,
      startTime: formatTimeString(o.startTime),
    })),
    note:
      'The card shows the top 3 ranked times with a "show more" expander for the rest - your reply must be ' +
      'ONE short sentence (e.g. "Here are the best times - the Top Choice adds the least driving.") and must ' +
      'NOT list or restate the dates/times in text; the card is the detail. When the user picks a card, call ' +
      'confirm_booking with this requestId and that slotSuggestionId.' +
      (assigneeName ? ` These times are from ${assigneeName}'s schedule and the appointment will be assigned to them.` : '') +
      (outsideServiceArea
        ? ' Remind the user once that this address is outside the service area and stays flagged "Outside Area" (they chose to override).'
        : ''),
  };
}

async function execConfirmBooking(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const requestId = String(input.requestId);
  const slotSuggestionId = String(input.slotSuggestionId);
  const bookingFormId = input.bookingFormId ? String(input.bookingFormId) : undefined;
  const requestedRaw = input.notifyChannel ? String(input.notifyChannel) : 'both';
  const accessCode = input.accessCode ? String(input.accessCode).trim() : '';
  const internalNotes = input.notes ? String(input.notes).trim() : '';

  deps.onActivity?.('Preparing the booking');
  let dateLabel = '';
  let timeLabel = '';
  let slotAssignedUserId = '';
  let slotAssigneeName = '';
  try {
    const resp = await RoutemizationService.getApiAppRoutemizationGetSlotsWithResponse(requestId);
    const slot = (resp?.slots ?? []).find((s) => s.id === slotSuggestionId);
    if (slot?.suggestedDate && slot.startTime) {
      const start = parseLocalDateTime(slot.suggestedDate, normSlotTime(slot.startTime));
      dateLabel = start ? formatDateMedium(start) : slot.suggestedDate;
      timeLabel = formatTimeString(normSlotTime(slot.startTime));
    }
    slotAssignedUserId = slot?.assignedUserId ?? '';
    // Show who the job is assigned to on the confirm card when it's a teammate.
    if (slotAssignedUserId && slotAssignedUserId !== deps.env.currentUserId) {
      slotAssigneeName =
        slot?.userDetail?.name ||
        [slot?.userDetail?.firstName, slot?.userDetail?.lastName].filter(Boolean).join(' ');
    }
  } catch {
    // Best-effort display; the backend still books from the ids.
  }

  // Display fallback (QA 86bavq6cj): when the slot re-fetch fails or returns
  // regenerated ids, the confirm card used to render with NO date/time lines
  // at all - just the header and the notify toggle. The model already knows
  // the chosen slot's date/time from the slot card, so it passes them along
  // for display and the card is never blank.
  if (!dateLabel && input.date) {
    const d = parseLocalDateTime(String(input.date));
    if (d) dateLabel = formatDateMedium(d);
  }
  if (!timeLabel && input.startTime) {
    timeLabel = formatTimeString(normSlotTime(String(input.startTime)));
  }

  // 'none' is a distinct "don't notify at all" choice, not a NotifyChannel -
  // only downgrade the SMS-bearing channels through resolveNotifyChannel.
  const smsRequested = requestedRaw === 'sms' || requestedRaw === 'both';
  const smsUnavailable = smsRequested && !deps.env.smsProviderConnected;
  const channel: 'sms' | 'email' | 'both' | 'none' =
    requestedRaw === 'none' ? 'none' : smsUnavailable ? 'email' : (requestedRaw as 'sms' | 'email' | 'both');
  const channelLabel = channel === 'sms' ? 'SMS' : channel === 'email' ? 'email' : channel === 'none' ? 'none' : 'SMS & email';
  // Booking confirmations are expected, so the toggle starts on (but is still an
  // explicit, visible opt-in the user can turn off).
  let willNotify = channel !== 'none';

  const approved = await deps.requestConfirmation({
    title: 'Book this appointment?',
    confirmLabel: 'Book it',
    lines: [
      ...(dateLabel ? [{ icon: 'calendar' as const, text: dateLabel }] : []),
      ...(timeLabel ? [{ icon: 'clock' as const, text: timeLabel }] : []),
      ...(slotAssigneeName ? [{ icon: 'user' as const, text: `Assigned to: ${slotAssigneeName}` }] : []),
      ...(accessCode ? [{ icon: 'map' as const, text: `Gate/access code: ${accessCode}` }] : []),
      ...(internalNotes ? [{ icon: 'bell' as const, text: `Note: ${internalNotes}` }] : []),
      ...(smsUnavailable ? [SMS_UNAVAILABLE_LINE] : []),
    ],
    notify: { channelLabel, defaultOn: channel !== 'none', onDecision: (v) => { willNotify = v; } },
  });
  if (!approved) return { booked: false, note: 'The user declined. Do not retry without new instructions.' };

  const notifySms = willNotify && (channel === 'sms' || channel === 'both');
  const notifyEmail = willNotify && (channel === 'email' || channel === 'both');

  // Mirror the New Booking modal: write the chosen slot's assigned user onto
  // the request before creating the appointment, so the booking lands on that
  // person's calendar (required by the backend when slots span team members).
  if (slotAssignedUserId) {
    try {
      await RequestsService.putApiAppRequestsUpdate({ id: requestId, assignedUserId: slotAssignedUserId });
    } catch {
      // Non-fatal: the backend can still resolve the assignee from the slot.
    }
  }

  deps.onActivity?.(willNotify ? 'Booking and sending the confirmation' : 'Creating the appointment');
  const dto: Routemize_DTOs_CreateAppointmentDTO = {
    requestId,
    slotSuggestionId,
    sendSmsConfirmation: notifySms,
    sendEmailConfirmation: notifyEmail,
    sendSmsReminders: notifySms,
    sendEmailReminders: notifyEmail,
    bookingFormId,
    // Gate/access code lands on the appointment's dedicated field, same as the
    // New Booking modal - never buried in a text note.
    accessCode: accessCode || null,
    // Extra instructions become an internal note on the appointment, written by
    // the create itself (same as the New Booking modal).
    internalNote: internalNotes || null,
  };
  // A failed create must come back STRUCTURED, not as a raw thrown error - a
  // bare error here is what used to send the model improvising "fallbacks"
  // (extra requests, job stops) instead of reporting the failure (QA 86bav5cnw).
  let appointment: Awaited<ReturnType<typeof AppointmentService.postApiAppAppointmentCreate>>;
  try {
    appointment = await AppointmentService.postApiAppAppointmentCreate(dto);
  } catch (err) {
    const raw = err instanceof Error ? err.message : 'Appointment creation failed.';
    const message = resolveSchedulingErrorMessage(err, raw);
    // eslint-disable-next-line no-console
    console.error('[Routy confirm_booking] appointment create failed', { requestId, slotSuggestionId, err });
    return {
      booked: false,
      error: message,
      note:
        `Creating the appointment FAILED (${message}). NO appointment exists, but the booking request ` +
        `(requestId ${requestId}) is still saved as a pending lead. Tell the user the ACTUAL reason above, ` +
        'and offer to try again (re-call confirm_booking once with the same ids). If it fails again, offer ' +
        `to open the manual booking form instead (open_manual_booking with requestId "${requestId}" and ` +
        'every detail already collected) so the user can finish scheduling themselves - do NOT route them ' +
        'to support for this; contact_support is a last resort only if the user prefers it or the manual ' +
        'form also fails. NEVER create a job stop/ad-hoc stop or another request as a substitute for this ' +
        'booking.',
    };
  }

  // The note was saved with the appointment (CreateAppointmentDTO.internalNote).
  const noteSaved = !!internalNotes;

  // The receipt is a card, not a paragraph - the model's text stays to one
  // short sentence.
  deps.onArtifact?.({
    type: 'booking_confirmed',
    title: appointment?.title ?? null,
    dateLabel: dateLabel || null,
    timeLabel: timeLabel || null,
    assignedTo: slotAssigneeName || null,
    notifiedVia: willNotify ? channelLabel : null,
    accessCode: accessCode || null,
  });

  return {
    booked: true,
    appointmentId: appointment?.id ?? null,
    date: dateLabel,
    time: timeLabel,
    ...(slotAssigneeName ? { assignedTo: slotAssigneeName } : {}),
    ...(accessCode ? { accessCodeSaved: true } : {}),
    ...(internalNotes ? { noteSaved } : {}),
    notified: willNotify,
    note:
      [
        'A "Booked" receipt card is shown with all the details - your reply must be ONE short warm sentence ' +
          '(e.g. "You\'re all set!") and must NOT restate the date/time/assignee/details in text.',
        willNotify && smsUnavailable
          ? 'No SMS provider is connected for this tenant, so the confirmation was sent by email instead of text - mention that substitution once.'
          : '',
      ]
        .filter(Boolean)
        .join(' '),
  };
}

/** Normalize a smart-fill candidate DTO into the ranking shape. */
function toRescheduleCandidate(d: {
  id?: string;
  contactName?: string | null;
  status?: unknown;
  meetingStartTimeUtc?: string;
  meetingEndTimeUtc?: string;
  latitude?: number | null;
  longitude?: number | null;
  timeAtLocationMinutes?: number | null;
  preBufferMinutes?: number | null;
  postBufferMinutes?: number | null;
  source?: string | null;
  preferredTimeLabel?: string | null;
}): SlotRescheduleCandidate {
  return {
    id: d.id as string,
    contactName: d.contactName || 'Appointment',
    status: String(d.status ?? ''),
    meetingStartUtc: parseBackendDate(d.meetingStartTimeUtc) ?? new Date(0),
    meetingEndUtc: parseBackendDate(d.meetingEndTimeUtc) ?? undefined,
    latitude: d.latitude as number,
    longitude: d.longitude as number,
    timeAtLocationMinutes: d.timeAtLocationMinutes ?? 60,
    preBufferMinutes: d.preBufferMinutes ?? 0,
    postBufferMinutes: d.postBufferMinutes ?? 0,
    source: d.source ?? null,
    preferredTimeLabel: d.preferredTimeLabel ?? null,
  };
}

async function execSmartFillGap(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const tenantNow = tenantShiftedNow(deps.env.companyTimezone);
  const date = input.date ? String(input.date) : toDateKey(tenantNow);
  const day = parseLocalDateTime(date);
  if (!day) throw new Error('Invalid date.');
  const { userId, isOther } = resolveAssignee(input, deps);

  deps.onActivity?.(`Looking for gaps on ${date}${isOther ? ` for ${assigneeLabel(input, isOther)}` : ''}`);
  const scheduleByDay = await fetchWorkScheduleByDay(userId);
  const hours = getWorkingHoursForDate(day, scheduleByDay);
  if (!hours.isWorkDay) return { found: 0, note: `${formatDateMedium(day)} is not a working day.` };

  const appts = await fetchDayAppointments(userId, day, deps.env.companyTimezone);
  const gaps = findOpenGaps(appts, day, hours.start, hours.end, tenantNow).filter((g) => g.durationMinutes >= 30);
  if (gaps.length === 0) return { found: 0, note: 'No open gaps of 30+ minutes remain on that day.' };

  let gap = [...gaps].sort((a, b) => b.durationMinutes - a.durationMinutes)[0];
  if (input.gapStartTime) {
    const target = parseLocalDateTime(date, String(input.gapStartTime));
    if (target) {
      const match = gaps.find(
        (g) =>
          (target.getTime() >= g.startTime.getTime() && target.getTime() < g.endTime.getTime()) ||
          Math.abs(g.startTime.getTime() - target.getTime()) <= 30 * 60_000,
      );
      if (match) gap = match;
    }
  }

  deps.onActivity?.('Finding appointments that fit');
  const mgr = new AppointmentManager();
  const from = new Date();
  const to = new Date(from);
  to.setDate(to.getDate() + 90);
  const dtos = await mgr.getSlotRescheduleCandidates(userId, from, to);
  const candidates = dtos
    .filter((d) => d.id && d.latitude != null && d.longitude != null && !(d.latitude === 0 && d.longitude === 0))
    .map(toRescheduleCandidate)
    // External calendar copies (Google/Outlook/Zoom) can't be moved through
    // Routemize - never offer one as a "move into this gap" candidate.
    .filter((c) => !c.source || !EXTERNAL_APPOINTMENT_SOURCES.has(c.source));

  const { start: routeStart, end: routeEnd } = await resolveRouteEndpointsFor(userId, deps);
  const before = gap.prevCoords ?? routeStart ?? null;
  const after = gap.nextCoords ?? routeEnd ?? null;
  const ranked = await rankCandidatesForSlot({
    candidates,
    before,
    after,
    previousStopName: gap.prevLabel ?? undefined,
    nextStopName: gap.nextLabel ?? undefined,
    excludeAppointmentIds: appts.map((a) => a.id).filter((id): id is string => !!id),
    totalGapMinutes: gap.durationMinutes,
    slotDate: day,
  });

  const top = ranked.scheduled.slice(0, 5);
  if (top.length === 0) {
    return { found: 0, note: 'No nearby appointments fit that gap — nothing close enough to move without adding a lot of driving.' };
  }

  const fillCandidates: RoutyFillCandidate[] = top.map((c) => {
    const driveIn = c.driveFromPrevMinutes ?? 0;
    const proposedStart = roundToStepMinutes(addMinutes(gap.startTime, driveIn), 5, 'up');
    const startLabel = formatDateMedium(c.meetingStartUtc);
    const timeLbl = formatTimeString(formatTimeOfDay24(c.meetingStartUtc, deps.env.companyTimezone));
    return {
      appointmentId: c.id,
      contactName: c.contactName,
      currentLabel: `${startLabel} · ${timeLbl}`,
      status: c.status,
      proximityMiles: c.proximityMiles ?? null,
      driveInMinutes: c.driveFromPrevMinutes != null ? Math.round(c.driveFromPrevMinutes) : null,
      driveOutMinutes: c.driveToNextMinutes != null ? Math.round(c.driveToNextMinutes) : null,
      proposedStartTime: formatTimeOfDay24(proposedStart),
      durationMinutes: c.timeAtLocationMinutes || 60,
    };
  });

  deps.onArtifact?.({
    type: 'fill_candidates',
    date,
    gapStartTime: formatTimeOfDay24(gap.startTime),
    gapEndTime: formatTimeOfDay24(gap.endTime),
    candidates: fillCandidates,
  });

  return {
    found: fillCandidates.length,
    gap: {
      date,
      start: formatTimeString(formatTimeOfDay24(gap.startTime)),
      end: formatTimeString(formatTimeOfDay24(gap.endTime)),
      durationMinutes: gap.durationMinutes,
    },
    candidates: fillCandidates.map((c) => ({
      contactName: c.contactName,
      currentLabel: c.currentLabel,
      proposedStartTime: formatTimeString(c.proposedStartTime),
      proximityMiles: c.proximityMiles,
    })),
    note:
      'Nearby appointments that could fill the gap are shown, ranked by least added driving. Recommend the ' +
      'top one; when the user picks a card, call confirm_fill.',
  };
}

async function execConfirmFill(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const appointmentId = String(input.appointmentId);
  const date = String(input.date);
  const newStartTime = String(input.newStartTime);
  const wantsNotify = input.notifyCustomer === true;
  let willNotify = false;

  const startLocal = parseLocalDateTime(date, newStartTime);
  if (!startLocal) throw new Error('Invalid date/time. Use YYYY-MM-DD and HH:MM.');

  deps.onActivity?.('Preparing to move the appointment');
  const current = await AppointmentService.getApiAppAppointmentGetById(appointmentId);
  const hasCustomer = !!current.contactId;
  let duration = input.durationMinutes ? Number(input.durationMinutes) : 0;
  if (!duration) {
    const s = parseBackendDate(current.startTimeUtc);
    const e = parseBackendDate(current.endTimeUtc);
    duration = s && e ? getTimeDifferenceInMinutes(s, e) : 60;
  }
  const endStr = formatTimeString(formatTimeOfDay24(addMinutes(startLocal, duration)));

  const { notify, effectiveChannel, warningLine } = buildNotifyOption(deps, 'both', wantsNotify, (v) => { willNotify = v; });
  const approved = await deps.requestConfirmation({
    title: 'Move this into your gap?',
    confirmLabel: 'Move it',
    lines: [
      { icon: 'user', text: current.title || 'this appointment' },
      { icon: 'calendar', text: formatDateMedium(startLocal) },
      { icon: 'clock', text: `${formatTimeString(newStartTime)} – ${endStr} · ${formatDurationMinutes(duration)}` },
      ...(hasCustomer ? [] : [{ icon: 'bell' as const, text: 'Ad-hoc stop — no customer to notify' }]),
      ...(hasCustomer && warningLine ? [warningLine] : []),
    ],
    notify: hasCustomer ? notify : undefined,
  });
  if (!approved) return { moved: false, note: 'The user declined. Do not retry without new instructions.' };

  const endTime = formatTimeOfDay24(addMinutes(startLocal, duration));
  const startUtc = tenantLocalToUtcDate(date, newStartTime, deps.env.companyTimezone);
  const endUtc = tenantLocalToUtcDate(date, endTime, deps.env.companyTimezone);
  deps.onActivity?.(willNotify ? 'Moving and notifying the customer' : 'Moving the appointment');
  const { restoredToScheduled } = await applyAppointmentUpdate(
    appointmentId,
    { startUtc, endUtc, notifyChannel: willNotify ? effectiveChannel : undefined },
    deps,
  );

  return {
    moved: true,
    appointmentId,
    date,
    newStartTime: formatTimeString(newStartTime),
    notified: willNotify,
    notifiedVia: willNotify ? effectiveChannel : null,
    ...(restoredToScheduled
      ? {
          restoredToScheduled: true,
          note: 'This appointment was Cancelled/No-Show and moving it RESTORED it to Scheduled - tell the user it is back on the calendar as Scheduled.',
        }
      : {}),
  };
}

async function execSetReminders(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const tenantNow = tenantShiftedNow(deps.env.companyTimezone);
  const date = input.date ? String(input.date) : toDateKey(tenantNow);
  const day = parseLocalDateTime(date);
  if (!day) throw new Error('Invalid date.');
  const channel = (input.channel ? String(input.channel) : 'both') as 'sms' | 'email' | 'both';
  const enabled = input.enabled === undefined ? true : Boolean(input.enabled);
  const { userId, isOther } = resolveAssignee(input, deps);

  deps.onActivity?.(`Checking appointments on ${date}${isOther ? ` for ${assigneeLabel(input, isOther)}` : ''}`);
  const appts = (await fetchDayAppointments(userId, day, deps.env.companyTimezone)).filter(
    (a): a is Appointment & { id: string } => !!a.id && isRoutemizeManaged(a),
  );
  if (appts.length === 0) return { updated: 0, note: 'No appointments on that day to update.' };

  // Turning SMS reminders ON is a no-op with no provider connected - never
  // silently claim they were enabled.
  const smsUnavailable = enabled && (channel === 'sms' || channel === 'both') && !deps.env.smsProviderConnected;
  const channelLabel = channel === 'both' ? 'SMS + email' : channel === 'sms' ? 'SMS' : 'email';
  const approved = await deps.requestConfirmation({
    title: `${enabled ? 'Turn on' : 'Turn off'} reminders?`,
    confirmLabel: enabled ? 'Turn on reminders' : 'Turn off reminders',
    lines: [
      ...(isOther ? [{ icon: 'user' as const, text: `For: ${assigneeLabel(input, isOther)}` }] : []),
      { icon: 'calendar', text: formatDateMedium(day) },
      {
        icon: 'bell',
        text: `${channelLabel} reminders ${enabled ? 'ON' : 'OFF'} for ${appts.length} appointment${
          appts.length > 1 ? 's' : ''
        }`,
      },
      ...(smsUnavailable ? [SMS_UNAVAILABLE_LINE] : []),
    ],
  });
  if (!approved) return { updated: 0, note: 'The user declined.' };

  const sms = channel === 'sms' || channel === 'both' ? (smsUnavailable ? undefined : enabled) : undefined;
  const email = channel === 'email' || channel === 'both' ? enabled : undefined;
  deps.onActivity?.(`Updating ${appts.length} appointment${appts.length > 1 ? 's' : ''}`);
  let updated = 0;
  for (const a of appts) {
    await applyAppointmentUpdate(a.id, { sendSmsReminders: sms, sendEmailReminders: email }, deps);
    updated++;
  }
  return {
    updated,
    channel,
    enabled,
    note: smsUnavailable
      ? `No SMS provider is connected for this tenant, so SMS reminders were NOT turned on${channel === 'both' ? ' (email reminders were)' : ''} - tell the user they need to connect one in Settings > Integrations first.`
      : undefined,
  };
}

async function execFindOpenDay(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const startDate = String(input.startDate);
  const start = parseLocalDateTime(startDate);
  if (!start) throw new Error('Invalid startDate. Use YYYY-MM-DD.');

  const rawCount = input.dayCount != null ? Number(input.dayCount) : 10;
  const dayCount = Math.min(21, Math.max(1, Number.isFinite(rawCount) ? Math.round(rawCount) : 10));
  const matchTitle = input.matchTitle ? String(input.matchTitle).toLowerCase() : '';
  const matchAddress = input.matchAddress ? String(input.matchAddress).toLowerCase() : '';
  const { userId, isOther } = resolveAssignee(input, deps);

  deps.onActivity?.(`Checking ${isOther ? `${assigneeLabel(input, isOther)}'s` : 'your'} upcoming days`);
  const scheduleByDay = await fetchWorkScheduleByDay(userId);

  const days: Array<{
    date: string;
    weekday: string;
    isWorkDay: boolean;
    appointmentCount: number;
    hasMatch: boolean;
  }> = [];

  for (let i = 0; i < dayCount; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    const hours = getWorkingHoursForDate(d, scheduleByDay);
    const appts = await fetchDayAppointments(userId, d, deps.env.companyTimezone);
    const hasMatch =
      matchTitle || matchAddress
        ? appts.some((a) => {
            const t = `${a.type ?? ''} ${a.customerName ?? ''}`.toLowerCase();
            const addr = (a.address ?? '').toLowerCase();
            return (matchTitle && t.includes(matchTitle)) || (matchAddress && addr.includes(matchAddress));
          })
        : appts.length > 0;
    days.push({
      date: toDateKey(d),
      weekday: WEEKDAY_NAMES[d.getDay()],
      isWorkDay: hours.isWorkDay,
      appointmentCount: appts.length,
      hasMatch,
    });
  }

  const firstOpen = days.find((x) => !x.hasMatch);
  const firstOpenBusinessDay = days.find((x) => !x.hasMatch && x.isWorkDay);

  return {
    matchedOn:
      matchTitle || matchAddress
        ? { title: input.matchTitle ?? null, address: input.matchAddress ?? null }
        : 'any appointment',
    days,
    firstOpenDay: firstOpen?.date ?? null,
    firstOpenBusinessDay: firstOpenBusinessDay?.date ?? null,
    takenDates: days.filter((x) => x.hasMatch).map((x) => x.date),
    note:
      'Propose firstOpenDay (mention if it is a weekend/non-working day and offer firstOpenBusinessDay). ' +
      'If the day is unclear, there is a conflict, or the user should decide, call pick_date with these as ' +
      'suggestions and takenDates flagged.',
  };
}

async function execPickDate(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const suggested = Array.isArray(input.suggestedDates) ? (input.suggestedDates as Record<string, unknown>[]) : [];
  const options = suggested
    .map((s) => ({
      date: typeof s.date === 'string' ? s.date : '',
      label: typeof s.label === 'string' ? s.label : '',
      note: typeof s.note === 'string' ? s.note : undefined,
    }))
    .filter((s) => s.date && s.label);
  const takenDates = Array.isArray(input.takenDates)
    ? (input.takenDates as unknown[]).map(String).filter(Boolean)
    : undefined;

  deps.onArtifact?.({
    type: 'date_picker',
    prompt: input.prompt ? String(input.prompt) : undefined,
    purpose: input.purpose ? String(input.purpose) : undefined,
    options: options.length ? options : undefined,
    defaultDate: input.defaultDate ? String(input.defaultDate) : undefined,
    minDate: input.minDate ? String(input.minDate) : undefined,
    takenDates,
  });

  return {
    shown: true,
    note:
      'A date picker is shown to the user. Your text reply must say why you need them to pick (e.g. the ' +
      'ambiguity or conflict) - never just "Done". Then STOP and wait - their next message contains the ' +
      'chosen day; continue the action (e.g. add the stop) on that day.',
  };
}

/**
 * Resolve the UTC instant that starts a "how far back to look for new
 * bookings" window. This is about WHEN A RECORD WAS CREATED (an audit/booking
 * timestamp), a completely different axis from occurrence date - so 'today'
 * means "created since tenant-local midnight", not "occurring today".
 */
function creationWindowStartUtc(window: string, timezone: string | undefined): Date {
  if (window === 'last-7-days') return new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  if (window === 'last-30-days') return new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const tenantNow = tenantShiftedNow(timezone);
  if (window === 'this-week') {
    const weekStart = new Date(tenantNow);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    return tenantLocalToUtcDate(toDateKey(weekStart), '00:00', timezone);
  }
  return tenantLocalToUtcDate(toDateKey(tenantNow), '00:00', timezone);
}

// Mirrors the dashboard's "Booked Today" widget (DashboardPage.tsx): every
// "Add a Stop" appointment carries this synthetic event type id, or a
// `stopLabel` for custom stop types. Ad-hoc stops are noise
// in a "who just booked?" answer (a tech adding an errand to their own route),
// and the widget drops them - so the tool must too, or the counts won't match.
const INTERNAL_STOP_EVENT_TYPE_ID = 'a1b2c3d4-e5f6-7890-abcd-ef1234567890';

async function execFindRecentlyBooked(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const windowInput = input.window ? String(input.window) : 'today';
  const window = (['today', 'this-week', 'last-7-days', 'last-30-days'].includes(windowInput) ? windowInput : 'today') as
    | 'today'
    | 'this-week'
    | 'last-7-days'
    | 'last-30-days';
  const maxResults = input.maxResults ? Math.max(1, Math.min(30, Number(input.maxResults))) : 15;

  // A specific person filter is allowed for yourself always; anyone else
  // requires manage-all-bookings, same rule every other assignedUserId tool enforces.
  const requestedUserId = input.assignedUserId ? String(input.assignedUserId).trim() : '';
  if (requestedUserId && requestedUserId !== deps.env.currentUserId && !deps.env.canManageAllBookings) {
    throw new Error(
      "You don't have permission to view other team members' bookings - only your own. Tell the user this " +
        'plainly and offer to check their own bookings instead.',
    );
  }

  deps.onActivity?.('Checking who just booked');
  const manager = new AppointmentManager(deps.env.companyTimezone);
  // dateFilter 'all' + client-side creationTime filtering - same pattern the
  // dashboard's "Booked Today" widget uses, since the server-side dateFilter
  // scopes by OCCURRENCE date, not creation date.
  const result = await manager.searchAppointments({ maxResultCount: 500, dateFilter: 'all', sorting: 'creationTime desc' });
  const items = (result.items as Routemize_DTOs_AppointmentListItem[] | undefined) ?? [];

  const windowStart = creationWindowStartUtc(window, deps.env.companyTimezone);
  let recent = items.filter((apt) => {
    if (apt.isExternal) return false;
    // Soft-deleted records (cancelled recurring occurrences, archived) are
    // still returned by the search API - the dashboard's Booked Today widget
    // excludes them, so exclude them here too or the counts won't match.
    const status = apt.status?.toString();
    if (status === Routemize_Constants_AppointmentStatus.CANCELLED || status === Routemize_Constants_AppointmentStatus.ARCHIVED) return false;
    const created = parseBackendDate(apt.creationTime);
    return !!created && created.getTime() >= windowStart.getTime();
  });

  // Team members without manage-all-bookings only ever see their own bookings
  // (matches the dashboard's Booked Today scoping for non-admins).
  if (!deps.env.canManageAllBookings) {
    recent = recent.filter((apt) => apt.attendees?.some((a) => a.id === deps.env.currentUserId));
  } else if (requestedUserId) {
    recent = recent.filter((apt) => apt.attendees?.some((a) => a.id === requestedUserId));
  }

  recent.sort((a, b) => (b.creationTime ?? '').localeCompare(a.creationTime ?? ''));

  const windowLabel =
    window === 'today' ? 'today' : window === 'this-week' ? 'this week' : window === 'last-7-days' ? 'the last 7 days' : 'the last 30 days';

  // Hydrate each booking from the detail endpoint (same as the dashboard's
  // Booked Today widget) - the list item alone has no lead source, no request
  // origin (channel), and no reliable appointment type, and it can't tell us
  // whether a record is an ad-hoc "Add a Stop" that the widget hides.
  const candidates = recent.slice(0, Math.max(maxResults * 2, 30));
  const [details, leadSourceMasterData] = await Promise.all([
    Promise.all(candidates.map((apt) => (apt.id ? AppointmentService.getApiAppAppointmentGetAppointmentDetail(apt.id).catch(() => null) : null))),
    MasterDataService.getApiAppMasterDataGet('ReferralType').catch(() => []),
  ]);

  const leadSourceCodeToName = new Map<string, string>();
  for (const item of leadSourceMasterData?.[0]?.items ?? []) {
    if (item.code && item.text) leadSourceCodeToName.set(item.code, item.text);
  }
  const detailById = new Map(details.filter((d) => !!d?.id).map((d) => [d!.id as string, d!]));

  const enriched = candidates
    .filter((apt) => {
      const detail = apt.id ? detailById.get(apt.id) : undefined;
      if (!detail) return true;
      const isAdHocStop = detail.eventTypeId === INTERNAL_STOP_EVENT_TYPE_ID || !!detail.stopLabel?.trim();
      return !isAdHocStop;
    })
    .slice(0, maxResults)
    .map((apt) => {
      const detail = apt.id ? detailById.get(apt.id) : undefined;
      const occursAt = parseBackendDate(apt.meetingStartTimeUtc) ?? parseBackendDate(apt.meetingStartTime);
      const occursEnd = parseBackendDate(apt.meetingEndTimeUtc) ?? parseBackendDate(apt.meetingEndTime);
      const bookedAt = parseBackendDate(apt.creationTime);
      const assignee = apt.attendees?.[0];
      const leadSourceCode = detail?.request?.leadSource ?? null;
      return {
        appointmentId: apt.id ?? null,
        contactName: [apt.contactFirstName, apt.contactLastName].filter(Boolean).join(' ') || null,
        service: detail?.request?.eventDetail?.eventName || apt.bookingFormName || apt.eventName || detail?.title || 'Appointment',
        leadSource: leadSourceCode ? leadSourceCodeToName.get(leadSourceCode) || leadSourceCode : null,
        // No request/origin on the detail means it was added internally (the
        // dashboard renders those rows with the INTERNAL badge for the same reason).
        channel: detail ? (detail.request?.origin != null ? String(detail.request.origin) : 'Internal') : null,
        occursDate: occursAt ? formatDateMedium(occursAt) : null,
        occursTime: occursAt ? formatTimeOfDay24(occursAt, deps.env.companyTimezone) : null,
        occursEndTime: occursEnd ? formatTimeOfDay24(occursEnd, deps.env.companyTimezone) : null,
        assignedTo: assignee ? assignee.name || [assignee.firstName, assignee.lastName].filter(Boolean).join(' ') || null : null,
        bookedAgo: bookedAt ? formatRelativeTime(bookedAt, { timezone: deps.env.companyTimezone }) : null,
        status: apt.status ?? null,
        address: apt.addressLine1 || null,
      };
    });

  const tally = (values: (string | null)[]): Record<string, number> => {
    const counts: Record<string, number> = {};
    for (const v of values) {
      const key = v || 'Unknown';
      counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  };

  return {
    window: windowLabel,
    count: enriched.length,
    scope: !deps.env.canManageAllBookings ? 'your own bookings only' : requestedUserId ? 'one team member' : 'the whole company',
    bookings: enriched,
    breakdown: enriched.length
      ? {
          byService: tally(enriched.map((b) => b.service)),
          byLeadSource: tally(enriched.map((b) => b.leadSource)),
          byChannel: tally(enriched.map((b) => b.channel)),
        }
      : null,
    note:
      enriched.length === 0
        ? `No new bookings created ${windowLabel}. This is about when the booking was MADE, not when the ` +
          "appointment happens - if the user expected to see something, double check they don't mean an " +
          'appointment that OCCURS today but was booked earlier (use get_schedule/find_appointments for that).'
        : `These are bookings CREATED ${windowLabel} (a "just booked" list) - the occursDate/occursTime fields ` +
          'are when each appointment will actually happen, which may be a different day. The counts here match ' +
          "the dashboard's Booked Today widget; when summarizing lead sources or service types for what was " +
          'booked, use THESE numbers, not get_business_stats (whose breakdowns cover a different appointment set).',
  };
}

async function execFindAppointments(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const startDate = String(input.startDate);
  const start = parseLocalDateTime(startDate);
  if (!start) throw new Error('Invalid startDate. Use YYYY-MM-DD.');

  const end = input.endDate ? parseLocalDateTime(String(input.endDate)) : null;
  const text = input.text ? String(input.text).toLowerCase() : '';
  const contactName = input.contactName ? String(input.contactName).toLowerCase() : '';
  const { userId, isOther } = resolveAssignee(input, deps);

  // Default to a ~1 week window; cap at 21 days.
  const lastDay =
    end && end.getTime() >= start.getTime()
      ? end
      : (() => {
          const d = new Date(start);
          d.setDate(d.getDate() + 7);
          return d;
        })();
  const rawSpan = Math.round((lastDay.getTime() - start.getTime()) / 86_400_000) + 1;
  const dayCount = Math.min(21, Math.max(1, rawSpan));

  const whose = isOther ? `${assigneeLabel(input, isOther)}'s` : 'your';
  deps.onActivity?.(text || contactName ? `Searching for "${text || contactName}"` : `Searching ${whose} appointments`);

  const matches: Array<{
    appointmentId: string;
    title: string;
    contactName: string | null;
    date: string;
    /** Weekday label for `date` (e.g. "Wed, Sep 23") - the model quotes it, never computes it. */
    dateLabel: string;
    startTime: string;
    endTime: string;
    startLabel: string;
    address: string | null;
    latitude: number | null;
    longitude: number | null;
    status: string | null;
    /** Who the appointment is assigned to ("you" or teammate name(s)). */
    assignedTo: string | null;
  }> = [];

  for (let i = 0; i < dayCount; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    const appts = await fetchDayAppointments(userId, d, deps.env.companyTimezone);
    for (const a of appts) {
      if (!a.id || !isRoutemizeManaged(a)) continue;
      const hay = `${a.type ?? ''} ${a.customerName ?? ''} ${a.address ?? ''}`.toLowerCase();
      if (text && !hay.includes(text)) continue;
      if (contactName && !(a.customerName ?? '').toLowerCase().includes(contactName)) continue;
      const dayKey = toDateKey(d);
      matches.push({
        appointmentId: a.id,
        title: a.type || 'Appointment',
        contactName: a.customerName || null,
        date: dayKey,
        dateLabel: formatDateKeyMedium(dayKey),
        // `a.startDateTime`/`endDateTime` come from `fetchDayAppointments`, which are
        // already tenant-shifted (local getters hold tenant wall-clock digits) — passing
        // `companyTimezone` here would double-shift the displayed time.
        startTime: formatTimeOfDay24(a.startDateTime),
        endTime: formatTimeOfDay24(a.endDateTime),
        startLabel: a.startTime,
        address: a.address || null,
        latitude: a.latitude ?? null,
        longitude: a.longitude ?? null,
        status: a.status ?? null,
        assignedTo: assigneeLabel(input, isOther),
      });
    }
  }

  // Company-wide pass (QA 86bavqwmv): the per-day loop above only sees ONE
  // person's calendar, so an appointment for the named contact assigned to a
  // DIFFERENT team member was invisible — Routy then wrongly reported "no
  // appointments for X that day". When the operator can manage all bookings
  // and asked by contact/text (without pinning a specific assignee), also run
  // the company-wide appointment search (the same endpoint the contact detail
  // page uses) so matches from EVERY member's calendar are included, each
  // tagged with who it is assigned to.
  let searchedWholeCompany = false;
  if ((contactName || text) && deps.env.canManageAllBookings && !input.assignedUserId) {
    searchedWholeCompany = true;
    const startKey = toDateKey(start);
    const endKey = toDateKey(lastDay);
    const seen = new Set(matches.map((m) => m.appointmentId));

    let companyItems: Routemize_DTOs_AppointmentListItem[] = [];
    try {
      if (contactName) {
        // Resolve the contact by name (with the same word-part retry
        // find_contact uses) and pull their appointments by contactId —
        // exact and independent of how the free-text search tokenizes names.
        let contacts = await searchContactsByText(contactName, 3);
        if (contacts.length === 0) {
          for (const token of contactName.split(/\s+/).filter((t) => t.length > 1)) {
            contacts = await searchContactsByText(token, 3);
            if (contacts.length > 0) break;
          }
        }
        for (const c of contacts.slice(0, 3)) {
          if (!c.id) continue;
          const req = {
            maxResultCount: 100,
            skipCount: 0,
            filter: { contactId: c.id },
          } as unknown as Routemize_DTOs_SearchParameters_PagedSortedAndFilteredRequestDto_1;
          const res = await AppointmentService.postApiAppAppointmentSearch(req);
          companyItems.push(...(((res as unknown as { items?: Routemize_DTOs_AppointmentListItem[] })?.items) ?? []));
        }
      } else {
        const manager = new AppointmentManager(deps.env.companyTimezone);
        const res = await manager.searchAppointments({ query: text, dateFilter: 'all', maxResultCount: 200 });
        companyItems = (res.items as Routemize_DTOs_AppointmentListItem[] | undefined) ?? [];
      }
    } catch {
      // Best-effort: the per-day results above still stand.
      searchedWholeCompany = false;
    }

    for (const it of companyItems) {
      if (!it.id || seen.has(it.id) || it.isExternal) continue;
      const startAt = parseBackendDate(it.meetingStartTimeUtc) ?? parseBackendDate(it.meetingStartTime);
      const endAt = parseBackendDate(it.meetingEndTimeUtc) ?? parseBackendDate(it.meetingEndTime);
      if (!startAt) continue;
      const dateKey = dateKeyInTenantTz(startAt, deps.env.companyTimezone);
      if (dateKey < startKey || dateKey > endKey) continue;
      const itContact = [it.contactFirstName, it.contactLastName].filter(Boolean).join(' ').trim();
      const itAddress = [it.addressLine1, it.city, it.state].filter(Boolean).join(', ') || null;
      const itTitle = it.bookingFormName || it.eventName || 'Appointment';
      const hay = `${itTitle} ${itContact} ${itAddress ?? ''}`.toLowerCase();
      if (text && !hay.includes(text)) continue;
      const assignees = (it.attendees ?? [])
        .map((u) => u.name || [u.firstName, u.lastName].filter(Boolean).join(' '))
        .filter(Boolean);
      seen.add(it.id);
      matches.push({
        appointmentId: it.id,
        title: itTitle,
        contactName: itContact || null,
        date: dateKey,
        // From the TENANT-local key - never formatDateMedium(startAt), which uses
        // the browser's local day for a real UTC instant (QA 86bcd7ja3).
        dateLabel: formatDateKeyMedium(dateKey),
        startTime: formatTimeOfDay24(startAt, deps.env.companyTimezone),
        endTime: endAt ? formatTimeOfDay24(endAt, deps.env.companyTimezone) : '',
        startLabel: formatTimeInTenantTz(startAt, deps.env.companyTimezone),
        address: itAddress,
        latitude: it.latitude ?? null,
        longitude: it.longitude ?? null,
        status: it.status?.toString() ?? null,
        assignedTo: assignees.join(', ') || null,
      });
    }
    matches.sort((a, b) => (a.date === b.date ? a.startTime.localeCompare(b.startTime) : a.date.localeCompare(b.date)));
  }

  // Collapse exact-slot duplicates FIRST: two records at the identical
  // date+time+address are almost certainly the same real-world visit even if
  // their title/contactName differ (e.g. a calendar-synced copy with an
  // auto-generated title alongside the native Routemize record) - without
  // this, they'd survive as two separate "distinct jobs" below and get shown
  // to the user as if they were different appointments to choose between.
  const seenSlots = new Set<string>();
  const slotDeduped = matches.filter((m) => {
    if (!m.address) return true;
    const slotKey = `${m.date}|${m.startTime}|${m.address.toLowerCase().trim()}`;
    if (seenSlots.has(slotKey)) return false;
    seenSlots.add(slotKey);
    return true;
  });

  // Collapse repeat occurrences of the SAME job (same address + title) into one
  // distinct job — a recurring "Job Stop" at one address is one job, not many.
  const jobMap = new Map<
    string,
    {
      title: string;
      address: string | null;
      contactName: string | null;
      latitude: number | null;
      longitude: number | null;
      occurrences: number;
      firstDate: string;
      firstDateLabel: string;
      lastDate: string;
      lastDateLabel: string;
      startTime: string;
      endTime: string;
      sampleAppointmentId: string;
    }
  >();
  for (const m of slotDeduped) {
    // Include contactName so two DIFFERENT customers who happen to share an
    // address/title (e.g. two units at one plaza) are never merged into "one job".
    const key = `${(m.address ?? '').toLowerCase().trim()}|${m.title.toLowerCase().trim()}|${(m.contactName ?? '').toLowerCase().trim()}`;
    const ex = jobMap.get(key);
    if (!ex) {
      jobMap.set(key, {
        title: m.title,
        address: m.address,
        contactName: m.contactName,
        latitude: m.latitude,
        longitude: m.longitude,
        occurrences: 1,
        firstDate: m.date,
        firstDateLabel: m.dateLabel,
        lastDate: m.date,
        lastDateLabel: m.dateLabel,
        startTime: m.startTime,
        endTime: m.endTime,
        sampleAppointmentId: m.appointmentId,
      });
    } else {
      ex.occurrences += 1;
      if (m.date < ex.firstDate) {
        ex.firstDate = m.date;
        ex.firstDateLabel = m.dateLabel;
        ex.startTime = m.startTime;
        ex.endTime = m.endTime;
        ex.sampleAppointmentId = m.appointmentId;
      }
      if (m.date > ex.lastDate) {
        ex.lastDate = m.date;
        ex.lastDateLabel = m.dateLabel;
      }
    }
  }
  const distinctJobs = [...jobMap.values()];

  return {
    count: slotDeduped.length,
    distinctJobCount: distinctJobs.length,
    searchedFrom: toDateKey(start),
    searchedFromLabel: formatDateKeyMedium(toDateKey(start)),
    searchedThrough: toDateKey(lastDay),
    searchedThroughLabel: formatDateKeyMedium(toDateKey(lastDay)),
    // Distinct jobs (deduped by address + title). PREFER these when the user
    // refers to "the job" — repeat days of the same site are one job.
    distinctJobs,
    appointments: slotDeduped.slice(0, 20),
    // Whether matches could come from EVERY team member's calendar (contact/
    // text search + manage-all permission) or only the one person searched.
    coverage: searchedWholeCompany
      ? "the WHOLE COMPANY - each match's assignedTo says whose calendar it is on"
      : `only ${whose} calendar`,
    note:
      matches.length === 0
        ? `No matching appointments in that window (this searched ${
            searchedWholeCompany ? "EVERY team member's calendar" : `${whose} calendar only`
          }; past dates are fully searchable). Try a wider range or different text before asking the user for the address/contact.`
        : distinctJobs.length === 1
          ? 'Only ONE distinct job (repeated across days) — this is almost certainly what the user means. Do NOT ask them to pick between its individual days; just use its address and continue (or confirm the single job once). The occurrences count/date range is for your awareness.'
          : "Multiple DISTINCT jobs found. If unsure which the user means, call present_appointments with ONE card per distinct job (not per day) so they can confirm, then continue.",
    dateNote:
      "Each date's weekday is in dateLabel (firstDateLabel/lastDateLabel on distinctJobs, searchedFromLabel/searchedThroughLabel for the range) - quote it VERBATIM (e.g. 'Wed, Sep 23'); never work out a weekday yourself.",
  };
}

async function execPresentAppointments(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const raw = Array.isArray(input.candidates) ? (input.candidates as Record<string, unknown>[]) : [];
  const mapped = raw
    .map((c) => ({
      appointmentId: typeof c.appointmentId === 'string' ? c.appointmentId : '',
      title: typeof c.title === 'string' ? c.title : 'Appointment',
      contactName: typeof c.contactName === 'string' ? c.contactName : undefined,
      date: typeof c.date === 'string' ? c.date : '',
      startTime: typeof c.startTime === 'string' ? c.startTime : '',
      endTime: typeof c.endTime === 'string' ? c.endTime : undefined,
      address: typeof c.address === 'string' ? c.address : undefined,
    }))
    .filter((c) => c.appointmentId && c.date && c.startTime);

  // Never show the same job twice. Three layers, all keeping the first seen:
  //  1. Exact appointmentId - trivially the same record.
  //  2. Same date+time+address - almost certainly the same real-world visit
  //     even when title/contactName differ (e.g. a synced calendar copy with
  //     an auto-generated title alongside the native Routemize record).
  //  3. Same address (or title when no address) + contact - a recurring job
  //     is one option; two different customers at one address/title are not.
  const seenIds = new Set<string>();
  const seenSlots = new Set<string>();
  const seenJobs = new Set<string>();
  const candidates = mapped
    .filter((c) => {
      if (seenIds.has(c.appointmentId)) return false;
      seenIds.add(c.appointmentId);

      if (c.address) {
        const slotKey = `${c.date}|${c.startTime}|${c.address.toLowerCase().trim()}`;
        if (seenSlots.has(slotKey)) return false;
        seenSlots.add(slotKey);
      }

      const jobKey = `${(c.address ?? c.title).toLowerCase().trim()}|${(c.contactName ?? '').toLowerCase().trim()}`;
      if (seenJobs.has(jobKey)) return false;
      seenJobs.add(jobKey);
      return true;
    })
    .slice(0, 3);

  if (candidates.length === 0) {
    return { shown: false, note: 'No valid candidates to present. Each needs appointmentId, date, and startTime.' };
  }

  deps.onArtifact?.({
    type: 'appointment_picker',
    prompt: input.prompt ? String(input.prompt) : undefined,
    purpose: input.purpose ? String(input.purpose) : undefined,
    allowNone: input.allowNone === undefined ? true : Boolean(input.allowNone),
    candidates,
  });
  return {
    shown: true,
    count: candidates.length,
    collapsedDuplicates: mapped.length - candidates.length,
    note:
      'Confirmation cards are shown to the user (exact/near-duplicate appointments were collapsed). Your ' +
      'text reply must ask which one they mean (e.g. "I found a couple of appointments around then - which ' +
      'one?") - never just "Done". Then STOP and wait for their tap; continue where you left off using the ' +
      "confirmed job's address - and remember the NEW stop goes in the timeframe the user asked for (e.g. " +
      'next week), not on the day of the job you just identified.',
  };
}

async function execPresentContacts(input: Record<string, unknown>, deps: RoutyToolDeps) {
  const raw = Array.isArray(input.candidates) ? (input.candidates as Record<string, unknown>[]) : [];
  const seenIds = new Set<string>();
  const candidates = raw
    .map((c) => ({
      contactId: typeof c.contactId === 'string' ? c.contactId : '',
      name: typeof c.name === 'string' ? c.name : 'Unknown',
      businessName: typeof c.businessName === 'string' ? c.businessName : undefined,
      phone: typeof c.phone === 'string' ? c.phone : undefined,
      address: typeof c.address === 'string' ? c.address : undefined,
    }))
    .filter((c) => c.contactId && c.name)
    .filter((c) => {
      if (seenIds.has(c.contactId)) return false;
      seenIds.add(c.contactId);
      return true;
    })
    .slice(0, 5);

  if (candidates.length === 0) {
    return { shown: false, note: 'No valid candidates to present. Each needs contactId and name.' };
  }

  deps.onArtifact?.({
    type: 'contact_picker',
    prompt: input.prompt ? String(input.prompt) : undefined,
    purpose: input.purpose ? String(input.purpose) : undefined,
    allowNone: input.allowNone === undefined ? true : Boolean(input.allowNone),
    candidates,
  });
  return {
    shown: true,
    count: candidates.length,
    note:
      'Contact cards are shown to the user. Your text reply must ask which one they mean (e.g. "I found a ' +
      'couple of contacts by that name - which one?") - never just "Done". Then STOP and wait for their tap; ' +
      "continue where you left off using the confirmed contact's contactId/address.",
  };
}

/**
 * Execute a tool by name. Returns a JSON-serializable result fed back to Claude
 * as the tool_result content. Errors are caught by the agent loop.
 */
export async function executeRoutyTool(
  name: string,
  input: Record<string, unknown>,
  deps: RoutyToolDeps,
): Promise<unknown> {
  switch (name) {
    case 'get_schedule':
      return execGetSchedule(input, deps);
    case 'audit_availability':
      return execAuditAvailability(input, deps);
    case 'audit_slot':
      return execAuditSlot(input, deps);
    case 'remove_calendar_events':
      return execRemoveCalendarEvents(input, deps);
    case 'find_contact':
      return execFindContact(input, deps);
    case 'find_team_member':
      return execFindTeamMember(input, deps);
    case 'request_missing_address':
      return execRequestMissingAddress(input, deps);
    case 'search_places':
      return execSearchPlaces(input, deps);
    case 'present_places':
      return execPresentPlaces(input, deps);
    case 'find_service_along_route':
      return execFindServiceAlongRoute(input, deps);
    case 'find_stop_slots':
      return execFindStopSlots(input, deps);
    case 'check_time_availability':
      return execCheckTimeAvailability(input, deps);
    case 'get_day_briefing':
      return execGetDayBriefing(input, deps);
    case 'get_daily_summary':
      return execGetDailySummary(input, deps);
    case 'get_week_report':
      return execGetWeekReport(input, deps);
    case 'get_appointment_brief':
      return execGetAppointmentBrief(input, deps);
    case 'reschedule_to_best_slot':
      return execRescheduleToBestSlot(input, deps);
    case 'optimize_route':
      return execOptimizeRoute(input, deps);
    case 'get_business_stats':
      return execGetBusinessStats(input, deps);
    case 'add_stop':
      return execAddStop(input, deps);
    case 'reschedule_appointment':
      return execRescheduleAppointment(input, deps);
    case 'update_appointment_location':
      return execUpdateAppointmentLocation(input, deps);
    case 'reassign_appointment':
      return execReassignAppointment(input, deps);
    case 'delay_remaining_appointments':
      return execDelayRemaining(input, deps);
    case 'move_day_appointments':
      return execMoveDayAppointments(input, deps);
    case 'update_appointment_status':
      return execUpdateStatus(input, deps);
    case 'add_multiple_stops':
      return execAddMultipleStops(input, deps);
    case 'plan_route':
      return execPlanRoute(input, deps);
    case 'repeat_stop':
      return execRepeatStop(input, deps);
    case 'block_time':
      return execBlockTime(input, deps);
    case 'block_days_off':
      return execBlockDaysOff(input, deps);
    case 'get_reschedule_options':
      return execGetRescheduleOptions(input, deps);
    case 'list_booking_forms':
      return execListBookingForms(input, deps);
    case 'find_pending_requests':
      return execFindPendingRequests(input, deps);
    case 'collect_booking':
      return execCollectBooking(input, deps);
    case 'create_booking_request':
      return execCreateBookingRequest(input, deps);
    case 'confirm_booking':
      return execConfirmBooking(input, deps);
    case 'smart_fill_gap':
      return execSmartFillGap(input, deps);
    case 'confirm_fill':
      return execConfirmFill(input, deps);
    case 'set_reminders':
      return execSetReminders(input, deps);
    case 'find_open_day':
      return execFindOpenDay(input, deps);
    case 'pick_date':
      return execPickDate(input, deps);
    case 'find_appointments':
      return execFindAppointments(input, deps);
    case 'find_recently_booked':
      return execFindRecentlyBooked(input, deps);
    case 'present_appointments':
      return execPresentAppointments(input, deps);
    case 'present_contacts':
      return execPresentContacts(input, deps);
    case 'get_contact_history':
      return execGetContactHistory(input, deps);
    case 'open_manual_booking':
      return execOpenManualBooking(input, deps);
    case 'contact_support':
      return execContactSupport(input, deps);
    case 'send_reschedule_link':
      return execSendRescheduleLink(input, deps);
    case 'collect_stops':
      return execCollectStops(input, deps);
    default:
      throw new Error(`Unknown tool: ${name}`);
  }
}
